import { CharacterCasing, DataType, InvariantDateTime, PopupSize, ShortcutTrigger, SummaryType, TimeZoneInfo, addToDate, addToDateOnly, compareArray, compareDate, compareDateOnly, compareDictionaries, createLocalEvent, createRegistryWithDefault, createSummaryTypes, dateOnlyToDateTime, dateOnlyToString, dateTimeToDateOnly, defaultFormatDateOnly, defaultFormatDateTime, defaultJsonReviver, endOf, endOfDateOnly, escapeHtml, formatDateTime, getArraySearchFunction, getBaseUrl, getDataTypeDefaultFormat, getFirstArrayElement, getGlobalResourceManager, getNumberArrayAverage, getNumberArraySum, getPropertyByPath, groupBy, isEnumDataType, isEquivalentValues, isNullOrEmpty, isNullOrWhiteSpace, isObject, postMessageToVsCode, remove, setPropertyByPath, startOf, startOfDateOnly, stringFormat, toCamelCase, toCamelCaseKey, toIso, toLowerCaseStart, toODataValue, updateViewTypeWithId } from "@neos/shared";
import { LRUCache } from "lru-cache";
import { Globalize } from "@mescius/wijmo";
import fileSaver from "file-saver";
import { createPlugin } from "seroval";
//#region src/api/apiClientRegistry.ts
var registry = createRegistryWithDefault("API client");
var registerApiClient = registry.register.bind(registry);
var getApiClient = registry.get.bind(registry);
function getApiClientBaseUrl(baseUrl) {
	return `${baseUrl}/webapi`;
}
//#endregion
//#region src/api/ApiParameterBinding.ts
/**
* Specifies the API parameter binding.
*/
var ApiParameterBinding = /* @__PURE__ */ function(ApiParameterBinding) {
	/**
	* Default
	*/
	ApiParameterBinding["default"] = "Default";
	/**
	* Query
	*/
	ApiParameterBinding["query"] = "Query";
	/**
	* Route
	*/
	ApiParameterBinding["route"] = "Route";
	/**
	* BodyProperty
	*/
	ApiParameterBinding["bodyProperty"] = "BodyProperty";
	/**
	* Body
	*/
	ApiParameterBinding["body"] = "Body";
	return ApiParameterBinding;
}({});
//#endregion
//#region src/errors/ServerMethodError.ts
/**
* Server method error
*/
var ServerMethodError = class extends Error {
	constructor(err) {
		super(err.detail ?? err.title);
		this.name = err.title;
		this.apiError = err;
	}
};
//#endregion
//#region src/viewModelMessage/ViewModelMessageType.ts
/**
* View model message type.
*/
var ViewModelMessageType = /* @__PURE__ */ function(ViewModelMessageType) {
	/** Info */
	ViewModelMessageType["info"] = "info";
	/** Positive */
	ViewModelMessageType["positive"] = "positive";
	/** Warning */
	ViewModelMessageType["warning"] = "warning";
	/** Danger */
	ViewModelMessageType["danger"] = "danger";
	return ViewModelMessageType;
}({});
//#endregion
//#region src/api/FileUploadState.ts
/**
* File upload state.
*/
var FileUploadState = /* @__PURE__ */ function(FileUploadState) {
	/** NotStarted */
	FileUploadState["notStarted"] = "NotStarted";
	/** InProgress */
	FileUploadState["inProgress"] = "InProgress";
	/** Failed */
	FileUploadState["failed"] = "Failed";
	/** Succeeded */
	FileUploadState["succeeded"] = "Succeeded";
	return FileUploadState;
}({});
//#endregion
//#region src/api/FileReference.ts
/**
* File reference.
*/
var FileReference = class FileReference {
	/**
	* Initializes a new instance of the FileReference class.
	* @param apiClient - API client.
	* @param url - Absolute file url.
	* @param value - Persisted value.
	* @param fileName - File name.
	* @param size - File size in bytes.
	*/
	constructor(apiClient, url, value, fileName, size) {
		this.uploadState = FileUploadState.notStarted;
		this._apiClient = apiClient;
		this.url = url;
		this.value = value;
		this.fileName = fileName;
		this.size = size;
	}
	/**
	* Creates a new instance from a value.
	* @param apiClient - API client.
	* @param value - Partial url.
	* @returns The instance.
	*/
	static fromValue(apiClient, value) {
		return new FileReference(apiClient, (value.includes("reporting/download/") ? FetchApiClient.getUrl(getBaseUrl(), value) : apiClient.getUrl(value)).toString(), value);
	}
	/**
	* Creates a new instance from a local file.
	* @param apiClient - API client.
	* @param localFileUrl - Local file URL (created with URL.createObjectURL)
	* @param fileName - File name.
	* @param size - File size in bytes.
	* @returns The instance.
	*/
	static fromFile(apiClient, localFileUrl, fileName, size) {
		return new FileReference(apiClient, localFileUrl, void 0, fileName, size);
	}
	/**
	* Creates a new instance from a content.
	* @param apiClient - API client.
	* @param fileName - File name.
	* @param content - File content.
	* @param contentType - File content type.
	* @returns The instance.
	*/
	static fromContent(apiClient, fileName, content, contentType) {
		return new FileReference(apiClient, URL.createObjectURL(new Blob([content], { type: contentType })), void 0, fileName, content.length);
	}
	/**
	* Creates a new instance from a binary content.
	* @param apiClient - API client.
	* @param fileName - File name.
	* @param content - File binary content.
	* @param contentType - File content type.
	* @returns The instance.
	*/
	static fromBinaryContent(apiClient, fileName, content, contentType) {
		return new FileReference(apiClient, URL.createObjectURL(new Blob([new Uint8Array(content)], { type: contentType })), void 0, fileName, content.length);
	}
	/**
	* Retries upload.
	*/
	retryUpload() {
		if (this.uploadState === FileUploadState.failed) this.startUpload();
	}
	/**
	* Starts upload.
	*/
	async startUpload() {
		this.uploadState = FileUploadState.inProgress;
		this.uploadPercentCompleted = void 0;
		const blob = await fetch(this.url).then((r) => r.blob());
		const data = new FormData();
		data.append("file", blob, this.fileName);
		const request = new XMLHttpRequest();
		request.open("POST", this._apiClient.getUrl("$neos/temp-storage/upload"));
		for (const [key, value] of Object.entries(this._apiClient.getHeaders())) request.setRequestHeader(key, value);
		request.upload.onprogress = (e) => {
			const percentCompleted = e.loaded / e.total * 100;
			this.uploadPercentCompleted = percentCompleted;
		};
		request.onload = async () => {
			if (request.status === 200) {
				this.uploadState = FileUploadState.succeeded;
				this.uploadPercentCompleted = void 0;
				this.value = JSON.parse(request.response);
			} else {
				this.uploadState = FileUploadState.failed;
				this.showErrorToast(request.status);
			}
			if (this.callback !== void 0) await this.callback(this);
		};
		request.onerror = async () => {
			this.uploadState = FileUploadState.failed;
			this.showErrorToast();
			if (this.callback !== void 0) await this.callback(this);
		};
		request.send(data);
	}
	showErrorToast(status = -1) {
		this._apiClient.eventManager.emit("viewModel:showToast", {
			buttons: [],
			dismissible: true,
			duration: 3e3,
			text: stringFormat(this._apiClient.globalResourceManager.get("FileUpload_FailedMessage"), status),
			title: this._apiClient.globalResourceManager.get("FileUpload_FailedTitle"),
			type: ViewModelMessageType.danger
		});
	}
	/**
	* Adds a callback method called when uploading has finished or failed.
	* @param callback Callback method.
	* @returns File reference.
	*/
	withCallback(callback) {
		this.callback = callback;
		return this;
	}
};
//#endregion
//#region src/enums/RelationType.ts
/**
* Specifies the relation type.
*/
var RelationType = /* @__PURE__ */ function(RelationType) {
	/**
	* The relation type is editable reference.
	*/
	RelationType["editableReference"] = "editableReference";
	/**
	* The relation type is reference.
	*/
	RelationType["reference"] = "reference";
	/**
	* The relation type is collection.
	*/
	RelationType["collection"] = "collection";
	return RelationType;
}({});
//#endregion
//#region src/viewModel/ValidationRule.ts
var ValidationRuleSeverityLevel = /* @__PURE__ */ function(ValidationRuleSeverityLevel) {
	/**
	* Information level.
	*/
	ValidationRuleSeverityLevel[ValidationRuleSeverityLevel["information"] = 0] = "information";
	/**
	* Warning level.
	*/
	ValidationRuleSeverityLevel[ValidationRuleSeverityLevel["warning"] = 1] = "warning";
	/**
	* Error level.
	*/
	ValidationRuleSeverityLevel[ValidationRuleSeverityLevel["error"] = 2] = "error";
	return ValidationRuleSeverityLevel;
}({});
var ValidationRuleValidationTypes = /* @__PURE__ */ function(ValidationRuleValidationTypes) {
	/**
	* all validation types.
	*/
	ValidationRuleValidationTypes[ValidationRuleValidationTypes["reportAll"] = 0] = "reportAll";
	/**
	* Information validation type.
	*/
	ValidationRuleValidationTypes[ValidationRuleValidationTypes["reportInformations"] = 1] = "reportInformations";
	/**
	* Warning validation type.
	*/
	ValidationRuleValidationTypes[ValidationRuleValidationTypes["reportWarnings"] = 2] = "reportWarnings";
	/**
	* Error validation type.
	*/
	ValidationRuleValidationTypes[ValidationRuleValidationTypes["reportErrors"] = 4] = "reportErrors";
	return ValidationRuleValidationTypes;
}({});
//#endregion
//#region src/viewModelProperty/ValidationMessage.ts
var ValidationMessage = class {
	constructor(severityLevel, text) {
		this.kind = "Custom";
		this.severityLevel = severityLevel;
		this.text = text;
	}
	withProperty(propertyName) {
		this.propertyName = propertyName;
		return this;
	}
};
var ConstraintValidationMessage = class extends ValidationMessage {
	constructor(propertyName, severityLevel, text, shortText) {
		super(severityLevel, text);
		this.kind = "Constraint";
		this.propertyName = propertyName;
		this.shortText = shortText;
	}
};
var ValidationRuleValidationMessage = class extends ValidationMessage {
	constructor(name, severityLevel, text) {
		super(severityLevel, text);
		this.kind = "ValidationRule";
		this.name = name;
	}
	static is(message) {
		return message.kind === "ValidationRule";
	}
};
//#endregion
//#region src/ai/AutoSuggestService.ts
/**
* Service to manage AI auto-suggestions.
* - Uses Model.hasOriginalValue() to determine if a property has been modified by the user
* - Avoids triggering auto-suggest during data loading
* - Avoids loops by ignoring modifications applied by the service itself
*/
var AutoSuggestService = class {
	constructor(applicationContext, apiClient) {
		this.pendingCalls = /* @__PURE__ */ new Map();
		this.isApplyingSuggestions = /* @__PURE__ */ new Set();
		this.applicationContext = applicationContext;
		this.apiClient = apiClient;
	}
	/**
	* Cancels a pending API call for a given ViewModel.
	* @param viewModelKey - Key of the ViewModel.
	*/
	cancelPendingCall(viewModelKey) {
		const pendingCall = this.pendingCalls.get(viewModelKey);
		if (pendingCall) {
			pendingCall.abortController.abort();
			this.pendingCalls.delete(viewModelKey);
		}
	}
	/**
	* Calls the auto-suggest API.
	* Does not call the API if the ViewModel is loading or if the service is already applying suggestions.
	* @param viewModel - ViewModel.
	* @param item - Modified model.
	* @param propertyName - Name of the modified property.
	* @param jsonModel - The complete model serialized in JSON.
	* @param previousValue - The previous value of the property before the change (optional).
	* @returns Promise of the response.
	*/
	async requestAutoSuggest(viewModel, item, propertyName, jsonModel, previousValue) {
		if (this.applicationContext.get("AutoSuggest") !== true) return null;
		const viewModelKey = this.getViewModelKey(viewModel, item);
		if (viewModel.loadingMeOrSomeParent || this.isApplyingSuggestions.has(viewModelKey)) return null;
		this.cancelPendingCall(viewModelKey);
		const currentValue = viewModel.getProperty(propertyName).getValue(item);
		if (this.isEmptyInput(currentValue)) {
			this.clearSuggestionsByOrigin(viewModel, item, propertyName);
			return null;
		}
		const abortController = new AbortController();
		try {
			const propertyPath = this.buildPropertyPath(viewModel, item, propertyName);
			let rootViewModel = viewModel;
			while (rootViewModel.parentViewModel) rootViewModel = rootViewModel.parentViewModel;
			const request = {
				uiViewName: rootViewModel.uiViewName,
				modifiedPropertyPath: propertyPath,
				jsonModel,
				previousValue
			};
			const promise = this.apiClient.callPostServerMethodAsync("$neos/ai/auto-suggest", ApiParameterBinding.body, request, void 0, true, abortController.signal);
			this.pendingCalls.set(viewModelKey, {
				promise,
				abortController,
				propertyPath
			});
			const response = await promise;
			this.pendingCalls.delete(viewModelKey);
			return response;
		} catch (error) {
			if (error instanceof Error && error.name === "AbortError") return null;
			this.pendingCalls.delete(viewModelKey);
			viewModel.logger.error("Error in auto-suggest API call:", error);
			return null;
		}
	}
	/**
	* Stores the received suggestions for the user to validate.
	* Suggestions are filtered and stored in the model, but are not applied directly.
	* Suggestions are ignored if:
	* - The property is not visible (getFormVisible returns false)
	* - The property is read-only (getReadOnly returns true)
	* - The current value differs from originalValue (the user has modified)
	* @param viewModel - ViewModel.
	* @param item - Model.
	* @param suggestions - List of suggestions.
	*/
	storeSuggestions(viewModel, item, suggestions, originPropertyName) {
		for (const suggestion of suggestions) this.storeSuggestion(viewModel, item, suggestion, originPropertyName);
	}
	/**
	* Stores a single suggestion in the model.
	* @param viewModel - ViewModel.
	* @param item - Model.
	* @param suggestion - Suggestion to store.
	* @returns true if the suggestion was stored, false otherwise.
	*/
	storeSuggestion(viewModel, item, suggestion, originPropertyName) {
		const property = viewModel.getProperty(suggestion.propertyName);
		if (!property.getFormVisible(item)) return false;
		if (property.getReadOnly(item)) return false;
		const currentValue = property.getValue(item);
		if (!this.areValuesEqual(currentValue, suggestion.originalValue)) return false;
		item.$appliedSuggestions.set(suggestion.propertyName, {
			suggestedValue: suggestion.value,
			text: suggestion.text,
			originPropertyName
		});
		viewModel.itemPropertyMetadataChangedEvent.raise({
			item,
			propertyName: suggestion.propertyName
		});
		return true;
	}
	/**
	* Indicates whether the service is applying suggestions for the ViewModel/item pair.
	* Allows the ViewModel to distinguish a user modification from a modification caused by auto-suggest.
	*/
	isApplying(viewModel, item) {
		const key = this.getViewModelKey(viewModel, item);
		return this.isApplyingSuggestions.has(key);
	}
	/**
	* Compares two values robustly (primitives and simple objects).
	*/
	areValuesEqual(a, b) {
		if (a == null && b == null) return true;
		if (a === b) return true;
		try {
			return JSON.stringify(a) === JSON.stringify(b);
		} catch {
			return false;
		}
	}
	/**
	* Gets a unique key for a ViewModel.
	* @param viewModel - ViewModel.
	* @param item - Model.
	* @returns Unique key.
	*/
	getViewModelKey(viewModel, item) {
		const transientKey = item?.$transientKey ?? "root";
		return `${viewModel.entityViewName}_${transientKey}`;
	}
	/**
	* Returns true if the value represents an empty user input.
	* Considers null/undefined and blank strings (after trim) as empty.
	*/
	isEmptyInput(value) {
		if (value == null) return true;
		if (typeof value === "string") return value.trim().length === 0;
		return false;
	}
	/**
	* Builds the complete property path in JSON Pointer format (RFC 6901).
	* Takes into account the hierarchy of child ViewModels and uses indices for collections.
	* @param viewModel - ViewModel.
	* @param item - Model.
	* @param propertyName - Name of the modified property.
	* @returns Path in JSON Pointer format (e.g.: /items/0/description).
	*/
	buildPropertyPath(viewModel, item, propertyName) {
		const pathSegments = [];
		let currentViewModel = viewModel;
		let currentItem = item;
		while (currentViewModel) if (currentViewModel.parentViewModel && currentViewModel.parentRelation) {
			const relationName = currentViewModel.parentRelation.name;
			const parentVM = currentViewModel.parentViewModel;
			const parentItem = parentVM?.current;
			const itemIndex = this.findItemIndex(parentItem, currentItem, relationName);
			if (itemIndex !== void 0) pathSegments.unshift(itemIndex.toString());
			pathSegments.unshift(relationName);
			currentViewModel = parentVM;
			currentItem = parentItem;
		} else break;
		pathSegments.push(propertyName);
		return `/${pathSegments.join("/")}`;
	}
	/**
	* Finds the index of an item in a parent collection.
	* @param parentItem - Parent model.
	* @param itemToFind - Item to find.
	* @param relationName - Name of the relation/collection.
	* @returns Index of the item, or undefined if not found.
	*/
	findItemIndex(parentItem, itemToFind, relationName) {
		if (!parentItem || !itemToFind) return;
		const collection = parentItem[relationName];
		if (Array.isArray(collection)) return collection.indexOf(itemToFind);
		if (collection && typeof collection === "object" && Symbol.iterator in collection) {
			let index = 0;
			for (const item of collection) {
				if (item === itemToFind) return index;
				index++;
			}
		}
	}
	/**
	* Serializes the complete model from the root ViewModel to JSON.
	* Goes up to the root ViewModel and serializes the entire datasource being entered.
	* @param viewModel - ViewModel (can be child or root).
	* @returns Complete serialized model, or null if empty.
	*/
	serializeModel(viewModel) {
		let rootViewModel = viewModel;
		while (rootViewModel.parentViewModel) rootViewModel = rootViewModel.parentViewModel;
		const currentItem = rootViewModel.current;
		if (!currentItem) return null;
		const visited = /* @__PURE__ */ new WeakSet();
		return this.serializeModelItem(currentItem, visited);
	}
	/**
	* Serializes a model item to JSON including all its properties and relations.
	* @param item - Item to serialize.
	* @param viewModel - ViewModel containing property definitions.
	* @returns Serialized item.
	*/
	/**
	* Serializes a relation (property or collection).
	* @param relationValue - Value of the relation.
	* @param visited - WeakSet to track visited objects and avoid circular references.
	* @param viewModel - ViewModel for serializing children (optional).
	* @returns Serialized relation.
	*/
	serializeRelation(relationValue, visited) {
		if (!relationValue) return relationValue;
		if (Array.isArray(relationValue)) return relationValue.map((item) => this.serializeValue(item, visited));
		return this.serializeValue(relationValue, visited);
	}
	/**
	* Serializes a value (can be an object, primitive, etc).
	* @param value - Value to serialize.
	* @param visited - WeakSet to track visited objects and avoid circular references.
	* @param viewModel - ViewModel for serializing models (optional).
	* @returns Serialized value.
	*/
	serializeValue(value, visited) {
		if (!value || typeof value !== "object") return value;
		if (value instanceof Model) return this.serializeModelItem(value, visited);
		return value;
	}
	/**
	* Serializes the properties and relations of a Model.
	* @param model - Model to serialize.
	* @param visited - WeakSet to track visited objects and avoid circular references.
	* @returns Serialized model.
	*/
	serializeModelItem(model, visited) {
		if (visited.has(model)) return null;
		visited.add(model);
		const result = {};
		const modelObj = model;
		const relationNames = new Set(model.$relations.keys());
		for (const [key, value] of Object.entries(modelObj)) if (!key.startsWith("$") && !relationNames.has(key)) result[key] = this.serializeValue(value, visited);
		for (const [relationName] of model.$relations) {
			const relationValue = modelObj[relationName];
			result[relationName] = this.serializeRelation(relationValue, visited);
		}
		return result;
	}
	/**
	* Clears suggestions that originated from the given property.
	*/
	clearSuggestionsByOrigin(viewModel, item, originPropertyName) {
		const entries = Array.from(item.$appliedSuggestions.entries());
		for (const [suggestedPropertyName, suggestion] of entries) if (suggestion.originPropertyName === originPropertyName) {
			item.$appliedSuggestions.delete(suggestedPropertyName);
			viewModel.itemPropertyMetadataChangedEvent.raise({
				item,
				propertyName: suggestedPropertyName
			});
		}
	}
	/**
	* Accepts a suggestion and applies it to the model property.
	* @param viewModel - ViewModel.
	* @param item - Model.
	* @param propertyName - Name of the property.
	* @returns true if the suggestion was accepted and applied, false otherwise.
	*/
	acceptSuggestion(viewModel, item, propertyName) {
		const suggestion = item.$appliedSuggestions.get(propertyName);
		if (!suggestion) return false;
		const property = viewModel.properties[propertyName];
		if (!property) return false;
		const viewModelKey = this.getViewModelKey(viewModel, item);
		this.isApplyingSuggestions.add(viewModelKey);
		try {
			property.setValue(item, suggestion.suggestedValue);
			item.$appliedSuggestions.delete(propertyName);
			return true;
		} finally {
			this.isApplyingSuggestions.delete(viewModelKey);
		}
	}
	/**
	* Rejects a suggestion by removing it without applying it.
	* @param viewModel - ViewModel.
	* @param item - Model.
	* @param propertyName - Name of the property.
	* @returns true if the suggestion was rejected, false otherwise.
	*/
	rejectSuggestion(viewModel, item, propertyName) {
		if (!item.$appliedSuggestions.get(propertyName)) return false;
		item.$appliedSuggestions.delete(propertyName);
		viewModel.itemPropertyMetadataChangedEvent.raise({
			item,
			propertyName
		});
		return true;
	}
	/**
	* Cleans up the service resources.
	*/
	dispose() {
		for (const [key] of this.pendingCalls) this.cancelPendingCall(key);
		this.pendingCalls.clear();
		this.isApplyingSuggestions.clear();
	}
};
//#endregion
//#region src/api/ApiErrorMessageOverrider.ts
/**
* Specifies the API error request type.
*/
var ApiErrorRequestType = /* @__PURE__ */ function(ApiErrorRequestType) {
	/**
	* Load.
	*/
	ApiErrorRequestType[ApiErrorRequestType["Load"] = 0] = "Load";
	/**
	* Save.
	*/
	ApiErrorRequestType[ApiErrorRequestType["Save"] = 1] = "Save";
	/**
	* Delete.
	*/
	ApiErrorRequestType[ApiErrorRequestType["Delete"] = 2] = "Delete";
	return ApiErrorRequestType;
}({});
/**
* Represents a list of API message overrides for replacing an API error title or message with a new one.
*/
var ApiErrorMessageOverrider = class {
	/**
	* Overrides the message of an API error with new one if a replacement is defined.
	* @param globalResourceManager - Global resource manager.
	* @template T Model type.
	* @param apiError API error.
	*/
	static overrideMessage(globalResourceManager, apiError, requestType) {
		for (const override of this.overrides) if (override.checkCondition(apiError, requestType)) {
			if (override.getReplacementTitle !== void 0) apiError.title = override.getReplacementTitle(globalResourceManager, apiError);
			if (override.getReplacementMessage !== void 0) {
				apiError.extensions.set("advancedText", apiError.detail);
				apiError.detail = override.getReplacementMessage(globalResourceManager, apiError);
			}
		}
	}
	static {
		this.overrides = [
			{
				name: "concurrency",
				checkCondition: (apiError) => apiError.instance === "/concurrency",
				getReplacementMessage: (globalResourceManager) => globalResourceManager.get("ConcurrencyError")
			},
			{
				name: "delete-not-found",
				checkCondition: (apiError, requestType) => apiError.type.endsWith("/entity-fetch-failed") && requestType === 2,
				getReplacementTitle: (globalResourceManager) => globalResourceManager.get("NotFoundOnDeleteErrorTitle"),
				getReplacementMessage: (globalResourceManager) => globalResourceManager.get("NotFoundOnDeleteErrorMessage")
			},
			{
				name: "load-not-found",
				checkCondition: (apiError, requestType) => apiError.type.endsWith("/entity-fetch-failed") && requestType === 0,
				getReplacementTitle: (globalResourceManager) => globalResourceManager.get("NotFoundOnLoadErrorTitle"),
				getReplacementMessage: (globalResourceManager) => globalResourceManager.get("NotFoundOnLoadErrorMessage")
			},
			{
				name: "save-not-found",
				checkCondition: (apiError, requestType) => apiError.type.endsWith("/entity-fetch-failed") && requestType === 1,
				getReplacementTitle: (globalResourceManager) => globalResourceManager.get("NotFoundOnSaveErrorTitle"),
				getReplacementMessage: (globalResourceManager) => globalResourceManager.get("NotFoundOnSaveErrorMessage")
			}
		];
	}
};
//#endregion
//#region src/events/MenuItemEventManager.ts
/**
* Global variable
*/
var currentMenuItemName;
/**
* Executing a callback while setting a **global variable** for the duration of
* its execution.
*
* @remarks - This is used to keep track of the menu item that triggered a given
* code execution (when the menu item type is 'Execute code').
*/
function runWithGlobalMenuItemContext(menuItemName, fn) {
	const previousMenuItemName = currentMenuItemName;
	currentMenuItemName = menuItemName;
	try {
		return fn();
	} finally {
		currentMenuItemName = previousMenuItemName;
	}
}
/**
* Consuming the information from the **global variable** and applying it to the
* given navigation options (if not already set).
*
* @param options - Navigation options to be enriched with the menu item id.
*/
function applyGlobalMenuItemContext(options) {
	if (!options.menuItemName && currentMenuItemName) options.menuItemName = currentMenuItemName;
}
//#endregion
//#region src/events/ToggleOverlayOptions.ts
var ToggleOverlayOptions = class {
	constructor(id) {
		this.id = id;
	}
	get state() {
		return this._state;
	}
	get target() {
		return this._target;
	}
	get event() {
		return this._event;
	}
	withState(state) {
		this._state = state;
		return this;
	}
	withTarget(target) {
		this._target = target;
		return this;
	}
	withEvent(event) {
		this._event = event;
		return this;
	}
};
//#endregion
//#region src/events/ui.ts
var currentUIEvent;
async function runWithEvent(uiEvent, fn) {
	currentUIEvent = uiEvent;
	try {
		await fn();
	} finally {
		currentUIEvent = void 0;
	}
}
function navigate(eventManager, options) {
	applyGlobalMenuItemContext(options);
	eventManager.emit("viewModel:navigate", options);
}
function toggleOverlay(viewModel, idOrOptions) {
	viewModel.eventManager.emit("overlay:toggle", {
		viewModel,
		options: typeof idOrOptions === "string" ? new ToggleOverlayOptions(idOrOptions).withEvent(currentUIEvent) : idOrOptions
	});
}
function showToast(eventManager, type, title, text, duration = 3e3, dismissible = true, ...buttons) {
	eventManager.emit("viewModel:showToast", {
		buttons,
		dismissible,
		duration,
		text,
		title,
		type
	});
}
//#endregion
//#region src/viewModel/ViewModelError.ts
/**
* View model error.
*/
var ViewModelError = class ViewModelError {
	constructor(title, text) {
		this.title = title;
		this.text = text;
	}
	static fromApiError(globalResourceManager, apiError) {
		if (Object.keys(apiError).length === 0) return new ViewModelError(globalResourceManager.get("ErrorHasOccurred"), "Invalid type system usage. Please check the error handling code.");
		if (apiError.status === 401) return {
			title: globalResourceManager.get("ExpiredSessionErrorTitle"),
			text: globalResourceManager.get("ExpiredSessionErrorText"),
			additionalData: { Status: apiError.status.toString() }
		};
		let error;
		if (apiError.errors[0]?.technical) error = {
			title: globalResourceManager.get("TechnicalErrorHasOccurred"),
			text: apiError.title,
			advancedText: apiError.errors[0].extensions.get("exception")
		};
		else if (apiError.technical) error = {
			title: globalResourceManager.get("TechnicalErrorHasOccurred"),
			text: apiError.title,
			advancedText: apiError.detail
		};
		else error = {
			title: apiError.detail ? apiError.title : globalResourceManager.get("ErrorHasOccurred"),
			text: apiError.detail ?? apiError.title,
			advancedText: apiError.extensions.get("advancedText"),
			additionalData: { status: apiError.status.toString() }
		};
		error.errors = apiError.errors.filter((e) => !e.technical).map((e) => e.detail ?? e.title);
		error.advancedErrors = apiError.errors.filter((e) => e.technical).map((e) => e.detail ?? e.title);
		error.additionalData = {
			Status: apiError.status.toString(),
			Path: apiError.path,
			"Trace ID": apiError.traceId
		};
		return error;
	}
};
//#endregion
//#region src/viewModelMessage/ViewModelMessageButton.ts
/**
* View model message button.
*/
var ViewModelMessageButton = class {
	constructor(type, customId, customText, icon, autofocus = false, isDisabled) {
		this.type = type;
		this.customId = customId;
		this.customText = customText;
		this.icon = icon;
		this.autofocus = autofocus;
		this.isDisabled = isDisabled ?? (() => false);
	}
};
//#endregion
//#region src/viewModelMessage/ViewModelMessageButtonType.ts
/**
* View model message button type.
*/
var ViewModelMessageButtonType = /* @__PURE__ */ function(ViewModelMessageButtonType) {
	/** Ok */
	ViewModelMessageButtonType["ok"] = "ok";
	/** Cancel */
	ViewModelMessageButtonType["cancel"] = "cancel";
	/** Yes */
	ViewModelMessageButtonType["yes"] = "yes";
	/** No */
	ViewModelMessageButtonType["no"] = "no";
	/** Custom */
	ViewModelMessageButtonType["custom"] = "custom";
	return ViewModelMessageButtonType;
}({});
//#endregion
//#region src/baseComponentViewModel/BaseComponentViewModel.ts
var selectFileEvent = "viewModel:selectFile";
/**
* Represents a base view model for UIViews and UIComponents.
*/
var BaseComponentViewModel = class {
	/**
	* Initializes a new instance of the ViewModelComponent class.
	* @param applicationContext - Application context.
	* @param eventManager - Event manager.
	* @param localizator - Localizator.
	* @param globalResourceManager - Global resource manager.
	* @param apiClient - API client.
	* @param notifications - Notifications.
	* @param logger - Logger.
	* @param resourceManager - Resource manager.
	*/
	constructor(applicationContext, eventManager, localizator, globalResourceManager, apiClient, notifications, logger, resourceManager, wrapInstance) {
		this.contextData = Object.freeze(/* @__PURE__ */ new Map());
		this.onInitialized = void 0;
		this.onDisposed = void 0;
		this.applicationContext = applicationContext;
		this.eventManager = eventManager;
		this.localizator = localizator;
		this.globalResourceManager = globalResourceManager;
		this.apiClient = apiClient;
		this.notifications = notifications;
		this.logger = logger;
		this.resourceManager = resourceManager;
		this.wrapInstance = wrapInstance ?? ((i) => i);
	}
	init() {
		this.initFields();
		this._unsubscribeConnectionId = this.notifications.connection.subscribeConnectionId((connectionId) => this.apiClient.setHeader("ConnectionId", connectionId));
	}
	initFields() {}
	dispose() {
		this.notifications.clearHandlers();
		this._unsubscribeConnectionId?.();
	}
	/**
	* Triggers an event to display a message.
	*
	* @param type - Type.
	* @param title - Title.
	* @param text - Text.
	* @param buttons - Buttons.
	* @returns Message response.
	*/
	showMessageAsync(type, title, text, ...buttons) {
		return new Promise((resolve) => {
			buttons = buttons.length > 0 ? buttons : [new ViewModelMessageButton(ViewModelMessageButtonType.ok, void 0, void 0, "validate", false)];
			this.eventManager.emit("viewModel:showMessage", {
				type,
				title,
				text,
				buttons: buttons.map((button) => ({
					...button,
					text: button.customText ?? this.globalResourceManager.get(`MessageButton_${button.type}`),
					onClick: () => resolve({
						buttonType: button.type,
						buttonCustomId: button.customId
					})
				})),
				dismissible: false,
				onDismiss: () => resolve({ buttonType: ViewModelMessageButtonType.cancel })
			});
		});
	}
	/**
	* Triggers an event to display a message with an input for the user to complete.
	* @param type - Type.
	* @param title - Title.
	* @param text - Text.
	* @param validateInput - Function returning an error message to display near the input when the input value is invalid.
	* @param buttons - Buttons.
	* @returns Message response containing the input value.
	*/
	showMessageWithInputAsync(type, title, text, validateInput, ...buttons) {
		const message = {
			type,
			title,
			text,
			dismissible: buttons.some((b) => b.type === ViewModelMessageButtonType.cancel),
			input: { validateInput },
			buttons: []
		};
		buttons = buttons.length > 0 ? buttons : [new ViewModelMessageButton(ViewModelMessageButtonType.ok, void 0, void 0, "validate", false, (inputValue) => validateInput?.(inputValue) != null)];
		return new Promise((resolve) => {
			message.dismissible = false;
			message.buttons = buttons.map((button) => ({
				...button,
				text: button.customText ?? this.globalResourceManager.get(`MessageButton_${button.type}`),
				onClick: () => resolve({
					buttonType: button.type,
					buttonCustomId: button.customId,
					inputValue: message.input?.value
				})
			}));
			message.onDismiss = () => resolve({ buttonType: ViewModelMessageButtonType.cancel });
			this.eventManager.emit("viewModel:showMessage", message);
		});
	}
	/**
	* Triggers an event to display a toast.
	*
	* @param type - Type.
	* @param title - Title.
	* @param text - Text.
	* @param duration - Duration.
	* @param dismissible - Value indicating whether the toast is dismissible.
	* @param buttons - Buttons to display in the toast.
	*/
	showToast(type, title, text, duration = 3e3, dismissible = true, ...buttons) {
		return showToast(this.eventManager, type, title, text, duration, dismissible, ...buttons);
	}
	/**
	* Triggers an event to display an error.
	*
	* @param error - Error.
	*/
	showError(error) {
		this.eventManager.emit("viewModel:showError", error);
	}
	showDevelopmentError(error) {
		if (this.applicationContext.getValue("ClusterInProduction", false)) this.logger.error(error instanceof Error ? error.message : error.text);
		else this.showError(error);
	}
	/**
	* Displays an API error.
	*/
	showApiError(err, requestType) {
		ApiErrorMessageOverrider.overrideMessage(this.globalResourceManager, err, requestType);
		this.showError(ViewModelError.fromApiError(this.globalResourceManager, err));
	}
	/**
	* Selects a file and immediately uploads it.
	* @param maxSizeInBytes - max file size in bytes.
	* @param types - accepted types.
	* @returns A reference to the selected file or null.
	*/
	selectFileAsync(maxSizeInBytes, types) {
		return this.selectFile(maxSizeInBytes, types, true);
	}
	/**
	* Selects a file to upload later.
	* @param maxSizeInBytes - max file size in bytes.
	* @param types Accepted types.
	* @returns A reference to the selected file or null.
	*/
	selectDeferredFileAsync(maxSizeInBytes, types) {
		return this.selectFile(maxSizeInBytes, types, false);
	}
	/**
	* Creates a promise for obtaining a file reference.
	* @param maxSizeInBytes - max file size in bytes.
	* @param types Accepted types.
	* @param startUpload Indicates whether the upload should be started immediately.
	* @returns File reference promise.
	*/
	selectFile(maxSizeInBytes, types, startUpload) {
		return new Promise((resolve) => {
			this.eventManager.emit(selectFileEvent, {
				maxSize: maxSizeInBytes,
				types,
				onOk: (name, size, objectURL) => {
					let reference = FileReference.fromFile(this.apiClient, objectURL, name, size);
					reference = this.wrapInstance(reference);
					if (startUpload) reference.startUpload();
					resolve(reference);
				},
				onCancel: () => {
					resolve(null);
				}
			});
		});
	}
	/**
	* Run expression and log
	* @param expressionName - name of expression to log
	* @param action - expression to run
	* @param args - args
	* @returns
	*/
	async runExpressionAndLogAsync(expressionName, action, args) {
		let result;
		if (!action) return result;
		const startTime = performance.now();
		try {
			if (args) result = await action.call(this, args);
			else result = await action.call(this);
			this.logPerformance(expressionName, startTime, result);
		} catch (error) {
			this.logPerformance(expressionName, startTime, void 0, error);
		}
		return result;
	}
	/**
	* Log the events
	*
	* @param methodName - method name
	* @param startTimePerformance - start time performance
	* @param error - message error
	*/
	logPerformance(methodName, startTimePerformance, result, error) {
		const elapsedTime = (performance.now() - startTimePerformance).toFixed(1);
		if (error) {
			this.logger.error(`${methodName} failed (${elapsedTime} ms) - ${error.message}`);
			error.message = `${this.logger.name} ${methodName} failed - ${error.message}`;
			this.showError(error);
		} else if (this.logger.isDebugEnabled) {
			let message = `${methodName} success (${elapsedTime} ms)`;
			if (result !== void 0 && typeof result !== "object") message += ` - ${result}`;
			this.logger.debug(message);
		}
	}
	/**
	* Executes a server data export.
	* @param options - The options.
	*/
	async exportServerDataAsync(options) {
		const response = await this.apiClient.postAsync({
			path: "$neos/exports/execute",
			data: {
				entityViewName: options.entityViewName,
				uiViewName: options.uiViewName,
				fileName: options.fileName,
				format: options.format,
				properties: options.properties,
				filter: options.filter,
				orderBy: options.orderBy,
				entityViewParameters: options.entityViewParameters,
				culture: options.culture,
				timeZone: options.timeZone,
				header: options.header,
				csvOptions: options.csvOptions
			}
		});
		if (!response.succeeded) this.showApiError(response.error);
	}
};
//#endregion
//#region src/filter/FilterComponent.ts
var FilterComponent = /* @__PURE__ */ function(FilterComponent) {
	FilterComponent["default"] = "default";
	FilterComponent["calendarDay"] = "calendarDay";
	FilterComponent["calendarMonth"] = "calendarMonth";
	FilterComponent["calendarYear"] = "calendarYear";
	return FilterComponent;
}({});
function moduleFilterComponentToCoreFilterComponentView(uiFilterComponentView) {
	switch (uiFilterComponentView) {
		case "CalendarDay": return "calendarDay";
		case "CalendarMonth": return "calendarMonth";
		case "CalendarYear": return "calendarYear";
		default: return "default";
	}
}
//#endregion
//#region src/filter/FilterOperator.ts
var FilterOperator = /* @__PURE__ */ function(FilterOperator) {
	FilterOperator["equal"] = "eq";
	FilterOperator["notEqual"] = "ne";
	FilterOperator["greater"] = "gt";
	FilterOperator["greaterOrEqual"] = "ge";
	FilterOperator["less"] = "lt";
	FilterOperator["lessOrEqual"] = "le";
	FilterOperator["in"] = "in";
	FilterOperator["notIn"] = "notin";
	FilterOperator["isNotNull"] = "isnotnull";
	FilterOperator["isNull"] = "isnull";
	FilterOperator["between"] = "between";
	FilterOperator["contains"] = "contains";
	FilterOperator["startsWith"] = "startswith";
	FilterOperator["endsWith"] = "endswith";
	FilterOperator["isMatch"] = "search.ismatch";
	return FilterOperator;
}({});
var moduleToCoreFilterOperatorMap = {
	Equal: "eq",
	NotEqual: "ne",
	Greater: "gt",
	GreaterOrEqual: "ge",
	Less: "lt",
	LessOrEqual: "le",
	In: "in",
	NotIn: "notin",
	IsNotNull: "isnotnull",
	IsNull: "isnull",
	Between: "between",
	Contains: "contains",
	StartsWith: "startswith",
	EndsWith: "endswith"
};
var coreToModuleFilterOperatorMap = Object.fromEntries(Object.entries(moduleToCoreFilterOperatorMap).map(([moduleFilterOperator, coreFilterOperator]) => [coreFilterOperator, moduleFilterOperator]));
function moduleFilterOperatorToCoreFilterOperator(uiCustomizationFilterOperatorType) {
	return uiCustomizationFilterOperatorType == null ? null : moduleToCoreFilterOperatorMap[uiCustomizationFilterOperatorType] ?? uiCustomizationFilterOperatorType;
}
function coreFilterOperatorToModuleFilterOperator(filterOperator) {
	return filterOperator == null ? null : coreToModuleFilterOperatorMap[filterOperator] ?? filterOperator;
}
var equalMetadata = {
	get caption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_Equal");
	},
	get lowerCaseCaption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_Equal_LowerCase");
	},
	image: "equal"
};
var notEqualMetadata = {
	get caption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_Different");
	},
	get lowerCaseCaption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_Different_LowerCase");
	},
	image: "notEqual"
};
var isNullMetadata = {
	get caption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_IsNull");
	},
	get lowerCaseCaption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_IsNull_LowerCase");
	},
	image: "isNull"
};
var isNotNullMetadata = {
	get caption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_IsNotNull");
	},
	get lowerCaseCaption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_IsNotNull_LowerCase");
	},
	image: "isNotNull"
};
var containsMetadata = {
	get caption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_Contains");
	},
	get lowerCaseCaption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_Contains_LowerCase");
	},
	image: "contains"
};
var startsWithMetadata = {
	get caption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_StartsWith");
	},
	get lowerCaseCaption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_StartsWith_LowerCase");
	},
	image: "startsWith"
};
var endsWithMetadata = {
	get caption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_EndsWith");
	},
	get lowerCaseCaption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_EndsWith_LowerCase");
	},
	image: "endsWith"
};
var greaterMetadata = {
	get caption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_Greater");
	},
	get lowerCaseCaption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_Greater_LowerCase");
	},
	image: "greater"
};
var lessMetadata = {
	get caption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_Less");
	},
	get lowerCaseCaption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_Less_LowerCase");
	},
	image: "less"
};
var greaterOrEqualMetadata = {
	get caption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_GreaterOrEqual");
	},
	get lowerCaseCaption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_GreaterOrEqual_LowerCase");
	},
	image: "greaterOrEqual"
};
var lessOrEqualMetadata = {
	get caption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_LessOrEqual");
	},
	get lowerCaseCaption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_LessOrEqual_LowerCase");
	},
	image: "lessOrEqual"
};
var betweenMetadata = {
	get caption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_Between");
	},
	get lowerCaseCaption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_Between_LowerCase");
	},
	image: "between"
};
var inMetadata = {
	get caption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_In");
	},
	get lowerCaseCaption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_In_LowerCase");
	},
	image: "in"
};
var notInMetadata = {
	get caption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_NotIn");
	},
	get lowerCaseCaption() {
		return getGlobalResourceManager(void 0).get("FilterOperator_NotIn_LowerCase");
	},
	image: "notIn"
};
var nullableOperators = {
	["isnotnull"]: isNotNullMetadata,
	["isnull"]: isNullMetadata
};
var booleanOperators = { ["eq"]: equalMetadata };
var stringOperators = {
	["contains"]: containsMetadata,
	["startswith"]: startsWithMetadata,
	["endswith"]: endsWithMetadata,
	["eq"]: equalMetadata,
	["ne"]: notEqualMetadata,
	["in"]: inMetadata,
	["notin"]: notInMetadata
};
var guidOperators = {
	["eq"]: equalMetadata,
	["ne"]: notEqualMetadata,
	["in"]: inMetadata,
	["notin"]: notInMetadata
};
var numberOperators = {
	["eq"]: equalMetadata,
	["ne"]: notEqualMetadata,
	["gt"]: greaterMetadata,
	["lt"]: lessMetadata,
	["ge"]: greaterOrEqualMetadata,
	["le"]: lessOrEqualMetadata,
	["between"]: betweenMetadata,
	["in"]: inMetadata,
	["notin"]: notInMetadata
};
var dateOperators = {
	["eq"]: equalMetadata,
	["ne"]: notEqualMetadata,
	["gt"]: greaterMetadata,
	["lt"]: lessMetadata,
	["ge"]: greaterOrEqualMetadata,
	["le"]: lessOrEqualMetadata,
	["between"]: betweenMetadata
};
var enumOperators = {
	["eq"]: equalMetadata,
	["ne"]: notEqualMetadata,
	["in"]: inMetadata,
	["notin"]: notInMetadata
};
var localizableStringOperators = {
	["contains"]: containsMetadata,
	["startswith"]: startsWithMetadata,
	["endswith"]: endsWithMetadata,
	["eq"]: equalMetadata,
	["ne"]: notEqualMetadata,
	["in"]: inMetadata,
	["notin"]: notInMetadata
};
var allOperators = {
	["eq"]: equalMetadata,
	["ne"]: notEqualMetadata,
	["gt"]: greaterMetadata,
	["ge"]: greaterOrEqualMetadata,
	["lt"]: lessMetadata,
	["le"]: lessOrEqualMetadata,
	["in"]: inMetadata,
	["notin"]: notInMetadata,
	["isnotnull"]: isNotNullMetadata,
	["isnull"]: isNullMetadata,
	["between"]: betweenMetadata,
	["contains"]: containsMetadata,
	["startswith"]: startsWithMetadata,
	["endswith"]: endsWithMetadata
};
/**
* Gets allowed operators for specified type.
* @param dataType The type of filtered property.
* @param isNullable A value indicating whether the property can be nullable.
* @returns A new object with the allowed operators.
*/
function getAllowedOperators(dataType, isNullable) {
	let operators;
	switch (dataType) {
		case DataType.decimal:
		case DataType.integer:
		case DataType.autoIncrementedInteger:
		case DataType.long:
			operators = numberOperators;
			break;
		case DataType.date:
		case DataType.dateTime:
		case DataType.invariantDateTime:
			operators = dateOperators;
			break;
		case DataType.boolean:
			operators = booleanOperators;
			break;
		case DataType.enum:
		case DataType.dataType:
			operators = enumOperators;
			break;
		case DataType.localizableString:
			operators = localizableStringOperators;
			break;
		case DataType.string:
			operators = stringOperators;
			break;
		case DataType.guid:
			operators = guidOperators;
			break;
		default:
			operators = {};
			break;
	}
	if (isNullable) return {
		...operators,
		...nullableOperators
	};
	return operators;
}
function isStandardFilterOperator(operator) {
	return Object.values(FilterOperator).includes(operator);
}
function isFilterOperatorWithoutValue(operator) {
	return operator === "isnull" || operator === "isnotnull";
}
function isArrayFilterOperator(operator) {
	return operator === "in" || operator === "notin";
}
var lookupValuePropertyOperators = new Set([
	"isnull",
	"isnotnull",
	"eq",
	"ne",
	"in",
	"notin"
]);
function isFilterOperatorOnLookupValueProperty(operator) {
	return lookupValuePropertyOperators.has(operator);
}
//#endregion
//#region src/entityViewParameters/ViewModelEntityViewParameter.ts
var ViewModelEntityViewParameter = class {
	constructor(owner) {
		this.required = false;
		this.filterable = false;
		this.filterRequired = false;
		this.filterRequirementUnfulfilled = false;
		this.filterVisible = false;
		this.filterPosition = -1;
		this.filterVisibleInPanel = false;
		this.filterPositionInPanel = -1;
		this.filterDefaultOperator = FilterOperator.equal;
		this._filterComponent = FilterComponent.default;
		this._userFilterComponent = null;
		this.saveInCustomView = false;
		this.owner = owner;
	}
	get caption() {
		if (this._caption) return this._caption;
		return this.owner.resourceManager.find(`entityViewParameters.${this.name}.caption`) ?? this.name;
	}
	set caption(value) {
		this._caption = value;
	}
	get filterCaption() {
		return this.caption;
	}
	get filterComponent() {
		return this._filterComponent;
	}
	set filterComponent(value) {
		this._filterComponent = value;
		this._userFilterComponent = null;
	}
	get userFilterComponent() {
		return this._userFilterComponent ?? this.filterComponent;
	}
	set userFilterComponent(value) {
		this._userFilterComponent = value;
	}
	getFilterDataType() {
		return this.dataType;
	}
	getFilterEnumMembers() {
		return this.enum?.getEnumMembers() ?? [];
	}
	getFilterOperators() {
		return { [this.filterDefaultOperator]: allOperators[this.filterDefaultOperator] };
	}
	getFilterFormat() {
		return getDataTypeDefaultFormat(this.getFilterDataType());
	}
	getFilterFormatOptions() {
		return {
			dataType: this.getFilterDataType(),
			format: this.getFilterFormat(),
			enum: this.enum,
			isExpressionCode: false
		};
	}
};
//#endregion
//#region src/entityViewParameters/ViewModelEntityViewParameters.ts
var ViewModelEntityViewParameters = class {
	filter(predicate) {
		return Object.values(this).filter((p) => p instanceof ViewModelEntityViewParameter && predicate(p));
	}
	find(predicate) {
		return Object.values(this).find((p) => p instanceof ViewModelEntityViewParameter && predicate(p));
	}
	getValues(predicate) {
		const values = {};
		for (const parameter of this) if (parameter instanceof ViewModelEntityViewParameter && (!predicate || predicate(parameter))) values[parameter.camelCaseName] = parameter.value;
		return values;
	}
	setValues(values) {
		for (const [key, value] of Object.entries(values)) {
			const parameter = this[key];
			if (parameter instanceof ViewModelEntityViewParameter) parameter.value = value;
		}
	}
	[Symbol.iterator]() {
		let index = -1;
		const parameters = Object.values(this).filter((p) => p instanceof ViewModelEntityViewParameter);
		return { next() {
			index++;
			return index >= parameters.length ? {
				value: void 0,
				done: true
			} : {
				value: parameters[index],
				done: false
			};
		} };
	}
};
//#endregion
//#region src/events/uiViewEvents.ts
var layoutUpdatedEvent = {
	...createLocalEvent(),
	layoutUpdatedTimeoutId: -1,
	raiseDebounced(viewModel) {
		if (this.handlers.length > 0) {
			window.clearTimeout(this.layoutUpdatedTimeoutId);
			this.layoutUpdatedTimeoutId = window.setTimeout(() => {
				uiViewEvents.layoutUpdated.raise({ viewModel });
			}, 500);
		}
	}
};
var uiViewEvents = {
	initialized: createLocalEvent(),
	layoutUpdated: layoutUpdatedEvent
};
//#endregion
//#region src/exporting/ServerDataExportOptions.ts
/**
* Represents the server data export options.
*/
var ServerDataExportOptions = class {
	/**
	* Initializes a new instance of the ExportingOptions class.
	* @param entityViewName - Entity view name.
	* @param fileName - File name.
	* @param format - Format.
	*/
	constructor(entityViewName, fileName, format) {
		this.entityViewName = entityViewName;
		this.fileName = fileName;
		this.format = format;
	}
	/**
	* Sets the UI view name.
	* @param uiViewName - The ui view name.
	* @returns The updated server data export options instance (not a new one).
	*/
	withUIViewName(uiViewName) {
		this.uiViewName = uiViewName;
		return this;
	}
	/**
	* Sets the properties to export.
	* @param properties - The properties to export.
	* @returns The updated server data export options instance (not a new one).
	*/
	withProperties(properties) {
		this.properties = properties;
		return this;
	}
	/**
	* Sets the filter or the $filter ODATA clause.
	* @param filter - The filter or the $filter ODATA clause.
	* @returns The updated server data export options instance (not a new one).
	*/
	withFilter(filter) {
		if (filter instanceof Filter) this.filter = filter.toODataString();
		else this.filter = filter;
		return this;
	}
	/**
	* Sets the sorts.
	* @param sorts - The sorts.
	* @returns The updated server data export options instance (not a new one).
	*/
	withSorts(sorts) {
		this.orderBy = sorts?.map((d) => d.toODataString()).join(",");
		return this;
	}
	/**
	* Sets the $orderby ODATA clause.
	* @param orderBy - The $orderby ODATA clause.
	* @returns The updated server data export options instance (not a new one).
	*/
	withOrderBy(orderBy) {
		this.orderBy = orderBy;
		return this;
	}
	/**
	* Sets the entity view parameters.
	* @param entityViewParameters - The entity view parameters.
	* @returns The updated server data export options instance (not a new one).
	*/
	withEntityViewParameters(entityViewParameters) {
		this.entityViewParameters = entityViewParameters instanceof Map ? Object.fromEntries(entityViewParameters) : entityViewParameters;
		return this;
	}
	/**
	* Sets the culture.
	* @param culture - The culture.
	* @returns The updated server data export options instance (not a new one).
	*/
	withCulture(culture) {
		this.culture = culture;
		return this;
	}
	/**
	* Sets the (optional) destination time-zone.
	* @param timeZone - The (optional) destination time-zone.
	* @returns The updated server data export options instance (not a new one).
	*/
	withTimeZone(timeZone) {
		this.timeZone = timeZone;
		return this;
	}
	/**
	* Sets the value indicating whether to generate the header row.
	* @param header - The value indicating whether to generate the header row.
	* @returns The updated server data export options instance (not a new one).
	*/
	withHeader(header) {
		this.header = header;
		return this;
	}
	/**
	* Sets the CSV-specific options.
	* @param csvOptions - The CSV-specific options.
	* @returns The updated server data export options instance (not a new one).
	*/
	withCsvOptions(csvOptions) {
		this.csvOptions = csvOptions;
		return this;
	}
};
//#endregion
//#region src/filter/FilteringBehavior.ts
var FilteringExecutionSide = /* @__PURE__ */ function(FilteringExecutionSide) {
	FilteringExecutionSide["server"] = "server";
	FilteringExecutionSide["client"] = "client";
	return FilteringExecutionSide;
}({});
//#endregion
//#region src/filter/FilterLogicalOperator.ts
var FilterLogicalOperator = /* @__PURE__ */ function(FilterLogicalOperator) {
	FilterLogicalOperator["and"] = " and ";
	FilterLogicalOperator["or"] = " or ";
	return FilterLogicalOperator;
}({});
//#endregion
//#region src/frames/FrameDisplayMode.ts
/**
* Specifies the frame display mode.
*/
var FrameDisplayMode = /* @__PURE__ */ function(FrameDisplayMode) {
	/**
	* The frame is a tab.
	*/
	FrameDisplayMode[FrameDisplayMode["tab"] = 0] = "tab";
	/**
	* The frame is a popup.
	*/
	FrameDisplayMode[FrameDisplayMode["popup"] = 1] = "popup";
	/**
	* The frame is a page.
	*/
	FrameDisplayMode[FrameDisplayMode["page"] = 2] = "page";
	/**
	* The frame is a nested tab.
	*/
	FrameDisplayMode[FrameDisplayMode["nestedTab"] = 3] = "nestedTab";
	/**
	* The frame is stacked.
	*/
	FrameDisplayMode[FrameDisplayMode["stack"] = 4] = "stack";
	/**
	* The frame is in a container.
	*/
	FrameDisplayMode[FrameDisplayMode["container"] = 5] = "container";
	/**
	* The frame is in background (hidden).
	* Only used by CustomViewCreateUI UI view with auto save enabled.
	*/
	FrameDisplayMode[FrameDisplayMode["background"] = 6] = "background";
	return FrameDisplayMode;
}({});
//#endregion
//#region src/grouping/Group.ts
/**
* Represents a group.
*/
var Group = class {
	/**
	* Initializes a group.
	*
	* @param groupDescription - Group description.
	* @param data - Data.
	* @param summaries - Summaries.
	*/
	constructor(groupDescription, data, summaries) {
		this.items = [];
		this.groups = [];
		this.totalRecordCount = void 0;
		this.expanded = false;
		this.groupDescription = groupDescription;
		this.data = data;
		this.summaries = summaries;
		this.value = getPropertyByPath(this.data, groupDescription.property.camelCaseName);
	}
};
//#endregion
//#region src/grouping/GroupDescription.ts
/**
* Represents a group description.
*/
var GroupDescription = class GroupDescription {
	/**
	* Initializes a group description.
	*
	* @param property - Property.
	* @param level - Level.
	* @param isLastLevel - Value indicating whether it is a last level group description.
	*/
	constructor(property, level, isLastLevel) {
		this.property = property;
		this.level = level;
		this.isLastLevel = isLastLevel;
	}
	/**
	* Gets the client group by paths.
	*/
	getClientGroupByPaths() {
		if (!this._clientGroupByPaths) {
			const paths = [this.getFilterPath()];
			if (this.property.lookupDefinition && this.property.lookupDefinition.displayProperty) {
				const displayProperty = this.property.lookupDefinition.displayProperty;
				let path = this.property.dataType ? this.property.getLookupDisplayPropertyPath() : `${this.property.camelCaseName}.${displayProperty.camelCaseName}`;
				if (displayProperty.dataType === DataType.localizableString) path += `.${this.property.owner.localizator.localizableStringCurrentLanguage}`;
				paths.push(path);
			}
			this._clientGroupByPaths = paths;
		}
		return this._clientGroupByPaths;
	}
	/**
	* Returns the OData string.
	* @param summaryDescriptions - Summary descriptions.
	* @returns The OData string.
	*/
	toOData(summaryDescriptions) {
		return GroupDescription.toOData([this], summaryDescriptions);
	}
	static toOData(groupDescriptions, summaryDescriptions) {
		let groupBy = `groupby((${groupDescriptions.map((d) => d.getServerGroupByPath()).join(",")})`;
		if (summaryDescriptions.length > 0) groupBy += `,aggregate(${summaryDescriptions.map((d) => d.toOData()).join(", ")})`;
		groupBy += `)`;
		return groupBy;
	}
	/**
	* Gets the filter.
	* @param value - Value.
	* @returns The filter.
	*/
	async getServerFilter(value) {
		if (this.property.lookupDefinition && !this.property.dataType && value != null) value = getPropertyByPath(value, this.property.lookupDefinition.valueProperty.camelCaseName);
		return await this.property.createFilterCondition(FilterOperator.equal, value, void 0) ?? Filter.create(this.getServerFilterPath(), FilterOperator.isNull);
	}
	getServerGroupByPath() {
		if (!this._serverGroupByPath) {
			const paths = [this.getServerFilterPath()];
			if (this.property.lookupDefinition && this.property.lookupDefinition.displayProperty) {
				const displayProperty = this.property.lookupDefinition.displayProperty;
				let path = this.property.dataType ? this.property.getLookupDisplayPropertyPath(true) : `${this.property.camelCaseName}/${displayProperty.camelCaseName}`;
				if (displayProperty.dataType === DataType.localizableString) path += `/${this.property.owner.localizator.localizableStringCurrentLanguage}`;
				paths.push(path.replaceAll(".", "/"));
			}
			this._serverGroupByPath = paths.join(",");
		}
		return this._serverGroupByPath;
	}
	getServerFilterPath() {
		this._serverFilterPath ??= this.getFilterPath().replaceAll(".", "/");
		return this._serverFilterPath;
	}
	getFilterPath() {
		let path = this.property.camelCaseName;
		if (this.property.lookupDefinition && !this.property.dataType) path += `.${this.property.lookupDefinition.valueProperty.camelCaseName}`;
		if (this.property.dataType === DataType.localizableString) path += `.${this.property.owner.localizator.localizableStringCurrentLanguage}`;
		return path;
	}
};
//#endregion
//#region src/grouping/GroupingBehavior.ts
var GroupingExecutionSide = /* @__PURE__ */ function(GroupingExecutionSide) {
	GroupingExecutionSide["server"] = "server";
	GroupingExecutionSide["client"] = "client";
	return GroupingExecutionSide;
}({});
//#endregion
//#region src/grouping/GroupList.ts
var GroupList = class extends Array {
	constructor(..._args) {
		super(..._args);
		this._hasMoreData = false;
		this.changedEvent = createLocalEvent();
	}
	/**
	* Gets a value indicating whether there is more data.
	*/
	get hasMoreData() {
		return this._hasMoreData;
	}
	/**
	* Gets the total record count.
	*/
	get totalRecordCount() {
		return this._totalRecordCount ?? this.length;
	}
	/**
	* Sets all items in the list. All previous items are removed.
	* @param items - Items.
	* @param hasMoreData - Value indicating whether there is more data.
	* @param totalRecordCount - Total record count.
	*/
	setItems(groups, hasMoreData = false, totalRecordCount) {
		this.splice(0, this.length, ...groups);
		this._hasMoreData = hasMoreData;
		this._totalRecordCount = totalRecordCount;
		this.changedEvent.raise(this);
	}
	/**
	* Appends items in the list.
	* @param items - Items.
	* @param hasMoreData - Value indicating whether there is more data.
	* @param totalRecordCount - Total record count.
	*/
	appendItems(groups, hasMoreData, totalRecordCount) {
		this.push(...groups);
		if (hasMoreData !== void 0) this._hasMoreData = hasMoreData;
		if (totalRecordCount !== void 0) this._totalRecordCount = totalRecordCount;
		this.changedEvent.raise(this);
	}
	/**
	* Removes all items of the list.
	*/
	clear() {
		this.setItems([]);
	}
};
//#endregion
//#region src/sorting/SortDirection.ts
var SortDirection = /* @__PURE__ */ function(SortDirection) {
	SortDirection["asc"] = "asc";
	SortDirection["desc"] = "desc";
	return SortDirection;
}({});
//#endregion
//#region src/model/ObservableList.ts
/**
* Represents the type of changes happening in the list.
*/
var ListChangedType = /* @__PURE__ */ function(ListChangedType) {
	ListChangedType[ListChangedType["itemAdded"] = 0] = "itemAdded";
	ListChangedType[ListChangedType["itemChanged"] = 1] = "itemChanged";
	ListChangedType[ListChangedType["itemRemoved"] = 2] = "itemRemoved";
	ListChangedType[ListChangedType["reset"] = 3] = "reset";
	return ListChangedType;
}({});
/**
* Represents the loading states of the list.
*/
var ListLoadingState = /* @__PURE__ */ function(ListLoadingState) {
	ListLoadingState[ListLoadingState["loaded"] = 0] = "loaded";
	ListLoadingState[ListLoadingState["loading"] = 1] = "loading";
	ListLoadingState[ListLoadingState["notLoaded"] = 2] = "notLoaded";
	return ListLoadingState;
}({});
/**
* Represents a list that provides notifications when items are changed, or when the whole list is refreshed.
*/
var ObservableList = class ObservableList extends Array {
	/**
	* Gets the instance to which this list belongs.
	*/
	get owner() {
		return this._owner;
	}
	/**
	* Gets the owner navigation property name.
	*/
	get ownerNavigationPropertyName() {
		return this._ownerNavigationPropertyName;
	}
	/**
	* Gets a value indicating whether the list is loaded.
	*/
	get isLoaded() {
		return this.loadingState === 0;
	}
	/**
	* Gets a value indicating whether there is more data.
	*/
	get hasMoreData() {
		return this._hasMoreData;
	}
	/**
	* Gets the total record count.
	*/
	get totalRecordCount() {
		return this._totalRecordCount ? this._totalRecordCount + this.addedItemsCount - this.deletedItems.length : this.length;
	}
	constructor(...items) {
		super(...items);
		this._itemPropertyChangedHandler = this.onItemPropertyChanged.bind(this);
		this._itemPropertyMetadataChangedHandler = this.onItemPropertyMetadataChanged.bind(this);
		this.loadingState = 2;
		this.addedItemsCount = 0;
		this.suspendsListChangedEvent = false;
		this.deletedItems = [];
		this._hasMoreData = false;
		this.subscribedEvent = createLocalEvent();
		this.unsubscribedEvent = createLocalEvent();
		this.listChangedEvent = createLocalEvent();
		this.itemPropertyMetadataChangedEvent = createLocalEvent();
		if (!(items.length === 1 && typeof items[0] === "number") && items.length > 0) {
			this.loadingState = 0;
			this.subscribe(...items);
		}
	}
	/**
	* Sets the function to wrap the instance.
	* @param wrapInstance - A function to wrap the instance.
	*/
	setWrapInstance(wrapInstance) {
		this._wrapInstance = wrapInstance;
	}
	/**
	* Sets the instance to which this list belongs.
	* @param value - The owner.
	* @param navigationPropertyName - The navigation property name.
	*/
	setOwner(value, navigationPropertyName) {
		if (this._owner === value && this._ownerNavigationPropertyName === navigationPropertyName) return;
		this._owner = value;
		this._ownerNavigationPropertyName = navigationPropertyName;
		this.forEach((item) => this.setOwnerNavigationProperty(item));
	}
	/**
	* Gets a value indicating whether the item has changes.
	* Returns true if it is a new element, if a property has changed or if a relation has changes.
	* @param excludeNonEmbeddedRelations - A value indicating whether to exclude non-embedded relations from the check.
	* @returns A value indicating whether the item has changes.
	*/
	hasChanges(excludeNonEmbeddedRelations = false) {
		return this.deletedItems.length > 0 || this.some((item) => item.hasChanges(excludeNonEmbeddedRelations));
	}
	/**
	* Gets a value indicating whether there is at least an item with errors.
	*/
	get hasErrors() {
		return this.some((item) => item.$hasErrors);
	}
	/**
	* Gets a value indicating whether there is at least an item with persistence errors.
	*/
	get hasPersistenceErrors() {
		return this.some((item) => item.$hasPersistenceErrors);
	}
	/**
	* Gets the number of persisted items.
	*/
	getPersistedItemsCount() {
		return this.length + this.deletedItems.length - this.addedItemsCount;
	}
	/**
	* Raises the event ListChanged with the specified arguments
	* @param args - arguments of the event.
	*/
	onItemPropertyChanged(args) {
		const rawItem = args.item.toRaw(true);
		const index = this.findIndex((item) => item.toRaw(true) === rawItem);
		this.onListChanged({
			type: 1,
			item: this[index],
			index,
			itemChangedArgs: args
		});
	}
	/**
	* Raises the event ItemPropertyMetadataChanged with the specified arguments.
	* @param args - arguments of the event.
	*/
	onItemPropertyMetadataChanged(args) {
		this.itemPropertyMetadataChangedEvent.raise(args);
	}
	/**
	* Appends new elements to the end of an array, and returns the new length of the array.
	* @param items - New elements to add to the array.
	*/
	push(...items) {
		items = this.wrapItems(items);
		for (const item of items) {
			super.push(item);
			this.addedItemsCount++;
			const index = this.length - 1;
			this.onListChanged({
				type: 0,
				item: this[index],
				index
			});
		}
		this.subscribe(...items);
		return this.length;
	}
	/**
	* Removes the last element from an array and returns it.
	* If the array is empty, undefined is returned and the array is not modified.
	*/
	pop() {
		const item = super.pop();
		if (item) {
			this.unsubscribe(item);
			this.onListChanged({
				type: 2,
				item,
				index: this.length
			});
			this.logDeletion(item, this.length);
		}
		return item;
	}
	/**
	* Removes the first element from an array and returns it.
	* If the array is empty, undefined is returned and the array is not modified.
	*/
	shift() {
		const item = super.shift();
		if (item) {
			this.unsubscribe(item);
			this.onListChanged({
				type: 2,
				item,
				index: 0
			});
			this.logDeletion(item, 0);
		}
		return item;
	}
	/**
	* Inserts new elements at the start of an array, and returns the new length of the array.
	* @param items - Elements to insert at the start of the array.
	*/
	unshift(...items) {
		items = this.wrapItems(items);
		const length = super.unshift(...items);
		this.subscribe(...items);
		this.addedItemsCount += items.length;
		this.onListChanged(items.length === 1 ? {
			type: 0,
			item: items[0],
			index: 0
		} : { type: 3 });
		return length;
	}
	/**
	* Removes elements from an array and, if necessary, inserts new elements in their place, returning the deleted elements.
	* @param start - The zero-based location in the array from which to start removing elements.
	* @param deleteCount - The number of elements to remove.
	* @param items - Elements to insert into the array in place of the deleted elements.
	* @returns An array containing the elements that were deleted.
	*/
	splice(start, deleteCount, ...items) {
		items = this.wrapItems(items);
		const removedItems = super.splice(start, deleteCount, ...items);
		this.subscribe(...items);
		this.unsubscribe(...removedItems);
		if (!this.suspendsListChangedEvent) {
			this.addedItemsCount += items.length;
			for (let i = 0; i < removedItems.length; i++) this.logDeletion(removedItems[i], start + i);
		}
		this.onListChanged(this.getSpliceListChangedArgs(items, removedItems, start));
		return removedItems;
	}
	getSpliceListChangedArgs(items, removedItems, start) {
		if (items.length === 1 && removedItems.length === 0) return {
			type: 0,
			item: this[start],
			index: start
		};
		if (items.length === 0 && removedItems.length === 1) return {
			type: 2,
			item: removedItems[0],
			index: start
		};
		return { type: 3 };
	}
	/**
	* Calls a defined callback function on each element of an array, and returns an array that contains the results.
	* @param callbackfn - A function that accepts up to three arguments. The map method calls the callbackfn function one time for each element in the array.
	* @param thisArg - An object to which the this keyword can refer in the callbackfn function. If thisArg is omitted, undefined is used as the this value.
	*/
	map(callbackfn, thisArg) {
		return [...super.map(callbackfn, thisArg)];
	}
	/**
	* Calls a defined callback function on each element of an array. Then, flattens the result into
	* a new array.
	* This is identical to a map followed by flat with depth 1.
	*
	* @param callback - A function that accepts up to three arguments. The flatMap method calls the
	* callback function one time for each element in the array.
	* @param thisArg - An object to which the this keyword can refer in the callback function. If
	* thisArg is omitted, undefined is used as the this value.
	*/
	flatMap(callback, thisArg) {
		return [...super.flatMap(callback, thisArg)];
	}
	/**
	* Removes all items of the list.
	*/
	clear(acceptChanges = false) {
		this.summaries = void 0;
		if (acceptChanges) this.setItems([]);
		else this.splice(0, this.length);
	}
	/**
	* Sets the item in the list at the specified index.
	* If an item exists at the specified index, it will be replaced.
	* @param item - Item to set.
	*/
	setItem(index, item) {
		try {
			this.suspendsListChangedEvent = true;
			this.splice(index, 1, item);
		} finally {
			this.suspendsListChangedEvent = false;
		}
		this.onListChanged({ type: 3 });
	}
	/**
	* Applies server-side reload results without tracking them as local edits.
	* @param changes - Items to replace at the specified indexes. When item is undefined, the entry is removed.
	*/
	applyReloadChanges(changes) {
		if (changes.length === 0) return;
		const operations = [...changes].sort((left, right) => right.index - left.index);
		let removedItemsCount = 0;
		try {
			this.suspendsListChangedEvent = true;
			for (const operation of operations) {
				if (operation.index < 0 || operation.index >= this.length) continue;
				if (operation.item) this.splice(operation.index, 1, operation.item);
				else {
					this.splice(operation.index, 1);
					removedItemsCount++;
				}
			}
		} finally {
			this.suspendsListChangedEvent = false;
		}
		this.decrementTotalRecordCount(removedItemsCount);
		this.onListChanged({ type: 3 });
	}
	/**
	* Sets all items in the list. All previous items are removed.
	* @param items - Items.
	* @param hasMoreData - Value indicating whether there is more data.
	* @param totalRecordCount - Total record count.
	*/
	setItems(items, hasMoreData = false, totalRecordCount) {
		this.loadingState = 0;
		try {
			this.suspendsListChangedEvent = true;
			this.splice(0, this.length, ...items);
		} finally {
			this.suspendsListChangedEvent = false;
		}
		this._hasMoreData = hasMoreData;
		this._totalRecordCount = totalRecordCount;
		this.addedItemsCount = 0;
		this.acceptDeletions();
		this.onListChanged({ type: 3 });
	}
	/**
	* Appends items in the list.
	* @param items - Items.
	* @param hasMoreData - Value indicating whether there is more data.
	* @param totalRecordCount - Total record count.
	*/
	appendItems(items, hasMoreData, totalRecordCount) {
		items = this.wrapItems(items);
		super.push(...items);
		this.subscribe(...items);
		if (hasMoreData !== void 0) this._hasMoreData = hasMoreData;
		if (totalRecordCount !== void 0) this._totalRecordCount = totalRecordCount;
		this.onListChanged({ type: 3 });
	}
	/**
	* Sorts the items in place with the sorts.
	* This method mutates the array and returns a reference to the same array.
	*/
	sortItems(sorts) {
		super.sort((a, b) => {
			let result = 0;
			for (const sort of sorts) {
				const valueA = getPropertyByPath(a, sort.propertyPath) ?? null;
				const valueB = getPropertyByPath(b, sort.propertyPath) ?? null;
				if (valueA !== valueB) {
					const comparison = (valueA === null || valueB === null ? valueB === null : valueA > valueB) ? 1 : -1;
					result += sort.direction === SortDirection.asc ? comparison : -comparison;
				}
			}
			return result;
		});
		this.onListChanged({ type: 3 });
	}
	/**
	* Accepts all changes.
	* The deleted items are definitely deleted and cannot be restored.
	* The changes made on the items are accepted.
	*/
	acceptChanges() {
		try {
			this.suspendsListChangedEvent = true;
			this.addedItemsCount = 0;
			this.acceptDeletions();
			for (const item of this) item.acceptChanges();
		} finally {
			this.suspendsListChangedEvent = false;
		}
		this.onListChanged({ type: 3 });
	}
	/**
	* Accepts the deleted items.
	* The deleted items cannot be restored.
	*/
	acceptDeletions() {
		const length = this.deletedItems.length;
		this.deletedItems.splice(0, length);
		this.decrementTotalRecordCount(length);
	}
	/**
	* Accepts the deleted item.
	* The specified deleted item cannot be restored.
	* @param deletedItem - Deleted item to accept.
	*/
	acceptDeletion(deletedItem) {
		const index = this.deletedItems.findIndex(({ item }) => item === deletedItem);
		if (index !== -1) {
			this.deletedItems.splice(index, 1);
			this.decrementTotalRecordCount(1);
		}
	}
	decrementTotalRecordCount(count) {
		if (this._totalRecordCount && this._totalRecordCount > 0) this._totalRecordCount = Math.max(0, this._totalRecordCount - count);
	}
	/**
	* Removes all the elements that match the conditions defined by the specified predicate.
	* @param predicate - The predicate delegate that defines the conditions of the elements to remove.
	* @returns The number of elements removed from the data source.
	*/
	removeAll(predicate) {
		let deletedItemsCount = 0;
		let lastDeletedItem = void 0;
		let lastDeletedItemIndex = -1;
		try {
			this.suspendsListChangedEvent = true;
			let index = this.length - 1;
			while (index >= 0) {
				const item = this[index];
				if (predicate(item)) {
					this.splice(index, 1);
					this.logDeletion(item, index);
					lastDeletedItem = item;
					lastDeletedItemIndex = index;
					deletedItemsCount++;
				}
				index--;
			}
			return deletedItemsCount;
		} finally {
			this.suspendsListChangedEvent = false;
			if (deletedItemsCount === 1) this.onListChanged({
				type: 2,
				item: lastDeletedItem,
				index: lastDeletedItemIndex
			});
			else if (deletedItemsCount > 1) this.onListChanged({ type: 3 });
		}
	}
	moveRange(newIndex, items) {
		const itemsToMove = [];
		for (const item of items) {
			const index = this.indexOf(item);
			if (index !== -1) {
				super.splice(index, 1);
				itemsToMove.push(item);
			}
		}
		super.splice(newIndex, 0, ...itemsToMove);
		this.onListChanged({ type: 3 });
	}
	/**
	* Rejects all changes.
	* The deleted items are restored.
	* The added items are removed.
	* The changes made on the items are rejected.
	*/
	rejectChanges() {
		try {
			this.suspendsListChangedEvent = true;
			this.rejectAdditions();
			this.rejectDeletions();
			for (const item of this) item.rejectChanges();
		} finally {
			this.suspendsListChangedEvent = false;
		}
		this.onListChanged({ type: 3 });
	}
	rejectAdditions() {
		for (const item of this.filter((item) => item.$new)) this.splice(this.indexOf(item), 1);
		this.addedItemsCount = 0;
	}
	rejectDeletions() {
		for (const { item, position } of this.deletedItems) this.splice(position, 0, item);
		this.deletedItems.splice(0);
	}
	logDeletion(item, position) {
		if (item.$new) this.addedItemsCount--;
		else this.deletedItems.push({
			item,
			position
		});
	}
	/**
	* Clones the observable list (the items are cloned).
	* @param asNew - Forces the cloned items to be new.
	* @returns The cloned observable list.
	*/
	clone(asNew = true) {
		const list = new ObservableList(...this.map((i) => i.clone(asNew)));
		list.setWrapInstance(this._wrapInstance);
		return list;
	}
	transformCollectionToApiData(excludeCollectionsWithoutChanges, references, referencePropertyName) {
		return this.filter((childItem) => !childItem.$virtual || childItem.hasChanges(true)).map((childItem) => {
			const childData = childItem.toApiRequestData(excludeCollectionsWithoutChanges, references);
			if (referencePropertyName) delete childData[referencePropertyName];
			return childData;
		});
	}
	subscribe(...items) {
		for (const item of items) if (item) {
			this.setOwnerNavigationProperty(item);
			item.$propertyChangedEvent.add(this._itemPropertyChangedHandler);
			item.$propertyMetadataChangedEvent.add(this._itemPropertyMetadataChangedHandler);
		}
		this.subscribedEvent.raise(items);
	}
	unsubscribe(...items) {
		for (const item of items) if (item) {
			this.removeOwnerNavigationProperty(item);
			item.$propertyChangedEvent.remove(this._itemPropertyChangedHandler);
			item.$propertyMetadataChangedEvent.remove(this._itemPropertyMetadataChangedHandler);
		}
		this.unsubscribedEvent.raise(items);
	}
	onListChanged(args) {
		if (!this.suspendsListChangedEvent) this.listChangedEvent.raise(args);
	}
	/**
	* Sets the owner navigation property.
	* @param item - The item of the collection
	*/
	setOwnerNavigationProperty(item) {
		if (this._ownerNavigationPropertyName) {
			item.toRaw()[this._ownerNavigationPropertyName] = this.owner;
			item.updateKey();
		}
	}
	/**
	* Removes the owner navigation property.
	* @param item - The item of the collection
	*/
	removeOwnerNavigationProperty(item) {
		if (this._ownerNavigationPropertyName) {
			delete item.toRaw()[this._ownerNavigationPropertyName];
			item.updateKey();
		}
	}
	wrapItems(items) {
		return this._wrapInstance ? items.map(this._wrapInstance) : items;
	}
};
//#endregion
//#region src/summaries/SummaryDescription.ts
/**
* Represents a summary description.
*/
var SummaryDescription = class {
	/**
	* Initializes a summary description.
	*
	* @param propertyName - Property name.
	* @param type - Type.
	*/
	constructor(propertyName, type) {
		this.propertyName = propertyName;
		this.type = type;
		this.path = propertyName.replaceAll(".", "/");
		this.alias = `${propertyName.replaceAll(".", "_")}_${type}`;
	}
	/**
	* Returns the OData string.
	* @param summaryDescriptions - Summary descriptions.
	* @returns The OData string.
	*/
	toOData() {
		switch (this.type) {
			case SummaryType.average: return `${this.path} with average as ${this.alias}`;
			case SummaryType.count: return `$count as ${this.alias}`;
			case SummaryType.countDistinct: return `${this.path} with countdistinct as ${this.alias}`;
			case SummaryType.max: return `${this.path} with max as ${this.alias}`;
			case SummaryType.min: return `${this.path} with min as ${this.alias}`;
			default: return `${this.path} with sum as ${this.alias}`;
		}
	}
	/**
	* Calculates the value of a summary.
	* @param items - Items.
	* @returns The summary value.
	*/
	calculate(items) {
		switch (this.type) {
			case SummaryType.average: {
				const values = this.getValues(items);
				return values.length > 0 ? getNumberArrayAverage(values) : 0;
			}
			case SummaryType.count: return items.length;
			case SummaryType.countDistinct: return new Set(this.getValues(items)).size;
			case SummaryType.max: return Math.max(...this.getValues(items));
			case SummaryType.min: return Math.min(...this.getValues(items));
			default: return getNumberArraySum(this.getValues(items));
		}
	}
	getValues(items) {
		const values = [];
		for (const item of items) {
			const value = getPropertyByPath(item, this.path, "/");
			if (value != null) values.push(value);
		}
		return values;
	}
	/**
	* Normalizes the summaries coming from the server.
	* @param summaryDescriptions - Summary descriptions.
	* @param summaries - Summaries coming from the server.
	* @returns The normalized summaries.
	*/
	static normalize(summaryDescriptions, summaries) {
		const normalizedSummaries = {};
		for (const summaryDescription of summaryDescriptions) if (summaryDescription.alias in summaries) normalizedSummaries[summaryDescription.propertyName] = summaries[summaryDescription.alias];
		return normalizedSummaries;
	}
	/**
	* Calculates the summaries.
	* @param summaryDescriptions - Summary descriptions.
	* @param items - Items.
	* @param useAlias - Value indicating whether to use aliases.
	* @returns The summaries.
	*/
	static calculate(summaryDescriptions, items, useAlias = false) {
		const summaries = {};
		for (const summaryDescription of summaryDescriptions) summaries[useAlias ? summaryDescription.alias : summaryDescription.propertyName] = summaryDescription.calculate(items);
		return summaries;
	}
};
//#endregion
//#region src/grouping/ServerGroup.ts
/**
* Represents a group.
*/
var ServerGroup = class ServerGroup extends Group {
	/**
	* Initializes a group.
	*
	* @param owner - Owner.
	* @param groupDescription - Group description.
	* @param data - Data.
	* @param summaryDescriptions - Summary descriptions.
	* @param parentGroup - Parent group.
	*/
	constructor(owner, groupDescription, data, summaries, parentGroup) {
		super(groupDescription, data, summaries);
		this.items = [];
		this.groups = [];
		this.totalRecordCount = void 0;
		this.expanded = false;
		this.owner = owner;
		this.parentGroup = parentGroup;
	}
	static fromServer(owner, groupDescription, data, summaryDescriptions, parentGroup) {
		return new ServerGroup(owner, groupDescription, ServerGroup.fixData(data, groupDescription.property, owner.apiClient), SummaryDescription.normalize(summaryDescriptions, data), parentGroup);
	}
	/**
	* Loads data.
	* @param top - The maximum number of records to load.
	* @returns - Value indicating that data has been loaded.
	*/
	loadData(top) {
		if (this.owner.loading) return false;
		if (this.groupDescription.isLastLevel) return this.loadItems(top);
		return this.loadGroups(top);
	}
	async loadItems(top) {
		if (this.totalRecordCount !== void 0 && this.items.length >= this.totalRecordCount) return false;
		try {
			if (await this.owner.callOnRetrieving()) return false;
			this.owner.datasource.loadingState = ListLoadingState.loading;
			const response = await this.owner.fetchItemsAsync({
				filter: (await this.getFilter()).toODataString(),
				skip: this.items.length,
				top
			});
			if (response.succeeded) {
				this.items.push(...response.data.map((item) => new this.owner.creator(item, this.owner)));
				this.totalRecordCount = response.totalRecordCount;
				this.owner.datasource.appendItems(this.items);
				this.owner.datasource.loadingState = ListLoadingState.loaded;
			} else this.owner.datasource.loadingState = ListLoadingState.notLoaded;
		} catch (error) {
			this.owner.logger.error("Error while loading items of the last server group level:", error);
			this.owner.datasource.loadingState = ListLoadingState.notLoaded;
		}
		return true;
	}
	async loadGroups(top) {
		if (this.totalRecordCount !== void 0 && this.groups.length >= this.totalRecordCount) return false;
		try {
			if (await this.owner.callOnRetrieving()) return false;
			this.owner.datasource.loadingState = ListLoadingState.loading;
			const groupDescription = this.owner.getGroupDescription(this.groupDescription.level + 1);
			const summaryDescriptions = this.owner.getGroupSummaryDescriptions();
			const response = await this.owner.fetchServerGroups({
				filter: await this.getFilter(),
				groupDescriptions: [groupDescription],
				summaryDescriptions,
				skip: this.groups.length,
				top,
				sorts: this.owner.getServerDataSorts().filter((sort) => sort.propertyName === groupDescription.property.camelCaseName)
			});
			if (response.succeeded) {
				this.groups.push(...response.data.map((group) => ServerGroup.fromServer(this.owner, groupDescription, group, summaryDescriptions, this)));
				this.totalRecordCount = response.totalRecordCount;
				this.owner.datasource.loadingState = ListLoadingState.loaded;
			} else this.owner.datasource.loadingState = ListLoadingState.notLoaded;
		} catch (error) {
			this.owner.logger.error("Error while loading server group:", error);
			this.owner.datasource.loadingState = ListLoadingState.notLoaded;
		}
		return true;
	}
	async getFilter() {
		return this.owner.getInitialFilter(this.owner.datasource).add(await this.getGroupFilter(), FilterLogicalOperator.and);
	}
	async getGroupFilter() {
		const filter = await this.parentGroup?.getGroupFilter() ?? new Filter();
		filter.add(await this.groupDescription.getServerFilter(this.value), FilterLogicalOperator.and);
		return filter;
	}
	static fixData(data, property, apiClient) {
		data = Model.fixPropertyValue(data, property.camelCaseName, property, apiClient);
		if (data.neosAdditionalData) {
			data.$additionalData = { [`${property.name}_DisplayProperty`]: Object.values(data.neosAdditionalData)[0] };
			delete data["neosAdditionalData"];
		}
		return data;
	}
};
//#endregion
//#region src/navigation/NavigationParameters.ts
/**
* Represents the navigation parameters.
*/
var NavigationParameters = class NavigationParameters extends Map {
	/**
	* Gets or sets a value indicating whether the view is in creation mode.
	*/
	get creationMode() {
		return !!this.creationOptions;
	}
	set creationMode(value) {
		if (value) this.creationOptions ??= {};
		else this.creationOptions = void 0;
	}
	/**
	* Creates a new instance of NavigationParameters by
	* - merging/overwriting the provided custom parameters (typically inherited
	*   from the parent ViewContext) with the current navigation parameters
	* - and copying the values of the special parameters.
	* @param customParameters - Custom parameters.
	* @returns New navigation parameters.
	*/
	clone(customParameters) {
		const parameters = new NavigationParameters([...customParameters, ...this]);
		parameters.id = this.id;
		parameters.creationOptions = this.creationOptions;
		parameters.cloneMode = this.cloneMode;
		parameters.pageNumber = this.pageNumber;
		parameters.recordsByPage = this.recordsByPage;
		parameters.lookupContext = this.lookupContext;
		parameters.dontExecuteUIViewRules = this.dontExecuteUIViewRules;
		return parameters;
	}
	/**
	* Creates a NavigationParameters instance from a plain object. (e.g. : used in VSCode extension to serialize/deserialize navigation parameters).
	* @param obj - The plain object containing navigation parameters.
	* @returns A new NavigationParameters instance.
	*/
	static fromObject(obj) {
		const parameters = new NavigationParameters();
		for (const key in obj) switch (key) {
			case "id":
				parameters.id = new Map(obj[key] ? Object.entries(obj[key]) : void 0);
				break;
			case "creationOptions":
				parameters.creationOptions = obj[key];
				break;
			case "cloneMode":
				parameters.cloneMode = obj[key];
				break;
			case "pageNumber":
				parameters.pageNumber = obj[key];
				break;
			case "recordsByPage":
				parameters.recordsByPage = obj[key];
				break;
			case "lookupContext":
				parameters.lookupContext = obj[key];
				break;
			case "dontExecuteUIViewRules":
				parameters.dontExecuteUIViewRules = obj[key];
				break;
			default:
				parameters.set(key, obj[key]);
				break;
		}
		return parameters;
	}
};
//#endregion
//#region src/navigation/NavigationTarget.ts
/**
* Specifies the navigation target.
*/
var NavigationTarget = /* @__PURE__ */ function(NavigationTarget) {
	/**
	* The view will be in a default open mode.
	*/
	NavigationTarget[NavigationTarget["default"] = 0] = "default";
	/**
	* The view will be in a new frame.
	* If the container is a tabs container, the view will be in a new tab.
	*/
	NavigationTarget[NavigationTarget["newFrame"] = 1] = "newFrame";
	/**
	* The view will open in the nested frame.
	*/
	NavigationTarget[NavigationTarget["nestedFrame"] = 2] = "nestedFrame";
	/**
	* The view will replace the current frame.
	*/
	NavigationTarget[NavigationTarget["sameFrame"] = 3] = "sameFrame";
	/**
	* The view will be in a popup.
	*/
	NavigationTarget[NavigationTarget["popup"] = 4] = "popup";
	/**
	* The view will be in background (hidden).
	* Only used by CustomViewCreateUI UI view with auto save enabled.
	*/
	NavigationTarget[NavigationTarget["background"] = 5] = "background";
	return NavigationTarget;
}({});
//#endregion
//#region src/navigation/NavigationOptions.ts
/**
* Represents the navigation options.
*/
var NavigationOptions = class NavigationOptions {
	/**
	* Initializes a new instance of the NavigationOptions class.
	* @param uiViewName - UI view name.
	*/
	constructor(uiViewName) {
		this.popupSize = PopupSize.auto;
		this.popupMaximizable = true;
		this.delegateToVsCode = true;
		this.uiViewName = uiViewName;
		this.target = NavigationTarget.default;
		this.parameters = new NavigationParameters();
		this.frameUrlParameters = /* @__PURE__ */ new Map();
		this.inheritViewContext = true;
	}
	static withRemote(remote, uiViewName) {
		const options = new NavigationOptions(uiViewName);
		options.remote = remote;
		return options;
	}
	/**
	* Sets the destination of the navigation event.
	* @param id - Name of the UIView.
	*/
	withUIViewName(id) {
		this.uiViewName = id;
		return this;
	}
	/**
	* Sets the type of target where the navigation will occur (e.g. NewFrame, NestedFrame, SameFrame, Popup, etc.).
	* @param target - Type of target where the navigation will occur (e.g. NewFrame, NestedFrame, SameFrame, Popup, etc.).
	*/
	onTarget(target) {
		this.target = target;
		return this;
	}
	/**
	* Sets the popup size.
	* @param popupSize - Popup size.
	*/
	withPopupSize(popupSize) {
		this.popupSize = popupSize;
		return this;
	}
	/**
	* Sets the popup position.
	* @param popupPosition - Popup position.
	*/
	withPopupPosition(popupPosition) {
		this.popupPosition = popupPosition;
		return this;
	}
	/**
	* Sets the popup maximizable.
	* @param popupPosition - Popup maximizable.
	*/
	withPopupMaximizable(popupMaximizable) {
		this.popupMaximizable = popupMaximizable;
		return this;
	}
	/**
	* Disables the view context inheritance and replaces the previous set of parameters with this new one.
	*
	*    WARNING: Previous parameters will be lost when using this method
	* (this includes the special parameters such as **id**, creationMode, pageNumber, recordsByPage, etc.
	* which are all carried by the NavigationParameters instance, alongside the normal parameters dictionary).
	* Make sure to call the fluent APIs for setting the special parameters **after** calling this method
	* and/or use `withParameter` (or `withParameters`) methods to add parameters without losing the previous ones.
	*
	* POSSIBLE FUTURE IMPROVEMENT: Consider raising an error if this method is called while any of the special parameters were already set.
	*
	* @param newParameters - A new map of parameters.
	*/
	withNewParameters(newParameters) {
		this.parameters = newParameters ?? new NavigationParameters();
		this.inheritViewContext = false;
		return this;
	}
	/**
	* Sets multiple parameters according to a given dictionary.
	* @param extraParameters - The map of key-values to force-merge into to the NavigationParameters internal object.
	*/
	withParameters(extraParameters) {
		for (const [key, value] of extraParameters) this.withParameter(key, value);
		return this;
	}
	/**
	* Sets a parameter.
	* @param key - Parameter key.
	* @param value - Parameter value.
	*/
	withParameter(key, value) {
		this.parameters.set(key, value);
		return this;
	}
	/**
	* Adds the identifier parameter.
	* @param id - Id.
	*/
	withId(id) {
		this.parameters.id = id ? toCamelCaseKey(id) : id;
		return this;
	}
	/**
	* Adds the creation mode parameter.
	* @param creationMode - Creation mode.
	* @param data - Creation data.
	* @param assign - Value indicating whether the creation data should be assigned instead of being used as default values.
	*/
	withCreationMode(creationMode = false, data = null, assign = false) {
		this.parameters.creationOptions = creationMode ? {
			data,
			assign
		} : void 0;
		return this;
	}
	/**
	* Adds the page number parameter.
	* @param pageNumber - Page number.
	*/
	withPageNumber(pageNumber) {
		this.parameters.pageNumber = pageNumber;
		return this;
	}
	/**
	* Adds the records by page parameter.
	* @param recordsByPage - Records by page.
	*/
	withRecordsByPage(recordsByPage) {
		this.parameters.recordsByPage = recordsByPage;
		return this;
	}
	/**
	* Sets the container identifier.
	* @param containerId - Container identifier.
	*/
	withContainerId(containerId) {
		this.containerId = containerId;
		return this;
	}
	/**
	* Sets the explicit title.
	* @param explicitTitle - Explicit title.
	* @remark The explicit title has the highest priority among the possible
	* sources for the frame title (explicit title, menu-based title, view model
	* title). The use of an explicit title is however discouraged and one should
	* rather rely on the menu-based title or the view model title.
	*/
	withTitle(explicitTitle) {
		this.explicitTitle = explicitTitle;
		return this;
	}
	/**
	* Sets the originating menu item name.
	* @param menuItemName - Menu item name.
	*/
	withMenuItemName(menuItemName) {
		this.menuItemName = menuItemName;
		return this;
	}
	/**
	* Sets the icon.
	* @param icon - Icon.
	*/
	withIcon(icon) {
		this.icon = icon;
		return this;
	}
	/**
	* Sets the frame identifier.
	* @param frameId - Frame identifier.
	*/
	withFrameId(frameId) {
		this.frameId = frameId;
		return this;
	}
	/**
	* Sets the value indicating whether the view is closeable by the user.
	* @param closeable - Value indicating whether the view is closeable by the user.
	*/
	withCloseable(closeable = true) {
		this.closeable = closeable;
		return this;
	}
	/**
	* Sets the value indicating whether the view is closeable by the user on click outside (for popups only).
	* @param closeable - Value indicating whether the view is closeable by the user on click outside (for popups only).
	*/
	withCloseableOnClickOutside(closeableOnClickOutside = true) {
		this.closeableOnClickOutside = closeableOnClickOutside;
		return this;
	}
	/**
	* Sets the data source to share with the view to open and the position at the opening.
	* @param datasource - Shared datasource.
	* @param position - Position at the opening.
	*/
	withDataSharing(datasource, position) {
		this.datasource = datasource;
		this.position = position;
		return this;
	}
	/**
	* Sets the filter.
	* @param filter - Filter.
	*/
	withFilter(filter) {
		this.filter = filter;
		return this;
	}
	withCallback(callback) {
		this.callback = callback;
		return this;
	}
	/**
	* Sets the lookup context.
	* @param lookupContext - Lookup context.
	*/
	withLookupContext(lookupContext) {
		this.parameters.lookupContext = lookupContext;
		return this;
	}
	/**
	* Sets the value indicating whether the navigation rule and the target ui view rules are executed.
	* @param dontExecuteUIViewRules - Value indicating whether the navigation rule and the target ui view rules are executed.
	*/
	withDontExecuteUIViewRules(dontExecuteUIViewRules) {
		this.parameters.dontExecuteUIViewRules = dontExecuteUIViewRules;
		return this;
	}
	/**
	* Sets a new dictionary of extra parameters present in the address upon arrival on this ui view.
	*
	*    WARNING: Previous parameters will be lost when using this method,
	* use withUrlParameter method to add parameters without losing the previous ones.
	*
	* FOR MORE INFO, see comments on withUrlParameter method and comments of the abstraction (includes warnings).
	* @param newParams - The new dictionary (strings values only).
	*/
	withNewUrlParameters(newParams) {
		this.frameUrlParameters = newParams ?? /* @__PURE__ */ new Map();
		return this;
	}
	/**
	* Sets multiple frame URL parameters according to a given dictionary.
	* FOR MORE INFO, see comments on withUrlParameter method and comments of the abstraction (includes warnings).
	* @param extraParams - The dictionary to force-merge into to the internal dictionary (strings values only).
	*/
	withUrlParameters(extraParams) {
		for (const [key, value] of extraParams) this.withUrlParameter(key, value);
		return this;
	}
	/**
	* Adds a frame URL parameter that is intended for the next frame (and that frame only).
	* FOR MORE INFO, see WARNINGS on the abstractions documentation comments.
	*
	* SPECIFICS: When being written to and read from the URL, this parameter will be prefixed with
	* 'f_' to be differentiated from global URL parameters.
	*
	* DEVELOPER NOTE: Some frame parameters may already be using custom prefixes,
	* the frame URL parameters will be prefixed with 'f_' in addition to the existing prefixes,
	* (e.g. 'neos_evp_' will end up written as 'f_neos_evp_' in the final URL).
	*
	* @param key - Parameter key.
	* @param value - String parameter value.
	*/
	withUrlParameter(key, value) {
		this.frameUrlParameters.set(key, value);
		return this;
	}
	/**
	* Creates a NavigationOptions instance from a plain object (e.g. : used in VSCode extension to serialize/deserialize navigation options).
	* @param obj - The plain object containing navigation options.
	* @returns A new NavigationOptions instance.
	*/
	static async fromObject(obj) {
		const options = new NavigationOptions(obj.uiViewName);
		options.target = obj.target;
		options.parameters = NavigationParameters.fromObject(obj.parameters);
		options.frameUrlParameters = new Map(obj.frameUrlParameters ? Object.entries(obj.frameUrlParameters) : void 0);
		options.containerId = obj.containerId;
		options.explicitTitle = obj.title;
		options.menuItemName = obj.menuItemName;
		options.icon = obj.icon;
		options.closeable = obj.closeable;
		options.closeableOnClickOutside = obj.closeableOnClickOutside;
		options.frameId = obj.frameId;
		options.popupSize = obj.popupSize;
		options.popupPosition = obj.popupPosition;
		options.popupMaximizable = obj.popupMaximizable;
		options.inheritViewContext = obj.inheritViewContext;
		options.datasource = obj.datasource;
		options.filter = obj.filter ? await Filter.fromJSON(obj.filter) : null;
		options.position = obj.position;
		return options;
	}
};
//#endregion
//#region src/navigation/NavigationResultState.ts
var NavigationResultState = /* @__PURE__ */ function(NavigationResultState) {
	NavigationResultState[NavigationResultState["ok"] = 1] = "ok";
	NavigationResultState[NavigationResultState["fail"] = 2] = "fail";
	NavigationResultState[NavigationResultState["cancel"] = 3] = "cancel";
	return NavigationResultState;
}({});
//#endregion
//#region src/navigation/NavigationResult.ts
/**
* Navigation result.
*/
var NavigationResultWithoutValue = class {
	/**
	* Initializes a new instance of the NavigationResultWithoutValue class.
	*/
	constructor(state) {
		this.state = state ?? NavigationResultState.ok;
	}
};
/**
* Navigation result.
*/
var NavigationResult = class extends NavigationResultWithoutValue {
	/**
	* Initializes a new instance of the NavigationResult class.
	*/
	constructor(state, value) {
		super(state);
		this.value = value;
	}
};
//#endregion
//#region src/reactivity/system.ts
var _system;
function ensureReactivity(fn) {
	if (!_system) throw new Error("No reactivity");
	return fn(_system);
}
function setReactivity(system) {
	_system = system;
}
var hasReactivity = () => !!_system;
var reactive = (value) => _system?.reactive(value) ?? value;
var computed = (get) => ensureReactivity((system) => system.computed(get));
var writableComputed = (options) => ensureReactivity((system) => system.writableComputed(options));
var watch = (source, cb, options) => ensureReactivity((system) => system.watch(source, cb, options));
//#endregion
//#region src/reactivity/ValidationRuleWatchers.ts
var ValidationRuleWatchers = class {
	constructor(owner) {
		this._watchers = /* @__PURE__ */ new WeakMap();
		this._owner = owner;
	}
	add(items) {
		if (!hasReactivity()) return;
		const properties = this._owner.properties;
		for (const item of items) {
			let itemWatchers = this._watchers.get(item);
			if (!itemWatchers) {
				itemWatchers = /* @__PURE__ */ new Map();
				this._watchers.set(item, itemWatchers);
			}
			this.addItemWatchers(item, itemWatchers, properties);
		}
	}
	remove(items) {
		if (!hasReactivity()) return;
		for (const item of items) {
			const itemWatchers = this._watchers.get(item);
			if (itemWatchers) {
				this.removeItemWatchers(itemWatchers);
				this._watchers.delete(item);
			}
		}
	}
	addItemWatchers(item, itemWatchers, properties) {
		for (const property of properties) if (!itemWatchers.has(property.camelCaseName)) {
			const stopWatch = watch(() => this._owner.validateProperty(item, property), (validationMessage) => this.persistConstraintValidationMessage(item, property, validationMessage));
			itemWatchers.set(property.camelCaseName, stopWatch);
		}
		const validationRules = this._owner.getValidationRules(ValidationRuleValidationTypes.reportAll).filter((r) => r.properties);
		for (const validationRule of validationRules) {
			const key = `VR_${validationRule.method.name}`;
			if (!itemWatchers.has(key)) {
				const stopWatch = watch(() => ({ text: this._owner.runValidationRule(item, validationRule) }), ({ text }) => this.persistValidationRuleValidationMessage(item, validationRule, text), { immediate: !item.$new && this._owner.behavior.persistValidationRuleMessagesOnLoad });
				itemWatchers.set(key, stopWatch);
			}
		}
	}
	persistConstraintValidationMessage(item, property, validationMessage) {
		if (!item.$modifiedProperties.has(property.camelCaseName)) return;
		if (validationMessage) item.replaceValidationMessage(validationMessage);
		else item.removeValidationMessageByPredicate(property.camelCaseName, (m) => m instanceof ConstraintValidationMessage);
	}
	persistValidationRuleValidationMessage(item, validationRule, text) {
		if (!item.$new && this._owner.behavior.persistValidationRuleMessagesOnLoad || item.$modifiedProperties.size > 0) this._owner.setValidationRuleMessage(item, validationRule, text);
	}
	removeItemWatchers(itemWatchers) {
		for (const [, stopWatch] of itemWatchers) stopWatch();
		itemWatchers.clear();
	}
};
//#endregion
//#region src/reactivity/ViewModelPropertyWatchers.ts
var ViewModelPropertyWatchers = class {
	constructor(owner) {
		this._watchers = /* @__PURE__ */ new WeakMap();
		this._owner = owner;
	}
	add(items) {
		if (!hasReactivity()) return;
		const properties = this.getPropertiesToWatch();
		if (properties.length === 0) return;
		for (const item of items) {
			let itemWatchers = this._watchers.get(item);
			if (!itemWatchers) {
				itemWatchers = /* @__PURE__ */ new Map();
				this._watchers.set(item, itemWatchers);
			}
			this.addItemWatchers(item, itemWatchers, properties);
		}
	}
	remove(items) {
		if (!hasReactivity()) return;
		const properties = this.getPropertiesToWatch();
		if (properties.length === 0) return;
		for (const item of items) {
			const itemWatchers = this._watchers.get(item);
			if (itemWatchers) {
				this.removeItemWatchers(itemWatchers, properties);
				this._watchers.delete(item);
			}
		}
	}
	getPropertiesToWatch() {
		return this._owner.properties.filter((p) => p.watch !== void 0);
	}
	addItemWatchers(item, itemWatchers, properties) {
		for (const property of properties) if (!itemWatchers.has(property.camelCaseName)) {
			const source = [() => property.getValue(item)];
			if (property.isFormatExpression()) source.push(() => property.getFormat(item));
			const stopWatch = watch(source, ([value], [oldValue]) => this.raiseItemChanged(item, property, value, oldValue), { deep: property.watch === "deep" });
			itemWatchers.set(property.camelCaseName, stopWatch);
		}
	}
	removeItemWatchers(itemWatchers, properties) {
		for (const property of properties) {
			const stopWatch = itemWatchers.get(property.camelCaseName);
			if (stopWatch) {
				stopWatch();
				itemWatchers.delete(property.camelCaseName);
			}
		}
	}
	raiseItemChanged(item, property, value, oldValue) {
		const owner = this._owner;
		const index = owner.datasource.indexOf(item);
		if (index === -1) return;
		if (owner.canLoadClientSideSummaries()) owner.loadClientSideTotalSummary(owner.datasource, property);
		owner.datasourceListChangedEvent.raise(owner, {
			type: ListChangedType.itemChanged,
			item,
			index,
			itemChangedArgs: {
				item,
				value,
				oldValue,
				propertyName: property.camelCaseName
			}
		});
	}
};
//#endregion
//#region src/reporting/core/ReportingQueryBuilder.ts
var ReportingQueryBuilder = class {
	/**
	* Gets the parameters from the generate options.
	* @param options - Generate options.
	* @returns array of key=value string.
	*/
	static getDictionaryOfParamsFromBaseOptions(options) {
		const params = /* @__PURE__ */ new Map();
		if (options.culture) params.set("culture", options.culture);
		if (options.filter) if (typeof options.filter === "string") params.set("$filter", options.filter);
		else params.set("$filter", options.filter.toODataString());
		if (options.parameters) for (const [key, value] of options.parameters) params.set(key, convertToSafeQueryStringValue(value));
		if (options.entityViewParameters) {
			const prefix = "neos_evp";
			for (const [key, value] of options.entityViewParameters) params.set(`${prefix}_${key}`, convertToSafeQueryStringValue(value));
		}
		if (options.stylesBundle) params.set("stylesBundle", options.stylesBundle);
		if (options.timeZone) params.set("timeZone", options.timeZone);
		return params;
	}
};
/**
* Converts the value to a safe query string value.
* @param value - Value to convert.
* @returns Safe query string value.
*/
function convertToSafeQueryStringValue(value) {
	if (value == null) return "";
	let encodedValue;
	if (typeof value === "string") encodedValue = encodeURIComponent(value);
	else {
		encodedValue = JSON.stringify(value);
		if (encodedValue.startsWith("\"") && encodedValue.endsWith("\"")) return encodedValue.substring(1, encodedValue.length - 1);
	}
	return encodedValue;
}
//#endregion
//#region src/reporting/core/UrlManager.ts
/**
* Manages the URLs for reporting.
*/
var UrlManager = class {
	constructor() {
		this.pathViewer = "Viewer/Index";
		this.pathDesigner = "Designer/Reports";
		this.pathExecute = "export";
	}
	/**
	* Gets a URL for editing a report template or a report styles bundle.
	*/
	getUrlDesigner(baseUrl, assets, options) {
		const { override, renderingOptions, styleDesignerOptions } = options || {};
		let url = this.getUrl(baseUrl, assets.reportName, this.pathDesigner);
		let separator = "?";
		function appendUrlParam(key, value, defaultValue) {
			if (value !== void 0 && value !== defaultValue) {
				url += `${separator}${key}=${typeof value === "string" ? encodeURIComponent(value) : value}`;
				separator = "&";
			}
		}
		appendUrlParam("businessClusterName", assets.businessClusterName);
		appendUrlParam("stylesBundleName", assets.stylesBundleName);
		appendUrlParam("allowOverride", override?.allowOverride, false);
		appendUrlParam("workingModuleName", override?.workingModuleName);
		appendUrlParam("tenantDataIdentifier", renderingOptions?.tenantDataIdentifier);
		appendUrlParam("reportGenerationConfigName", renderingOptions?.reportGenerationConfigName);
		appendUrlParam("isAutoApply", styleDesignerOptions?.isAutoApply, true);
		appendUrlParam("isShowNativeStyles", styleDesignerOptions?.isShowNativeStyles, false);
		return url;
	}
	getUrlViewer(baseUrl, options, extraViewerSpecificParams) {
		let url = this.getUrl(baseUrl, options.reportName, this.pathViewer);
		url = this.appendGenerateOptionsToUrl(url, options, extraViewerSpecificParams);
		return url;
	}
	getUrlExecute(baseUrl, options) {
		let url = this.getUrl(baseUrl, options.reportName, this.pathExecute);
		url = this.appendGenerateOptionsToUrl(url, options, null);
		return url;
	}
	getUrl(baseUrl, reportName, path) {
		return `${baseUrl}/${path}/${reportName}`;
	}
	/**
	* Appends the generate options to the url.
	* @param url - Url.
	* @param options - Generate options.
	* @returns The url with the generate options.
	*/
	appendGenerateOptionsToUrl(url, options, extraViewerSpecificParams) {
		const params = extraViewerSpecificParams ?? [];
		const baseQueryParams = ReportingQueryBuilder.getDictionaryOfParamsFromBaseOptions(options);
		for (const [key, value] of baseQueryParams) params.push(`${key}=${value}`);
		if (params.length > 0) url += `?${params.join("&")}`;
		return url;
	}
};
//#endregion
//#region src/reporting/core/Reporting.ts
var Reporting = class Reporting {
	static {
		this._printSettingsUIViewName = "UserReportPrintSettingsUI";
	}
	static {
		this._pendingReportOptions = /* @__PURE__ */ new Map();
	}
	static getBaseUrl(baseUrl = getBaseUrl()) {
		return `${baseUrl}/reporting`;
	}
	/**
	* Gets the navigation options of the native Neos Report Viewer UI View.
	* @param options - Options.
	* @returns Navigation options.
	*/
	static getShowReportNavigationOptions(options) {
		const queryParams = ReportingQueryBuilder.getDictionaryOfParamsFromBaseOptions(options);
		if (options.initialViewerMode) queryParams.set("initialReportViewerMode", options.initialViewerMode);
		return new NavigationOptions("NeosReportViewerUI").withId(new Map([["reportName", options.reportName], ["title", options.title]])).withTitle(options.title || options.reportName).withParameter("ReportOptions", options).withUrlParameters(queryParams);
	}
	static canNavigateToPrintSettings(uiViewPermitter) {
		return "isTauri" in globalThis && uiViewPermitter.canRead(this._printSettingsUIViewName);
	}
	static getPrintSettingsNavigationOptions(printSettingsName, options) {
		const navigationOptions = new NavigationOptions(this._printSettingsUIViewName).withPopupMaximizable(false).withParameter("PrintSettingsName", printSettingsName);
		if (options) navigationOptions.withParameter("ReportOptions", options);
		return navigationOptions;
	}
	/**
	* Ask the server to generate a report.
	* @param options - Options.
	* @param callingUIViewName - The name of the UI View that is calling this function.
	*/
	static async executeReportAsync(options, dependencies) {
		if (dependencies.callingUIViewName !== this._printSettingsUIViewName) {
			if (dependencies.callingUIViewName && !options.uiViewName) options.withUiViewName(dependencies.callingUIViewName);
			if (Reporting.canNavigateToPrintSettings(dependencies.uiViewPermitter)) {
				const printSettingsName = options.printSettings ?? options.reportName;
				const instantPrintSettings = Reporting.findInstantPrintSettings(dependencies.applicationContext, printSettingsName);
				if (instantPrintSettings) options.withClientContext(new Map([["PrintJobs", instantPrintSettings.printJobs]]));
				else {
					navigate(dependencies.eventManager, Reporting.getPrintSettingsNavigationOptions(printSettingsName, options));
					return;
				}
			}
		}
		const url = new UrlManager().getUrlExecute(dependencies.reportingBaseUrl, options.baseReportOptions);
		const request = new Request(url, {
			method: "post",
			headers: FetchApiClient.getHeaders(dependencies.applicationContext)
		});
		if (options.uiViewName) request.headers.set("neos-ui-view-name", options.uiViewName);
		request.headers.set("Neos-ReportName", options.baseReportOptions.reportName);
		const response = await FetchApiClient.fetchWithTimeout(request, FetchApiClient.getDefaultTimeout(dependencies.applicationContext.webApiTimeout));
		if (response.ok) {
			const generationIdentifier = await response.json();
			this._pendingReportOptions.set(generationIdentifier, options);
		} else {
			const error = await FetchApiClient.parseError(request, response);
			error.path = url;
			dependencies.eventManager.emit("viewModel:showError", ViewModelError.fromApiError(dependencies.globalResourceManager, error));
		}
	}
	/**
	* Gets the report designer url.
	* @param assets - Mandatory report name and optional styles bundle name.
	* @returns The report designer url. For example: https://localhost:5001/reporting/Designer/Reports/ReportName.
	*/
	static getReportDesignerUrl(assets, options) {
		const urlManager = new UrlManager();
		const baseUrl = Reporting.getBaseUrl();
		return urlManager.getUrlDesigner(baseUrl, assets, options);
	}
	/**
	* Gets the report viewer url.
	* @param options - Report viewer options.
	* @param uiViewName - The name of the UIView showing the report (if any)
	*   (used for showing reports in conjonction with a quick search filter).
	* @returns The report viewer url. For example:
	*   https://localhost:5001/reporting/Viewer/Index/MyReport?uiViewName=MyReportUI&$filter=Filter&param1=value1&param2=value2.
	* @remarks
	* - The tenant is included in the url only if the withTenant parameter is true and the tenant is not null.
	* - The uiViewName should actually always be defined (either as the native `NeosReportViewerUI` or as a custom UI View name).
	*     - The case when uiViewName is undefined is theoretical and should not happen in practice.
	*/
	static getReportViewerUrl(baseUrl, options, uiViewName) {
		const urlManager = new UrlManager();
		const extraViewerSpecificParams = [`uiViewName=${uiViewName}`];
		if (options.initialViewerMode) extraViewerSpecificParams.push(`initialReportViewerMode=${options.initialViewerMode}`);
		return urlManager.getUrlViewer(baseUrl, options.baseReportOptions, extraViewerSpecificParams);
	}
	static popPendingReportOptions(generationIdentifier) {
		const reportOptions = this._pendingReportOptions.get(generationIdentifier);
		if (reportOptions) this._pendingReportOptions.delete(generationIdentifier);
		return reportOptions;
	}
	static getReportFileUrl(baseUrl, generationIdentifier, fileIdentifier, isAttachment) {
		let fileUrl = baseUrl;
		fileUrl += generationIdentifier !== fileIdentifier ? `/webapi/$neos/reports/download/${fileIdentifier}` : `/reporting/download/${fileIdentifier}`;
		if (isAttachment) fileUrl += "?mode=attachment";
		return fileUrl;
	}
	static findInstantPrintSettings(applicationContext, name) {
		return applicationContext.get("UserInstantReportPrintSettings")?.find((settings) => settings.name === name);
	}
};
//#endregion
//#region src/reporting/options/base/PrivateBaseReportOptions.ts
var PrivateBaseReportOptions = class {
	/**
	* Initializes a new instance of the InternalReportOptions class.
	* @param reportName - Report name.
	*/
	constructor(reportName) {
		this.reportName = reportName;
		this.uiViewName = null;
		this.culture = null;
		this.filter = null;
		this.parameters = null;
		this.entityViewParameters = null;
		this.stylesBundle = null;
		this.timeZone = null;
	}
	/**
	* Sets the UI view name.
	* (OPTIONAL) Used for passing the 'neos-ui-view-name' HTTP header when using QuickSearch, REF. bug/26663).
	* @param uiViewName - UIView.
	* @returns - The current instance.
	*/
	withUiViewName(uiViewName) {
		this.uiViewName = uiViewName;
		return this;
	}
	/**
	* Sets the culture.
	* @param culture - Culture.
	* @returns - The current instance.
	*/
	withCulture(culture) {
		this.culture = culture;
		return this;
	}
	/**
	* Sets the name of the style bundle and collection to apply.
	* REMARK: At the moment, there is only one parameter because we will always apply the 'DefaultCollection'.
	* @param stylesBundle - Name of the bundle containing the collection of styles to apply.
	* @returns - The current instance.
	*/
	withStylesCollection(stylesBundle) {
		this.stylesBundle = stylesBundle;
		return this;
	}
	/**
	* Sets the name of the time zone to apply on the DateTime properties.
	* @param timeZone - Name of the time zone.
	* @returns - The current instance.
	*/
	withTimeZone(timeZone) {
		this.timeZone = timeZone;
		return this;
	}
	/**
	* Sets the filter.
	* @param filter - Filter.
	* @returns - The current instance.
	*/
	withFilter(filter) {
		this.filter = filter;
		return this;
	}
	/**
	* Adds one parameter.
	* @param key - Parameter key.
	* @param value - Parameter value.
	* @returns - The current instance.
	*/
	withParameter(key, value) {
		this.parameters ??= /* @__PURE__ */ new Map();
		this.parameters.set(key, value);
		return this;
	}
	/**
	* Sets multiple parameters.
	* @param parameters - Report parameters
	* @returns - The current instance.
	*/
	withParameters(parameters) {
		this.parameters = parameters;
		return this;
	}
	/**
	* Adds one entity-view parameter.
	* @param key - Parameter key.
	* @param value - Parameter value.
	* @returns - The current instance.
	*/
	withEntityViewParameter(key, value) {
		this.entityViewParameters ??= /* @__PURE__ */ new Map();
		this.entityViewParameters.set(key, value);
		return this;
	}
	/**
	* Sets multiple entity-view parameters.
	* @param entityViewParameters - Report parameters
	* @returns - The current instance.
	*/
	withEntityViewParameters(entityViewParameters) {
		this.entityViewParameters = entityViewParameters;
		return this;
	}
};
//#endregion
//#region src/reporting/options/base/AbstractReportOptions.ts
var AbstractReportOptions = class {
	/**
	* Initializes a new instance of the BaseReportOptions class.
	* @param reportName - Report name.
	*/
	constructor(reportName) {
		this.baseReportOptions = new PrivateBaseReportOptions(reportName);
	}
	/**
	* Gets the name of the template report.
	*/
	get reportName() {
		return this.baseReportOptions.reportName;
	}
	/**
	* Gets the UI view name.
	* (OPTIONAL) Used for passing the 'neos-ui-view-name' HTTP header when using QuickSearch, REF. bug/26663).
	*/
	get uiViewName() {
		return this.baseReportOptions.uiViewName;
	}
	/**
	* Gets the culture.
	*/
	get culture() {
		return this.baseReportOptions.culture;
	}
	/**
	* Gets the stylesBundle.
	*/
	get stylesBundle() {
		return this.baseReportOptions.stylesBundle;
	}
	/**
	* Gets the stylesBundle.
	*/
	get timeZone() {
		return this.baseReportOptions.timeZone;
	}
	/**
	* Gets the filter.
	*/
	get filter() {
		return this.baseReportOptions.filter;
	}
	/**
	* Gets the parameters.
	*/
	get parameters() {
		return this.baseReportOptions.parameters;
	}
	/**
	* Gets the entity view parameters.
	*/
	get entityViewParameters() {
		return this.baseReportOptions.entityViewParameters;
	}
	/**
	* Creates a new instance of the BaseReportOptions class.
	* @param generateReportOptions - Generate report options.
	*/
	static create(generateReportOptions) {
		const instance = new this(generateReportOptions.reportName);
		instance.baseReportOptions = generateReportOptions;
		return instance;
	}
	/**
	* Sets the UI view name.
	* (OPTIONAL) Used for passing the 'neos-ui-view-name' HTTP header when using QuickSearch, REF. bug/26663).
	* @param uiViewName - UIView.
	* @returns - The current instance.
	*/
	withUiViewName(uiViewName) {
		this.baseReportOptions.withUiViewName(uiViewName);
		return this;
	}
	/**
	* Sets the culture.
	* @param culture - Culture.
	* @returns - The current instance.
	*/
	withCulture(culture) {
		this.baseReportOptions.withCulture(culture);
		return this;
	}
	/**
	* Sets the name of the styles bundle and collection to apply.
	* REMARK: At the moment, there is only one parameter because we will always apply the 'DefaultCollection'.
	* @param stylesBundle - Name of the bundle containing the collection of styles to apply.
	* @returns - The current instance.
	*/
	withStylesCollection(stylesBundle) {
		this.baseReportOptions.withStylesCollection(stylesBundle);
		return this;
	}
	/**
	* Sets the name of the time zone to apply on the DateTime properties.
	* @param timeZone - Name of the time zone.
	* @returns - The current instance.
	*/
	withTimeZone(timeZone) {
		this.baseReportOptions.withTimeZone(timeZone);
		return this;
	}
	/**
	* Sets the filter.
	* @param filter - Filter.
	* @returns - The current instance.
	*/
	withFilter(filter) {
		this.baseReportOptions.withFilter(filter);
		return this;
	}
	/**
	* Adds one parameter.
	* @param key - Parameter key.
	* @param value - Parameter value.
	* @returns - The current instance.
	*/
	withParameter(key, value) {
		this.baseReportOptions.withParameter(key, value);
		return this;
	}
	/**
	* Sets multiple parameters.
	* @param parameters - Report parameters
	* @returns - The current instance.
	*/
	withParameters(parameters) {
		this.baseReportOptions.withParameters(parameters);
		return this;
	}
	/**
	* Adds one entity-view parameter.
	* @param key - Parameter key.
	* @param value - Parameter value.
	* @returns - The current instance.
	*/
	withEntityViewParameter(key, value) {
		this.baseReportOptions.withEntityViewParameter(key, value);
		return this;
	}
	/**
	* Sets multiple entity-view parameters.
	* @param entityViewParameters - Report parameters
	* @returns - The current instance.
	*/
	withEntityViewParameters(entityViewParameters) {
		this.baseReportOptions.withEntityViewParameters(entityViewParameters);
		return this;
	}
};
//#endregion
//#region src/reporting/options/ExecuteReportOptions.ts
/**
* Represents the options for ExecuteReport function.
*/
var ExecuteReportOptions = class extends AbstractReportOptions {
	withPrintSettings(printSettings) {
		this.printSettings = printSettings;
		return this;
	}
	withClientContext(clientContext) {
		this.clientContext = clientContext;
		return this;
	}
};
//#endregion
//#region src/sorting/BaseSort.ts
/**
* Represents a base class for sorting.
* A property sort has a  property name and a direction.
* A expression sort has only an expression.
*/
var BaseSort = class {};
//#endregion
//#region src/sorting/Sort.ts
var Sort = class extends BaseSort {
	constructor(propertyName, direction, propertyPath) {
		super();
		this._propertyName = propertyName;
		this._propertyPath = propertyPath ?? propertyName;
		this._direction = direction;
	}
	get propertyName() {
		return this._propertyName;
	}
	get propertyPath() {
		return this._propertyPath;
	}
	get direction() {
		return this._direction;
	}
	/**
	* Gets the string representation of the sort
	* @returns The string representation of the sort.
	*/
	toString() {
		return `${this.propertyName} ${this.direction}`;
	}
	/**
	* Converts the sort to an OData string.
	* @returns The OData string.
	*/
	toODataString() {
		return `${this.propertyPath.replaceAll(".", "/")} ${this.direction}`;
	}
};
//#endregion
//#region src/sorting/SortingBehavior.ts
var SortingExecutionSide = /* @__PURE__ */ function(SortingExecutionSide) {
	SortingExecutionSide["server"] = "server";
	SortingExecutionSide["clientWhenFullDataLoaded"] = "clientWhenFullDataLoaded";
	return SortingExecutionSide;
}({});
//#endregion
//#region src/summaries/SummaryResult.ts
var SummaryResult = class SummaryResult {
	static {
		this.empty = new SummaryResult();
	}
	constructor(summaries) {
		this._summaries = summaries;
	}
	tryGetValueWithRequest(summaryRequest, result) {
		return this.tryGetValue(summaryRequest.property, summaryRequest.summaryType, result);
	}
	tryGetValue(property, summaryType, result) {
		if (!this._summaries) {
			result.value = null;
			return false;
		}
		const value = this._summaries.get(`${property.name}_${summaryType}`);
		if (value === void 0) {
			result.value = null;
			return false;
		}
		result.value = value;
		return true;
	}
	static normalizeSummaries(summaryDescriptions, summaries) {
		const normalizedSummaries = /* @__PURE__ */ new Map();
		for (const [request, summaryDescription] of summaryDescriptions) if (summaryDescription.alias in summaries) normalizedSummaries.set(`${request.property.name}_${request.summaryType}`, summaries[summaryDescription.alias]);
		return normalizedSummaries;
	}
};
//#endregion
//#region src/summaries/GroupSummaryResult.ts
var GroupSummaryResult = class extends SummaryResult {
	constructor(key, summaries) {
		super(summaries);
		this.key = key;
	}
};
//#endregion
//#region src/summaries/SummaryLoadMode.ts
/**
* Specifies the summary load mode.
*/
var SummaryLoadMode = /* @__PURE__ */ function(SummaryLoadMode) {
	/**
	* Auto.
	*/
	SummaryLoadMode[SummaryLoadMode["auto"] = 0] = "auto";
	/**
	* Server.
	*/
	SummaryLoadMode[SummaryLoadMode["server"] = 1] = "server";
	/**
	* Client.
	*/
	SummaryLoadMode[SummaryLoadMode["client"] = 2] = "client";
	return SummaryLoadMode;
}({});
//#endregion
//#region src/viewModelProperty/LookupDefinition.ts
var maxLookupContainsSearchTerms = 5;
var LookupMainMode = /* @__PURE__ */ function(LookupMainMode) {
	LookupMainMode["paginatedSearchOnServer"] = "PaginatedSearchOnServer";
	LookupMainMode["exhaustiveListInCombobox"] = "ExhaustiveListInCombobox";
	return LookupMainMode;
}({});
var LookupSearchMode = /* @__PURE__ */ function(LookupSearchMode) {
	LookupSearchMode["default"] = "Default";
	LookupSearchMode["quickSearch"] = "QuickSearch";
	return LookupSearchMode;
}({});
var LookupTemplateMode = /* @__PURE__ */ function(LookupTemplateMode) {
	LookupTemplateMode["default"] = "default";
	LookupTemplateMode["own"] = "own";
	return LookupTemplateMode;
}({});
function getLookupDisplayProperty(definition) {
	return definition.displayProperty ?? definition.valueProperty;
}
function getLookupSearchProperty(definition) {
	if (definition.searchMode === "QuickSearch") return getLookupDisplayProperty(definition);
	return definition.searchProperty ?? getLookupDisplayProperty(definition);
}
function getLookupEditingUIViewId(keyProperties, isReference, value) {
	const id = /* @__PURE__ */ new Map();
	for (const keyProperty of keyProperties) id.set(keyProperty, isReference ? getPropertyByPath(value, keyProperty) : value);
	return id;
}
function getLookupSearchFilter(definition, inputValue, language) {
	if (definition.searchMode === "QuickSearch") return Filter.create("isMatch", FilterOperator.isMatch, inputValue);
	const searchProperty = getLookupSearchProperty(definition);
	const containsInputValue = getLookupContainsInputValue(inputValue);
	const searchTerms = getLookupSearchTerms(containsInputValue);
	const shouldUseContainsFilter = isLookupContainsSearchActive(definition, inputValue, searchTerms);
	if (shouldBuildContainsFilterBySearchTerm(definition, searchProperty, shouldUseContainsFilter, searchTerms)) {
		const filter = new Filter(null, FilterLogicalOperator.and);
		for (const searchTerm of searchTerms) filter.add(getLookupPropertyFilter(searchProperty, FilterOperator.contains, searchTerm, language));
		return filter;
	}
	switch (searchProperty.dataType) {
		case DataType.autoIncrementedInteger:
		case DataType.integer:
		case DataType.decimal: return getLookupPropertyFilter(searchProperty, FilterOperator.equal, Number(inputValue), language);
		default:
			if (shouldUseContainsFilter) return getLookupPropertyFilter(searchProperty, FilterOperator.contains, getLookupContainsFilterValue(definition, inputValue, containsInputValue), language);
			return getStringValueLookupSearchFilter(searchProperty, inputValue, language);
	}
}
function getLookupContainsInputValue(inputValue) {
	if (!isInputValueWithWildcards(inputValue)) return inputValue;
	return inputValue.substring(1, inputValue.length - 1);
}
function shouldBuildContainsFilterBySearchTerm(definition, searchProperty, shouldUseContainsFilter, searchTerms) {
	if (definition.searchProperty === void 0 || !shouldUseContainsFilter) return false;
	if (!isStringLookupProperty(searchProperty)) return false;
	return searchTerms.length > 1 && searchTerms.length <= maxLookupContainsSearchTerms;
}
function isLookupContainsSearchActive(definition, inputValue, searchTerms = getLookupSearchTerms(getLookupContainsInputValue(inputValue))) {
	const searchProperty = getLookupSearchProperty(definition);
	if (definition.filterOperator === FilterOperator.contains) return true;
	if (hasSingleSidedWildcard(inputValue)) return false;
	return isInputValueWithWildcards(inputValue) || searchTerms.length > 1 && isStringLookupProperty(searchProperty);
}
function hasSingleSidedWildcard(inputValue) {
	return inputValue.length > 1 && inputValue.startsWith("*") !== inputValue.endsWith("*");
}
function getLookupContainsFilterValue(definition, inputValue, containsInputValue) {
	return definition.filterOperator === FilterOperator.contains && !isInputValueWithWildcards(inputValue) ? inputValue : containsInputValue;
}
function isStringLookupProperty(searchProperty) {
	return searchProperty.dataType === void 0 || searchProperty.dataType === DataType.string || searchProperty.dataType === DataType.localizableString;
}
function getLookupSearchTerms(inputValue) {
	return inputValue.split(/\s+/).map((value) => value.trim()).filter((value) => value.length > 0);
}
function getStringValueLookupSearchFilter(displayProperty, inputValue, language) {
	if (inputValue.length < 2) return getLookupPropertyFilter(displayProperty, FilterOperator.startsWith, inputValue, language);
	const startWildcard = inputValue.startsWith("*");
	const endWildcard = inputValue.endsWith("*");
	if (startWildcard) return getLookupPropertyFilter(displayProperty, FilterOperator.endsWith, inputValue.substring(1), language);
	return getLookupPropertyFilter(displayProperty, FilterOperator.startsWith, endWildcard ? inputValue.substring(0, inputValue.length - 1) : inputValue, language);
}
function isInputValueWithWildcards(inputValue) {
	return inputValue.length > 2 && inputValue.startsWith("*") && inputValue.endsWith("*");
}
function getLookupPropertyPath(lookupProperty, language) {
	let propertyPath = lookupProperty.camelCaseName;
	if (lookupProperty.dataType === DataType.localizableString) propertyPath = `${propertyPath}.${language}`;
	return propertyPath;
}
function getLookupPropertyFilter(lookupProperty, operator, value, language) {
	return Filter.create(getLookupPropertyPath(lookupProperty, language), operator, value);
}
//#endregion
//#region src/viewModel/DataSharingManager.ts
/**
* Represents a manager that handles the sharing of data sources between view models.
*/
var DataSharingManager = class {
	/**
	* Indicates whether the view model is sharing its datasource with another view model.
	*/
	get isSharingDatasource() {
		return this.sharedProperties != null;
	}
	/**
	* Initializes a new instance of the `DatasourceSharingManager` class.
	* @param viewModel View model on which the manager operates.
	*/
	constructor(viewModel) {
		this.sharedDatasource = false;
		this.viewModel = viewModel;
	}
	checkCoherence(viewModelThatShares) {
		if (this.sharedDatasource) viewModelThatShares = this.viewModel.datasource[0]?.$viewModel;
		if (viewModelThatShares === void 0) return;
		viewModelThatShares.dataSharingManager.setSharedProperties();
		const incoherences = [...this.checkSubViewsCoherence(viewModelThatShares), ...this.checkPropertiesCoherence(viewModelThatShares)];
		this.displayIncoherence(incoherences, viewModelThatShares.uiViewName);
	}
	/**
	* Indicates whether a property is part of the properties that are shared with another UI view.
	* @param propertyName - Property name.
	* @returns `true` when the property is shared; otherwhise `false`.
	*/
	isSharedProperty(propertyName) {
		return this.sharedProperties?.has(propertyName) ?? false;
	}
	/**
	* Sets the list of properties of the view model that are shared with another view model.
	*/
	setSharedProperties() {
		this.sharedProperties = /* @__PURE__ */ new Set();
		for (const property of this.viewModel.properties.filter((p) => p.fromWebAPI)) this.sharedProperties.add(property.camelCaseName);
	}
	checkSubViewsCoherence(viewModelThatShares) {
		const incoherences = [];
		for (const subview of this.viewModel.subViews) if (!viewModelThatShares.subViews.some((psv) => psv.parentRelation.name === subview.parentRelation.name && psv.entityViewName === subview.entityViewName)) incoherences.push(stringFormat(this.viewModel.globalResourceManager.get("IncoherentUiViewForDataSharing_Subview"), subview.parentRelation.name, subview.entityViewName, viewModelThatShares.uiViewName));
		return incoherences;
	}
	checkPropertiesCoherence(viewModelThatShares) {
		const incoherences = [];
		for (const property of this.viewModel.properties) {
			if (this.viewModel.entityViewName !== viewModelThatShares.entityViewName) {
				if (!property.fromWebAPI || property.isComputed) continue;
				const parentProperty = this.getParentProperty(property.name, viewModelThatShares, incoherences);
				if (parentProperty && parentProperty.dataType !== property.dataType) incoherences.push(stringFormat(this.viewModel.globalResourceManager.get("IncoherentUiViewForDataSharing_PropertyType"), property.name));
			}
			this.checkLookupDisplayPropertyCoherence(property, viewModelThatShares, incoherences);
		}
		return incoherences;
	}
	checkLookupDisplayPropertyCoherence(property, viewModelThatShares, incoherences) {
		if (property.dataType && property.lookupDefinition?.displayProperty) {
			const parentProperty = viewModelThatShares.findProperty(property.name);
			if (parentProperty && parentProperty.lookupDefinition?.displayProperty?.camelCaseName !== property.lookupDefinition.displayProperty.camelCaseName) incoherences.push(stringFormat(this.viewModel.globalResourceManager.get("IncoherentUiViewForDataSharing_PropertyLookupDisplayProperty"), property.name));
		}
	}
	/**
	* In development, displays an error message when there is an incoherence between UI views that share data.
	* In production, only logs the error.
	* @param incoherences Incoherences.
	*/
	displayIncoherence(incoherences, parentUiViewName) {
		if (incoherences.length === 0) return;
		const errorMessage = stringFormat(this.viewModel.globalResourceManager.get("IncoherentUiViewForDataSharing"), parentUiViewName, this.viewModel.uiViewName, incoherences.join("\n"));
		this.viewModel.showDevelopmentError(new Error(errorMessage));
	}
	getParentProperty(propertyName, parentViewModel, incoherences) {
		const parentProperty = parentViewModel.findProperty(propertyName);
		if (parentProperty === void 0) incoherences.push(stringFormat(this.viewModel.globalResourceManager.get("IncoherentUiViewForDataSharing_Property"), propertyName, parentViewModel.uiViewName));
		return parentProperty;
	}
};
//#endregion
//#region src/viewModelAction/ViewModelActionBadgeSeverity.ts
/**
* View model action size.
*/
var ViewModelActionBadgeSeverity = /* @__PURE__ */ function(ViewModelActionBadgeSeverity) {
	/** None */
	ViewModelActionBadgeSeverity["none"] = "none";
	/** Info */
	ViewModelActionBadgeSeverity["info"] = "info";
	/** Positive */
	ViewModelActionBadgeSeverity["positive"] = "positive";
	/** Warning */
	ViewModelActionBadgeSeverity["warning"] = "warning";
	/** Danger */
	ViewModelActionBadgeSeverity["danger"] = "danger";
	return ViewModelActionBadgeSeverity;
}({});
//#endregion
//#region src/viewModelAction/ViewModelActionConstraint.ts
/**
* View model action constraint.
*/
var ViewModelActionConstraint = /* @__PURE__ */ function(ViewModelActionConstraint) {
	/** no constraint */
	ViewModelActionConstraint["none"] = "None";
	/** on datasource item */
	ViewModelActionConstraint["onDatasourceItem"] = "OnDatasourceItem";
	return ViewModelActionConstraint;
}({});
//#endregion
//#region src/viewModelAction/ViewModelActionDisplayMode.ts
/**
* View model action style.
*/
var ViewModelActionDisplayMode = /* @__PURE__ */ function(ViewModelActionDisplayMode) {
	/** Icon before text */
	ViewModelActionDisplayMode["iconBeforeText"] = "iconBeforeText";
	/** Icon */
	ViewModelActionDisplayMode["icon"] = "icon";
	/** Text */
	ViewModelActionDisplayMode["text"] = "text";
	return ViewModelActionDisplayMode;
}({});
//#endregion
//#region src/viewModelAction/ViewModelActionLocation.ts
/**
* View model action location.
*/
var ViewModelActionLocation = /* @__PURE__ */ function(ViewModelActionLocation) {
	/** Toolbar */
	ViewModelActionLocation["toolbar"] = "toolbar";
	/** Datagrid row */
	ViewModelActionLocation["datagridRow"] = "datagridRow";
	/** Datagrid row context menu */
	ViewModelActionLocation["datagridRowContextMenu"] = "datagridRowContextMenu";
	/** Datagrid footer */
	ViewModelActionLocation["datagridFooter"] = "datagridFooter";
	/** Panel */
	ViewModelActionLocation["panel"] = "panel";
	/** Child of */
	ViewModelActionLocation["childOf"] = "childOf";
	/** Relative to */
	ViewModelActionLocation["relativeTo"] = "relativeTo";
	/** Hidden */
	ViewModelActionLocation["hidden"] = "hidden";
	return ViewModelActionLocation;
}({});
//#endregion
//#region src/viewModelAction/ViewModelActionLocationEntry.ts
var ViewModelActionLocationEntry = class ViewModelActionLocationEntry {
	static toolbar() {
		return new ViewModelActionLocationEntry(ViewModelActionLocation.toolbar);
	}
	static datagridRow() {
		return new ViewModelActionLocationEntry(ViewModelActionLocation.datagridRow);
	}
	static datagridRowContextMenu() {
		return new ViewModelActionLocationEntry(ViewModelActionLocation.datagridRowContextMenu);
	}
	static datagridFooter() {
		return new ViewModelActionLocationEntry(ViewModelActionLocation.datagridFooter);
	}
	static panel() {
		return new ViewModelActionLocationEntry(ViewModelActionLocation.panel);
	}
	static childOf(parentActionName) {
		return new ViewModelActionLocationEntry(ViewModelActionLocation.childOf).withParentActionName(parentActionName);
	}
	static relativeTo(parentActionName) {
		return new ViewModelActionLocationEntry(ViewModelActionLocation.relativeTo).withParentActionName(parentActionName);
	}
	static hidden() {
		return new ViewModelActionLocationEntry(ViewModelActionLocation.hidden);
	}
	constructor(location) {
		this.position = 0;
		this.beginGroup = false;
		this.location = location;
	}
	/**
	* Gets the group.
	*/
	get group() {
		return this._group ?? this.groupFn?.();
	}
	/**
	* Sets the group.
	*/
	set group(value) {
		this._group = value;
	}
	withParentActionName(parentActionName) {
		this.parentActionName = parentActionName;
		return this;
	}
	withPosition(position) {
		this.position = position;
		return this;
	}
	withBeginGroup(beginGroup = true) {
		this.beginGroup = beginGroup;
		return this;
	}
	withGroupFn(groupFn) {
		this.groupFn = groupFn;
		return this;
	}
};
//#endregion
//#region src/viewModelAction/ViewModelActionSize.ts
/**
* View model action size.
*/
var ViewModelActionSize = /* @__PURE__ */ function(ViewModelActionSize) {
	/** Medium */
	ViewModelActionSize["medium"] = "medium";
	/** Extrasmall */
	ViewModelActionSize["extrasmall"] = "extrasmall";
	/** Small */
	ViewModelActionSize["small"] = "small";
	/** Large */
	ViewModelActionSize["large"] = "large";
	/** Extralarge */
	ViewModelActionSize["extralarge"] = "extralarge";
	/** Input */
	ViewModelActionSize["input"] = "input";
	return ViewModelActionSize;
}({});
//#endregion
//#region src/viewModelAction/ViewModelActionStyle.ts
/**
* View model action style.
*/
var ViewModelActionStyle = /* @__PURE__ */ function(ViewModelActionStyle) {
	ViewModelActionStyle["default"] = "default";
	ViewModelActionStyle["positive"] = "positive";
	ViewModelActionStyle["primary"] = "primary";
	ViewModelActionStyle["danger"] = "danger";
	ViewModelActionStyle["ghost"] = "ghost";
	ViewModelActionStyle["ghostPositive"] = "ghost-positive";
	ViewModelActionStyle["ghostPrimary"] = "ghost-primary";
	ViewModelActionStyle["ghostDanger"] = "ghost-danger";
	ViewModelActionStyle["link"] = "link";
	return ViewModelActionStyle;
}({});
//#endregion
//#region src/viewModelAction/ViewModelActionType.ts
/**
* View model action style.
*/
var ViewModelActionType = /* @__PURE__ */ function(ViewModelActionType) {
	ViewModelActionType["none"] = "none";
	ViewModelActionType["openView"] = "openView";
	ViewModelActionType["code"] = "code";
	ViewModelActionType["toggleOverlay"] = "toggleOverlay";
	return ViewModelActionType;
}({});
//#endregion
//#region src/viewModelAction/ViewModelActionBase.ts
/**
* ViewModelActionBase.
*/
var ViewModelActionBase = class {
	/**
	* Gets the caption.
	*/
	get caption() {
		return this._caption ?? this.defaultCaption;
	}
	/**
	* Sets the caption.
	*/
	set caption(value) {
		this._caption = value;
	}
	/**
	* Gets the default caption.
	*/
	get defaultCaption() {
		return this.owner.resourceManager.find(`actions.${this.name}.caption`) ?? this.name;
	}
	/**
	* Gets the description.
	*/
	get description() {
		return this._description ?? this.owner.resourceManager.find(`actions.${this.name}.description`) ?? this.caption;
	}
	/**
	* Sets the description.
	*/
	set description(value) {
		this._description = value;
	}
	/**
	* Gets the location.
	*/
	get location() {
		return this.locations[0]?.location ?? ViewModelActionLocation.toolbar;
	}
	/**
	* Sets the location.
	*/
	set location(value) {
		const location = this.locations[0];
		if (location) location.location = value;
	}
	/**
	* Gets the parent action name.
	*/
	get parentActionName() {
		return this.locations[0]?.parentActionName;
	}
	/**
	* Sets the parent action name.
	*/
	set parentActionName(value) {
		const location = this.locations[0];
		if (location) location.parentActionName = value;
	}
	/**
	* Gets the position.
	*/
	get position() {
		return this.locations[0]?.position ?? 0;
	}
	/**
	* Sets the position.
	*/
	set position(value) {
		const location = this.locations[0];
		if (location) location.position = value;
	}
	/**
	* Gets a value indicating whether the action starts a group.
	*/
	get beginGroup() {
		return this.locations[0]?.beginGroup ?? false;
	}
	/**
	* Sets a value indicating whether the action starts a group.
	*/
	set beginGroup(value) {
		const location = this.locations[0];
		if (location) location.beginGroup = value;
	}
	/**
	* Gets the group.
	*/
	get group() {
		return this.locations[0]?.group;
	}
	/**
	* Sets the group.
	*/
	set group(value) {
		const location = this.locations[0];
		if (location) location.group = value;
	}
	/**
	* Gets the position in datagrid context menu.
	*/
	get datagridContextMenuPosition() {
		return this.locations.find((l) => l.location === ViewModelActionLocation.datagridRowContextMenu)?.position;
	}
	/**
	* Gets the position in datagrid context menu.
	*/
	set datagridContextMenuPosition(value) {
		const location = this.locations.find((l) => l.location === ViewModelActionLocation.datagridRowContextMenu);
		if (value !== void 0) if (location) location.position = value;
		else this.locations.push(ViewModelActionLocationEntry.datagridRowContextMenu().withPosition(value));
		else if (location) this.locations.splice(this.locations.indexOf(location), 1);
	}
	getVisible(_item, _items) {
		return this._visible;
	}
	setVisible(value) {
		this._visible = value;
	}
	getEnabled(_item, _items) {
		return this._enabled;
	}
	setEnabled(value) {
		this._enabled = value;
	}
	getBadge(_item, _items) {
		return this._badge;
	}
	setBadge(value) {
		this._badge = value;
	}
	/**
	* Gets the value indicating whether the action is in progress.
	*/
	get inProgress() {
		return this._inProgress;
	}
	/**
	* Gets the keyboard shortcut.
	*/
	get keyboardShortcut() {
		return this._keyboardShortcut?.toString();
	}
	/**
	* Sets the keyboard shortcut.
	*/
	set keyboardShortcut(value) {
		this._keyboardShortcut = value ? ShortcutTrigger.parse(value) : void 0;
	}
	/**
	* Initialize a new ViewModelPropertyBase instance.
	*
	* @param owner - ViewModel owner.
	*/
	constructor(owner) {
		this.actionType = ViewModelActionType.none;
		this.locations = [ViewModelActionLocationEntry.toolbar()];
		this._visible = true;
		this._enabled = true;
		this.displayMode = ViewModelActionDisplayMode.iconBeforeText;
		this.size = ViewModelActionSize.medium;
		this.style = ViewModelActionStyle.default;
		this.badgeSeverity = ViewModelActionBadgeSeverity.none;
		this._badge = void 0;
		this._inProgress = false;
		this.constraint = ViewModelActionConstraint.none;
		this.isStandardAction = false;
		this.owner = owner;
	}
	/**
	* Runs the action.
	*/
	async run(item, items, event) {
		try {
			this._inProgress = true;
			if (this.actionType === ViewModelActionType.openView && this.uiViewToOpenName) await this.owner.navigateAsync(new NavigationOptions(this.uiViewToOpenName));
			else if (this.actionType === ViewModelActionType.toggleOverlay && this.overlayId) this.owner.toggleOverlay(new ToggleOverlayOptions(this.overlayId).withEvent(event));
			else if (this.actionType === ViewModelActionType.code) await runWithEvent(event, () => this.execute(item, items));
		} finally {
			this._inProgress = false;
		}
	}
	/**
	* Gets a value indicating whether the action can be run by keyboard shortcut.
	*/
	canBeRunByKeyboardShortcut(item, items) {
		return this.owner.canExecuteAction(this) && !this.isDisabled(item, items);
	}
	/**
	* Gets a value indicating whether the action is disabled.
	*/
	isDisabled(item, items) {
		return this.owner.busy || this.inProgress || !this.canExecuteOnConstraint(item) || !this.getEnabled(item, items);
	}
	init() {}
	getAbsoluteLocations() {
		const relativeLocations = this.locations.filter((l) => l.location === ViewModelActionLocation.relativeTo);
		if (relativeLocations.length === 0) return this.locations;
		const locations = this.locations.filter((l) => l.location !== ViewModelActionLocation.relativeTo);
		for (const relativeLocation of relativeLocations) {
			const relativeAction = relativeLocation.parentActionName ? this.owner.findAction(relativeLocation.parentActionName) : void 0;
			if (!relativeAction) continue;
			locations.push(...relativeAction.getAbsoluteLocations().map((l) => {
				const absolutePosition = Math.min(Math.max(l.position, -0xe8d4a51000), 0xe8d4a51000);
				return new ViewModelActionLocationEntry(l.location).withParentActionName(l.parentActionName).withPosition(absolutePosition + relativeLocation.position / 1e4).withGroupFn(l.groupFn);
			}));
		}
		return locations;
	}
	/**
	* Execute the action.
	*/
	async execute(_item, _items) {}
	canExecuteOnConstraint(item) {
		return this.constraint !== ViewModelActionConstraint.onDatasourceItem || item !== void 0;
	}
};
//#endregion
//#region src/viewModelAction/ViewModelStandardAction.ts
/**
* View model standard action.
*/
var ViewModelStandardAction = /* @__PURE__ */ function(ViewModelStandardAction) {
	ViewModelStandardAction["add"] = "Add";
	ViewModelStandardAction["clone"] = "Clone";
	ViewModelStandardAction["close"] = "Close";
	ViewModelStandardAction["export"] = "Export";
	ViewModelStandardAction["more"] = "More";
	ViewModelStandardAction["refresh"] = "Refresh";
	ViewModelStandardAction["remove"] = "Remove";
	ViewModelStandardAction["save"] = "Save";
	return ViewModelStandardAction;
}({});
//#endregion
//#region src/viewModelAction/standardActions/AddAction.ts
var AddAction = class extends ViewModelActionBase {
	constructor(..._args) {
		super(..._args);
		this.name = ViewModelStandardAction.add;
		this.actionType = ViewModelActionType.code;
		this.locations = [ViewModelActionLocationEntry.toolbar().withPosition(-9e4)];
		this.style = ViewModelActionStyle.primary;
		this.displayMode = ViewModelActionDisplayMode.text;
		this.iconName = "add";
		this._keyboardShortcut = new ShortcutTrigger("N", false, false, true);
		this.isStandardAction = true;
	}
	get defaultCaption() {
		return this.owner.globalResourceManager.get("Add");
	}
	getVisible() {
		return !this.owner.canNeverExecuteAddAction;
	}
	getEnabled() {
		return this.owner.canExecuteAddAction();
	}
	async execute() {
		await this.owner.executeAddActionAsync();
	}
	init() {
		if (this.owner.isMain && this.owner.monoRecord && this.owner.subViews.some((s) => s.parentRelation.type === RelationType.collection)) {
			this.displayMode = ViewModelActionDisplayMode.iconBeforeText;
			this.locations[0] = ViewModelActionLocationEntry.childOf().withParentActionName(ViewModelStandardAction.more).withPosition(-9e4);
		} else {
			this.displayMode = ViewModelActionDisplayMode.text;
			this.locations[0] = ViewModelActionLocationEntry.toolbar().withPosition(-9e4);
		}
	}
};
//#endregion
//#region src/viewModelAction/standardActions/CloneAction.ts
var CloneAction = class extends ViewModelActionBase {
	constructor(..._args) {
		super(..._args);
		this.name = ViewModelStandardAction.clone;
		this.actionType = ViewModelActionType.code;
		this.locations = [ViewModelActionLocationEntry.childOf().withParentActionName(ViewModelStandardAction.more).withPosition(-6e4).withBeginGroup(), ViewModelActionLocationEntry.datagridRowContextMenu().withPosition(9e4)];
		this.displayMode = ViewModelActionDisplayMode.icon;
		this.iconName = "clone";
		this._keyboardShortcut = new ShortcutTrigger("C", false, false, true);
		this.isStandardAction = true;
	}
	get defaultCaption() {
		return this.owner.globalResourceManager.get("Clone");
	}
	getVisible() {
		return !this.owner.canNeverExecuteCloneAction;
	}
	getEnabled(item, items) {
		return this.owner.canExecuteCloneAction(items);
	}
	async execute(_item, items) {
		await this.owner.executeCloneActionAsync(items);
	}
};
//#endregion
//#region src/viewModelAction/standardActions/CloseAction.ts
var CloseAction = class extends ViewModelActionBase {
	constructor(..._args) {
		super(..._args);
		this.name = ViewModelStandardAction.close;
		this.actionType = ViewModelActionType.code;
		this.locations = [ViewModelActionLocationEntry.toolbar().withPosition(9e4)];
		this.displayMode = ViewModelActionDisplayMode.icon;
		this.iconName = "close";
		this.style = ViewModelActionStyle.ghost;
		this._keyboardShortcut = new ShortcutTrigger("W", false, false, true);
		this.isStandardAction = true;
	}
	get defaultCaption() {
		return this.owner.globalResourceManager.get(this.isStackFrame() ? "Back" : "Close");
	}
	getEnabled() {
		return this.owner.canExecuteCloseAction;
	}
	canBeRunByKeyboardShortcut() {
		return this.owner.mainViewModel.canExecuteCloseAction;
	}
	async execute() {
		await this.owner.mainViewModel.executeCloseActionAsync();
	}
	init() {
		if (this.isStackFrame()) {
			this.iconName = "arrowLeft";
			this.displayMode = ViewModelActionDisplayMode.iconBeforeText;
			this.locations[0] = ViewModelActionLocationEntry.toolbar().withPosition(-1e5);
		} else {
			this.iconName = "close";
			this.displayMode = ViewModelActionDisplayMode.icon;
			this.locations[0] = ViewModelActionLocationEntry.toolbar().withPosition(9e4);
		}
	}
	isStackFrame() {
		return this.owner.ownerFrame?.displayMode === FrameDisplayMode.stack;
	}
};
//#endregion
//#region src/exporting/ExportFormat.ts
/**
* Specifies the export format.
*/
var ExportFormat = /* @__PURE__ */ function(ExportFormat) {
	/**
	* CSV format.
	*/
	ExportFormat["csv"] = "csv";
	return ExportFormat;
}({});
//#endregion
//#region src/viewModelAction/standardActions/ExportAction.ts
var ExportAction = class extends ViewModelActionBase {
	constructor(..._args) {
		super(..._args);
		this.name = ViewModelStandardAction.export;
		this.actionType = ViewModelActionType.code;
		this.locations = [ViewModelActionLocationEntry.childOf().withParentActionName(ViewModelStandardAction.more).withPosition(-4e4)];
		this.displayMode = ViewModelActionDisplayMode.icon;
		this.iconName = "export";
		this.isStandardAction = true;
	}
	get defaultCaption() {
		return this.owner.globalResourceManager.get("ServerDataExport_ToolbarButton");
	}
	getVisible() {
		return this.owner.entityViewName !== void 0 && !this.owner.creationMode;
	}
	getEnabled() {
		return this.owner.canExecuteExportAction();
	}
	async execute() {
		if (this.owner.entityViewName == null) throw new Error("Module usage error: cannot export data if the owner view-model is not bound to an entity view name.");
		const uiViewName = this.owner.applicationContext.get("NameOfNeosDataExportUI");
		if (uiViewName !== void 0) {
			const options = new NavigationOptions(uiViewName).withCreationMode(true).withParameter("OwnerViewModel", this.owner);
			await this.owner.navigateAsync(options);
		} else await this.owner.exportServerDataAsync(this.owner.createServerDataExportOptions("Export.csv", ExportFormat.csv));
	}
};
//#endregion
//#region src/viewModelAction/standardActions/MoreAction.ts
var MoreAction = class extends ViewModelActionBase {
	constructor(..._args) {
		super(..._args);
		this.name = ViewModelStandardAction.more;
		this.locations = [ViewModelActionLocationEntry.toolbar().withPosition(1e5)];
		this.displayMode = ViewModelActionDisplayMode.icon;
		this.iconName = "dotsHorizontal";
		this.isStandardAction = true;
	}
	get defaultCaption() {
		return this.owner.globalResourceManager.get("More");
	}
	getEnabled() {
		return !this.owner.busy;
	}
};
//#endregion
//#region src/viewModelAction/standardActions/RefreshAction.ts
var RefreshAction = class extends ViewModelActionBase {
	constructor(..._args) {
		super(..._args);
		this.name = ViewModelStandardAction.refresh;
		this.actionType = ViewModelActionType.code;
		this.locations = [ViewModelActionLocationEntry.toolbar().withPosition(-7e4)];
		this.displayMode = ViewModelActionDisplayMode.icon;
		this.iconName = "refresh";
		this._keyboardShortcut = new ShortcutTrigger("R", true);
		this.isStandardAction = true;
	}
	get defaultCaption() {
		return this.owner.globalResourceManager.get("Refresh");
	}
	getVisible() {
		return this.owner.isMain;
	}
	getEnabled() {
		return this.owner.canExecuteRefreshAction;
	}
	canBeRunByKeyboardShortcut() {
		return this.owner.masterViewModel.canExecuteRefreshAction;
	}
	async execute() {
		await this.owner.masterViewModel.executeRefreshActionAsync();
	}
};
//#endregion
//#region src/viewModelAction/standardActions/RemoveAction.ts
var RemoveAction = class extends ViewModelActionBase {
	constructor(..._args) {
		super(..._args);
		this.name = ViewModelStandardAction.remove;
		this.actionType = ViewModelActionType.code;
		this.locations = [ViewModelActionLocationEntry.childOf().withParentActionName(ViewModelStandardAction.more).withPosition(-5e4), ViewModelActionLocationEntry.datagridRowContextMenu().withPosition(1e5)];
		this.iconName = "remove";
		this.displayMode = ViewModelActionDisplayMode.icon;
		this.style = ViewModelActionStyle.danger;
		this._keyboardShortcut = new ShortcutTrigger("D", true);
		this.isStandardAction = true;
	}
	get defaultCaption() {
		return this.owner.globalResourceManager.get("Remove");
	}
	getVisible() {
		return !this.owner.canNeverExecuteRemoveAction;
	}
	getEnabled(item, items) {
		return this.owner.canExecuteRemoveAction(items);
	}
	async execute(item, items) {
		await this.owner.executeRemoveActionAsync(items);
	}
};
//#endregion
//#region src/viewModelAction/standardActions/RestoreOriginalViewAction.ts
var RestoreOriginalViewAction = class extends ViewModelActionBase {
	constructor(..._args) {
		super(..._args);
		this.name = "RestoreOriginalView";
		this.actionType = ViewModelActionType.code;
		this.locations = [ViewModelActionLocationEntry.childOf().withParentActionName(ViewModelStandardAction.more).withPosition(-3e4).withBeginGroup()];
		this.iconName = "reset";
		this.isStandardAction = true;
	}
	get defaultCaption() {
		return this.owner.globalResourceManager.get("SelectOriginalView");
	}
	getVisible() {
		return this.owner.customViewManager.userCustomViewMode === "Auto" && !!this.owner.currentCustomViewId;
	}
	async execute() {
		const owner = this.owner;
		if (owner.currentCustomViewId && await owner.customViewManager.deleteAutoCustomViewAsync(owner, owner.currentCustomViewId)) await owner.customViewManager.selectCustomViewAsync(owner);
	}
};
//#endregion
//#region src/viewModelAction/standardActions/SaveAction.ts
var SaveAction = class extends ViewModelActionBase {
	constructor(..._args) {
		super(..._args);
		this.name = ViewModelStandardAction.save;
		this.actionType = ViewModelActionType.code;
		this.locations = [ViewModelActionLocationEntry.toolbar().withPosition(-8e4)];
		this.displayMode = ViewModelActionDisplayMode.icon;
		this.iconName = "save";
		this._keyboardShortcut = new ShortcutTrigger("S", true);
		this.isStandardAction = true;
	}
	get defaultCaption() {
		return this.owner.globalResourceManager.get("Save");
	}
	getVisible() {
		return !this.owner.canNeverExecuteSaveAction;
	}
	getEnabled() {
		return this.owner.canExecuteSaveAction;
	}
	async execute() {
		await this.owner.masterViewModel.executeSaveActionAsync();
	}
	canBeRunByKeyboardShortcut() {
		return this.owner.masterViewModel.canExecuteSaveAction;
	}
};
//#endregion
//#region src/viewModel/ViewModelActions.ts
var ViewModelActions = class {
	/**
	* Initialize a new ViewModelActions instance.
	*
	* @param owner - ViewModel owner.
	*/
	constructor(owner) {
		this.owner = owner;
		this.add = new AddAction(this.owner);
		this.remove = new RemoveAction(this.owner);
		this.save = new SaveAction(this.owner);
		this.clone = new CloneAction(this.owner);
		this.close = new CloseAction(this.owner);
		this.refresh = new RefreshAction(this.owner);
		this.export = new ExportAction(this.owner);
		this.more = new MoreAction(this.owner);
		this.restoreOriginalView = new RestoreOriginalViewAction(this.owner);
	}
	init() {
		this.forEach((action) => action.init());
	}
	forEach(predicate) {
		for (const action of Object.values(this)) if (action instanceof ViewModelActionBase) predicate(action);
	}
	filterByLocationAndItem(requestedLocation, parentActionName, item, items) {
		return this.filterByLocation(requestedLocation, parentActionName, (action) => this.owner.canExecuteAction(action) && action.getVisible(item, items));
	}
	filterByLocation(requestedLocation, parentActionName, predicate) {
		const isMatchingParent = (location) => requestedLocation !== ViewModelActionLocation.childOf || location.parentActionName === parentActionName;
		const matchesPredicate = (action) => !predicate || predicate(action);
		const actionsWithLocation = [];
		this.forEach((action) => {
			for (const location of action.getAbsoluteLocations()) if (location.location === requestedLocation && isMatchingParent(location) && matchesPredicate(action)) actionsWithLocation.push({
				action,
				location
			});
		});
		actionsWithLocation.sort((a1, a2) => a1.location.position - a2.location.position);
		return actionsWithLocation.map((a) => a.action);
	}
	toReversed() {
		return Object.values(this).filter((p) => p instanceof ViewModelActionBase).reverse();
	}
	[Symbol.iterator]() {
		let index = -1;
		const actions = Object.values(this).filter((p) => p instanceof ViewModelActionBase);
		return { next() {
			index++;
			return index >= actions.length ? {
				value: void 0,
				done: true
			} : {
				value: actions[index],
				done: false
			};
		} };
	}
};
//#endregion
//#region src/viewModel/ViewModelBusinessEventArgs.ts
var ReferenceRetrievingTrigger = /* @__PURE__ */ function(ReferenceRetrievingTrigger) {
	ReferenceRetrievingTrigger["lookupUIView"] = "LookupUIView";
	ReferenceRetrievingTrigger["lookupSuggestions"] = "LookupSuggestions";
	ReferenceRetrievingTrigger["lookupValidation"] = "LookupValidation";
	return ReferenceRetrievingTrigger;
}({});
//#endregion
//#region src/enums/EnumMember.ts
function isEnumMember(value) {
	return !!value && typeof value === "object" && "name" in value;
}
//#endregion
//#region src/viewModelProperty/ContentAlign.ts
var ContentAlign = /* @__PURE__ */ function(ContentAlign) {
	ContentAlign["start"] = "start";
	ContentAlign["center"] = "center";
	ContentAlign["end"] = "end";
	return ContentAlign;
}({});
//#endregion
//#region src/filter/FilterOperation.ts
var FilterOperation = /* @__PURE__ */ function(FilterOperation) {
	FilterOperation["none"] = "none";
	FilterOperation["now"] = "now";
	FilterOperation["today"] = "today";
	FilterOperation["addDays"] = "addDays";
	FilterOperation["addMonths"] = "addMonths";
	FilterOperation["addYears"] = "addYears";
	FilterOperation["startOfDay"] = "startOfDay";
	FilterOperation["startOfWeek"] = "startOfWeek";
	FilterOperation["startOfMonth"] = "startOfMonth";
	FilterOperation["startOfYear"] = "startOfYear";
	FilterOperation["endOfDay"] = "endOfDay";
	FilterOperation["endOfWeek"] = "endOfWeek";
	FilterOperation["endOfMonth"] = "endOfMonth";
	FilterOperation["endOfYear"] = "endOfYear";
	FilterOperation["specificMonth"] = "specificMonth";
	FilterOperation["specificYear"] = "specificYear";
	return FilterOperation;
}({});
//#endregion
//#region src/filter/FilterValue.ts
var FilterValue = class {
	constructor(operation, ...args) {
		this.args = [];
		this.operation = operation;
		this.args = args;
	}
	toODataValue() {
		return toODataValue(this.toValue());
	}
	toValue() {
		switch (this.operation) {
			case FilterOperation.now: return /* @__PURE__ */ new Date();
			case FilterOperation.today: return dateTimeToDateOnly(/* @__PURE__ */ new Date());
			case FilterOperation.addDays: return this.toAddDateValue("days");
			case FilterOperation.addMonths: return this.toAddDateValue("months");
			case FilterOperation.addYears: return this.toAddDateValue("years");
			case FilterOperation.startOfDay: return this.toStartOfDateValue("day");
			case FilterOperation.startOfWeek: return this.toStartOfDateValue("week");
			case FilterOperation.startOfMonth: return this.toStartOfDateValue("month");
			case FilterOperation.startOfYear: return this.toStartOfDateValue("year");
			case FilterOperation.endOfDay: return this.toEndOfDateValue("day");
			case FilterOperation.endOfWeek: return this.toEndOfDateValue("week");
			case FilterOperation.endOfMonth: return this.toEndOfDateValue("month");
			case FilterOperation.endOfYear: return this.toEndOfDateValue("year");
			case FilterOperation.specificMonth: return this.args[0];
			case FilterOperation.specificYear: return this.args[0];
			default: throw new Error(`Unknown filter operation: ${this.operation}`);
		}
	}
	toString() {
		switch (this.operation) {
			case FilterOperation.now: return defaultFormatDateTime(/* @__PURE__ */ new Date());
			case FilterOperation.today: return this.toDateString(dateTimeToDateOnly(/* @__PURE__ */ new Date()));
			case FilterOperation.addDays: return this.toDateString(this.toAddDateValue("days"));
			case FilterOperation.addMonths: return this.toDateString(this.toAddDateValue("months"));
			case FilterOperation.addYears: return this.toDateString(this.toAddDateValue("years"));
			case FilterOperation.startOfDay: return this.toDateString(this.toStartOfDateValue("day"));
			case FilterOperation.startOfWeek: return this.toDateString(this.toStartOfDateValue("week"));
			case FilterOperation.startOfMonth: return this.toDateString(this.toStartOfDateValue("month"));
			case FilterOperation.startOfYear: return this.toDateString(this.toStartOfDateValue("year"));
			case FilterOperation.endOfDay: return this.toDateString(this.toEndOfDateValue("day"));
			case FilterOperation.endOfWeek: return this.toDateString(this.toEndOfDateValue("week"));
			case FilterOperation.endOfMonth: return this.toDateString(this.toEndOfDateValue("month"));
			case FilterOperation.endOfYear: return this.toDateString(this.toEndOfDateValue("year"));
			case FilterOperation.specificMonth: return this.toDateStringFormat(this.toStartOfDateValue("month"), "MMMM yyyy");
			case FilterOperation.specificYear: return this.toDateStringFormat(this.toStartOfDateValue("year"), "yyyy");
			default: throw new Error(`Unknown filter operation: ${this.operation}`);
		}
	}
	equals(value) {
		return this.operation === value.operation && this.args.length === value.args.length && this.args.every((arg, i) => arg === value.args[i]);
	}
	toAddDateValue(unit) {
		const [arg0, arg1] = this.args;
		if (typeof arg0 === "number") return dateTimeToDateOnly(addToDate(/* @__PURE__ */ new Date(), unit, arg0));
		if (typeof arg1 !== "number") throw new TypeError("Argument 1 must be a number");
		if (arg0 instanceof Date) return dateTimeToDateOnly(addToDate(arg0, unit, arg1));
		if (typeof arg0 === "string") return addToDateOnly(arg0, unit, arg1);
		throw new Error("Argument 0 must be a date, a date only string or a number");
	}
	toStartOfDateValue(unit) {
		const arg0 = this.args[0] ?? /* @__PURE__ */ new Date();
		if (arg0 instanceof Date) {
			const value = startOf(arg0, unit);
			if (unit !== "day") return dateTimeToDateOnly(value);
			return value;
		}
		if (typeof arg0 === "string") return startOfDateOnly(arg0, unit);
		throw new Error("Argument 0 must be a date or a date only string");
	}
	toEndOfDateValue(unit) {
		const arg0 = this.args[0] ?? /* @__PURE__ */ new Date();
		if (arg0 instanceof Date) {
			const value = endOf(arg0, unit);
			if (unit !== "day") return dateTimeToDateOnly(value);
			return value;
		}
		if (typeof arg0 === "string") return endOfDateOnly(arg0, unit);
		throw new Error("Argument 0 must be a date or a date only string");
	}
	toDateString(date) {
		return typeof date === "string" ? dateOnlyToString(date) : defaultFormatDateOnly(date);
	}
	toDateStringFormat(date, format) {
		return dateOnlyToString(date, format);
	}
};
//#endregion
//#region src/viewModelProperty/format.ts
function formatValue(value, options, dependencies) {
	if (value instanceof FilterValue) return value.toString();
	switch (options.dataType) {
		case DataType.boolean: return options.isExpressionCode ? formatExpressionBooleanValue(dependencies.globalResourceManager, value) : formatBooleanValue(dependencies.globalResourceManager, value);
		case DataType.localizableString: return formatLocalizableStringValue(dependencies.localizator, value);
		case DataType.dataType:
		case DataType.enum: return formatEnumValue(value, options.enum);
		case DataType.date: return formatDateValue(value, options.format);
		case DataType.dateTime:
		case DataType.invariantDateTime: return formatDateTimeValue(value, options.format);
		case DataType.decimal:
		case DataType.integer:
		case DataType.long: return formatNumberValue(value, options.format);
		case DataType.stringArray: return formatStringArrayValue(value);
		case DataType.image:
		case DataType.file: return formatFileReferenceValue(value);
		default: return formatStringValue(value);
	}
}
function formatValues(values, options, dependencies) {
	if (options.enum) return options.enum.getEnumMembers().filter((v) => values.includes(v.name)).map((m) => m.caption);
	return values.flat().map((value) => formatValue(value, options, dependencies));
}
function formatLookupReferenceValue(value, lookupDefinition, dependencies) {
	if (value && typeof value === "object") {
		const displayProperty = getLookupDisplayProperty(lookupDefinition);
		value = getPropertyByPath(value, displayProperty.camelCaseName);
		return formatValue(value, displayProperty, dependencies);
	}
	return "";
}
async function formatLookupScalarValue(value, item, property, displayProperty) {
	if (value == null) return "";
	return formatLookupScalarDisplayValue(value, item instanceof Model ? await getModelLookupScalarDisplayValue(value, item, property) : await getObjectLookupScalarDisplayValue(value, item, property), displayProperty, property.owner);
}
async function getModelLookupScalarDisplayValue(value, item, property) {
	if (value !== property.getValue(item)) return fetchLookupScalarDisplayValue(value, property);
	if (!property.lookupDisplayProperty && !property.hasLookupDisplayPropertyValue(item) && !property.hasRunningLookupDisplayPropertyValueInvalidation(item)) await item.invalidateValueProperty(property.camelCaseName, false);
	return property.getLookupDisplayPropertyValue(item);
}
async function getObjectLookupScalarDisplayValue(value, item, property) {
	let displayValue;
	if (item) displayValue = await property.getLookupDisplayPropertyValue(item);
	if (displayValue === void 0) displayValue = await fetchLookupScalarDisplayValue(value, property);
	return displayValue;
}
async function fetchLookupScalarDisplayValue(value, property) {
	const lookupDefinition = property.lookupDefinition;
	const newDisplayValue = await getEntityViewPropertyValueAsync(property.owner.apiClient, lookupDefinition.searchEntityViewName, lookupDefinition.displayProperty.camelCaseName, lookupDefinition.valueProperty.camelCaseName, value);
	return newDisplayValue.isValid ? newDisplayValue.value : void 0;
}
function formatLookupScalarValueSync(value, item, property, displayProperty) {
	if (value == null) return "";
	let displayValue;
	if (item && property.hasLookupDisplayPropertyValue(item)) displayValue = property.getLookupDisplayPropertyValueSync(item);
	return formatLookupScalarDisplayValue(value, displayValue, displayProperty, property.owner);
}
function formatLookupScalarDisplayValue(value, displayValue, displayProperty, dependencies) {
	return displayValue == null ? `<${value}>` : formatValue(displayValue, displayProperty, dependencies);
}
function formatBooleanValue(globalResourceManager, value) {
	if (value === true) return globalResourceManager.get("Yes");
	if (value === false) return globalResourceManager.get("No");
	return globalResourceManager.get("Undefined");
}
function formatExpressionBooleanValue(globalResourceManager, value) {
	if (value === "true") return globalResourceManager.get("Yes");
	if (value === "false") return globalResourceManager.get("No");
	if (typeof value === "string" && value.startsWith("=")) return globalResourceManager.get("Expression");
	return globalResourceManager.get("Undefined");
}
function formatLocalizableStringValue(localizator, value) {
	if (value && typeof value === "object") return localizator.translateLocalizableDefinedString(value) ?? "";
	if (typeof value === "string") return value;
	return "";
}
function formatEnumValue(value, enumType) {
	return enumType?.findEnumMember(value)?.caption ?? "";
}
function formatDateValue(value, format) {
	if (value && typeof value === "string") value = dateOnlyToDateTime(value);
	if (value instanceof Date) return Globalize.formatDate(value, getLazyValue(format) ?? "d");
	return "";
}
function formatDateTimeValue(value, format) {
	if (value && typeof value === "string") {
		value = dateOnlyToDateTime(value);
		format = "d";
	}
	if (value instanceof Date) return Globalize.formatDate(value, getLazyValue(format) ?? "g");
	return "";
}
function formatNumberValue(value, format) {
	if (typeof value === "number") return Globalize.formatNumber(value, getLazyValue(format) ?? "n");
	return "";
}
function formatFileReferenceValue(value) {
	return value instanceof FileReference ? value.url : "";
}
function formatStringValue(value) {
	return value ?? "";
}
function formatStringArrayValue(value) {
	return Array.isArray(value) ? value.filter((v) => v != null).map(formatStringValue).join(", ") : "";
}
function getLazyValue(value) {
	return typeof value === "function" ? value() : value;
}
//#endregion
//#region src/viewModelProperty/ViewModelPropertyBase.ts
/**
* ViewModelProperty.
*/
var ViewModelPropertyBase = class ViewModelPropertyBase {
	/**
	* Gets the caption.
	*
	* @returns Caption.
	*/
	get caption() {
		if (this._caption) return this._caption;
		return this.owner.resourceManager.find(`properties.${this.name.replaceAll(".", "_")}.caption`) ?? this.name;
	}
	/**
	* Sets the caption.
	*
	* @param value - Caption.
	*/
	set caption(value) {
		this._caption = value;
	}
	/**
	* Gets the placeholder.
	*
	* @returns Placeholder.
	*/
	get placeholder() {
		if (this._placeholder) return this._placeholder;
		return this.owner.resourceManager.find(`properties.${this.name.replaceAll(".", "_")}.placeholder`);
	}
	/**
	* Sets the placeholder.
	*
	* @param value - Placeholder.
	*/
	set placeholder(value) {
		this._placeholder = value;
	}
	/**
	* Gets the datagrid caption.
	*/
	get datagridCaption() {
		if (this._datagridCaption) return this._datagridCaption;
		return this.owner.resourceManager.find(`properties.${this.name.replaceAll(".", "_")}.datagridCaption`) ?? this.caption;
	}
	/**
	* Sets the datagrid caption.
	*/
	set datagridCaption(value) {
		this._datagridCaption = value;
	}
	/** {@inheritdoc ViewModelProperty.enumTypeName} */
	get enumTypeName() {
		return this.enum?.$name;
	}
	/** {@inheritdoc ViewModelProperty.filterPositionInPanel} */
	get filterPositionInPanel() {
		if (this._filterPositionInPanel !== void 0) return this._filterPositionInPanel;
		return this.filterVisibleInPanel ? this.filterPosition : -1;
	}
	set filterPositionInPanel(value) {
		this._filterPositionInPanel = value;
	}
	/** {@inheritdoc ViewModelProperty.filterDefaultOperator} */
	get filterDefaultOperator() {
		if (this._filterDefaultOperator) return this._filterDefaultOperator;
		if (this.lookupDefinition) return FilterOperator.equal;
		switch (this.getDisplayDataType()) {
			case DataType.string:
			case DataType.localizableString: return FilterOperator.contains;
			case DataType.color:
			case DataType.file:
			case DataType.image:
			case DataType.stringArray: return FilterOperator.isNotNull;
			default: return FilterOperator.equal;
		}
	}
	set filterDefaultOperator(value) {
		this._filterDefaultOperator = value;
	}
	/** {@inheritdoc ViewModelProperty.filterOperators} */
	get filterOperators() {
		if (this._filterOperators) return this._filterOperators;
		return Object.keys(this.getFilterOperators());
	}
	set filterOperators(operators) {
		const allowedOperators = Object.keys(this.getFilterOperators());
		for (const operator of operators) if (!allowedOperators.includes(operator)) throw new Error(stringFormat(this.owner.globalResourceManager.get("FilterOperatorIsNotAllowed"), operator, this.getFilterDataType(), this.name));
		this._filterOperators = operators;
	}
	/** {@inheritdoc ViewModelProperty.removeFilterOperators} */
	removeFilterOperators(excludedOperators) {
		this._filterOperators = this.filterOperators.filter((op) => !excludedOperators.includes(op));
	}
	/** {@inheritdoc ViewModelProperty.filterComponent} */
	get filterComponent() {
		return this._filterComponent;
	}
	set filterComponent(value) {
		this._filterComponent = value;
		this._userFilterComponent = null;
	}
	/**
	* Gets the user-selected filter component view.
	*/
	get userFilterComponent() {
		return this._userFilterComponent ?? this.filterComponent;
	}
	/**
	* Sets the user-selected filter component view.
	*/
	set userFilterComponent(value) {
		this._userFilterComponent = value;
	}
	/** {@inheritdoc ViewModelProperty.horizontalAlign} */
	get horizontalAlign() {
		return this._horizontalAlign ?? this.getDefaultHorizontalAlign();
	}
	set horizontalAlign(value) {
		this._horizontalAlign = value;
	}
	/**
	* Gets the documentation.
	*/
	get documentation() {
		if (this._documentation) return this._documentation;
		return this.owner.resourceManager.find(`properties.${this.name.replaceAll(".", "_")}.documentation`);
	}
	/**
	* Sets documentation
	*/
	set documentation(value) {
		this._documentation = value;
	}
	/**
	* Initialize a new ViewModelPropertyBase instance.
	*
	* @param owner - ViewModel owner.
	*/
	constructor(owner) {
		this.characterCasing = CharacterCasing.normal;
		this.whiteSpaceHandling = void 0;
		this.datagridFrozen = false;
		this.datagridMovable = true;
		this.datagridPosition = 0;
		this.datagridWidth = void 0;
		this.datagridMinWidth = void 0;
		this.datagridMaxWidth = void 0;
		this.filterOnly = false;
		this.isAutoIncrement = false;
		this.filterable = true;
		this.filterRequired = false;
		this.filterRequirementUnfulfilled = false;
		this.filterVisible = false;
		this.filterPosition = 0;
		this.filterVisibleInPanel = false;
		this.customFilterOperators = [];
		this._filterComponent = FilterComponent.default;
		this._userFilterComponent = null;
		this.quickSearch = false;
		this.isExpressionCode = false;
		this.isComputed = false;
		this.fromWebAPI = true;
		this.displayable = true;
		this.sortable = false;
		this.serverSideSortable = false;
		this.serverSideReadOnly = false;
		this.multiLines = false;
		this.groupable = true;
		this.summaryTypes = createSummaryTypes();
		this.referenceEntityViewHasApiGet = false;
		this.ellipsis = true;
		this.triggersAIAutoSuggest = false;
		this._datagridVisibleIsExpression = false;
		this._datagridVisibleByDefault = false;
		this._formVisible = true;
		this._required = false;
		this._readOnly = false;
		this._maxValue = void 0;
		this._minValue = void 0;
		this._scale = void 0;
		this._tabStop = true;
		this.owner = owner;
	}
	/**
	* Gets a value indicating whether the property is visible by default in the datagrid.
	*/
	get datagridVisibleByDefault() {
		return this._datagridVisibleByDefault;
	}
	/**
	* Gets a value indicating whether the property can be visible in the datagrid.
	*/
	get canBeVisibleInDatagrid() {
		return this.displayable && !(this._datagridVisibleIsExpression && !this.datagridVisibleByDefault);
	}
	/** {@inheritdoc ViewModelProperty.datagridVisible} */
	get datagridVisible() {
		if (this._datagridVisible === void 0 || !this.canBeVisibleInDatagrid) return this.datagridVisibleByDefault;
		return this._datagridVisible;
	}
	/** {@inheritdoc ViewModelProperty.datagridVisible} */
	set datagridVisible(value) {
		this._datagridVisible = value;
	}
	/** {@inheritdoc ViewModelProperty.getFormVisible} */
	getFormVisible(_item) {
		return this._formVisible;
	}
	/** {@inheritdoc ViewModelProperty.setFormVisible} */
	setFormVisible(value) {
		this._formVisible = value;
	}
	/** {@inheritdoc ViewModelProperty.getRequired} */
	getRequired(_item) {
		return this._required;
	}
	/** {@inheritdoc ViewModelProperty.getRequired} */
	setRequired(value) {
		this._required = value;
	}
	/** {@inheritdoc ViewModelProperty.getReadOnly} */
	getReadOnly(_item) {
		return this._readOnly;
	}
	/** {@inheritdoc ViewModelProperty.setReadOnly} */
	setReadOnly(value) {
		this._readOnly = value;
	}
	/** {@inheritdoc ViewModelProperty.getMaxValue} */
	getMaxValue(_item) {
		return this._maxValue;
	}
	/** {@inheritdoc ViewModelProperty.setMaxValue} */
	setMaxValue(value) {
		this._maxValue = value;
	}
	/** {@inheritdoc ViewModelProperty.getMinValue} */
	getMinValue(_item) {
		return this._minValue;
	}
	/** {@inheritdoc ViewModelProperty.setMinValue} */
	setMinValue(value) {
		this._minValue = value;
	}
	/** {@inheritdoc ViewModelProperty.getScale} */
	getScale(_item) {
		return this._scale;
	}
	/** {@inheritdoc ViewModelProperty.setScale} */
	setScale(value) {
		this._scale = value;
	}
	/** {@inheritdoc ViewModelProperty.getTabStop} */
	getTabStop(_item) {
		return this._tabStop;
	}
	/** {@inheritdoc ViewModelProperty.setTabStop} */
	setTabStop(value) {
		this._tabStop = value;
	}
	/** {@inheritdoc ViewModelProperty.getValue} */
	getValue(item) {
		return item[this.camelCaseName];
	}
	/** {@inheritdoc ViewModelProperty.setValue} */
	setValue(item, value) {
		item[this.camelCaseName] = value;
	}
	/** {@inheritdoc ViewModelProperty.resetValue} */
	resetValue(item) {
		if (this._required) switch (this.dataType) {
			case DataType.integer:
				if (this.isAutoIncrement) {
					this.setValue(item, 0);
					return;
				}
				break;
			case DataType.boolean:
				this.setValue(item, false);
				return;
		}
		this.setValue(item, void 0);
	}
	/** {@inheritdoc ViewModelProperty.getDisplayPath} */
	getDisplayPath(server = false) {
		let path = this.camelCaseName;
		let dataType = this.dataType;
		if (this.lookupDefinition) {
			const displayProperty = getLookupDisplayProperty(this.lookupDefinition);
			dataType = displayProperty.dataType;
			if (!this.dataType) path += `.${displayProperty.camelCaseName}`;
			else if (this.lookupDefinition.displayProperty) path = this.getLookupDisplayPropertyPath(server);
		}
		if (dataType === DataType.localizableString) path += `.${this.owner.localizator.currentLanguage}`;
		return path;
	}
	/** {@inheritdoc ViewModelProperty.getSortPath} */
	getSortPath(server = false) {
		if (this.sortProperty) return this.sortProperty.getDisplayPath(server);
		return this.getDisplayPath(server);
	}
	/** {@inheritdoc ViewModelProperty.getEnumMembers} */
	getEnumMembers() {
		return this.enum?.getEnumMembers() ?? [];
	}
	/** {@inheritdoc ViewModelProperty.getEnabledEnumMembers} */
	getEnabledEnumMembers(item) {
		return this.getEnumMembers().filter((m) => m.getEnabled(item));
	}
	/** {@inheritdoc ViewModelProperty.findEnumMember} */
	findEnumMember(name) {
		return this.enum?.findEnumMember(name);
	}
	/** {@inheritdoc ViewModelProperty.getEnumMember} */
	getEnumMember(name) {
		if (!this.enum) throw new Error(stringFormat(this.owner.globalResourceManager.get("EnumMemberDoesNotExist"), name));
		return this.enum.getEnumMember(name);
	}
	/** @inheritdoc */
	getFilterPaths() {
		const clientSideFilter = this.owner.clientSideFilter;
		const paths = [];
		const push = (path, dataType) => {
			if (!clientSideFilter) path = path.toLowerCase();
			paths.push(path);
			if (dataType === DataType.localizableString) paths.push(`${path}.${this.owner.localizator.currentLanguage}`);
		};
		push(this.camelCaseName, this.dataType);
		if (this.oDataPath) push(this.oDataPath.toLowerCase(), this.dataType);
		if (this.lookupDefinition) if (this.dataType) {
			if (this.lookupDefinition.displayProperty) push(this.getLookupDisplayPropertyPath(!clientSideFilter), this.lookupDefinition.displayProperty.dataType);
		} else {
			push(`${this.camelCaseName}.${this.lookupDefinition.valueProperty.camelCaseName}`, this.lookupDefinition.valueProperty.dataType);
			if (this.lookupDefinition.displayProperty) push(`${this.camelCaseName}.${this.lookupDefinition.displayProperty.camelCaseName}`, this.lookupDefinition.displayProperty.dataType);
		}
		return paths;
	}
	/** @inheritdoc */
	isFilterPath(path) {
		return this.getFilterPaths().map((p) => p.toLowerCase()).includes(path.toLowerCase());
	}
	/** @inheritdoc */
	getFilterPathAndDataType(operator) {
		let path = this.oDataPath ?? this.camelCaseName;
		let dataType = this.dataType;
		if (this.lookupDefinition) {
			if (isFilterOperatorWithoutValue(operator)) return [path, dataType];
			if (this.dataType) {
				if (this.lookupDefinition.displayProperty && !isFilterOperatorOnLookupValueProperty(operator)) {
					path = this.getLookupDisplayPropertyPath(!this.owner.clientSideFilter);
					dataType = this.lookupDefinition.displayProperty.dataType;
				}
			} else if (isFilterOperatorOnLookupValueProperty(operator)) {
				path += `.${this.lookupDefinition.valueProperty.camelCaseName}`;
				dataType = this.lookupDefinition.valueProperty.dataType;
			} else {
				const displayProperty = getLookupDisplayProperty(this.lookupDefinition);
				path += `.${displayProperty.camelCaseName}`;
				dataType = displayProperty.dataType;
			}
		}
		if (dataType === DataType.localizableString) path += `.${this.owner.localizator.currentLanguage}`;
		return [path, dataType];
	}
	/** @inheritdoc */
	getFilterOperatorOrDefault(operator) {
		return operator in this.getFilterOperators() ? operator : this.filterDefaultOperator;
	}
	/** @inheritdoc */
	findCustomFilterOperator(id) {
		return this.customFilterOperators.find((o) => o.id === id);
	}
	/** @inheritdoc */
	extractOperatorAndValuesFromFilterCondition(condition) {
		if (condition.customOperatorId) return {
			operator: condition.customOperatorId,
			value1: condition.values[0],
			value2: condition.values[1]
		};
		const operator = this.getFilterOperatorOrDefault(condition.filterOperator);
		if (isFilterOperatorWithoutValue(operator)) return {
			operator,
			value1: void 0,
			value2: void 0
		};
		if (isEnumDataType(this.dataType)) return this.extractOperatorAndValuesFromFilterConditionOnEnumProperty(condition, operator);
		if (isArrayFilterOperator(operator)) return {
			operator,
			value1: condition.values.flat(),
			value2: void 0
		};
		if (this.lookupDefinition) return this.extractOperatorAndValuesFromFilterConditionOnLookupProperty(condition, operator);
		const value1 = condition.values[0];
		if (this.dataType === DataType.boolean) return {
			operator,
			value1: this.extractBooleanValueFromFilterConditionValue(value1),
			value2: void 0
		};
		const value2 = operator === FilterOperator.between ? condition.values[1] : void 0;
		if (this.dataType === DataType.date) return {
			operator,
			value1: this.extractDateValueFromFilterConditionValue(value1),
			value2: this.extractDateValueFromFilterConditionValue(value2)
		};
		return {
			operator,
			value1: condition.values[0],
			value2
		};
	}
	extractOperatorAndValuesFromFilterConditionOnEnumProperty(condition, operator) {
		const values = new Set(condition.values.flat());
		return {
			operator,
			value1: this.getEnumMembers().filter((v) => values.has(v.name)),
			value2: void 0
		};
	}
	extractOperatorAndValuesFromFilterConditionOnLookupProperty(condition, operator) {
		if (operator === FilterOperator.equal || operator === FilterOperator.notEqual) return {
			operator,
			value1: condition.values.flat(),
			value2: void 0
		};
		return {
			operator,
			value1: condition.values[0],
			value2: void 0
		};
	}
	extractBooleanValueFromFilterConditionValue(value) {
		if (value === true || value === "true") return [{
			name: value,
			caption: this.owner.globalResourceManager.get("Yes")
		}];
		if (value === false || value === "false") return [{
			name: value,
			caption: this.owner.globalResourceManager.get("No")
		}];
		if (value === "=") return [{
			name: value,
			caption: this.owner.globalResourceManager.get("Expression")
		}];
	}
	extractDateValueFromFilterConditionValue(value) {
		if (typeof value === "string") return dateOnlyToDateTime(value);
		return value;
	}
	async createFilterCondition(operator, value1, value2, keepEmpty) {
		if (!isStandardFilterOperator(operator)) return this.createFilterConditionWithCustomOperator(operator);
		const [path, dataType] = this.getFilterPathAndDataType(operator);
		if (isFilterOperatorWithoutValue(operator)) return Filter.createComplexFilter(path, operator, this.oDataPrefix, this.oDataSuffix);
		if (value1 == null) return keepEmpty ? this.createEmptyFilterCondtion(path, operator) : void 0;
		if (dataType === DataType.boolean) return this.createBooleanFilterCondition(path, operator, value1, keepEmpty);
		if (isEnumDataType(dataType)) return this.createEnumFilterCondition(path, operator, value1, keepEmpty);
		if (dataType === DataType.date) return this.createDateFilterCondition(path, operator, value1, value2);
		if (dataType === DataType.localizableString) return this.createLocalizableStringFilterCondition(path, operator, value1);
		return this.createDefaultFilterCondition(path, operator, value1, value2);
	}
	/** @inheritdoc */
	isReadOnlyExpression() {
		return Object.getPrototypeOf(this).getReadOnly !== ViewModelPropertyBase.prototype.getReadOnly;
	}
	/** @inheritdoc */
	isRequiredExpression() {
		return Object.getPrototypeOf(this).getRequired !== ViewModelPropertyBase.prototype.getRequired;
	}
	/** @inheritdoc */
	isFormVisibleExpression() {
		return Object.getPrototypeOf(this).getFormVisible !== ViewModelPropertyBase.prototype.getFormVisible;
	}
	/** @inheritdoc */
	isTabStopExpression() {
		return Object.getPrototypeOf(this).getTabStop !== ViewModelPropertyBase.prototype.getTabStop;
	}
	/** @inheritdoc */
	isFormatExpression() {
		return Object.getPrototypeOf(this).getFormat !== ViewModelPropertyBase.prototype.getFormat;
	}
	/** @inheritdoc */
	getDefaultHorizontalAlign() {
		switch (this.getDisplayDataType()) {
			case DataType.boolean:
			case DataType.image: return ContentAlign.center;
			case DataType.autoIncrementedInteger:
			case DataType.decimal:
			case DataType.integer:
			case DataType.long: return ContentAlign.end;
			default: return ContentAlign.start;
		}
	}
	/** @inheritdoc */
	getTextAlign() {
		switch (this.horizontalAlign) {
			case ContentAlign.center: return "center";
			case ContentAlign.end: return "end";
			default: return "start";
		}
	}
	getDisplayDataType() {
		return this.lookupDefinition ? getLookupDisplayProperty(this.lookupDefinition).dataType : this.dataType;
	}
	/**
	* Gets the minimal initial width according to the data type.
	* @param dataType - Data type.
	* @returns The minimal initial width.
	*/
	getMinInitialWidth() {
		switch (this.getDisplayDataType()) {
			case DataType.date: return 140;
			case DataType.dateTime:
			case DataType.invariantDateTime: return 200;
			default: return 60;
		}
	}
	getFormat(item) {
		if (this.dataType === DataType.decimal && item) return this.getDecimalFormat(item);
		return this.format ?? getDataTypeDefaultFormat(this.dataType);
	}
	getDecimalFormat(item) {
		const scale = this.getScale(item);
		if (scale == null) return this.format;
		if (this.format) {
			const formatSpecifier = this.format[0];
			if ([
				"C",
				"D",
				"E",
				"F",
				"G",
				"N",
				"P",
				"R"
			].includes(formatSpecifier.toUpperCase())) return this.format.length === 1 ? `${formatSpecifier}${scale}` : this.format;
		}
		return `n${scale}`;
	}
	/**
	* @inheritdoc ViewModelProperty.getFormatOptions
	*/
	getFormatOptions(item, format) {
		return {
			dataType: this.dataType,
			format: format ?? (() => this.getFormat(item instanceof Model ? item : void 0)),
			enum: this.enum,
			isExpressionCode: this.isExpressionCode
		};
	}
	/**
	* @inheritdoc ViewModelProperty.hasAsyncFormattedValue
	*/
	hasAsyncFormattedValue() {
		return !!this.dataType && !!this.lookupDefinition?.displayProperty;
	}
	/**
	* @inheritdoc ViewModelProperty.getFormattedValue
	*/
	getFormattedValue(item) {
		return this.formatValueSync(this.getValue(item), item);
	}
	/**
	* @inheritdoc ViewModelProperty.formatValue
	*/
	async formatValue(value, item, format) {
		if (this.lookupDefinition) {
			if (!this.dataType) return formatLookupReferenceValue(value, this.lookupDefinition, this.owner);
			if (this.lookupDefinition.displayProperty) return formatLookupScalarValue(value, item, this, this.lookupDefinition.displayProperty);
		}
		return formatValue(value, this.getFormatOptions(item, format), this.owner);
	}
	/**
	* @inheritdoc ViewModelProperty.formatValue
	*/
	formatValueSync(value, item, format) {
		if (this.lookupDefinition) {
			if (!this.dataType) return formatLookupReferenceValue(value, this.lookupDefinition, this.owner);
			if (this.lookupDefinition.displayProperty) return formatLookupScalarValueSync(value, item, this, this.lookupDefinition.displayProperty);
		}
		return formatValue(value, this.getFormatOptions(item, format), this.owner);
	}
	async createFilterConditionWithCustomOperator(operator) {
		const customOperator = this.findCustomFilterOperator(operator);
		if (!customOperator) return;
		return new Filter(await customOperator.getFilterCondition());
	}
	createBooleanFilterCondition(path, operator, value1, keepEmpty) {
		if (Array.isArray(value1)) {
			if (value1.length !== 1) return keepEmpty ? this.createEmptyFilterCondtion(path, operator) : void 0;
			value1 = value1[0];
		}
		const value = ViewModelPropertyBase.getEnumMemberValue(value1);
		if (operator === FilterOperator.in) operator = FilterOperator.equal;
		else if (operator === FilterOperator.notIn) operator = FilterOperator.notEqual;
		return Filter.createComplexFilter(path, operator, this.oDataPrefix, this.oDataSuffix, value);
	}
	createEnumFilterCondition(path, operator, value1, keepEmpty) {
		let values;
		if (Array.isArray(value1)) values = isEnumMember(value1[0]) ? value1.map((m) => m.name) : value1;
		else values = [ViewModelPropertyBase.getEnumMemberValue(value1)];
		if (values.length === 0) return keepEmpty ? this.createEmptyFilterCondtion(path, operator) : void 0;
		if (values.length > 1) {
			if (operator === FilterOperator.equal) operator = FilterOperator.in;
			else if (operator === FilterOperator.notEqual) operator = FilterOperator.notIn;
		}
		return Filter.createComplexFilter(path, operator, this.oDataPrefix, this.oDataSuffix, ...values);
	}
	static getEnumMemberValue(value) {
		return isEnumMember(value) ? value.name : value;
	}
	createDateFilterCondition(path, operator, value1, value2) {
		value1 = this.createDateFilterConditionValue(value1);
		if (operator === FilterOperator.between && value2 != null) {
			value2 = this.createDateFilterConditionValue(value2);
			return Filter.createComplexFilter(path, operator, this.oDataPrefix, this.oDataSuffix, value1, value2);
		}
		return Filter.createComplexFilter(path, operator, this.oDataPrefix, this.oDataSuffix, value1);
	}
	createDateFilterConditionValue(value) {
		if (Array.isArray(value)) return value.map((v) => this.createDateFilterConditionValue(v));
		if (value instanceof Date) return dateTimeToDateOnly(value);
		return value;
	}
	createLocalizableStringFilterCondition(path, operator, value1) {
		if (typeof value1 === "object" && !Array.isArray(value1)) value1 = value1[this.owner.localizator.currentLanguage];
		return Filter.createComplexFilter(path, operator, this.oDataPrefix, this.oDataSuffix, value1);
	}
	createDefaultFilterCondition(path, operator, value1, value2) {
		if (operator === FilterOperator.between && value2 != null) return Filter.createComplexFilter(path, operator, this.oDataPrefix, this.oDataSuffix, value1, value2);
		return Filter.createComplexFilter(path, operator, this.oDataPrefix, this.oDataSuffix, value1);
	}
	createEmptyFilterCondtion(path, operator) {
		return Filter.createComplexFilter(path, operator, this.oDataPrefix, this.oDataSuffix);
	}
	/** @inheritdoc ViewModelProperty.enableWatcher */
	enableWatcher(deep) {
		this.watch = deep ? "deep" : "shallow";
	}
	/** @inheritdoc ViewModelProperty.getLookupDisplayPropertyPath */
	getLookupDisplayPropertyPath(server = false) {
		if (this.lookupDisplayProperty) return this.lookupDisplayProperty.camelCaseName;
		if (server) return `neosadditionaldata.${(this.camelCaseName + this.lookupDefinition.displayProperty.camelCaseName).replaceAll(".", "")}`;
		return `$additionalData.${this.getLookupDisplayPropertyAdditionalDataKey()}`;
	}
	/** @inheritdoc ViewModelProperty.hasLookupDisplayPropertyValue */
	hasLookupDisplayPropertyValue(item) {
		if (this.lookupDisplayProperty) return this.lookupDisplayProperty.camelCaseName in item;
		if (item.$additionalData) {
			const additionalData = item.$additionalData;
			return this.getLookupDisplayPropertyAdditionalDataKey() in additionalData;
		}
		return false;
	}
	/** @inheritdoc ViewModelProperty.getLookupDisplayPropertyValue */
	async getLookupDisplayPropertyValue(item) {
		if (item instanceof Model) {
			if (this.lookupDisplayProperty) return this.lookupDisplayProperty.getValue(item);
			return item.getAdditionalDataAsync(this.getLookupDisplayPropertyAdditionalDataKey());
		}
		if (this.lookupDisplayProperty) return getPropertyByPath(item, this.lookupDisplayProperty.camelCaseName);
		if (item.$additionalData) return item.$additionalData[this.getLookupDisplayPropertyAdditionalDataKey()];
	}
	/** @inheritdoc ViewModelProperty.getLookupDisplayPropertyValueSync */
	getLookupDisplayPropertyValueSync(item) {
		if (item instanceof Model) {
			if (this.lookupDisplayProperty) return this.lookupDisplayProperty.getValue(item);
			return item.getAdditionalData(this.getLookupDisplayPropertyAdditionalDataKey());
		}
		if (this.lookupDisplayProperty) return getPropertyByPath(item, this.lookupDisplayProperty.camelCaseName);
		if (item.$additionalData) return item.$additionalData[this.getLookupDisplayPropertyAdditionalDataKey()];
	}
	/** @inheritdoc ViewModelProperty.setLookupDisplayPropertyValue */
	setLookupDisplayPropertyValue(item, value) {
		if (this.lookupDisplayProperty) {
			this.lookupDisplayProperty.setValue(item, value);
			return;
		}
		item.$additionalData ??= {};
		item.$additionalData[this.getLookupDisplayPropertyAdditionalDataKey()] = value;
	}
	/** @inheritdoc ViewModelProperty.hasRunningLookupDisplayPropertyValueInvalidation */
	hasRunningLookupDisplayPropertyValueInvalidation(item) {
		if (this.lookupDisplayProperty) return false;
		return item.hasRunningAdditionalDataInvalidation(this.getLookupDisplayPropertyAdditionalDataKey());
	}
	getLookupDisplayPropertyAdditionalDataKey() {
		return `${this.name}_DisplayProperty`;
	}
	get filterCaption() {
		return !isNullOrWhiteSpace(this.datagridCaption) ? this.datagridCaption : this.caption;
	}
	getFilterEnumMembers() {
		return this.getEnabledEnumMembers(void 0);
	}
	getFilterDataType() {
		return this.getDisplayDataType();
	}
	getFilterOperators() {
		const customOperators = {};
		for (const customOperator of this.customFilterOperators) customOperators[customOperator.id] = {
			caption: customOperator.name,
			lowerCaseCaption: customOperator.name.toLowerCase(),
			image: customOperator.icon
		};
		return {
			...customOperators,
			...getAllowedOperators(this.getFilterDataType(), !this.getRequired(void 0))
		};
	}
	getFilterFormat() {
		return this.getFormat(void 0);
	}
	getFilterFormatOptions() {
		return {
			dataType: this.getFilterDataType(),
			format: this.getFilterFormat(),
			enum: this.enum,
			isExpressionCode: this.isExpressionCode
		};
	}
	_getFilterComponentCalendar() {
		return this.filterComponent == FilterComponent.default ? FilterComponent.calendarDay : this.filterComponent;
	}
	toggleFilterDateComponentView() {
		this._userFilterComponent = this.userFilterComponent === FilterComponent.default ? this._getFilterComponentCalendar() : FilterComponent.default;
	}
};
//#endregion
//#region src/viewModel/ViewModelProperties.ts
var ViewModelProperties = class {
	/**
	* Initialize a new ViewModelActions instance.
	*
	* @param owner - ViewModel owner.
	*/
	constructor(owner) {
		this.$owner = owner;
	}
	[Symbol.iterator]() {
		let index = -1;
		const properties = Object.values(this).filter((p) => p instanceof ViewModelPropertyBase);
		return { next() {
			index++;
			return index >= properties.length ? {
				value: void 0,
				done: true
			} : {
				value: properties[index],
				done: false
			};
		} };
	}
	addNew(name, dataType, enumType) {
		const camelCaseName = toCamelCase(name);
		this[camelCaseName] = new class extends ViewModelPropertyBase {
			constructor(..._args) {
				super(..._args);
				this.name = name;
				this.camelCaseName = camelCaseName;
				this.dataType = dataType;
				this.fromWebAPI = false;
				this.enum = enumType;
			}
		}(this.$owner);
		return this[camelCaseName];
	}
	filter(predicate) {
		return Object.values(this).filter((p) => p instanceof ViewModelPropertyBase && predicate(p));
	}
	find(predicate) {
		return Object.values(this).find((p) => p instanceof ViewModelPropertyBase && predicate(p));
	}
	map(callback) {
		const results = [];
		for (const element of Object.values(this)) if (element instanceof ViewModelPropertyBase) results.push(callback(element));
		return results;
	}
};
//#endregion
//#region src/viewModel/ViewModel.ts
var DeletionMode = /* @__PURE__ */ function(DeletionMode) {
	/**
	* Items are always logically deleted.
	* This mode is used when the deletion mode of the entity view is `Logical`.
	*/
	DeletionMode[DeletionMode["logical"] = 0] = "logical";
	/**
	* Items are physically deleted but when the deletion fails, the user is asked if they want to logically delete the item.
	* This mode is used when the deletion mode of the entity view is `Physical` and the entity has a status property configured.
	*/
	DeletionMode[DeletionMode["logicalWhenPhysicalFails"] = 1] = "logicalWhenPhysicalFails";
	/**
	* Items are always physically deleted.
	* This mode is used when the deletion mode of the entity view is `Physical`.
	*/
	DeletionMode[DeletionMode["physical"] = 2] = "physical";
	return DeletionMode;
}({});
var StatusFilterMode = /* @__PURE__ */ function(StatusFilterMode) {
	/**
	* All items.
	*/
	StatusFilterMode[StatusFilterMode["allItems"] = 0] = "allItems";
	/**
	* Active items only.
	*/
	StatusFilterMode[StatusFilterMode["activeItems"] = 1] = "activeItems";
	/**
	* Inactive items only.
	*/
	StatusFilterMode[StatusFilterMode["inactiveItems"] = 2] = "inactiveItems";
	return StatusFilterMode;
}({});
/**
* View model.
*
* CLASS INVARIANT: (parentRelation !== undefined) => (parentViewModel != null)
* If there is a data-related relation with a parent ViewModel, then there is a parent ViewModel.
* The opposite is not true because the current ViewModel may have a parent ViewModel
* without their data having any relation whatsoever.
*
* CONTRAPOSITIVE CLASS INVARIANT: (parentViewModel === undefined) => (parentRelation === undefined)
*/
var ViewModel = class extends BaseComponentViewModel {
	/**
	* Gets the caption to display in messages.
	*/
	get caption() {
		return this.resourceManager.find("caption");
	}
	/**
	* Gets the message to display when asking the user to confirm a deletion.
	*/
	get deletionConfirmationMessage() {
		return this.resourceManager.find("deletionConfirmationMessage");
	}
	/**
	* Gets or sets the title of the UI view.
	*/
	get title() {
		return this._title;
	}
	set title(value) {
		this._title = value;
	}
	/**
	* Gets the documentation.
	*/
	get documentation() {
		return this.resourceManager.find("documentation");
	}
	/**
	* Gets the relations defined on the model.
	*/
	get relationships() {
		const modelType = this.creator;
		const array = [];
		modelType.$relations.forEach((value, key) => {
			array.push({
				name: key,
				...value
			});
		});
		return array;
	}
	/**
	* Gets or sets a value indicating whether creating a new item is allowed.
	*/
	get creationAllowed() {
		return this._creationAllowed;
	}
	set creationAllowed(value) {
		this._creationAllowed = value;
	}
	/**
	* Gets a value indicating whether cloning an item is allowed.
	*
	* @param _item - Model for which cloning an item is gotten.
	* @returns true if cloning an item is allowed; otherwise false.
	*/
	getCloningAllowed(_item) {
		return this._cloningAllowed && this.lookupContext?.templateMode !== LookupTemplateMode.default;
	}
	/**
	* Sets a value indicating whether cloning an item is allowed.
	*
	* @param value - true if cloning an item is allowed; otherwise false.
	*/
	setCloningAllowed(value) {
		this._cloningAllowed = value;
	}
	/**
	* Gets a value indicating whether deleting an item is allowed.
	*
	* @param _item - Model for which deleting an item is gotten.
	* @returns true if deleting an item is allowed; otherwise false.
	*/
	getDeletionAllowed(_item) {
		return this._deletionAllowed && this.lookupContext?.templateMode !== LookupTemplateMode.default;
	}
	/**
	* Sets a value indicating whether deleting an item is allowed.
	*
	* @param _item - Model for which deleting an item is set.
	*/
	setDeletionAllowed(value) {
		this._deletionAllowed = value;
	}
	/**
	* Gets a value indicating whether updating an item is allowed.
	*
	* @param _item - Model for which updating an item is gotten.
	* @returns true if updating an item is allowed; otherwise false.
	*/
	getUpdateAllowed(_item) {
		return this._updateAllowed && this.lookupContext?.templateMode !== LookupTemplateMode.default;
	}
	/**
	* Sets a value indicating whether updating an item is allowed.
	*
	* @param _item - Model for which updating an item is set.
	*/
	setUpdateAllowed(value) {
		this._updateAllowed = value;
	}
	/**
	* Gets the owner frame, checking parent view models if not defined on this one.
	*/
	getOwnerFrame() {
		if (this.ownerFrame) return this.ownerFrame;
		if (this.parentViewModel) return this.parentViewModel.getOwnerFrame();
	}
	/**
	* Gets the remote information, checking parent view models if not defined on this one.
	*/
	getRemote() {
		if (this.remote) return this.remote;
		if (this.parentViewModel) return this.parentViewModel.getRemote();
	}
	/**
	* Gets the properties as array.
	*/
	getProperties() {
		return Array.from(this.properties);
	}
	/**
	* Gets the visible properties.
	*/
	getVisibleProperties() {
		return this.properties.filter((p) => p.getFormVisible(void 0));
	}
	/**
	* Gets the visible properties in datagrid sorted by the datagrid position.
	*/
	getDatagridProperties() {
		return this.properties.filter((p) => p.canBeVisibleInDatagrid).sort((p1, p2) => {
			return (p1.datagridVisible ? p1.datagridPosition : Number.MAX_VALUE) - (p2.datagridVisible ? p2.datagridPosition : Number.MAX_VALUE);
		});
	}
	/**
	* Gets the displayable properties.
	*/
	getDisplayableProperties() {
		return this.properties.filter((p) => p.displayable);
	}
	/**
	* Gets the filterable properties.
	*/
	getFilterableProperties() {
		return this.properties.filter((p) => (p.displayable || p.filterOnly) && p.filterable);
	}
	/**
	* Gets the filterable attributes.
	*/
	getFilterableAttributes() {
		return [...this.getFilterableProperties(), ...this.entityViewParameters.filter((p) => p.filterable)];
	}
	/**
	* Gets the properties for which the filter is required.
	*/
	getPropertiesWithRequiredFilter() {
		return this.properties.filter((p) => p.filterRequired);
	}
	/**
	* Gets the value indicating whether the save action can never be executed.
	*/
	get canNeverExecuteSaveAction() {
		if (!this.isMain) return true;
		return this.canNeverExecuteSave;
	}
	/**
	* Gets the value indicating whether the save can never be executed.
	*/
	get canNeverExecuteSave() {
		if (!this.uiViewPermitter.canSave(this.uiViewName)) return true;
		if (this._updateAllowed && !this.editingUIViewName) return false;
		if (this._creationAllowed && !this.addingUIViewName) return false;
		if (this._deletionAllowed && !this.isMain) return false;
		return true;
	}
	/**
	* Gets the value indicating whether the add action can never be executed. This is the case when :
	*  - creationAllowed is not an expression and _creationAllowed has the value false.
	*  - The creation permission is not allowed.
	*/
	get canNeverExecuteAddAction() {
		return !this._creationAllowed || !this.uiViewPermitter.canCreate(this.addingUIViewName ?? this.uiViewName);
	}
	/**
	* Gets the value indicating whether the edit action can never be executed. This is the case when :
	*  - updateAllowed is not an expression and _updateAllowed has the value false.
	*  - The update permission is not allowed.
	*/
	get canNeverExecuteEditAction() {
		return !this._updateAllowed || !this.uiViewPermitter.canUpdate(this.uiViewName);
	}
	/**
	* Gets the value indicating whether the clone action can never be executed. This is the case when :
	*  - cloningAllowed is not an expression and _cloningAllowed has the value false.
	*  - canNeverExecuteAddAction has the value false.
	*/
	get canNeverExecuteCloneAction() {
		return !this._cloningAllowed || this.canNeverExecuteAddAction;
	}
	/**
	/**
	* Gets the value indicating whether the remove action can never be executed. This is the case when :
	*  - deletionAllowed is not an expression and _deletionAllowed has the value false.
	*  - The deletion permission is allowed.
	*/
	get canNeverExecuteRemoveAction() {
		return !this._deletionAllowed || !this.uiViewPermitter.canDelete(this.uiViewName);
	}
	/**
	* Gets the value indicating whether the add action can be executed.
	*/
	canExecuteAddAction(busy = this.busy) {
		if (busy || !this.canExecuteAddOrCloneAction) return false;
		if (this.isMain && this.monoRecord && this.current && this.current.$new) return false;
		return true;
	}
	/**
	* Gets the value indicating whether the clone action can be executed.
	*/
	canExecuteCloneAction(items = this.selectedItems) {
		if (this.busy || !this.canExecuteAddOrCloneAction) return false;
		if (this.creationMode) return false;
		if (items.length > 1) return false;
		const item = items[0] ?? this.current;
		return item !== void 0 && !item.$new && this.getCloningAllowed(item);
	}
	/**
	* Gets the value indicating whether the export action can be executed.
	* We don't allow export:
	* - if the model is busy (operation in progress)
	* - if the model is dirty (has changes needing to be saved)
	*/
	canExecuteExportAction() {
		if (this.busy) return false;
		if (this.hasChanges) return false;
		return true;
	}
	/**
	* Gets the value indicating whether the close action can be executed.
	*/
	get canExecuteCloseAction() {
		return !this.busy;
	}
	/**
	* Gets the value indicating whether the refresh action can be executed.
	*/
	get canExecuteRefreshAction() {
		return !this.busy && !this.creationMode;
	}
	/**
	* Gets the value indicating whether the remove action can be executed.
	*/
	canExecuteRemoveAction(items = this.selectedItems) {
		if (this.busy) return false;
		items = this.getItemsToRemove(items);
		if (items.length === 0) return false;
		for (const item of items) if (!this.getDeletionAllowed(item)) return false;
		return true;
	}
	/**
	* Gets the value indicating whether the save action can be executed.
	*/
	get canExecuteSaveAction() {
		if (this.canNeverExecuteSaveAction) return false;
		if (this.busy) return false;
		if (!this.hasChanges) return false;
		if (!this.canSaveModifiedItems) return false;
		return true;
	}
	/**
	* Gets the value indicating whether the add or clone action can be executed.
	*/
	get canExecuteAddOrCloneAction() {
		if (!this.creationAllowed) return false;
		if (this.isSubViewModel && !this.isParentCurrentDefined) return false;
		return true;
	}
	get canSaveModifiedItems() {
		const modifiedNotCreated = this.modifiedItems.filter((i) => !i.$new);
		if (modifiedNotCreated.length > 0 && (!this.uiViewPermitter.canUpdate(this.uiViewName) || !modifiedNotCreated.some((i) => this.getUpdateAllowed(i)))) return false;
		return true;
	}
	/**
	* Gets the entity view parameters as array.
	*/
	getEntityViewParameters() {
		return Array.from(this.entityViewParameters);
	}
	/**
	* Gets the methods.
	*/
	get methods() {
		return {};
	}
	/**
	* Gets methods triggered when a specific item property has changed.
	*/
	getPropertyChangedHandlers() {
		return this.propertyChangedHandlers;
	}
	/**
	* Gets the reference retrieving rules.
	* Function's list grouped by reference name.
	*/
	getReferenceRetrievingEventRules() {
		return this.referenceRetrievingEventRules;
	}
	/**
	* Gets the reference selected rules.
	* Function's list grouped by reference name.
	*/
	getReferenceSelectedEventRules() {
		return this.referenceSelectedEventRules;
	}
	/**
	* Gets the full datasource for filtered viewModels.
	*/
	get fullDatasource() {
		return this.datasource;
	}
	/**
	* Gets a value indicating whether the view model is a nest view model on computed.
	*/
	get isNestedViewModelOnComputed() {
		return this.originalViewModel !== void 0;
	}
	/**
	* Gets the original view model of a view model that is as nest view model on computed.
	*/
	get originalViewModel() {}
	/**
	* Gets the datasource.
	*/
	get datasource() {
		this.invalidateDatasource();
		return this._datasource;
	}
	invalidateDatasource() {
		if (this.parentViewModel && this.parentRelation) {
			const parent = this.parentViewModel.current;
			if (!parent) {
				if (this._datasource.length > 0 || this._datasource.hasChanges()) this._datasource = new ObservableList();
				return;
			}
			this.setCollectionAsDataSourceFromParentItem(this.parentRelation, parent);
			this.setReferenceAsDataSourceFromParentItem(this.parentRelation, parent);
		}
	}
	setCollectionAsDataSourceFromParentItem(parentRelation, parent) {
		if (parentRelation.type === RelationType.collection && parent.$relations.has(parentRelation.name)) {
			const relationList = parent.getRelationList(parentRelation.name);
			if (relationList !== this._datasource) {
				this.setDatasource(relationList);
				if (!parentRelation.embeddedForReading && !parent.$new && parentRelation.autoLoad && relationList.loadingState === ListLoadingState.notLoaded) this.loadDataAsync();
				else this.refreshCollectionDataSource(relationList);
			}
		}
	}
	refreshCollectionDataSource(relationList) {
		this.onDatasourceListChanged({ type: ListChangedType.reset });
		if (this.getClientSideSort() && this.dataSorts.length > 0) relationList.sortItems(this.dataSorts);
		if (this.clientSideFilter) this.clientSideFilterData(false);
	}
	setReferenceAsDataSourceFromParentItem(parentRelation, parent) {
		if (parentRelation.type === RelationType.editableReference || parentRelation.type === RelationType.reference) {
			const relationList = parent.getRelationList(parentRelation.name);
			if (relationList !== this._datasource) {
				this.ensureNonEmptyDatasourceOnEditableReference(parentRelation, relationList);
				this._position = 0;
				this.setDatasource(relationList);
				if (parentRelation.embeddedForReading) parent[parentRelation.name] = relationList[0];
				this.onDatasourceListChanged({ type: ListChangedType.reset });
				if (!parentRelation.embeddedForReading && this.currentId && parentRelation.autoLoad && relationList.loadingState === ListLoadingState.notLoaded) this.loadDataAsync();
			}
		}
	}
	ensureNonEmptyDatasourceOnEditableReference(parentRelation, datasource) {
		if (datasource.length === 0 && parentRelation.type === RelationType.editableReference) {
			const newItem = reactive(new this.creator(void 0, this));
			datasource.setItems([newItem]);
		}
	}
	setDatasource(datasource) {
		this.unsubscribeDatasource(this._datasource);
		this._datasource.position = this._position;
		this._datasource = datasource;
		if (this._datasource.position) this._position = this._datasource.position;
		this.subscribeDatasource(this._datasource);
		this.datasourceChangedEvent.raise(this._datasource);
	}
	/**
	* Gets the current position in the datasource.
	*/
	get position() {
		return this._position;
	}
	/**
	* Sets the current position in the datasource.
	*/
	set position(value) {
		this.requestChangingPositionAsync(value);
	}
	/**
	* Request a change for the position.
	* @param newPosition The new position requested.
	* @returns true if the position has been changed, false otherwise.
	*/
	async requestChangingPositionAsync(newPosition) {
		if (this._position === newPosition) return false;
		const positionChangingArgs = {
			cancel: false,
			oldValue: this._position,
			value: newPosition
		};
		await this.runExpressionAndLogAsync("onPositionChanging", this.onPositionChanging, positionChangingArgs);
		if (positionChangingArgs.cancel) return false;
		await this.setPosition(newPosition);
		return true;
	}
	async setPosition(position, runPositionChanged = true) {
		this._position = position;
		this.onCurrentChanged();
		this.positionChangedEvent.raise(this._position);
		if (runPositionChanged) await this.trackEventHandler(this.runExpressionAndLogAsync("onPositionChanged", this.onPositionChanged));
	}
	/**
	* Gets the current item in the datasource.
	*/
	get current() {
		return this.datasource[this.position];
	}
	/**
	* Gets a value indicating whether data is being loaded
	* for this particular ViewModel, regardless of any parent ViewModels.
	*
	* Historically, this was the only implementation:
	* we only checked whether the owner datasource was already being loaded
	* and this getter was not checking whether any **related** parent ViewModel was loading.
	*
	* Early 2025, a new (recursive) `loadingMeOrSomeParent` was introduced.
	*
	* BACKWARD COMPATIBILITY: Most existing calls, including transpilation,
	* still use this getter instead of the new recursive one.
	*
	* Remark about the future: if a parameterized method such as
	* `ViewModel.getLoadingFlag(checkRelatedParents : boolean)` is added,
	* this getter and its C# abstraction may end up deprecated.
	*/
	get loading() {
		return this.datasource.loadingState === ListLoadingState.loading;
	}
	/**
	* Gets a value indicating whether data is being loaded by some data-related parent view-model.
	* - This method is recursive.
	* - This method relies on a class invariant (see class documentation).
	*/
	get loadingSomeParent() {
		if (this.isSubViewModel) return this.parentViewModel.loadingMeOrSomeParent;
		return false;
	}
	/**
	* Gets a value indicating whether data is being loaded
	* for the current view-model or by some data-related parent view-model.
	* - This method is recursive.
	* - This method relies on a class invariant (see class documentation).
	*/
	get loadingMeOrSomeParent() {
		return this.loading || this.loadingSomeParent;
	}
	/**
	* Gets a value indicating whether more data can be retrieved.
	*/
	get hasMoreData() {
		return this.groupDescriptions.length > 0 ? this.groups.hasMoreData : this.datasource.hasMoreData;
	}
	/**
	* Gets or sets the message for a waiting overlay.
	*/
	get waitingOverlayMessage() {
		return this._waitingOverlayMessage;
	}
	set waitingOverlayMessage(value) {
		if (this.parentViewModel) this.parentViewModel.waitingOverlayMessage = value;
		else this._waitingOverlayMessage = value;
	}
	/** Invokes an action and displays an overlay with the specified message during execution.
	* @param message The message to display during execution.
	* @param action The asynchronous action to launch.
	* @returns The result of the asynchronous action.
	*/
	async invokeWithWaitingOverlayAsync(message, action) {
		this.waitingOverlayMessage = message;
		try {
			return await action();
		} finally {
			this.waitingOverlayMessage = void 0;
		}
	}
	/**
	* Gets a value indicating whether all data are loaded.
	*/
	get fullDataLoaded() {
		return this.pageNumber === 1 && !this.hasMoreData;
	}
	/**
	* Gets the modified items of the datasource.
	*/
	get modifiedItems() {
		return this.datasource.filter((e) => e.$hasChanges);
	}
	/**
	* Gets the created items of the datasource.
	*/
	get createdItems() {
		return this.datasource.filter((e) => e.$new);
	}
	/**
	* Gets a value indicating whether the view model is busy.
	*/
	get busy() {
		return this._busy || this.datasource.loadingState === ListLoadingState.loading || this.subViews.some((vm) => vm.busy);
	}
	/**
	* Gets a value indicating whether the view model is saving.
	*/
	get saving() {
		return this._saving || this.subViews.some((vm) => vm.saving);
	}
	/**
	* Gets a value indicating whether datasource has changes.
	*/
	get hasChanges() {
		if (this._forceModified) return true;
		if (this.datasource.hasChanges()) return true;
		for (const viewModel of this.viewModels) if (!viewModel.parentRelation && viewModel.hasChanges) return true;
		for (const [, viewModel] of this.viewContainers) if (viewModel.hasChanges) return true;
		return false;
	}
	/**
	* Gets a value indicating whether there is at least an item with errors.
	*/
	get hasErrors() {
		return this.datasource.hasErrors;
	}
	/**
	* Gets a value indicating whether there is at least an item with persistence errors.
	*/
	get hasPersistenceErrors() {
		return this.datasource.hasPersistenceErrors;
	}
	/**
	* Gets the items with at least one persistence error.
	*/
	get persistenceErrorItems() {
		return this.getPersistenceErrorItems(this.datasource);
	}
	/**
	* Gets the typed parentViewModel.
	*/
	getParentViewModel() {
		return this.parentViewModel;
	}
	/**
	* Gets the highest view model in the view.
	*/
	get mainViewModel() {
		if (this.parentViewModel === void 0) return this;
		return this.parentViewModel.mainViewModel;
	}
	/**
	* Gets the master view model in the view.
	*/
	get masterViewModel() {
		if (this.isMain || !this.parentViewModel) return this;
		return this.parentViewModel.masterViewModel;
	}
	/**
	* Gets the value indicating whether the view model is a sub viewModel.
	* - This getter is related to a class invariant (see class documentation).
	*/
	get isSubViewModel() {
		return this.parentRelation !== void 0;
	}
	/**
	* Gets the value indicating whether the view model is an embedded sub viewModel for reading.
	* - This getter is related to a class invariant (see class documentation).
	*/
	get isEmbeddedSubViewModelForReading() {
		return this.parentRelation?.embeddedForReading === true;
	}
	/**
	* Gets the value indicating whether the view model is the main view model.
	*/
	get isMain() {
		return !this.isSubViewModel;
	}
	/**
	* Gets the value indicating whether the parent view model current item is defined.
	*/
	get isParentCurrentDefined() {
		return this.parentViewModel?.current !== void 0;
	}
	/**
	* Gets the current item id.
	*/
	get currentId() {
		if (this.parentRelation?.type === RelationType.reference) {
			const keyProperties = this.creator.$keyProperties;
			const keyValues = this.parentRelation.associations.map((n) => this.getParentItem()[n]);
			if (keyValues.some((v) => v === void 0 || v === null)) return;
			return new Map(Array.from(keyProperties).map((key, i) => [key, keyValues[i]]));
		}
		return this._currentId;
	}
	/**
	* Sets the current item id.
	*/
	set currentId(value) {
		if (!this._currentId && !value) return;
		if (this._currentId && value && compareDictionaries(this._currentId, value)) return;
		this._currentId = value;
		this.eventManager.emit("viewModel:currentIdChanged", this);
		if (__NEOS_STUDIO_VSCODE__ && value?.has("name")) {
			postMessageToVsCode({
				command: "currentIdChanged",
				newId: Object.fromEntries(value)
			});
			updateViewTypeWithId(value.get("name"));
		}
	}
	/**
	* Gets or sets the value indicating that we are in creation mode.
	*/
	get creationMode() {
		return !!this.creationOptions;
	}
	set creationMode(value) {
		if (value) this.creationOptions ??= {};
		else this.creationOptions = void 0;
	}
	get dataSorts() {
		return this._dataSorts.filter((s) => s instanceof Sort);
	}
	set dataSorts(dataSorts) {
		this._dataSorts = dataSorts;
	}
	/**
	* Gets a value indicating whether the sorting should be done on client-side.
	*
	* When there are server groups, the sorting is done on the server.
	* When it is an embedded collection for reading, the sorting is done on the client.
	* When full data is loaded and the sorting execution side is set `clientWhenFullDataLoaded` or the parameter is set to `true`, the sorting is done on the client.
	* Otherwise, the sorting is done on the server.
	*/
	getClientSideSort(forceClientWhenFullDataLoaded = false) {
		if (this.groupingBehavior.executionSide === GroupingExecutionSide.server && this.groupDescriptions.length > 0) return false;
		if (this.isEmbeddedSubViewModelForReading) return true;
		if (this.fullDataLoaded && (this.sortingBehavior.executionSide === SortingExecutionSide.clientWhenFullDataLoaded || forceClientWhenFullDataLoaded)) return true;
		return false;
	}
	/**
	* Gets the quick search place holder.
	*/
	get quickSearchPlaceHolder() {
		return this.resourceManager.find("quickSearchPlaceHolder");
	}
	/**
	* Gets a value indicating whether the filter should be done on client-side.
	*/
	get clientSideFilter() {
		return this.isEmbeddedSubViewModelForReading || this.filteringBehavior.executionSide === FilteringExecutionSide.client;
	}
	/**
	* Gets the selected items.
	*/
	get selectedItems() {
		return this.datasource.filter((item) => this.isSelected(item));
	}
	/**
	* Gets the total record count.
	*/
	get totalRecordCount() {
		return this.groupDescriptions.length > 0 ? this.groups.totalRecordCount : this.datasource.totalRecordCount;
	}
	get currentCustomViewId() {
		return this._currentCustomViewId;
	}
	set currentCustomViewId(value) {
		this._currentCustomViewId = value;
		this.customViewManager.onCurrentCustomViewIdChanged(this);
	}
	get isCustomViewEnabled() {
		return !this.contextData.get("DisableCustomView");
	}
	async initCustomView() {
		if (this.isMain && this.customViewManager.userCustomViewMode && this.isCustomViewEnabled) {
			this.customViewManager.saveViewModelState(this);
			await this.customViewManager.setDefaultCustomViewAsync(this);
		}
	}
	/**
	* Initializes a new ViewModel instance.
	*
	* @param options - Options.
	*/
	constructor(creator, options) {
		super(options.applicationContext, options.eventManager, options.localizator, options.globalResourceManager, options.apiClient, options.notifications, options.logger, options.resourceManager, options.wrapInstance);
		this._forceModified = false;
		this._title = "";
		this._creationAllowed = true;
		this._cloningAllowed = true;
		this._deletionAllowed = true;
		this._updateAllowed = true;
		this.rootElement = null;
		this.deletionMode = 2;
		this.statusFilterMode = 0;
		this.description = "";
		this.computedChangedEvent = createLocalEvent();
		this.icon = "";
		this.isOnComputedChangedCallConfigured = false;
		this.loadDataOnStart = false;
		this.monoRecord = false;
		this.validateLocalizableString = true;
		this.computeds = { owner: this };
		this.propertyChangedHandlers = {};
		this.referenceRetrievingEventRules = {};
		this.referenceSelectedEventRules = {};
		this.validationRules = [];
		this.datasourceChangedEvent = createLocalEvent();
		this.positionChangedEvent = createLocalEvent();
		this.initialized = false;
		this.isIdleAfterInitialization = false;
		this._busy = false;
		this._saving = false;
		this._ignoreLastAddedItem = false;
		this.isAddingAddNewItem = false;
		this._transientKey = 0;
		this._nestedViewModels = /* @__PURE__ */ new Map();
		this.viewModels = [];
		this.sortingBehavior = { executionSide: SortingExecutionSide.server };
		this._dataSorts = [];
		this.namedFilters = [];
		this.filteringBehavior = { executionSide: FilteringExecutionSide.server };
		this.clientSideFilteredDatasourceChangedEvent = createLocalEvent();
		this.recordsByPageList = [
			5,
			10,
			20,
			50
		];
		this.groupingBehavior = {
			executionSide: GroupingExecutionSide.server,
			expandGroupsOnLoad: false
		};
		this.groupDescriptions = [];
		this.groups = new GroupList();
		this.datasourceListChangedEvent = createLocalEvent();
		this.itemPropertyMetadataChangedEvent = createLocalEvent();
		this.customValidationMessages = [];
		this.validatingEvent = createLocalEvent();
		this.validatedEvent = createLocalEvent();
		this.onInitialized = void 0;
		this.onRetrieving = void 0;
		this.onRetrieved = void 0;
		this.onPositionChanging = void 0;
		this.onPositionChanged = void 0;
		this.onDataSaving = void 0;
		this.onDataSaved = void 0;
		this.onItemAdding = void 0;
		this.onItemAdded = void 0;
		this.onItemCloning = void 0;
		this.onItemSelectionChanging = void 0;
		this.onItemSelectionChanged = void 0;
		this.onRemoving = void 0;
		this.onItemRemoving = void 0;
		this.onItemRemoved = void 0;
		this.onClosing = void 0;
		this.onNavigating = void 0;
		this.arrayComputedListChangedHandler = /* @__PURE__ */ new Map();
		this.viewContainers = /* @__PURE__ */ new Map();
		this.summaryLoadVersions = /* @__PURE__ */ new WeakMap();
		this.devStyleRules = [];
		this.userStyleRules = [];
		this._pendingEventHandlers = /* @__PURE__ */ new Set();
		this.creator = creator;
		const vm = this.wrapInstance(this);
		vm.setFrame(options.frame);
		this.remote = options.remote;
		this.id = options.id;
		this.properties = new ViewModelProperties(vm);
		this.actions = new ViewModelActions(vm);
		this.entityViewParameters = new ViewModelEntityViewParameters();
		this._propertyWatchers = new ViewModelPropertyWatchers(vm);
		this._validationRuleWatchers = new ValidationRuleWatchers(vm);
		this.dataSharingManager = new DataSharingManager(vm);
		this.uiViewPermitter = options.uiViewPermitter;
		this.customViewManager = options.customViewManager;
		this.behavior = this.getBehavior();
		this._datasourceSubscribedHandler = this.onDatasourceSubscribed.bind(vm);
		this._datasourceUnsubscribedHandler = this.onDatasourceUnsubscribed.bind(vm);
		this._datasourceListChangedHandler = this.onDatasourceListChanged.bind(vm);
		this._itemPropertyMetadataChangedHandler = this.onItemPropertyMetadataChanged.bind(vm);
		const parameters = options.parameters ?? this.ownerFrame?.parameters;
		this.viewContext = new Map(parameters);
		this._currentId = parameters?.id;
		this.creationOptions = parameters?.creationOptions;
		this.cloneMode = parameters?.cloneMode ?? false;
		this.pageNumber = parameters?.pageNumber ?? 1;
		this.recordsByPage = parameters?.recordsByPage ?? 50;
		this.dontExecuteUIViewRules = parameters?.dontExecuteUIViewRules ?? false;
		this.dataFilter = this.ownerFrame?.filter ?? new Filter();
		this.urlContext = new Map(this.ownerFrame?.frameUrlParameters);
		this.initializeLookupContext(parameters);
		this._datasource = this.getInitialDatasource();
		this.subscribeDatasource(vm._datasource);
		this._position = this.ownerFrame?.position ?? -1;
		this.apiClient.jsonReviver = (key, value) => value;
		this.$super = this.initSuper();
		return vm;
	}
	getBehavior() {
		const uiDefaultBehavior = this.applicationContext.uiDefaultBehavior;
		return {
			datagridEllipsis: uiDefaultBehavior.datagridEllipsis ?? true,
			datagridInfiniteScrolling: uiDefaultBehavior.datagridInfiniteScrolling ?? false,
			datagridInputNumberType: uiDefaultBehavior.datagridInputNumberType ?? "default",
			filterBarDisplayMode: uiDefaultBehavior.filterBarDisplayMode ?? "inputs",
			initSelectionOnLoad: uiDefaultBehavior.initSelectionOnLoad ?? true,
			removeCurrentWithNoSelection: uiDefaultBehavior.removeCurrentWithNoSelection ?? false,
			flushPendingEventHandlersOnSave: uiDefaultBehavior.flushPendingEventHandlersOnSave ?? true,
			persistValidationRuleMessagesOnLoad: uiDefaultBehavior.persistValidationRuleMessagesOnLoad ?? true
		};
	}
	setFrame(frame) {
		if (frame) {
			this.ownerFrame = frame;
			this.ownerFrame.viewModel = this;
		}
	}
	getInitialDatasource() {
		let datasource;
		if (this.ownerFrame?.datasource) {
			datasource = this.ownerFrame.datasource;
			this.dataSharingManager.sharedDatasource = true;
		} else datasource = new ObservableList();
		return datasource;
	}
	initializeLookupContext(parameters) {
		if (parameters?.lookupContext) {
			this.lookupContext = parameters.lookupContext;
			if (this.lookupContext.templateMode === LookupTemplateMode.default) {
				this.addingUIViewName = parameters.get("neos-lookup-adding-ui-view-name");
				if (parameters.get("neos-lookup-multi-selection")) this.behavior.initSelectionOnLoad = false;
			}
		}
	}
	/**
	* super keyword simulation for method and getter not defined in this class.
	*/
	initSuper() {
		let superLevel = 0;
		const getEventRuleProxy = (instance, fctName, superProto, property) => {
			return new Proxy(instance[fctName], { apply: (target, thisArg, argumentsList) => {
				const superFunction = superProto[fctName];
				if (superFunction) try {
					superLevel++;
					return superFunction.apply(thisArg, argumentsList);
				} finally {
					superLevel--;
				}
				else throw new Error(stringFormat(thisArg.globalResourceManager.get("FunctionNoExistsInSuperClass"), property));
			} });
		};
		const getGetterProxy = (instance, superProto, property) => {
			const getter = Object.getOwnPropertyDescriptor(superProto, property)?.get;
			if (getter) {
				const getterValue = getter.call(this);
				if (property === "methods") return new Proxy(getterValue, { get: (target, property) => {
					const targetValue = getterValue[property.toString()];
					if (typeof targetValue !== "function") return targetValue;
					return new Proxy(getterValue[property.toString()], { apply: (target, thisArg, argumentsList) => {
						try {
							superLevel++;
							return target.apply(thisArg, argumentsList);
						} finally {
							superLevel--;
						}
					} });
				} });
				return getterValue;
			}
			return Reflect.get(instance, property);
		};
		return new Proxy(this, { get: (target, property) => {
			const instance = target;
			const fctName = `$${property.toString()}`;
			const superProto = this.getParentPrototype(superLevel);
			if (typeof instance[fctName] === "function") return getEventRuleProxy(instance, fctName, superProto, property.toString());
			return getGetterProxy(instance, superProto, property.toString());
		} });
	}
	/**
	* Returns the parent prototype according to the specified level
	* @param level the level of prototype, 0 is parent, 1 is grandparent, etc..
	* @returns the prototype
	*/
	getParentPrototype(level) {
		let proto = Object.getPrototypeOf(this);
		for (let i = 0; i <= level; i++) proto = Object.getPrototypeOf(proto);
		return proto;
	}
	/**
	* Disposes the view model.
	*/
	async disposeAsync() {
		super.dispose();
		this.unsubscribeDatasource(this._datasource);
		this.rootElement = null;
		if (this._autoSuggestService) {
			this._autoSuggestService.dispose();
			this._autoSuggestService = void 0;
		}
		await this.runExpressionAndLogAsync("onDisposed", this.onDisposed);
		if (this.ownerFrame) {
			if (__NEOS_STUDIO_VSCODE__ && this.ownerFrame.displayMode !== FrameDisplayMode.popup && this.isMain) postMessageToVsCode({ command: "closeUIView" });
			if (this.ownerFrame.viewModel === this) this.ownerFrame.viewModel = void 0;
			this.ownerFrame = void 0;
		}
	}
	/** @inheritdoc */
	runExpressionAndLogAsync(expressionName, action, args) {
		if (this.dontExecuteUIViewRules) return Promise.resolve(void 0);
		return super.runExpressionAndLogAsync(expressionName, action, args);
	}
	/**
	* Subscribes to ListChanged event of datasource
	* @param datasource - ObservableList that contains data.
	*/
	subscribeDatasource(datasource) {
		if (!this.dataSharingManager.sharedDatasource) this._datasource.setWrapInstance((item) => {
			item.$viewModel ??= this;
			return item;
		});
		datasource.subscribedEvent.add(this._datasourceSubscribedHandler);
		datasource.unsubscribedEvent.add(this._datasourceUnsubscribedHandler);
		datasource.listChangedEvent.add(this._datasourceListChangedHandler);
		datasource.itemPropertyMetadataChangedEvent.add(this._itemPropertyMetadataChangedHandler);
		this.onDatasourceSubscribed(datasource);
	}
	/**
	* Unsubscribes to ListChanged event of datasource
	* @param datasource - ObservableList that contains data.
	*/
	unsubscribeDatasource(datasource) {
		if (!this.dataSharingManager.sharedDatasource) this._datasource.setWrapInstance(void 0);
		datasource.subscribedEvent.remove(this._datasourceSubscribedHandler);
		datasource.unsubscribedEvent.remove(this._datasourceUnsubscribedHandler);
		datasource.listChangedEvent.remove(this._datasourceListChangedHandler);
		datasource.itemPropertyMetadataChangedEvent.remove(this._itemPropertyMetadataChangedHandler);
	}
	onDatasourceSubscribed(items) {
		this._propertyWatchers?.add(items);
		this._validationRuleWatchers?.add(items);
	}
	onDatasourceUnsubscribed(items) {
		this._propertyWatchers?.remove(items);
		this._validationRuleWatchers?.remove(items);
	}
	/**
	* Handles the datasource list changed event.
	*/
	onDatasourceListChanged(args) {
		if (args.itemChangedArgs) this.onDatasourceListItemChanged(args.itemChangedArgs);
		else if (!this.loading && this.canLoadClientSideSummaries()) this.loadTotalSummaries(this.datasource, SummaryLoadMode.client);
		if (this.addNewItem && !this.datasource.includes(this.addNewItem)) this.resetAddNewItem();
		this.datasourceListChangedEvent.raise(this, args);
		this.lastAddedItem = void 0;
		if (args.type === ListChangedType.reset) this.ensurePositionIsValid();
	}
	onItemPropertyMetadataChanged(args) {
		this.itemPropertyMetadataChangedEvent.raise(args);
	}
	/**
	* Handles the datasource list item changed event.
	*/
	onDatasourceListItemChanged(args) {
		const property = this.findProperty(args.propertyName);
		if (property) {
			if (args.item.$keyProperties.has(args.propertyName) && args.item.getValidationMessage(property.camelCaseName)?.severityLevel !== ValidationRuleSeverityLevel.error) this.propagateKeyChangesInRelations(args.item);
			this.invalidateNestedCollections(args);
			this.invalidateNestedReferences(args);
			if (args.item.$trackChanges && !this.isNestedViewModelOnComputed) {
				this.updateLookupFreeValueProperty(property, args);
				if (this._autoSuggestService && !this._autoSuggestService.isApplying(this, args.item) && args.item.$appliedSuggestions.has(args.propertyName)) args.item.$appliedSuggestions.delete(args.propertyName);
				this.handleAutoSuggest(property, args);
				this.trackEventHandler(this.runExpressionAndLogAsync(`propertyChanged[${property.name}]`, this.propertyChangedHandlers[property.camelCaseName], args));
			}
			if (this.canLoadClientSideSummaries()) this.loadClientSideTotalSummary(this.datasource, property);
		}
	}
	updateLookupFreeValueProperty(property, args) {
		if (args.value != void 0) {
			const propertiesWithLookupFreeValue = this.properties.filter((p) => p.lookupFreeValuePropertyName === args.propertyName);
			for (const property of propertiesWithLookupFreeValue) property.setValue(args.item, void 0);
			if (property.lookupFreeValuePropertyName && args.propertyName === property.camelCaseName) this.getProperty(property.lookupFreeValuePropertyName).setValue(args.item, void 0);
		}
	}
	invalidateNestedCollections(args) {
		this.subViews.forEach((subUIView) => {
			if (subUIView.parentRelation.type === RelationType.collection && subUIView.parentRelation.path.includes(".") && subUIView.parentRelation.referenceKeyProperties?.includes(args.propertyName)) if (args.value == void 0) subUIView.clear(true);
			else if (subUIView.parentRelation.embeddedForReading) {
				delete args.item[subUIView.parentRelation.name];
				subUIView.invalidateDatasource();
			} else {
				args.item.updateKey();
				if (subUIView.parentRelation.autoLoad) subUIView.loadDataAsync();
			}
		});
	}
	invalidateNestedReferences(args) {
		this.subViews.forEach((subUIView) => {
			if (subUIView.parentRelation.type === RelationType.reference && subUIView.parentRelation.associations.includes(args.propertyName)) {
				args.item.getRelationList(subUIView.parentRelation.name).clear(true);
				if (args.value && subUIView.parentRelation.autoLoad) subUIView.loadDataAsync();
				else subUIView.datasource.loadingState = ListLoadingState.notLoaded;
			} else if (subUIView.parentRelation.type === RelationType.reference && subUIView.parentRelation.name === args.propertyName && args.value == void 0) args.item.getRelationList(subUIView.parentRelation.name).clear(true);
		});
	}
	/**
	* Handles AI auto-suggest for a property if enabled.
	*/
	handleAutoSuggest(property, args) {
		if (!property.triggersAIAutoSuggest) return;
		this._autoSuggestService ??= new AutoSuggestService(this.applicationContext, this.apiClient);
		const jsonModel = this._autoSuggestService.serializeModel(this);
		this._autoSuggestService.requestAutoSuggest(this, args.item, property.camelCaseName, jsonModel, args.oldValue).then((response) => {
			if (response?.suggestions) this._autoSuggestService?.storeSuggestions(this, args.item, response.suggestions, property.camelCaseName);
		}).catch((error) => {
			this.logger.error("Error during auto-suggest:", error);
		});
	}
	/**
	* Clears all auto-suggestion indicators (UI + data) for this ViewModel and its sub-views.
	*/
	clearAutoSuggestIndicators(datasource = this.datasource) {
		for (const item of datasource) {
			item.$appliedSuggestions.clear();
			for (const [relation] of item.$relations) {
				const subViewModel = this.getSubViewModel(relation);
				const relationList = item.getRelationList(relation);
				subViewModel.clearAutoSuggestIndicators(relationList);
			}
		}
	}
	/**
	* Accepts and applies the AI suggestion for a property.
	* Returns true if the acceptance was performed.
	*/
	acceptAutoSuggest(item, propertyName) {
		this._autoSuggestService ??= new AutoSuggestService(this.applicationContext, this.apiClient);
		return this._autoSuggestService.acceptSuggestion(this, item, propertyName);
	}
	/**
	* Cancels the AI suggestion for a property by restoring the previous value.
	* Returns true if the cancellation was performed.
	*/
	cancelAutoSuggest(item, propertyName) {
		this._autoSuggestService ??= new AutoSuggestService(this.applicationContext, this.apiClient);
		return this._autoSuggestService.rejectSuggestion(this, item, propertyName);
	}
	ensurePositionIsValid() {
		if (this.datasource.length === 0) this.setPosition(-1);
		else if (this.position < 0) {
			if (this.groupDescriptions.length === 0) this.setPosition(0);
		} else if (this.position >= this.datasource.length) this.setPosition(this.datasource.length - 1);
		else this.setPosition(this.position);
	}
	/**
	* Initializes.
	*/
	async initAsync(preventLoadData = false) {
		super.init();
		try {
			this.apiClient.setHeader("neos-ui-view-name", this.uiViewName);
			this.actions.init();
			this.dataSharingManager.checkCoherence();
			await uiViewEvents.initialized.raiseAsync({ viewModel: this });
			await this.runExpressionAndLogAsync("onInitialized", this.onInitialized);
			await this.initCustomView();
			if (this.creationOptions) {
				await this.createItemOnInitialization(this.creationOptions);
				return;
			}
			if (!preventLoadData && this.shouldLoadDataOnInit()) this.loadDataOnInit().catch((error) => this.showError(error));
			else this.isIdleAfterInitialization = true;
		} finally {
			this.initialized = true;
		}
	}
	shouldLoadDataOnInit() {
		return this.isMain && !this.dataSharingManager.sharedDatasource && (this.loadDataOnStart || this.cloneMode);
	}
	async loadDataOnInit() {
		try {
			await this.loadDataAsync();
			if (this.cloneMode && (!this.current || !await this.cloneItemAsync(this.current, true))) await this.closeAsync(false);
		} finally {
			this.isIdleAfterInitialization = true;
		}
	}
	async createItemOnInitialization(creationOptions) {
		if (creationOptions.data) if (creationOptions.assign) await this.addNewAndAssignCreationData(creationOptions.data);
		else await this.addRangeAsync([new this.creator(creationOptions.data, this)], true);
		else await this.addNewAsync();
		this.isIdleAfterInitialization = true;
	}
	async addNewAndAssignCreationData(creationData) {
		try {
			this._ignoreLastAddedItem = true;
			const item = await this.addNewAsync();
			if (item) await this.assignCreationDataOnNewItem(item, creationData);
		} finally {
			this._ignoreLastAddedItem = false;
		}
	}
	async assignCreationDataOnNewItem(item, creationData) {
		for (const key in item.fixPropertyValues(creationData)) {
			const value = creationData[key];
			if (Array.isArray(value) && item.$relations.get(key)) {
				const subViewModel = this.getSubViewModel(key);
				for (const subCreationData of value) await subViewModel.addNewAndAssignCreationData(subCreationData);
			} else if (value != null) setPropertyByPath(item, key, value);
		}
	}
	/**
	* Finds the property.
	*
	* @param name - Property name (not case sensitive).
	* @returns Property or undefined if the property does not exist.
	*/
	findProperty(name) {
		const lowerCaseName = name.toLowerCase();
		return this.properties.find((p) => p.name.toLowerCase() === lowerCaseName);
	}
	/**
	* Returns the property from its name.
	*
	* @param name - Property name (not case sensitive).
	* @returns Property.
	*/
	getProperty(name) {
		const property = this.findProperty(name);
		if (!property) throw new Error(stringFormat(this.globalResourceManager.get("PropertyDoesNotExist"), name));
		return property;
	}
	/**
	* Returns the property validation rules according to the specified validation types.
	*
	* @param propertyName - Name of the property (PascalCasing) whose value has changed.
	* @param validationTypes - Validation types.
	* @returns A array of validation rules.
	*/
	getValidationRules(validationTypes) {
		if (validationTypes === ValidationRuleValidationTypes.reportAll) return this.validationRules.sort((a, b) => b.severityLevel - a.severityLevel);
		let validationRules = [];
		if (validationTypes & ValidationRuleValidationTypes.reportErrors) validationRules = this.validationRules.filter((v) => v.severityLevel === ValidationRuleSeverityLevel.error);
		if (validationTypes & ValidationRuleValidationTypes.reportWarnings) validationRules = validationRules.concat(this.validationRules.filter((v) => v.severityLevel === ValidationRuleSeverityLevel.warning));
		if (validationTypes & ValidationRuleValidationTypes.reportInformations) validationRules = validationRules.concat(this.validationRules.filter((v) => v.severityLevel === ValidationRuleSeverityLevel.information));
		return validationRules;
	}
	/**
	* Finds the action.
	*
	* @param name - Action name (not case sensitive).
	* @returns Action or undefined if the action does not exist.
	*/
	findAction(name) {
		return Array.from(this.actions).find((a) => a.name.toLowerCase() === name.toLowerCase());
	}
	/**
	* Finds the action by keyboard shortcut.
	*
	* @param keyboardShortcut - Keyboard shortcut.
	* @returns Action or undefined if the action does not exist.
	*/
	findActionByKeyboardShortcut(keyboardShortcut) {
		for (const action of this.actions.toReversed()) if (keyboardShortcut === action.keyboardShortcut) return action;
	}
	/**
	* Returns the action from its name.
	*
	* @param name - Action name (not case sensitive).
	* @returns Action.
	*/
	getAction(name) {
		const action = this.findAction(name);
		if (!action) throw new Error(stringFormat(this.globalResourceManager.get("ActionDoesNotExist"), name));
		return action;
	}
	/**
	* Gets a value indicating whether the specified action can be executed.
	* @param action - UI view action.
	* @returns A boolean indicating whether the specified action can be executed.
	*/
	canExecuteAction(action) {
		return action.isStandardAction || this.uiViewPermitter.canExecuteAction(this.uiViewName, action.name);
	}
	/**
	* Executes the add action.
	*/
	async executeAddActionAsync() {
		if (!this.canExecuteAddAction()) return;
		if (this.addingUIViewName) await this.navigateAsync(this.getAddingUIViewNavigationOptions());
		else await this.addNewAsync();
	}
	/**
	* Executes the clone action.
	*/
	async executeCloneActionAsync(items = this.selectedItems) {
		if (!this.canExecuteCloneAction(items)) return;
		const source = items[0] ?? this.current;
		if (this.addingUIViewName) {
			const options = new NavigationOptions(this.addingUIViewName).withId(source.$key);
			options.parameters.cloneMode = true;
			await this.navigateAsync(options);
			return;
		}
		await this.cloneAsync(source);
	}
	/**
	* Executes the close action.
	*/
	async executeCloseActionAsync() {
		if (!this.canExecuteCloseAction) return;
		await this.closeAsync();
	}
	/**
	* Executes the refresh action.
	*/
	async executeRefreshActionAsync() {
		if (!this.canExecuteRefreshAction) return;
		if (await this.checkAndSaveDataAsync()) await this.loadDataAsync(Math.max(this.recordsByPage, this.datasource.getPersistedItemsCount()));
	}
	/**
	* Executes the remove action.
	*/
	async executeRemoveActionAsync(items = this.selectedItems) {
		if (!this.canExecuteRemoveAction(items)) return;
		const shouldClose = this.currentId ?? this.creationMode;
		if (await this.deleteSelectedItemsAsync(this.getItemsToRemove(items)) && shouldClose) await this.closeAsync();
	}
	/**
	* Executes the save action.
	*/
	async executeSaveActionAsync() {
		if (!this.canExecuteSaveAction) return;
		await this.saveDataAsync();
	}
	/**
	* Adds an item at the end of the datasource.
	*
	* @param item - Item to add.
	* @param markAsVirtual - A value indicating whether item should be mark as virtual once the item has been added.
	* @returns Value indicating whether the item has been added.
	*/
	addAsync(item, markAsVirtual = false) {
		return this.addRangeAsync([item], false, markAsVirtual);
	}
	/**
	* Inserts an item at the specified position.
	*
	* @param position - Position.
	* @param item - Item to insert.
	* @returns Value indicating whether the item has been inserted.
	*/
	insertAsync(position, item, markAsVirtual = false) {
		return this.insertRangeAsync(position, [item], false, markAsVirtual);
	}
	async onAdding(item, markAsNew, markAsVirtual) {
		item.$viewModel ??= this;
		const itemAddingArgs = {
			item,
			markAsNew,
			markAsVirtual,
			cancel: false
		};
		await this.runExpressionAndLogAsync("onItemAdding", this.onItemAdding, itemAddingArgs);
		return itemAddingArgs;
	}
	async onAdded(item) {
		const itemAddedArgs = { item };
		await this.runExpressionAndLogAsync("onItemAdded", this.onItemAdded, itemAddedArgs);
	}
	async onCloning(source, target) {
		target.$viewModel ??= this;
		const itemCloningArgs = {
			source,
			item: target,
			cancel: false
		};
		await this.runExpressionAndLogAsync("onItemCloning", this.onItemCloning, itemCloningArgs);
		return !itemCloningArgs.cancel;
	}
	/**
	* Imports items at the end of the datasource.
	* @param items - Items to import.
	* @param markAsNew - A value indicating whether items should be mark as new.
	* @returns Value indicating whether the items have been added.
	*/
	importRangeAsync(items, markAsNew = false) {
		return this.addRangeAsync(items.map((item) => new this.creator(item, this)), markAsNew);
	}
	/**
	* Adds items at the end of the datasource.
	* @param items - Items to add.
	* @param markAsNew - A value indicating whether items should be mark as new.
	* @param markAsVirtual - A value indicating whether item should be mark as virtual once the item has been added.
	* @returns Value indicating whether the items have been added.
	*/
	async addRangeAsync(items, markAsNew = false, markAsVirtual = false) {
		this.throwWhenAddNotSupported();
		if (items.length === 0) return false;
		try {
			this._busy = true;
			items = items.map(reactive);
			const args = await Promise.all(items.map((i) => this.onAdding(i, markAsNew, markAsVirtual)));
			if (args.some((a) => a.cancel)) return false;
			await this.addItemsAsync(args);
			return true;
		} finally {
			this._busy = false;
		}
	}
	/**
	* Inserts items at the specified position.
	* @param position - Position.
	* @param items - Items to insert.
	* @param markAsNew - A value indicating whether items should be mark as new.
	* @param markAsVirtual - A value indicating whether items should be mark as virtual once the item has been added.
	* @returns Value indicating whether the items have been inserted.
	*/
	async insertRangeAsync(position, items, markAsNew = false, markAsVirtual = false) {
		this.throwWhenAddNotSupported();
		if (items.length === 0) return false;
		try {
			this._busy = true;
			items = items.map(reactive);
			const args = await Promise.all(items.map((i) => this.onAdding(i, markAsNew, markAsVirtual)));
			if (args.some((a) => a.cancel)) return false;
			await this.addItemsAsync(args, position);
			return true;
		} finally {
			this._busy = false;
		}
	}
	/**
	* Clones a item and adds it at the end of the datasource.
	* @param source - The item from which the new item will be cloned.
	* @returns A promise with the new item that is the result of the cloning operation.
	*/
	async cloneAsync(source) {
		this.throwWhenAddNotSupported();
		return this.cloneItemAsync(source, this.monoRecord);
	}
	async cloneItemAsync(source, replace) {
		try {
			this._busy = true;
			await source.awaitRunningAdditionalDataInvalidations();
			const target = reactive(source.clone());
			this.resetRelationKeys(target);
			if (!await this.onCloning(source, target)) return;
			this.currentId = void 0;
			if (replace) {
				this.creationMode = true;
				if (this.ownerFrame) this.ownerFrame.dataSavedCallback = void 0;
				this.datasource.setItem(this.datasource.indexOf(source), target);
			} else await this.addItemsAsync([{
				item: target,
				cancel: false,
				markAsVirtual: false,
				markAsNew: false
			}]);
			return target;
		} finally {
			this._busy = false;
		}
	}
	async addItemsAsync(args, position) {
		if (this.addNewItem && !args.some((a) => a.item === this.addNewItem)) await this.commitAddNewItem();
		if (position === void 0 || position > this.fullDatasource.length) position = this.fullDatasource.length;
		this.markAsNewOnAdd(args);
		const lastAddedItem = args[args.length - 1].item;
		if (!this._ignoreLastAddedItem) this.lastAddedItem = lastAddedItem;
		this.fullDatasource.splice(position, 0, ...args.map((a) => a.item));
		if (!this.loading) await this.setPositionOnLastAddedItemAsync(lastAddedItem);
		for (const a of args) {
			this.ownerFrame?.itemAddedCallback?.({ item: a.item });
			await this.onAdded(a.item);
		}
		if (!this.loading) await this.selectOnlyCurrentAsync();
		this.markAsVirtualOnAdd(args);
	}
	markAsNewOnAdd(args) {
		for (const a of args) if (a.markAsNew) a.item.$new = true;
	}
	markAsVirtualOnAdd(args) {
		for (const a of args) if (a.markAsVirtual) a.item.markAsVirtual();
	}
	async setPositionOnLastAddedItemAsync(item) {
		const lastItemIndex = this.datasource.indexOf(item);
		if (lastItemIndex !== -1) await this.requestChangingPositionAsync(lastItemIndex);
	}
	throwWhenAddNotSupported() {
		if (this.parentRelation) {
			if (this.parentViewModel && !this.parentViewModel.current) throw new Error(this.globalResourceManager.get("CannotAddDetailWithoutMaster"));
			if (this.parentRelation.type === RelationType.editableReference) throw new Error(stringFormat(this.globalResourceManager.get(`AddingNotSupportedOnEditableReference`), this.mainViewModel.uiViewName, this.parentRelation.name));
		}
	}
	/**
	* Creates a new item.
	* @returns the new created item.
	*/
	createNewItem() {
		const item = reactive(new this.creator(void 0, this));
		this.initializeRelationKeys(item);
		return item;
	}
	/**
	* Propagates the key values to the relations.
	*
	* @param item - Item that contains the keys to propagate.
	*/
	propagateKeyChangesInRelations(item) {
		for (const [relation, { type }] of item.$relations) {
			const subViewModel = this.getSubViewModel(relation);
			for (const subItem of item.getRelationList(relation)) {
				if (type === RelationType.collection) subViewModel.initializeRelationKeys(subItem, item);
				subViewModel.propagateKeyChangesInRelations(subItem);
			}
		}
	}
	/**
	* Adds a new item instance of the datasource.
	*
	* @returns A promise with the new instance added to the datasource.
	*/
	addNewAsync(markAsVirtual = false) {
		return this.behavior.datagridInfiniteScrolling && this.hasMoreData ? this.addNewAtTopAsync(markAsVirtual) : this.addNewAtBottomAsync(markAsVirtual);
	}
	async addNewAtTopAsync(markAsVirtual) {
		const item = this.createNewItem();
		if (!await this.insertAsync(0, item, markAsVirtual)) return;
		return this.datasource[0];
	}
	async addNewAtBottomAsync(markAsVirtual) {
		const item = this.createNewItem();
		if (!await this.addAsync(item, markAsVirtual)) return;
		return this.datasource.at(-1);
	}
	/**
	* Inserts a new item instance at the specified position.
	*
	* @param position - Position.
	* @returns A promise with the new instance inserted to the datasource.
	*/
	async insertNewAsync(position) {
		const item = this.createNewItem();
		if (!await this.insertAsync(position, item)) return;
		return this.datasource[Math.min(position, this.datasource.length - 1)];
	}
	/**
	* Adds the "add new item" at the end of the datasource.
	* @returns A promise with the "add new item".
	*/
	async addAddNewItem() {
		this.isAddingAddNewItem = true;
		this.addNewItem = await this.addNewAsync(true);
		this.isAddingAddNewItem = !!this.addNewItem;
		return this.addNewItem;
	}
	/**
	* Commits the "add new item".
	* @param cancelable - Value indicating whether the "add new item" can be cancelled.
	* @returns A promise with the value indicating whether the "add new item" has been committed.
	*/
	async commitAddNewItem(cancelable = true) {
		if (!this.addNewItem) return false;
		const hasChanges = this.addNewItem.hasChanges();
		if (!hasChanges && cancelable) await this.deleteDataAsync(this.addNewItem);
		const committed = hasChanges || cancelable;
		if (committed) this.resetAddNewItem();
		return committed;
	}
	/**
	* Restes the "add new item".
	*/
	resetAddNewItem() {
		this.addNewItem = void 0;
		this.isAddingAddNewItem = false;
	}
	/**
	* Removes the specified item from the datasource.
	*
	* @param item - item to remove.
	* @returns Value indicating whether the item has been removed.
	*/
	remove(item) {
		return remove(this.fullDatasource, item);
	}
	/**
	* Clears the datasource.
	*/
	clear(acceptChanges = false) {
		this.datasource.clear(acceptChanges);
		this.groups.clear();
		this.position = -1;
		this.pageNumber = 1;
	}
	/**
	* Triggered after current item changed.
	*/
	onCurrentChanged() {
		if (!this.current) {
			this.subViews.forEach((vm) => vm.onDatasourceListChanged({ type: ListChangedType.reset }));
			return;
		}
		this.subViews.forEach((vm) => vm.invalidateDatasource());
	}
	/**
	* Shares the datasource with another view model.
	* @param viewModel - View model that receives the shared datasource.
	*/
	shareDatasource(viewModel) {
		viewModel.dataSharingManager.sharedDatasource = true;
		viewModel.setDatasource(this._datasource);
		if (this._datasource.position === void 0) viewModel.setPosition(this.position, false);
		this.datasourceChangedEvent.add((datasource) => viewModel.setDatasource(datasource));
		this.positionChangedEvent.add((position) => viewModel.position = position);
		viewModel.positionChangedEvent.add((position) => this.position = position);
	}
	/**
	* Gets the editing UI view navigation options.
	*
	* @param item - Item.
	* @returns Navigation options.
	*/
	getEditingUIViewNavigationOptions(item) {
		if (!this.editingUIViewName) throw new Error(this.globalResourceManager.get("EditingUIViewNameIsUndefined"));
		return new NavigationOptions(this.editingUIViewName).withId(item.$key);
	}
	/**
	* Gets the adding UI view navigation options.
	*
	* @returns Navigation options.
	*/
	getAddingUIViewNavigationOptions() {
		if (!this.addingUIViewName) throw new Error(this.globalResourceManager.get("AddingUIViewNameIsUndefined"));
		const options = new NavigationOptions(this.addingUIViewName).withCreationMode(true);
		if (this.lookupContext?.templateMode === LookupTemplateMode.default) {
			options.onTarget(this.viewContext.get("neos-lookup-adding-ui-view-target")).withPopupSize(this.viewContext.get("neos-lookup-adding-ui-view-popup-size"));
			options.inheritViewContext = false;
			const savedCallback = this.viewContext.get("neos-lookup-adding-ui-view-saved-callback");
			options.dataSavedCallback = async (sender, result, monoRecordInstance) => {
				await savedCallback(sender, result, monoRecordInstance);
				await this.closeAsync(false, new NavigationResult(NavigationResultState.cancel));
			};
		} else {
			const relationKeys = this.getRelationKeys((r) => r.embeddedForWriting);
			if (relationKeys.size > 0) {
				const parentKey = /* @__PURE__ */ new Map();
				for (const [key, value] of relationKeys) {
					options.withParameter(key, value);
					parentKey.set(key, value);
				}
				options.withParameter("neos-parent-relation-key", parentKey);
			}
		}
		return options;
	}
	/**
	* Navigates to a view.
	* @param options - Navigation options.
	*/
	async navigateAsync(options) {
		options.callerRemote = this.getRemote()?.name;
		if (options.inheritViewContext) options.withNewParameters(options.parameters.clone(this.viewContext));
		const navigatingArgs = {
			cancel: false,
			options
		};
		if (!options.parameters.dontExecuteUIViewRules) {
			await this.runExpressionAndLogAsync("onNavigating", this.onNavigating, navigatingArgs);
			if (navigatingArgs.cancel) return;
		}
		navigate(this.eventManager, navigatingArgs.options);
	}
	/**
	* Closes the current view.
	* @param displayUnsavedChangesMessage - Display unsaved changes message
	* @param result - Navigation result
	*/
	async closeAsync(displayUnsavedChangesMessage = true, result) {
		const closingArgs = {
			cancel: false,
			result: result ?? new NavigationResult(),
			displayUnsavedChangesMessage
		};
		await this.runExpressionAndLogAsync("onClosing", this.onClosing, closingArgs);
		if (closingArgs.cancel) return false;
		if (!closingArgs.displayUnsavedChangesMessage || await this.checkAndSaveDataAsync()) {
			for (const [, childViewModel] of this.viewContainers) await childViewModel.closeAsync(false);
			this.eventManager.emit("viewModel:close", this);
			this.ownerFrame?.callback?.(closingArgs.result);
			return true;
		}
		return false;
	}
	async returnItemAsync(item) {
		return this.closeAsync(void 0, {
			state: NavigationResultState.ok,
			value: item
		});
	}
	async returnItemsAsync(items) {
		return this.closeAsync(void 0, {
			state: NavigationResultState.ok,
			value: items
		});
	}
	/**
	* Adds a sub view model.
	*
	* @param relationName - Relation name.
	* @param subViewModel - Sub view model.
	*/
	addSubViewModel(relationName, subViewModel) {
		subViewModel.parentRelation = {
			name: relationName,
			...this.creator.$relations.get(relationName)
		};
		this.addViewModel(subViewModel);
		this.subViews.add(relationName, subViewModel);
	}
	/**
	* Adds a nested view model.
	* @param name - Nested view model name.
	* @param nestedViewModel - Nested view model.
	*/
	addNestedViewModel(name, nestedViewModel) {
		this._nestedViewModels.set(name, nestedViewModel);
	}
	/**
	* Adds a view model.
	*
	* @param viewModel - View model.
	*/
	addViewModel(viewModel, setViewContext = true) {
		this.viewModels.push(viewModel);
		viewModel.parentViewModel = this;
		if (setViewContext) viewModel.viewContext = this.viewContext;
	}
	/**
	* Finds a sub view model by relation property name.
	* The search based on the relation property name is not case sensitive.
	*
	* @param relationPropertyName - Relation property name.
	*/
	findSubViewModel(relationPropertyName) {
		relationPropertyName = relationPropertyName.toUpperCase();
		return this.subViews.find((vm) => vm.parentRelation.name.toUpperCase() === relationPropertyName);
	}
	/**
	* Gets a sub view model by relation property name.
	* The search based on the relation property name is not case sensitive.
	*
	* @param relationPropertyName - Relation property name.
	*/
	getSubViewModel(relationPropertyName) {
		const subViewModel = this.findSubViewModel(relationPropertyName);
		if (!subViewModel) throw new Error(stringFormat(this.globalResourceManager.get("SubViewModelDoesNotExist"), relationPropertyName));
		return subViewModel;
	}
	/**
	* Gets a nested view model by name.
	*
	* @param name - Name.
	*/
	getNestedViewModel(name) {
		const nestedViewModel = this._nestedViewModels.get(name);
		if (!nestedViewModel) throw new Error(stringFormat(this.globalResourceManager.get("NestedViewModelDoesNotExist"), name));
		return nestedViewModel.value;
	}
	/**
	* Finds a view model by identifier.
	*
	* @param id - Identifier.
	*/
	findViewModelById(id) {
		const viewModel = this.viewModels.find((vm) => vm.id === id);
		if (viewModel) return viewModel;
		return Array.from(this._nestedViewModels.values()).map((vm) => vm.value).find((vm) => vm.id === id);
	}
	/**
	* Gets a view model by identifier.
	*
	* @param id - Identifier.
	*/
	getViewModelById(id) {
		const viewModel = this.findViewModelById(id);
		if (!viewModel) throw new Error(stringFormat(this.globalResourceManager.get("ViewModelWithIdDoesNotExist"), id));
		return viewModel;
	}
	/**
	* Removes a view model.
	* @param viewModel - View model.
	*/
	removeViewModel(viewModel) {
		return remove(this.viewModels, viewModel);
	}
	/**
	* Gets the keys of the relation in master-detail.
	*
	* @param embedded - A value indicating whether the relation is embedded or not.
	* @param item - Item. When the item is undefined, the item will be the current item.
	* @returns Map containing the property names and values constituting the relationship.
	*/
	getRelationKeys(embedded, item) {
		if (!this.parentRelation || !this.parentViewModel) return /* @__PURE__ */ new Map();
		item ??= this.parentViewModel.current;
		if (!item) return /* @__PURE__ */ new Map();
		const relation = item.$relations.get(this.parentRelation.name);
		if (relation?.referencePropertyName) return this.getRelationKeysFromReferenceProperty(embedded, item, relation.referencePropertyName);
		const associations = this.parentRelation.associations;
		if (associations.length === 0) return /* @__PURE__ */ new Map();
		let keyValues;
		if (relation?.referenceKeyProperties) keyValues = this.getKeyValuesFromReferenceKeyProperties(item, relation.referenceKeyProperties);
		else if (!item.$key || embedded(this.parentRelation)) keyValues = [...item.getKey().values()];
		else keyValues = item.getKeyValues();
		return new Map(keyValues.map((value, index) => [associations[index], value]));
	}
	getKeyValuesFromReferenceKeyProperties(item, referenceKeyProperties) {
		let keyValues = [];
		for (const referenceKeyPropertyName of referenceKeyProperties) {
			const keyValue = item[referenceKeyPropertyName];
			const referenceEntityViewScalarKeyProperties = this.parentViewModel?.getProperty(referenceKeyPropertyName).referenceEntityViewScalarKeyProperties;
			if (referenceEntityViewScalarKeyProperties) {
				const referenceKeyValues = referenceEntityViewScalarKeyProperties.map((name) => keyValue[name]);
				keyValues = keyValues.concat([...referenceKeyValues]);
			} else keyValues.push(keyValue);
		}
		return keyValues;
	}
	getRelationKeysFromReferenceProperty(embedded, item, referencePropertyName) {
		if (embedded(this.parentRelation)) return new Map([[referencePropertyName, item]]);
		const parentItemKey = item.$key ?? item.getKey();
		const result = /* @__PURE__ */ new Map();
		parentItemKey.forEach((value, key) => {
			result.set(`${referencePropertyName}.${key}`, value);
		});
		return result;
	}
	/**
	* Model instance whose relationship contains this element.
	* It is for example the instance of Order that contains an OrderDetails collection containing this element.
	* @returns Parent item or undefined whether this element does not belong to a relationship.
	*/
	getParentItem() {
		return this.datasource.owner;
	}
	/**
	* Initializes the key properties of a related item in master detail relation.
	*
	* @param item - Item to initialize.
	* @param parentItem - Parent item.
	*/
	initializeRelationKeys(item, parentItem) {
		let parentRelationKeys;
		if (this.parentRelation === void 0) parentRelationKeys = this.viewContext.get("neos-parent-relation-key");
		const relationKeys = parentRelationKeys ?? this.getRelationKeys((r) => r.embeddedForWriting, parentItem);
		for (const [key, value] of relationKeys) {
			if (!(key in this.properties)) continue;
			if (!item.isEquivalentValues(item[key], value, key)) {
				if (parentItem && !item.isVirtual) {
					item.suspendPropertyValueInvalidations(key);
					item[key] = value;
					item.resumePropertyValueInvalidations(key);
				} else {
					item.toRaw()[key] = value;
					item.invalidateValueProperty(key, false);
				}
				if (item.$key?.has(key) === true) {
					const property = getPropertyByPath(item, key);
					item.$key.set(key, property);
					item.setOriginalValue(key, property);
				}
			}
		}
	}
	/**
	* Resets the key properties of a related item in master detail relation.
	* @param item - Item to reset relation.
	* @param parentItem - Parent item.
	*/
	resetRelationKeys(item, parentItem) {
		for (const [relation] of item.$relations) {
			const subViewModel = this.getSubViewModel(relation);
			for (const subItem of item.getRelationList(relation)) subViewModel.resetRelationKeys(subItem, item);
		}
		this.resetKey(item.toRaw(), parentItem);
	}
	/**
	* Resets the key properties values of the specified item.
	* If item has a parent relation resets only the values of the parent keys.
	* @param item - Item to reset.
	* @param parentItem - Parent item.
	*/
	resetKey(item, parentItem) {
		if (item.$keyProperties.size === 0) return;
		let keyProperties = [];
		if (parentItem) keyProperties = this.getParentKeyProperties(item, Array.from(parentItem.$keyProperties));
		if (keyProperties.length === 0) keyProperties = Array.from(item.$keyProperties);
		for (const keyPropertyName of keyProperties) {
			const keyProperty = this.findProperty(keyPropertyName);
			if (keyProperty) keyProperty.resetValue(item);
		}
	}
	/**
	* Returns the parent key properties.
	* @param item - Item in the relation.
	* @param parentKey - Parent key of the relation.
	* @returns An array of key property names if parent key match; otherwise, an empty array.
	*/
	getParentKeyProperties(item, parentKey) {
		if (parentKey.length === 0) return [];
		const key = Array.from(item.$keyProperties);
		if (key.length === 0 || key.length < parentKey.length) return [];
		const keyProperties = [];
		for (let i = 0; i < parentKey.length; i++) {
			const keyName = key[i];
			const parentKeyName = parentKey[i];
			if (this.isPartOfPrimaryKey(item, keyName, i, parentKeyName)) keyProperties.push(keyName);
		}
		return keyProperties;
	}
	/**
	* Returns a value indicating whether the specified key is part of the primary key of the parent.
	* @param item - Item in the relation.
	* @param keyName - Key name to test.
	* @param order - Order into the primary key.
	* @param parentKeyName - Parent key name.
	* @returns true if the key is part of the primary key; otherwise, false.
	*/
	isPartOfPrimaryKey(item, keyName, order, parentKeyName) {
		let index = parentKeyName.lastIndexOf(".");
		if (index !== -1) parentKeyName = parentKeyName.substring(index + 1);
		index = keyName.lastIndexOf(".");
		if (index !== -1) keyName = keyName.substring(index + 1);
		if (parentKeyName === keyName) return true;
		const relationKeys = Array.from(this.getRelationKeys((r) => r.embeddedForWriting, item).keys());
		return order >= 0 && order < relationKeys.length && relationKeys[order] === keyName;
	}
	/**
	* Gets a value indicating whether the data can be loaded.
	*/
	canLoadData() {
		if (this.isSubViewModel && !this.isParentCurrentDefined || !this.uiViewPermitter.canRead(this.uiViewName)) return false;
		return true;
	}
	resetUnfulfilledPropertiesFiltering() {
		this.getPropertiesWithRequiredFilter().forEach((propRequiringFilter) => {
			propRequiringFilter.filterRequirementUnfulfilled = false;
		});
	}
	resolveUnfulfilledPropertiesFiltering() {
		const violations = [];
		this.getPropertiesWithRequiredFilter().forEach((propRequiringFilter) => {
			let matchesAtLeastOneFilterPath = false;
			propRequiringFilter.getFilterPaths().forEach((pathForPropRequiringFilter) => {
				if (this.dataFilter.find(pathForPropRequiringFilter)) matchesAtLeastOneFilterPath = true;
			});
			if (!matchesAtLeastOneFilterPath) {
				violations.push(propRequiringFilter.caption);
				propRequiringFilter.filterRequirementUnfulfilled = true;
			}
		});
		return violations;
	}
	async showMessageAboutUnfulfilledPropertiesFilteringAsync(propCaptions) {
		const resourceStringName = propCaptions.length > 1 ? "UnfulfilledPropertyFiltrations" : "UnfulfilledPropertyFiltration";
		const messageTitle = this.globalResourceManager.get(`${resourceStringName}_Title`);
		const messageText = stringFormat(this.globalResourceManager.get(`${resourceStringName}_Text`), propCaptions.join(", "));
		await this.showMessageAsync(ViewModelMessageType.danger, messageTitle, messageText, new ViewModelMessageButton(ViewModelMessageButtonType.ok));
	}
	async verifyPresenceOfRequiredFiltersOrShowMessageAsync() {
		this.resetUnfulfilledPropertiesFiltering();
		const propCaptions = this.resolveUnfulfilledPropertiesFiltering();
		if (propCaptions.length > 0) {
			await this.showMessageAboutUnfulfilledPropertiesFilteringAsync(propCaptions);
			return false;
		}
		return true;
	}
	/**
	* Loads data from the server via an API call.
	* We need to keep the datasource reference because it can change during the loading process.
	* The current parent item can change for example on a subview model on a reference because we don't wait for the loading to finish.
	*/
	async loadDataAsync(top = this.recordsByPage, datasource = this.datasource) {
		if (this.parentRelation?.embeddedForWriting) top = 2147483647;
		datasource.loadingState = ListLoadingState.loading;
		if (!this.canLoadData()) {
			datasource.loadingState = ListLoadingState.notLoaded;
			return;
		}
		this.clearAutoSuggestIndicators();
		try {
			if (await this.tryLoadServerGroups(datasource)) return;
			this.groups.clear();
			if (await this.callOnRetrieving()) {
				datasource.loadingState = ListLoadingState.loaded;
				return;
			}
			if (!await this.verifyPresenceOfRequiredFiltersOrShowMessageAsync()) {
				datasource.loadingState = ListLoadingState.notLoaded;
				return;
			}
			const selectionState = this.saveSelectionStateBeforeLoad(datasource);
			this.loadTotalSummaries(datasource, SummaryLoadMode.server);
			const success = this.currentId ? await this.loadCurrentItemAsync(datasource, this.current?.$key ?? this.currentId) : await this.loadItemsAsync(datasource, top);
			await this.restoreSelectionStateAfterLoad(datasource, selectionState);
			await this.callOnRetrieved(datasource);
			this.tryLoadClientGroups(datasource);
			datasource.loadingState = success ? ListLoadingState.loaded : ListLoadingState.notLoaded;
		} catch (error) {
			datasource.loadingState = ListLoadingState.notLoaded;
			throw error;
		}
	}
	/**
	* Ensures that the data is loaded.
	* If the data is not loaded, it loads the data.
	* If loading is in progress, it doesn't wait for the loading to finish.
	*/
	async ensureDataLoadedAsync(datasource = this.datasource) {
		if (datasource.loadingState !== ListLoadingState.notLoaded) return;
		if (this.parentRelation && (this.parentRelation.type === RelationType.editableReference || this.parentRelation.type === RelationType.reference) && !this.currentId) return;
		await this.loadDataAsync(void 0, datasource);
	}
	/**
	* Loads more data.
	*/
	async loadMoreData(top = this.recordsByPage, datasource = this.datasource) {
		if (this.loading || !this.hasMoreData) return;
		const hasGroupDescriptions = this.groupDescriptions.length > 0;
		if (hasGroupDescriptions && this.groupingBehavior.executionSide === GroupingExecutionSide.client) return;
		const previousLoadingState = datasource.loadingState;
		try {
			datasource.loadingState = ListLoadingState.loading;
			let success;
			if (hasGroupDescriptions) success = await this.loadServerGroups(datasource, top, false);
			else {
				if (await this.callOnRetrieving()) {
					datasource.loadingState = ListLoadingState.loaded;
					return;
				}
				const skip = datasource.getPersistedItemsCount();
				success = await this.loadItemsAsync(datasource, top, skip, false);
				await this.callOnRetrieved(datasource.slice(skip, skip + top));
			}
			datasource.loadingState = success ? ListLoadingState.loaded : previousLoadingState;
		} catch {
			datasource.loadingState = previousLoadingState;
		}
	}
	/**
	* Reloads the specified items from the server.
	* @param items Items to reload.
	*/
	async reloadRangeDataAsync(items) {
		if (items.length > 0) await this.reloadDataAsync(items, this.datasource);
		else throw new Error(this.globalResourceManager.get("ReloadRangeDataItemsRequired"));
	}
	async callOnRetrieving() {
		const retrievingArgs = { cancel: false };
		await this.runExpressionAndLogAsync("onRetrieving", this.onRetrieving, retrievingArgs);
		if (retrievingArgs.cancel || !await this.checkSortAsync()) return true;
		return false;
	}
	async callOnRetrieved(items) {
		await this.runExpressionAndLogAsync("onRetrieved", this.onRetrieved, { items });
	}
	async runReferenceRetrievingRuleAsync(propertyName, referenceRetrievingArgs) {
		await this.runExpressionAndLogAsync(`referenceRetrieving[${propertyName}]`, this.referenceRetrievingEventRules[propertyName.replaceAll(".", "_")], referenceRetrievingArgs);
		return referenceRetrievingArgs;
	}
	async runReferenceSelectedRuleAsync(propertyName, referenceSelectedArgs) {
		await this.runExpressionAndLogAsync(`referenceSelected[${propertyName}]`, this.referenceSelectedEventRules[propertyName.replaceAll(".", "_")], referenceSelectedArgs);
		return referenceSelectedArgs;
	}
	async loadReferenceDataAsync(options) {
		const filterReference = new Filter();
		if (options.filter && !options.filter.isEmpty) filterReference.add(options.filter, FilterLogicalOperator.and);
		const referenceRetrievingArgs = await this.runReferenceRetrievingRuleAsync(options.property.camelCaseName, {
			item: options.item,
			trigger: options.trigger,
			inputValue: options.inputValue,
			filter: new Filter(),
			sorts: [],
			entityViewParameters: {}
		});
		if (!referenceRetrievingArgs.filter.isEmpty) filterReference.add(referenceRetrievingArgs.filter, FilterLogicalOperator.and);
		return options.apiClient.getAllAsync({
			path: options.path,
			filter: filterReference.toODataString(this),
			includeTotalCount: options.includeTotalCount,
			signal: options.signal,
			sorts: [...referenceRetrievingArgs.sorts, ...options.sorts],
			pageNumber: options.pageNumber,
			recordsByPage: options.recordsByPage,
			queryOrRouteData: referenceRetrievingArgs.entityViewParameters
		});
	}
	async checkAndLoadDataAsync() {
		if (await this.checkAndSaveDataAsync()) {
			this.pageNumber = 1;
			await this.loadDataAsync();
		}
	}
	/**
	* Refreshes groups.
	*/
	async refreshGroupsAsync() {
		this.clearSort();
		if (this.groupingBehavior.executionSide === GroupingExecutionSide.client) {
			this.groups.clear();
			this.tryLoadClientGroups(this.datasource);
		} else {
			this.clear(true);
			await this.loadDataAsync();
		}
	}
	/**
	* Checks if the view model has changes and shows a message to save if necessary
	*/
	async checkAndSaveDataAsync() {
		if (this.hasChanges) {
			if (__NEOS_STUDIO_VSCODE__) {
				postMessageToVsCode({ command: "confirmClose" });
				return true;
			}
			switch ((await this.showMessageAsync(ViewModelMessageType.warning, this.globalResourceManager.get("HasChangesWarningTitle"), this.globalResourceManager.get("HasChangesWarningText"), new ViewModelMessageButton(ViewModelMessageButtonType.yes, void 0, this.globalResourceManager.get("HasChangesYesText"), "save", true), new ViewModelMessageButton(ViewModelMessageButtonType.no, void 0, this.globalResourceManager.get("HasChangesNoText"), "unsave"), new ViewModelMessageButton(ViewModelMessageButtonType.cancel, void 0, this.globalResourceManager.get("HasChangesCancelText"), "close"))).buttonType) {
				case ViewModelMessageButtonType.yes: {
					if (!await this.saveDataAsync()) return false;
					const childViewModels = this.viewModels.filter((vm) => !vm.parentRelation && !vm.dataSharingManager.sharedDatasource).concat([...this.viewContainers.values()].filter((vm) => !vm.dataSharingManager.sharedDatasource));
					if (childViewModels.length > 0) return (await Promise.all(childViewModels.map((vm) => vm.saveDataAsync()))).every(Boolean);
					return true;
				}
				case ViewModelMessageButtonType.cancel: return false;
			}
		}
		return true;
	}
	/**
	* Gets the initial filter.
	*/
	getInitialFilter(datasource) {
		const filter = this.dataFilter.toSimplifiedFilter();
		const relationKeys = this.getRelationKeys((r) => r.embeddedForReading, datasource.owner);
		if (relationKeys.size > 0) for (const [key, value] of relationKeys) filter.add(Filter.create(key, FilterOperator.equal, value), FilterLogicalOperator.and);
		return filter;
	}
	async createLoadAllFilterAsync(datasource) {
		const filter = this.getInitialFilter(datasource);
		if (this.lookupContext?.property) {
			const referenceRetrievingArgs = await this.lookupContext.viewModel.runReferenceRetrievingRuleAsync(this.lookupContext.property.camelCaseName, {
				item: this.lookupContext.item ?? this.lookupContext.viewModel.current,
				filter: new Filter(),
				sorts: this.getServerDataSorts(),
				entityViewParameters: this.entityViewParameters.getValues(),
				inputValue: this.viewContext.get("neos-lookup-input"),
				trigger: ReferenceRetrievingTrigger.lookupUIView
			});
			if (!referenceRetrievingArgs.filter.isEmpty) filter.add(referenceRetrievingArgs.filter, FilterLogicalOperator.and);
			this.entityViewParameters.setValues(referenceRetrievingArgs.entityViewParameters);
		}
		return filter;
	}
	async loadItemsAsync(datasource, top, skip = (this.pageNumber - 1) * this.recordsByPage, replace = true) {
		if (!this.entityViewName) return false;
		const filter = await this.createLoadAllFilterAsync(datasource);
		const response = await this.fetchItemsAsync({
			top,
			skip,
			filter: filter.toODataString(this),
			prompt: this.filterPrompt
		});
		if (!response.succeeded) {
			if (replace) this.clear(true);
			return false;
		}
		const items = response.data.map((item) => reactive(new this.creator(item, this)));
		if (replace) datasource.setItems(items, response.hasMoreData, response.totalRecordCount);
		else datasource.appendItems(items, response.hasMoreData, response.totalRecordCount);
		return true;
	}
	async loadCurrentItemAsync(datasource, key) {
		if (!this.entityViewName) return false;
		const response = await this.fetchItemAsync(key);
		if (!response.succeeded) {
			this.showApiError(response.error, ApiErrorRequestType.Load);
			this.clear(true);
			return false;
		}
		const item = reactive(new this.creator(response.data, this));
		datasource.setItems([item]);
		return true;
	}
	async reloadDataAsync(items, datasource) {
		if (this.loadingMeOrSomeParent || this.isEmbeddedSubViewModelForReading) return;
		if (!this.canLoadData() || !this.entityViewName) return;
		const targets = this.getReloadTargets(items, datasource);
		if (targets.length === 0) return;
		if (await this.callOnRetrieving()) return;
		const previousLoadingState = datasource.loadingState;
		datasource.loadingState = ListLoadingState.loading;
		try {
			if (this.currentId ? await this.reloadCurrentItemAsync(datasource, targets[0]) : await this.reloadItemsAsync(datasource, targets)) {
				this.loadTotalSummaries(datasource, SummaryLoadMode.auto);
				this.tryLoadClientGroups(datasource);
				datasource.loadingState = ListLoadingState.loaded;
			} else datasource.loadingState = previousLoadingState;
		} catch (error) {
			datasource.loadingState = previousLoadingState;
			throw error;
		}
	}
	async reloadCurrentItemAsync(datasource, target) {
		const response = await this.fetchItemAsync(target.key);
		let item;
		if (response.succeeded) item = this.restoreItem(response.data, target.item);
		else if (response.error.status === 404 || response.error.instance === "/not-found") item = void 0;
		else {
			this.showApiError(response.error, ApiErrorRequestType.Load);
			return false;
		}
		datasource.applyReloadChanges([{
			index: target.index,
			item
		}]);
		await this.callOnRetrieved(item ? [item] : []);
		return true;
	}
	async reloadItemsAsync(datasource, targets) {
		const filter = await this.createLoadAllFilterAsync(datasource);
		filter.add(new Filter(null, FilterLogicalOperator.or, ...targets.map((target) => new Filter(null, FilterLogicalOperator.and, ...Array.from(target.key.entries(), ([propertyName, value]) => Filter.create(propertyName, FilterOperator.equal, value))))));
		const response = await this.fetchItemsAsync({
			top: targets.length,
			filter: filter.toODataString(this),
			prompt: this.filterPrompt
		}, false);
		if (!response.succeeded) {
			this.showApiError(response.error, ApiErrorRequestType.Load);
			return false;
		}
		const reloadedItems = this.applyReloadedItems(datasource, targets, response.data);
		await this.callOnRetrieved(reloadedItems);
		return true;
	}
	getReloadTargets(items, datasource) {
		const targets = [];
		const targetKeys = /* @__PURE__ */ new Set();
		for (const item of items) {
			const key = item.$key;
			if (!key) continue;
			const index = datasource.indexOf(item);
			if (index === -1) continue;
			const keyId = this.getReloadKeyId(key);
			if (targetKeys.has(keyId)) continue;
			targetKeys.add(keyId);
			targets.push({
				item,
				index,
				key,
				keyId
			});
		}
		return targets;
	}
	applyReloadedItems(datasource, targets, reloadedItemsData) {
		const targetsByKey = new Map(targets.map((target) => [target.keyId, target]));
		const itemsByKey = /* @__PURE__ */ new Map();
		for (const data of reloadedItemsData) {
			const key = this.creator.getKeyFromData(data);
			const keyId = this.getReloadKeyId(key);
			const target = targetsByKey.get(keyId);
			if (!target) continue;
			itemsByKey.set(keyId, this.restoreItem(data, target.item));
		}
		const changes = [];
		const appliedItems = [];
		for (const target of targets) {
			const reloadedItem = itemsByKey.get(target.keyId);
			changes.push({
				index: target.index,
				item: reloadedItem
			});
			if (reloadedItem) appliedItems.push(reloadedItem);
		}
		datasource.applyReloadChanges(changes);
		return appliedItems;
	}
	getReloadKeyId(key) {
		return JSON.stringify(Array.from(key.entries()));
	}
	async tryLoadServerGroups(datasource) {
		if (this.groupDescriptions.length > 0 && this.groupingBehavior.executionSide === GroupingExecutionSide.server) {
			if (await this.loadServerGroups(datasource)) datasource.loadingState = ListLoadingState.loaded;
			else datasource.loadingState = ListLoadingState.notLoaded;
			return true;
		}
		return false;
	}
	/**
	* Loads groups.
	* @param datasource Observable list to load groups into.
	* @param [top] Number of groups to load.
	* @param [replace] Indicates whether already loaded groups must be replace.
	* @returns true when loading succeeds; otherwise false.
	*/
	async loadServerGroups(datasource, top = this.recordsByPage, replace = true) {
		if (await this.callOnRetrieving()) return true;
		if (!this.entityViewName) return false;
		const groupDescription = this.getGroupDescription(0);
		const summaryDescriptions = this.getGroupSummaryDescriptions();
		this.loadTotalSummaries(datasource, SummaryLoadMode.server);
		const response = await this.fetchServerGroups({
			filter: this.getInitialFilter(datasource),
			groupDescriptions: [groupDescription],
			summaryDescriptions,
			top,
			skip: replace ? void 0 : this.groups.length,
			sorts: this.getServerDataSorts().filter((sort) => sort.propertyName === groupDescription.property.camelCaseName)
		});
		if (!response.succeeded) {
			if (replace) this.clear(true);
			return false;
		}
		if (this.groupingBehavior.expandGroupsOnLoad) this.showError(new ViewModelError(this.globalResourceManager.get("TechnicalErrorHasOccurred"), this.globalResourceManager.get("CannotExpandServerGroupsOnLoad")));
		const groups = response.data.map((group) => ServerGroup.fromServer(this, groupDescription, group, summaryDescriptions));
		if (replace) {
			datasource.clear(true);
			this.groups.setItems(groups, response.hasMoreData, response.totalRecordCount);
		} else this.groups.appendItems(groups, response.hasMoreData, response.totalRecordCount);
		return true;
	}
	tryLoadClientGroups(datasource) {
		if (this.groupDescriptions.length > 0 && this.groupingBehavior.executionSide === GroupingExecutionSide.client) {
			const groups = this.loadClientGroups(datasource, this.getGroupDescription(0), this.getGroupSummaryDescriptions());
			this.groups.setItems(groups, false, groups.length);
			return true;
		}
		return false;
	}
	loadClientGroups(items, groupDescription, groupSummaryDescriptions) {
		const clientGroupByPaths = groupDescription.getClientGroupByPaths();
		const nextGroupDescription = groupDescription.isLastLevel ? void 0 : this.getGroupDescription(groupDescription.level + 1);
		return groupBy(items, (i) => {
			const key = {};
			for (const groupByPath of clientGroupByPaths) key[groupByPath] = getPropertyByPath(i, groupByPath);
			return key;
		}, (i) => i, (k1, k2) => clientGroupByPaths.every((groupByPath) => isEquivalentValues(k1[groupByPath], k2[groupByPath]))).map((groupedList) => {
			const group = new Group(groupDescription, groupedList.key, SummaryDescription.calculate(groupSummaryDescriptions, groupedList.values, false));
			if (this.groupingBehavior.expandGroupsOnLoad) group.expanded = true;
			if (nextGroupDescription) group.groups = this.loadClientGroups(groupedList.values, nextGroupDescription, groupSummaryDescriptions);
			else group.items = groupedList.values;
			return group;
		});
	}
	getGroupDescription(level) {
		return new GroupDescription(this.getProperty(this.groupDescriptions[level]), level, level === this.groupDescriptions.length - 1);
	}
	async checkSortAsync() {
		const notServerSideSortableProperties = [];
		for (const dataSort of this.dataSorts) {
			const property = this.getProperty(dataSort.propertyName);
			if (!property.serverSideSortable) notServerSideSortableProperties.push(property);
		}
		if (notServerSideSortableProperties.length > 0) {
			if ((await this.showMessageAsync(ViewModelMessageType.warning, this.globalResourceManager.get("NotServerSideSortablePropertyWarningTitle"), stringFormat(this.globalResourceManager.get("NotServerSideSortablePropertyWarningText"), notServerSideSortableProperties.map((p) => p.name).join(", ")), new ViewModelMessageButton(ViewModelMessageButtonType.ok), new ViewModelMessageButton(ViewModelMessageButtonType.cancel))).buttonType === ViewModelMessageButtonType.cancel) return false;
			for (const property of notServerSideSortableProperties) this.removeSort(property.name);
		}
		return true;
	}
	async fetchItemsAsync(request, showApiError = true) {
		this.logger.debug(`GET ALL - ${this.entityViewName}`);
		const response = await this.apiClient.getAllAsync({
			path: this.entityViewName,
			sorts: request.sorts ?? this.getServerDataSorts(),
			queryOrRouteData: this.entityViewParameters.getValues(),
			...request
		});
		if (showApiError && !response.succeeded) this.showApiError(response.error, ApiErrorRequestType.Load);
		return response;
	}
	async fetchItemAsync(key) {
		const id = FetchApiClient.getId(Array.from(key.values()));
		this.logger.debug(`GET - ${this.entityViewName}/${id}`);
		return this.apiClient.getAsync({
			path: this.entityViewName,
			id
		});
	}
	/**
	* Accepts all the changes made to the datasource since the data was loaded or since the last time acceptChanges() was called.
	*/
	acceptChanges() {
		this.datasource.acceptChanges();
	}
	/**
	* Rejects all the changes made to the datasource since the data was loaded or since the last time acceptChanges() was called.
	*/
	rejectChanges() {
		this.datasource.rejectChanges();
	}
	/**
	* Forces this instance to be marked as modified, regardless of whether any of its properties have actually changed.
	*/
	markAsChanged() {
		this._forceModified = true;
	}
	/**
	* Cancels this instance to be marked as modified.
	*/
	unmarkAsChanged() {
		this._forceModified = false;
	}
	/**
	* Persists the changes of dataSource on the server via post or put calls to the API.
	*
	* @param datasource - Optional datasource, if not provides use internal datasource.
	* @returns - A value indicating whether the save operation has succeeded.
	*/
	async saveDataAsync(datasource = this.datasource) {
		try {
			this._busy = true;
			this._saving = true;
			if (this.isMain) this.waitingOverlayMessage = this.globalResourceManager.get("SaveWaitingMessage");
			await this.flushPendingEventHandlers();
			const dataSavingArgs = { cancel: false };
			await this.runExpressionAndLogAsync("onDataSaving", this.onDataSaving, dataSavingArgs);
			if (dataSavingArgs.cancel) return false;
			for (const item of datasource) if (item.hasChanges(true)) this.propagateKeyChangesInRelations(item);
			this.resetPersistanceErrors(datasource);
			if (!await this.validateDataAsync(false, datasource, true)) return false;
			this.saveSubViewModelsStateBeforeSave();
			const { createdItems, modifiedItems } = await this.saveItemsAsync(datasource);
			await this.restoreSubViewModelsStateAfterSave();
			const result = {
				hasSucceeded: !this.hasPersistenceErrors,
				createdItems,
				modifiedItems
			};
			await this.runExpressionAndLogAsync("onDataSaved", this.onDataSaved, result);
			if (!this.hasPersistenceErrors) this.clearAutoSuggestIndicators();
			await this.ownerFrame?.dataSavedCallback?.(this, result, this.monoRecord ? this.current : void 0);
			return !this.showPersistenceErrors(datasource);
		} finally {
			this._saving = false;
			this._busy = false;
			if (this.isMain) this.waitingOverlayMessage = void 0;
		}
	}
	resetPersistanceErrors(datasource) {
		for (const item of datasource) {
			item.$persistenceError = void 0;
			for (const [relation] of item.$relations) {
				const subViewModel = this.getSubViewModel(relation);
				const relationList = item.getRelationList(relation);
				subViewModel.resetPersistanceErrors(relationList);
			}
		}
	}
	/**
	* Validates data changes.
	*
	* @param validateUnchangedItems - A value indicating whether to validate unchanged items.
	* @param datasource - Optional datasource, if not provides use internal datasource.
	* @param excludeNonEmbeddedRelations - A value indicating whether to exclude non-embedded relations from the check.
	* @returns - A value indicating whether the data is valid.
	*/
	async validateDataAsync(validateUnchangedItems = false, datasource = this.datasource, excludeNonEmbeddedRelations = false) {
		const uploadErrors = this.getUploadErrors(excludeNonEmbeddedRelations, datasource);
		if (uploadErrors.length > 0) {
			this.showError({
				title: this.globalResourceManager.get("ValidationError"),
				text: this.formatValidationMessages(uploadErrors)
			});
			this.validatedEvent.raise(false);
			return false;
		}
		const dataLoadingErrors = this.getDataLoadingErrors(datasource);
		if (dataLoadingErrors.length > 0) {
			this.showError({
				title: this.globalResourceManager.get("ValidationError"),
				text: this.formatValidationMessages(dataLoadingErrors)
			});
			this.validatedEvent.raise(false);
			return false;
		}
		await this.validateAsync(ValidationRuleValidationTypes.reportErrors, validateUnchangedItems, datasource, excludeNonEmbeddedRelations);
		const validationErrors = this.getValidationMessages(ValidationRuleSeverityLevel.error, excludeNonEmbeddedRelations, datasource);
		if (validationErrors.length > 0) {
			this.displayValidationErrors(validationErrors);
			this.validatedEvent.raise(false);
			return false;
		}
		await this.validateAsync(ValidationRuleValidationTypes.reportWarnings, validateUnchangedItems, datasource, excludeNonEmbeddedRelations);
		const validationWarnings = this.getValidationMessages(ValidationRuleSeverityLevel.warning, excludeNonEmbeddedRelations, datasource);
		if (validationWarnings.length > 0) {
			if ((await this.displayValidationWarningsAsync(validationWarnings)).buttonType === ViewModelMessageButtonType.no) {
				this.validatedEvent.raise(false);
				return false;
			}
		}
		await this.validateAsync(ValidationRuleValidationTypes.reportInformations, validateUnchangedItems, datasource, excludeNonEmbeddedRelations);
		const validationInformations = this.getValidationMessages(ValidationRuleSeverityLevel.information, excludeNonEmbeddedRelations, datasource);
		if (validationInformations.length > 0) await this.displayValidationInformationsAsync(validationInformations);
		this.validatedEvent.raise(true);
		return true;
	}
	/**
	* Updates the current id.
	* @param key The new id
	*/
	updateCurrentId(key) {
		if (this.creationMode || this.currentId) {
			this.currentId = key;
			this.creationMode = false;
		}
	}
	displayValidationErrors(validationMessages) {
		this.showError({
			title: this.globalResourceManager.get("ValidationError"),
			text: this.formatValidationMessages(validationMessages)
		});
	}
	async displayValidationWarningsAsync(validationMessages) {
		return this.showMessageAsync(ViewModelMessageType.warning, this.globalResourceManager.get("ValidationWarning"), `${this.formatValidationMessages(validationMessages)}\n${this.globalResourceManager.get("ValidationWarningText")}`, new ViewModelMessageButton(ViewModelMessageButtonType.yes, void 0, void 0, "validate"), new ViewModelMessageButton(ViewModelMessageButtonType.no, void 0, void 0, "close", true));
	}
	async displayValidationInformationsAsync(validationMessages) {
		this.showMessageAsync(ViewModelMessageType.info, this.globalResourceManager.get("ValidationInformation"), this.formatValidationMessages(validationMessages));
	}
	async saveItemsAsync(datasource) {
		const createdItems = [];
		const modifiedItems = [];
		await this.saveDeletionsAsync(datasource);
		for (const item of datasource) {
			if (!item.hasChanges(true)) {
				await this.saveRelations(item);
				continue;
			}
			let currentChanged = false;
			const newItem = await this.saveItemAsync(item);
			if (newItem) {
				this.updateCurrentId(newItem.$key);
				const index = datasource.indexOf(item);
				datasource.setItem(index, newItem);
				if (item.$new) createdItems.push(newItem);
				else modifiedItems.push(newItem);
				if (index === this.position) currentChanged = true;
				this.propagateKeyChangesInRelations(newItem);
				await this.saveRelations(newItem);
			}
			if (!currentChanged) this.onCurrentChanged();
		}
		return {
			createdItems,
			modifiedItems
		};
	}
	/**
	* Saves the pending deletions of a data source.
	* @param datasource Data source.
	*/
	async saveDeletionsAsync(datasource) {
		if (!this.parentRelation?.embeddedForWriting) {
			for (const { item } of datasource.deletedItems) await this.callDeleteApiAsync(item);
			datasource.acceptDeletions();
		}
	}
	/**
	* Persists an item on the server via post or put calls to the API.
	*
	* @param item - item to persist.
	* @returns persisted item.
	*/
	async saveItemAsync(item) {
		if (!this.entityViewName) return;
		let response;
		const data = item.toApiRequestData(true);
		if (item.$new) response = await this.apiClient.postAsync({
			path: this.entityViewName,
			data
		});
		else response = await this.apiClient.putAsync({
			path: this.entityViewName,
			id: FetchApiClient.getId(item.getKeyValues()),
			data
		});
		if (!response.succeeded) {
			item.$persistenceError = response.error;
			return;
		}
		return this.restoreItem(response.data, item);
	}
	restoreItem(data, originalItem) {
		data.$selected = originalItem.$selected;
		data.$relationSelectionStates = originalItem.$relationSelectionStates;
		for (const uiProperty of this.properties.filter((p) => !p.fromWebAPI && !p.isComputed)) data[uiProperty.camelCaseName] = uiProperty.getValue(originalItem);
		const newItem = new this.creator(data, this);
		this.restoreRelations(newItem, originalItem);
		return reactive(newItem);
	}
	restoreRelations(item, oldItem) {
		for (const [relation, { type, embeddedForWriting }] of item.$relations) {
			if (!embeddedForWriting) {
				item.getRelationList(relation).setItems(oldItem.getRelationList(relation));
				continue;
			}
			const itemProperty = item[relation];
			const oldItemProperty = oldItem[relation];
			if (type === RelationType.editableReference && itemProperty instanceof Model && oldItemProperty instanceof Model) this.restoreRelations(itemProperty, oldItemProperty);
			if (type === RelationType.collection && Array.isArray(itemProperty) && Array.isArray(oldItemProperty)) this.restoreEmbeddedCollection(itemProperty, oldItemProperty);
		}
	}
	restoreEmbeddedCollection(items, oldItems) {
		for (const item of items) if (item instanceof Model && item.$transientKey) {
			const oldItem = oldItems.find((i) => i.$transientKey === item.$transientKey);
			if (oldItem) this.restoreRelations(item, oldItem);
		}
	}
	async saveRelations(item) {
		for (const [relation, { embeddedForWriting }] of item.$relations) {
			const subViewModel = this.getSubViewModel(relation);
			const relationList = item.getRelationList(relation);
			if (embeddedForWriting) {
				relationList.acceptDeletions();
				for (const subItem of relationList.filter((i) => i.hasChanges())) await subViewModel.saveRelations(subItem);
			} else if (subViewModel.canNeverExecuteSave) relationList.forEach((i) => i.acceptChanges());
			else await subViewModel.saveDataAsync(relationList);
		}
	}
	/**
	* Removes the specified item from the datasource.
	*
	* @param item - Item to remove.
	* @returns Value indicating whether the item has been removed.
	*/
	async deleteDataAsync(item) {
		return this.deleteRangeDataAsync([item]);
	}
	/**
	* Removes the specified items from the datasource.
	*
	* @param items - Items to remove.
	* @returns Value indicating whether the items have been removed.
	*/
	async deleteRangeDataAsync(items) {
		try {
			if (this.parentRelation?.type === RelationType.editableReference) throw new Error(stringFormat(this.globalResourceManager.get("DeletionNotSupportedOnEditableReference"), this.mainViewModel.uiViewName, this.parentRelation.name));
			this._busy = true;
			const removingPromises = [];
			for (const item of items) {
				const itemRemovingArgs = {
					item,
					cancel: false,
					preventDefault: false
				};
				removingPromises.push({
					action: this.runExpressionAndLogAsync("onItemRemoving", this.onItemRemoving, itemRemovingArgs),
					args: itemRemovingArgs
				});
			}
			await Promise.all(removingPromises.map((r) => r.action));
			if (removingPromises.some((r) => r.args.cancel)) return false;
			if (!await this.removeItemsAsync(removingPromises.map((r) => r.args))) return false;
			if (this.position > this.datasource.length - 1) await this.requestChangingPositionAsync(this.datasource.length - 1);
			else this.setPosition(this.position);
			await this.selectOnlyCurrentAsync();
			return true;
		} finally {
			this._busy = false;
		}
	}
	async removeItemsAsync(itemRemovingArgs) {
		for (const { item } of itemRemovingArgs.filter((i) => !i.preventDefault)) {
			if (this.isMain) {
				if (!await this.callDeleteApiAsync(item)) return false;
				this.remove(item);
				this.datasource.acceptDeletion(item);
			} else this.remove(item);
			await this.runExpressionAndLogAsync("onItemRemoved", this.onItemRemoved, { item });
		}
		return true;
	}
	async callDeleteApiAsync(item) {
		if (item.$new || this.entityViewName == null) return true;
		const id = FetchApiClient.getId(item.getKeyValues());
		const path = this.deletionEntityViewName ?? this.entityViewName;
		const deleteResponse = await this.apiClient.deleteAsync({
			path,
			id
		});
		if (deleteResponse.succeeded) return true;
		if (this.deletionMode === 1 && deleteResponse.error.instance === "/foreign-key-conflict") {
			const logicalDeletionData = item.toApiRequestData(true);
			logicalDeletionData[this.statusPropertyName] = this.inactiveStatusValue;
			const logicalDeletionResponse = await this.apiClient.putAsync({
				path,
				id,
				data: logicalDeletionData
			});
			return this.checkPutResponse(logicalDeletionResponse, logicalDeletionData);
		}
		this.showApiError(deleteResponse.error, ApiErrorRequestType.Delete);
		return false;
	}
	/**
	* Checks the response of a PUT request and displays an error message if needed.
	* @param response Response from the API.
	* @param data Data sent in the PUT request.
	* @returns `true` when the PUT request has succeeded or the reload fail was expected , `false` otherwise.
	*/
	checkPutResponse(response, data) {
		if (response.succeeded) return true;
		const entityViewWithLogicalDeletionSupport = this.statusPropertyName != null;
		const saveSuccessWithFailedReload = response.error.instance === "/not-found" && response.error.extensions.get("saveSuccess") === true;
		if (entityViewWithLogicalDeletionSupport && saveSuccessWithFailedReload && this.statusFilterMode === 1 && data[this.statusPropertyName] !== this.activeStatusValue) return true;
		this.showApiError(response.error, ApiErrorRequestType.Save);
		return false;
	}
	/**
	* Gets the server data sorts.
	*/
	getServerDataSorts() {
		return this.dataSorts.map((s) => new Sort(s.propertyName, s.direction, this.findProperty(s.propertyName)?.getSortPath(true) ?? s.propertyPath));
	}
	/**
	* Changes sort.
	*
	* @param propertyName - Name of property.
	* @param direction - Sort direction.
	*
	* MAINTAINER COMMENT: While working on Groups,
	* I noticed that the setSort method was always preceded by clearSort.
	* => This `setSort` method could theoretically be removed (only `addSort` seems useful).
	* BUT IT IS EXPOSED, SO DO NOT REMOVE!
	*/
	setSort(propertyName, direction) {
		const index = this.findSortIndex(propertyName);
		if (index >= 0) this._dataSorts[index] = this.buildPropertySort(propertyName, direction);
		else this.addSort(propertyName, direction);
	}
	/**
	* Adds property sort.
	*
	* @param propertyName - Name of property.
	* @param direction - Sort direction.
	*/
	addSort(propertyName, direction) {
		this._dataSorts.push(this.buildPropertySort(propertyName, direction));
	}
	buildPropertySort(propertyName, direction) {
		return new Sort(propertyName, direction, this.normalizeSortPropertyName(propertyName));
	}
	/**
	* Removes sort.
	*
	* @param propertyName - Name of property.
	*/
	removeSort(propertyName) {
		propertyName = this.normalizeSortPropertyName(propertyName);
		const index = this.findSortIndex(propertyName);
		if (index !== -1) this._dataSorts.splice(index, 1);
	}
	/**
	* Clears sort.
	*/
	clearSort() {
		this._dataSorts.length = 0;
	}
	/**
	* Finds sort.
	*
	* @param propertyName - Name of property.
	* @returns Sort if found.
	*/
	findSort(propertyName) {
		const propertyPath = this.normalizeSortPropertyName(propertyName);
		return this.dataSorts.find((s) => s.propertyName === propertyPath);
	}
	/**
	* Finds sort index.
	*
	* @param propertyName - Name of property.
	* @returns Index of item.
	*/
	findSortIndex(propertyName) {
		const propertyPath = this.normalizeSortPropertyName(propertyName);
		return this.dataSorts.findIndex((s) => s.propertyName === propertyPath);
	}
	/**
	* Sorts the data.
	*
	* @param serverWhenFullDataLoaded - If `true`, the sorting will be done on the server when full data is loaded. (e.g. set to `true` by datagrid)
	*/
	async sortAsync(serverWhenFullDataLoaded = false) {
		let currentKey = /* @__PURE__ */ new Map();
		if (this.current !== void 0) currentKey = this.current.getKey();
		if (this.getClientSideSort(!serverWhenFullDataLoaded)) this.datasource.sortItems(this.dataSorts);
		else await this.checkAndLoadDataAsync();
		const newPosition = this.datasource.findIndex((item) => {
			const itemKey = item.getKey();
			for (const [property, value] of currentKey) if (itemKey.get(property) !== value) return false;
			return true;
		});
		if (newPosition !== -1) this.setPosition(newPosition);
	}
	/**
	* Normalizes the sort property name.
	*
	* If it is a ViewModel property, the sort property name is replaced by the property path (to handle localizable string properties).
	*
	* @param propertyName - Sort property name.
	*/
	normalizeSortPropertyName(propertyName) {
		return this.findProperty(propertyName)?.getSortPath() ?? propertyName;
	}
	setFilter(filter) {
		this.dataFilter = filter;
	}
	convertFilterFromJsonAsync(json) {
		return Filter.fromJSON(json, this);
	}
	async filterDataAsync(filter) {
		if (filter) this.dataFilter = filter;
		if (this.clientSideFilter) this.clientSideFilterData(true);
		else await this.checkAndLoadDataAsync();
		uiViewEvents.layoutUpdated.raiseDebounced(this);
	}
	clientSideFilterData(raiseEvent) {
		let items = void 0;
		if (!this.dataFilter.isEmpty) {
			const newPropertyPaths = new Map(this.properties.map((p) => [p.name, p.camelCaseName]));
			items = this.dataFilter.toSimplifiedFilter().renamePropertyPaths(newPropertyPaths).evaluate(this.datasource, this.getClientFilterEvaluationOptions());
			raiseEvent = true;
		}
		if (raiseEvent) {
			this.resetPositionAfterClientSideFilteredDatasourceChanged(items);
			this.clientSideFilteredDatasourceChangedEvent.raise(items);
		}
	}
	getClientFilterEvaluationOptions() {
		const quickSearchProperties = {};
		const uiViewQuickSearchProperties = this.properties.filter((p) => p.quickSearch);
		this.checkClientQuickSearchProperties(uiViewQuickSearchProperties);
		for (const property of uiViewQuickSearchProperties) quickSearchProperties[property.getDisplayPath()] = { format: (value, item) => property.formatValueSync(value, item) };
		const matchFunction = getArraySearchFunction(this.datasource, { properties: quickSearchProperties });
		return {
			viewModel: this,
			matchFn: (item, value) => matchFunction(value).includes(item)
		};
	}
	checkClientQuickSearchProperties(properties) {
		if (this.quickSearchPropertyNames) {
			const notFoundPropertyNames = this.quickSearchPropertyNames.filter((name) => !properties.some((p) => p.name === name));
			if (notFoundPropertyNames.length > 0) this.showDevelopmentError(new Error(stringFormat(this.globalResourceManager.get("ClientQuickSearchWillNotWorkCorrectlyBecauseOfMissingProperties"), notFoundPropertyNames.join(", "))));
		}
	}
	resetPositionAfterClientSideFilteredDatasourceChanged(items) {
		if (items?.length === 0) {
			this.position = -1;
			return;
		}
		const current = this.current;
		if (!current) {
			this.position = 0;
			return;
		}
		if (items && current && !items.includes(current)) this.position = this.datasource.indexOf(items[0]);
	}
	/**
	* Gets a value indicating whether a specified item is selected.
	*
	* @param item - Item.
	* @returns Value indicating whether the specified item is selected.
	*/
	isSelected(item) {
		return item.$selected;
	}
	async applySelectionAsync(item, value) {
		if (this.isSelected(item) === value) return;
		const itemSelectionChangingArgs = {
			item,
			cancel: false
		};
		await this.runExpressionAndLogAsync("onItemSelectionChanging", this.onItemSelectionChanging, itemSelectionChangingArgs);
		if (itemSelectionChangingArgs.cancel) return;
		item.$selected = value;
		await this.runExpressionAndLogAsync("onItemSelectionChanged", this.onItemSelectionChanged, { item });
	}
	async selectItemAsync(arg) {
		const item = this.getItemForSelection(arg);
		if (item) await this.applySelectionAsync(item, true);
	}
	/**
	* Selects specified items of the current datasource.
	*/
	async selectItemsAsync(items) {
		for (const item of items) await this.applySelectionAsync(item, true);
	}
	/**
	* Selects all items of the current datasource.
	*/
	async selectAllItemsAsync() {
		await this.selectItemsAsync(this.datasource);
	}
	async unselectItemAsync(arg) {
		const item = this.getItemForSelection(arg);
		if (item) await this.applySelectionAsync(item, false);
	}
	/**
	* Unselects specified items of the current datasource.
	*/
	async unselectItemsAsync(items) {
		for (const item of items) await this.applySelectionAsync(item, false);
	}
	/**
	* Unselects all items of the current datasource.
	*/
	async unselectAllItemsAsync() {
		await this.unselectItemsAsync(this.datasource);
	}
	async toggleItemSelectionAsync(arg) {
		const item = this.getItemForSelection(arg);
		if (item) await this.applySelectionAsync(item, !this.isSelected(item));
	}
	getItemForSelection(arg) {
		return typeof arg === "number" ? this.datasource[arg] : arg;
	}
	async setCurrentAndSelectAsync(item) {
		await this.setPositionAndSelectAsync(this.datasource.indexOf(item));
	}
	async setPositionAndSelectAsync(position) {
		await this.requestChangingPositionAsync(position);
		await this.selectOnlyCurrentAsync();
	}
	async selectOnlyCurrentAsync() {
		if (this.current) await this.selectOnlyItemAsync(this.current);
		else await this.unselectAllItemsAsync();
	}
	async selectOnlyItemAsync(arg) {
		const item = this.getItemForSelection(arg);
		for (const i of this.datasource) await this.applySelectionAsync(i, item === i);
	}
	saveSelectionStateBeforeLoad(datasource) {
		if (this.parentRelation) return this.getParentItem().popRelationSelectionState(this.parentRelation.name);
		return this.saveSelectionState(datasource);
	}
	saveSelectionState(datasource) {
		if (datasource.length === 0) return;
		const state = { selectedKeys: /* @__PURE__ */ new Set() };
		const relations = /* @__PURE__ */ new Map();
		for (const item of datasource) {
			const key = item.getKeyValues().join("/");
			if (item.$selected) state.selectedKeys.add(key);
			const relationStates = /* @__PURE__ */ new Map();
			for (const subViewModel of this.subViews) {
				const relationState = subViewModel.saveSelectionState(item.getRelationList(subViewModel.parentRelation.name));
				if (relationState) relationStates.set(subViewModel.parentRelation.name, relationState);
			}
			if (relationStates.size > 0) relations.set(key, relationStates);
		}
		if (relations.size > 0) state.relations = relations;
		return state;
	}
	async restoreSelectionStateAfterLoad(datasource, state) {
		if (state) this.restoreSelectionState(datasource, state);
		else if (this.behavior.initSelectionOnLoad) await this.initSelectionStateAfterLoad(datasource, this.position);
	}
	restoreSelectionState(datasource, state) {
		let selectCurrentItem = this.behavior.initSelectionOnLoad;
		for (const item of datasource) {
			const key = item.getKeyValues().join("/");
			if (state.selectedKeys.has(key)) {
				item.$selected = true;
				selectCurrentItem = false;
			}
			const relationStates = state.relations?.get(key);
			if (!relationStates) continue;
			for (const [relationName, relationState] of relationStates) {
				const subViewModel = this.getSubViewModel(relationName);
				if (subViewModel.parentRelation.embeddedForReading) subViewModel.restoreSelectionState(item.getRelationList(relationName), relationState);
				else {
					item.$relationSelectionStates ??= /* @__PURE__ */ new Map();
					item.$relationSelectionStates.set(relationName, relationState);
				}
			}
		}
		if (selectCurrentItem && this.current) this.current.$selected = true;
	}
	async initSelectionStateAfterLoad(datasource, position) {
		const item = datasource[position];
		if (item) await this.selectItemAsync(item);
		for (const item of this.datasource) for (const [relationName, { embeddedForReading }] of item.$relations) if (embeddedForReading) {
			const subViewModel = this.getSubViewModel(relationName);
			const datasource = item.getRelationList(relationName);
			const position = subViewModel.datasource === datasource ? subViewModel.position : 0;
			await subViewModel.initSelectionStateAfterLoad(datasource, position);
		}
	}
	/**
	* Deletes selected items of the datasource.
	* If only one item is present in the datasource, deletes this item event if it is not selected.
	*
	* @returns Value indicating whether the items has been removed.
	*/
	async deleteSelectedItemsAsync(items = this.getItemsToRemove(this.selectedItems)) {
		if (items.length === 0) return false;
		const removingArgs = {
			cancel: false,
			displayConfirmationMessage: this.isMain && items.some((i) => !i.$new),
			items
		};
		try {
			this._busy = true;
			await this.runExpressionAndLogAsync("onRemoving", this.onRemoving, removingArgs);
		} finally {
			this._busy = false;
		}
		if (removingArgs.cancel || removingArgs.displayConfirmationMessage && !await this.confirmDeletionAsync(items, removingArgs.confirmationMessageTitle, removingArgs.confirmationMessageText)) return false;
		return this.deleteRangeDataAsync(items);
	}
	getItemsToRemove(items) {
		if (this.monoRecord || this.behavior.removeCurrentWithNoSelection && items.length === 0) return this.current ? [this.current] : [];
		return items;
	}
	/**
	* Displays a pop-up window for deletion confirmation.
	*
	* @returns Value indicating whether the deletion is confirmed.
	*/
	async confirmDeletionAsync(itemsToRemove, customConfirmationMessageTitle, customConfirmationMessageText) {
		const title = customConfirmationMessageTitle ?? this.getDefaultDeletionConfirmationTitle(itemsToRemove);
		const text = customConfirmationMessageText ?? this.getDefaultDeletionConfirmationText(itemsToRemove);
		return (await this.showMessageAsync(ViewModelMessageType.danger, title, text, new ViewModelMessageButton(ViewModelMessageButtonType.yes, void 0, this.globalResourceManager.get("RemoveConfirmationYesText"), "remove"), new ViewModelMessageButton(ViewModelMessageButtonType.no, void 0, this.globalResourceManager.get("RemoveConfirmationNoText"), "close"))).buttonType === ViewModelMessageButtonType.yes;
	}
	/**
	* Gets the default confirmation messages displayed when deleting items.
	* @param itemsToRemoveCount Number of items being removed.
	* @returns Title and text of the confirmation message.
	*/
	getDefaultDeletionConfirmationText(items) {
		let confirmationText = this.deletionConfirmationMessage ?? void 0;
		if (confirmationText === void 0) {
			const elementCaption = this.getElementCaption(items);
			let confirmationMessageName;
			if (this.isMain) confirmationMessageName = this.deletionMode === 0 ? "RemoveMainLogicallyConfirmationText" : "RemoveMainConfirmationText";
			else confirmationMessageName = this.deletionMode === 0 ? "RemoveLogicallyConfirmationText" : "RemoveConfirmationText";
			confirmationText = stringFormat(this.globalResourceManager.get(confirmationMessageName), items.length, elementCaption);
		} else confirmationText = confirmationText.replace("{ItemsCount}", items.length.toString());
		return confirmationText;
	}
	/**
	* Gets the default confirmation messages displayed when deleting items.
	* @param itemsToRemoveCount Number of items being removed.
	* @returns Title and text of the confirmation message.
	*/
	getDefaultDeletionConfirmationTitle(items) {
		const elementCaption = this.getElementCaption(items);
		return stringFormat(this.globalResourceManager.get("RemoveConfirmationTitle"), items.length, elementCaption);
	}
	/**
	* Gets the caption of one or several items.
	* @param items Items.
	* @returns
	*/
	getElementCaption(items) {
		const resourceKey = items.length === 1 ? "Item" : "Items";
		return toLowerCaseStart(this.caption ?? this.globalResourceManager.get(resourceKey));
	}
	/**
	* Gets a value indicating whether navigating to the previous page is possible.
	*/
	get canMovePreviousPage() {
		return this.pageNumber > 1;
	}
	/**
	* Gets a value indicating whether navigating to the next page is possible.
	*/
	get canMoveNextPage() {
		return !this.loading && this.hasMoreData;
	}
	/**
	* Moves to the first page.
	*
	* @returns Value indicating whether navigation has been done.
	*/
	async moveToFirstPage() {
		return this.moveToSpecificPage(1);
	}
	/**
	* Moves to the next page.
	*
	* @returns Value indicating whether navigation has been done.
	*/
	async moveToNextPage() {
		if (!this.hasMoreData) return false;
		return this.moveToSpecificPage(this.pageNumber + 1);
	}
	/**
	* Moves to the previous page.
	*
	* @returns Value indicating whether navigation has been done.
	*/
	async moveToPreviousPage() {
		if (this.pageNumber === 1) return false;
		return this.moveToSpecificPage(this.pageNumber - 1);
	}
	/**
	* Moves to a specific page.
	*
	* @param pageNumber - Page number.
	* @returns Value indicating whether navigation has been done.
	*/
	async moveToSpecificPage(pageNumber) {
		if (this.pageNumber === pageNumber) return false;
		if (!await this.checkAndSaveDataAsync()) return false;
		this.pageNumber = pageNumber;
		this._position = -1;
		await this.loadDataAsync();
		return true;
	}
	/**
	* Formats validation messages to display them.
	* @param validationMessages - validation messages.
	* @returns A string representing the validation messages text.
	*/
	formatValidationMessages(validationMessages) {
		return [...new Set(validationMessages.map((vm) => vm.text))].join("\n");
	}
	/**
	* Returns the items with at least one validation messages.
	* @param severityLevel - Severity level.
	* @param excludeNonEmbeddedRelations - A value indicating whether to exclude non-embedded relations from the check.
	* @param datasource - Datasource.
	* @returns A array of items that have validation messages.
	*/
	getValidationMessages(severityLevel, excludeNonEmbeddedRelations = false, datasource = this.datasource) {
		const validationMessages = this.customValidationMessages.filter((m) => m.severityLevel === severityLevel);
		for (const item of datasource) {
			validationMessages.push(...item.getValidationMessages(severityLevel));
			for (const [relation, { embeddedForWriting }] of item.$relations) if (!excludeNonEmbeddedRelations || embeddedForWriting) {
				const subViewModel = this.getSubViewModel(relation);
				validationMessages.push(...subViewModel.getValidationMessages(severityLevel, excludeNonEmbeddedRelations, item.getRelationList(relation)));
			}
		}
		return validationMessages;
	}
	/**
	* Validates the datasource and potentially sets validation messages for the specified validation type.
	* @param validationTypes - Validation types.
	* @param validateUnchangedItems - A value indicating whether to validate unchanged items.
	* @param datasource - Datasource.
	* @param excludeNonEmbeddedRelations - A value indicating whether to exclude non-embedded relations from the check.
	*/
	async validateAsync(validationTypes, validateUnchangedItems = false, datasource = this.datasource, excludeNonEmbeddedRelations = false) {
		this.customValidationMessages.clear();
		await Promise.all(this.validatingEvent.handlers.map((h) => h(validationTypes)));
		for (const item of datasource) {
			if (validateUnchangedItems || item.hasChanges(excludeNonEmbeddedRelations)) await this.validateItemAsync(item, validationTypes);
			for (const [relation, { embeddedForWriting }] of item.$relations) if (!excludeNonEmbeddedRelations || embeddedForWriting) await this.getSubViewModel(relation).validateAsync(validationTypes, validateUnchangedItems, item.getRelationList(relation), excludeNonEmbeddedRelations);
		}
	}
	/**
	* Validates the specified item and potentially sets validation messages for the specified validation type.
	*
	* @param item - item to verify.
	* @param validationTypes - Validation types.
	*/
	async validateItemAsync(item, validationTypes) {
		item.$validationMessages.clear();
		for (const property of this.properties) {
			const validationMessage = this.validateProperty(item, property);
			if (validationMessage) item.replaceValidationMessage(validationMessage);
		}
		const validationRules = this.getValidationRules(validationTypes);
		for (const validationRule of validationRules) {
			const text = this.runValidationRule(item, validationRule);
			if (text) this.setValidationRuleMessage(item, validationRule, text);
		}
	}
	validateProperty(item, property) {
		let validationMessage = this.validateInvalidProperty(item, property);
		validationMessage ??= this.validateRequiredProperty(item, property);
		validationMessage ??= this.validateMinMaxProperty(item, property);
		if (this.validateLocalizableString && property.dataType === DataType.localizableString) validationMessage ??= this.validateLocalizableStringProperty(item, property);
		return validationMessage;
	}
	validateRequiredProperty(item, property) {
		if (this.isPropertyRequired(item, property) && this.hasRequiredError(item, property)) return new ConstraintValidationMessage(property.camelCaseName, ValidationRuleSeverityLevel.error, stringFormat(this.globalResourceManager.get("ValidationMessage_RequiredFieldValue_Text"), property.caption), this.globalResourceManager.get("ValidationMessage_RequiredFieldValue_ShortText"));
	}
	isPropertyRequired(item, property) {
		if (item.isVirtual) return false;
		if (property.serverSideReadOnly && property.getReadOnly(item)) return false;
		return property.getRequired(item);
	}
	validateInvalidProperty(item, property) {
		const invalidValue = item.getInvalidValue(property.camelCaseName);
		if (invalidValue !== null) return new ConstraintValidationMessage(property.camelCaseName, ValidationRuleSeverityLevel.error, stringFormat(this.globalResourceManager.get("ValidationMessage_InvalidFieldValue_Text"), invalidValue, property.caption), this.globalResourceManager.get("ValidationMessage_InvalidFieldValue_ShortText"));
	}
	validateMinMaxProperty(item, property) {
		const value = getPropertyByPath(item, property.camelCaseName);
		if (value != null) {
			const maxValue = property.getMaxValue(item);
			if (maxValue != null && maxValue < value) {
				const formattedMaxValue = property.formatValueSync(maxValue, item);
				return new ConstraintValidationMessage(property.camelCaseName, ValidationRuleSeverityLevel.error, stringFormat(this.globalResourceManager.get("ValidationMessage_MaxFieldValue_Text"), property.caption, formattedMaxValue), stringFormat(this.globalResourceManager.get("ValidationMessage_MaxFieldValue_ShortText"), formattedMaxValue));
			}
			const minValue = property.getMinValue(item);
			if (minValue != null && minValue > value) {
				const formattedMinValue = property.formatValueSync(minValue, item);
				return new ConstraintValidationMessage(property.camelCaseName, ValidationRuleSeverityLevel.error, stringFormat(this.globalResourceManager.get("ValidationMessage_MinFieldValue_Text"), property.caption, formattedMinValue), stringFormat(this.globalResourceManager.get("ValidationMessage_MinFieldValue_ShortText"), formattedMinValue));
			}
		}
	}
	validateLocalizableStringProperty(item, property) {
		const value = property.getValue(item);
		if (!value) return;
		if (!value[this.localizator.localizableStringRequiredLanguage]) return new ConstraintValidationMessage(property.camelCaseName, ValidationRuleSeverityLevel.error, stringFormat(this.globalResourceManager.get("ValidationMessage_LocalizableString_DefaultTranslationIsMissing_Text"), this.localizator.localizableStringRequiredLanguage, property.caption), stringFormat(this.globalResourceManager.get("ValidationMessage_LocalizableString_DefaultTranslationIsMissing_ShortText"), this.localizator.localizableStringRequiredLanguage));
		const missingLanguages = this.localizator.localizableStringLanguages.filter((l) => !value[l]);
		if (missingLanguages.length > 1) {
			const missingLanguagesText = missingLanguages.join(", ");
			return new ConstraintValidationMessage(property.camelCaseName, ValidationRuleSeverityLevel.warning, stringFormat(this.globalResourceManager.get("ValidationMessage_LocalizableString_TranslationsAreMissing_Text"), missingLanguagesText, property.caption), stringFormat(this.globalResourceManager.get("ValidationMessage_LocalizableString_TranslationsAreMissing_ShortText"), missingLanguagesText));
		}
		if (missingLanguages.length === 1) {
			const missingLanguagesText = missingLanguages.join(", ");
			return new ConstraintValidationMessage(property.camelCaseName, ValidationRuleSeverityLevel.warning, stringFormat(this.globalResourceManager.get("ValidationMessage_LocalizableString_TranslationIsMissing_Text"), missingLanguages.join(", "), property.caption), stringFormat(this.globalResourceManager.get("ValidationMessage_LocalizableString_TranslationIsMissing_ShortText"), missingLanguagesText));
		}
	}
	/**
	* Returns upload errors that prevent saving.
	* @param excludeNonEmbeddedRelations - A value indicating whether to exclude non-embedded relations from the check.
	* @param datasource - Datasource.
	* @returns A array of validation messages.
	*/
	getUploadErrors(excludeNonEmbeddedRelations, datasource) {
		const messages = [];
		for (const item of datasource) {
			messages.push(...this.getItemUploadErrors(item));
			for (const [relation, { embeddedForWriting }] of item.$relations) if (!excludeNonEmbeddedRelations || embeddedForWriting) {
				const subViewModel = this.getSubViewModel(relation);
				messages.push(...subViewModel.getUploadErrors(excludeNonEmbeddedRelations, item.getRelationList(relation)));
			}
		}
		return messages;
	}
	/**
	* Returns data loading errors that prevent saving.
	* @param datasource - Datasource.
	* @returns A array of validation messages.
	*/
	getDataLoadingErrors(datasource) {
		const messages = [];
		const changedItems = datasource.filter((i) => !i.$new && i.hasChanges(true));
		for (const item of changedItems) for (const [relation, { embeddedForReading, embeddedForWriting, autoLoad }] of item.$relations) {
			if (embeddedForReading || !embeddedForWriting || !autoLoad) continue;
			const relationList = item.getRelationList(relation);
			if (relationList.loadingState !== ListLoadingState.loaded) messages.push(new ValidationMessage(ValidationRuleSeverityLevel.error, this.globalResourceManager.get("DataLoading_NotLoaded")));
			const subViewModel = this.getSubViewModel(relation);
			messages.push(...subViewModel.getDataLoadingErrors(relationList));
		}
		return messages;
	}
	/**
	* Returns upload errors that prevent saving on an item.
	* @param item - the item.
	* @returns A array of validation messages.
	*/
	getItemUploadErrors(item) {
		const result = [];
		for (const property of this.properties.filter((p) => p.dataType === DataType.image || p.dataType === DataType.file)) {
			const fileReference = property.getValue(item);
			if (fileReference && !fileReference.value) result.push(new ConstraintValidationMessage(property.camelCaseName, ValidationRuleSeverityLevel.error, fileReference.uploadState === FileUploadState.inProgress ? stringFormat(this.globalResourceManager.get("FileUpload_InProgress"), property.caption) : stringFormat(this.globalResourceManager.get("FileUpload_HasFailed"), property.caption)));
		}
		return result;
	}
	hasRequiredError(item, property) {
		const parent = this.getParentItem();
		if (parent?.$new && this.parentRelation?.embeddedForWriting && parent.isRelationScalarProperty(property.camelCaseName)) return false;
		const value = property.getValue(item);
		if (value === void 0 || value === null) return true;
		if (property.dataType === DataType.string) return value === "";
		if (property.dataType === DataType.localizableString && this.localizator.localizableStringLanguages.length === 1) {
			const currentLanguageValue = value[this.localizator.localizableStringCurrentLanguage];
			return isNullOrEmpty(currentLanguageValue);
		}
		if (property.dataType === DataType.stringArray) return value.length === 0;
		return false;
	}
	runValidationRule(item, validationRule) {
		return this.runExpressionAndLog(`validationRule[${validationRule.method.name}]`, validationRule.method, { item });
	}
	setValidationRuleMessage(item, validationRule, text) {
		function setValidationMessage(propertyName) {
			if (text) {
				const validationMessage = new ValidationRuleValidationMessage(validationRule.method.name, validationRule.severityLevel, text).withProperty(propertyName);
				item.replaceValidationMessage(validationMessage);
			} else item.removeValidationMessageByPredicate(propertyName, (m) => ValidationRuleValidationMessage.is(m) && m.name === validationRule.method.name);
		}
		if (!validationRule.properties) {
			setValidationMessage(void 0);
			return;
		}
		for (const propertyName of validationRule.properties) setValidationMessage(propertyName);
	}
	onComputedChanged(name, oldValue, newValue) {
		if (oldValue instanceof ObservableList) {
			const list = oldValue;
			const handler = this.arrayComputedListChangedHandler.get(list);
			if (handler) {
				list.listChangedEvent.remove(handler);
				this.arrayComputedListChangedHandler.delete(list);
			}
		}
		if (newValue instanceof ObservableList) {
			const list = newValue;
			const handler = (args) => {
				if (args.type !== ListChangedType.itemChanged) this.computedChangedEvent.raise(name, newValue, newValue);
			};
			list.listChangedEvent.add(handler);
			this.arrayComputedListChangedHandler.set(newValue, handler);
		}
		this.computedChangedEvent.raise(name, oldValue, newValue);
	}
	runExpressionAndLog(expressionName, action, args) {
		let result;
		const startTime = performance.now();
		try {
			result = action.call(this, args);
			this.logPerformance(expressionName, startTime, result);
		} catch (error) {
			this.logPerformance(expressionName, startTime, void 0, error);
		}
		return result;
	}
	/**
	* Gets the items with at least one persistence error.
	* @param datasource - Data source.
	* @returns The items with persistence error.
	*/
	getPersistenceErrorItems(datasource) {
		return datasource.filter((e) => e.$persistenceError);
	}
	/**
	* Displays the first persistence error if the data source has at least one error.
	* @param datasource - Data source.
	* @returns A value indicating whether persistence errors has been shown.
	*/
	showPersistenceErrors(datasource) {
		const persistenceErrorItems = this.getPersistenceErrorItems(datasource);
		if (persistenceErrorItems.length > 0 && persistenceErrorItems[0].$persistenceError) {
			this.showApiError(persistenceErrorItems[0].$persistenceError, ApiErrorRequestType.Save);
			return true;
		}
		return false;
	}
	/**
	* Toggles an overlay.
	*/
	toggleOverlay(idOrOptions) {
		toggleOverlay(this, idOrOptions);
	}
	/**
	* (LEGACY VERSION)
	* Ask PDF report generation.
	* @param reportName Name of the report to execute.
	* @param filter Filter data.
	*/
	async executeReportAsync(reportName, filter, parameters) {
		const options = new ExecuteReportOptions(reportName);
		if (filter) options.withFilter(filter);
		if (parameters) options.withParameters(parameters);
		return this.executeReportWithOptionsAsync(options);
	}
	/**
	* (CURRENT VERSION)
	* Ask PDF report generation.
	* @param options report options.
	* @returns Promise
	*/
	async executeReportWithOptionsAsync(options) {
		await Reporting.executeReportAsync(options, {
			reportingBaseUrl: this.getRemote()?.reportingBaseUrl ?? Reporting.getBaseUrl(),
			applicationContext: this.applicationContext,
			eventManager: this.eventManager,
			globalResourceManager: this.globalResourceManager,
			uiViewPermitter: this.uiViewPermitter,
			callingUIViewName: this.uiViewName
		});
	}
	/**
	* Show a report viewer in the native Neos Report Viewer UI View.
	* @param options report options.
	* @returns Promise
	*/
	showReportAsync(options) {
		options.withRemote(this.getRemote());
		return this.navigateAsync(Reporting.getShowReportNavigationOptions(options));
	}
	/**
	* Transpiled from ViewModel.CreateServerDataExportOptions.
	* Creates the **default** server data export options from the current context.
	* This includes settings such as _the current time zone of the client_ and should be overloaded if UTC is preferred.
	* Example usage: TechnicalDemos/DGFilterExportUI.yml
	* @param fileName - The file name.
	* @param format - The format.
	* @returns The options.
	*/
	createServerDataExportOptions(fileName, format) {
		if (!this.entityViewName) throw new Error(this.globalResourceManager.get("ServerDataExport_EntityViewCouldBeDetermined"));
		const options = new ServerDataExportOptions(this.entityViewName, fileName, format).withUIViewName(this.uiViewName).withTimeZone(TimeZoneInfo.local.id);
		options.withProperties(this.getDatagridProperties().filter((p) => p.datagridVisible && p.fromWebAPI).map((p) => p.name));
		const filter = this.getInitialFilter(this.datasource);
		if (!filter.isEmpty) options.withFilter(filter);
		const serverDataSorts = this.getServerDataSorts();
		if (serverDataSorts.length > 0) options.withSorts(serverDataSorts);
		const entityViewParameters = this.entityViewParameters.getValues();
		if (Object.keys(entityViewParameters).length > 0) options.withEntityViewParameters(entityViewParameters);
		return options;
	}
	saveSubViewModelsStateBeforeSave() {
		for (const subViewModel of this.subViews) if (subViewModel.parentRelation.embeddedForWriting) {
			subViewModel.saveStateBeforeSave();
			subViewModel.saveSubViewModelsStateBeforeSave();
		}
	}
	saveStateBeforeSave() {
		const selectedKeys = [];
		for (const item of this.datasource) {
			item.$transientKey = (++this._transientKey).toString();
			if (item.$selected) selectedKeys.push(item.$transientKey);
		}
		this._stateBeforeSave = {
			currentKey: this.current?.$transientKey,
			selectedKeys
		};
	}
	async restoreSubViewModelsStateAfterSave() {
		for (const subViewModel of this.subViews) if (subViewModel.parentRelation.embeddedForWriting) {
			subViewModel.restoreStateAfterSave();
			subViewModel.restoreSubViewModelsStateAfterSave();
		}
	}
	restoreStateAfterSave() {
		for (const item of this.datasource) {
			if (!this._stateBeforeSave || !item.$transientKey) continue;
			if (this._stateBeforeSave.currentKey === item.$transientKey) this.setPosition(this.datasource.indexOf(item), false);
			if (this._stateBeforeSave.selectedKeys.includes(item.$transientKey)) item.$selected = true;
		}
		this._stateBeforeSave = void 0;
		this._transientKey = 0;
	}
	registerViewContainer(containerId, containerViewModel) {
		this.viewContainers.set(containerId, containerViewModel);
	}
	/**
	* Finds the view model of a view container in the current UIView by the specified container identifier.
	* @param containerId The container identifier.
	* @returns The view model of the founded view container or null when the view container was not found.
	*/
	findViewModelOfViewContainer(containerId) {
		return this.viewContainers.get(containerId);
	}
	/**
	* Gets the view model of a view container in the current UIView by the specified container identifier.
	* @param containerId The container identifier.
	* @returns The view model of the founded view container.
	*/
	getViewModelOfViewContainer(containerId) {
		const viewModel = this.findViewModelOfViewContainer(containerId);
		if (!viewModel) throw new Error(stringFormat(this.globalResourceManager.get("ViewContainerNotFound"), containerId));
		return viewModel;
	}
	/**
	* Gets the group summary descriptions.
	* @returns The group summary descriptions.
	*/
	getGroupSummaryDescriptions() {
		return this.getSummaryDescriptions((p) => p.groupSummaryType);
	}
	/**
	* Gets the total summary descriptions.
	* @returns The total summary descriptions.
	*/
	getTotalSummaryDescriptions() {
		return this.getSummaryDescriptions((p) => p.totalSummaryType);
	}
	getSummaryDescriptions(getSummaryType) {
		const summaryDescriptions = [];
		for (const property of this.getDisplayableProperties()) {
			const summaryType = getSummaryType(property);
			if (summaryType !== void 0) summaryDescriptions.push(new SummaryDescription(property.camelCaseName, summaryType));
		}
		return summaryDescriptions;
	}
	async loadTotalSummaries(datasource, mode) {
		if (mode === SummaryLoadMode.auto) mode = this.getAutoSummaryLoadMode();
		const version = mode === SummaryLoadMode.client ? void 0 : this.createSummaryLoadVersion(datasource);
		const summaryDescriptions = this.getTotalSummaryDescriptions();
		const summaries = await this.internalLoadSummaries(summaryDescriptions, datasource, mode);
		if (version !== void 0 && !this.isSummaryLoadVersionCurrent(datasource, version)) return;
		datasource.summaries = summaries !== void 0 ? SummaryDescription.normalize(summaryDescriptions, summaries) : void 0;
	}
	async loadSummaries(requests) {
		const summaryDescriptions = new Map(requests.map((r) => [r, new SummaryDescription(r.property.camelCaseName, r.summaryType)]));
		const summaries = await this.internalLoadSummaries(Array.from(summaryDescriptions.values()), this.datasource, this.getAutoSummaryLoadMode());
		return summaries !== void 0 ? new SummaryResult(SummaryResult.normalizeSummaries(summaryDescriptions, summaries)) : SummaryResult.empty;
	}
	createSummaryLoadVersion(datasource) {
		const nextVersion = (this.summaryLoadVersions.get(datasource) ?? 0) + 1;
		this.summaryLoadVersions.set(datasource, nextVersion);
		return nextVersion;
	}
	isSummaryLoadVersionCurrent(datasource, version) {
		return this.summaryLoadVersions.get(datasource) === version;
	}
	async internalLoadSummaries(summaryDescriptions, datasource, mode) {
		if (summaryDescriptions.length === 0) return;
		if (mode === SummaryLoadMode.client) return datasource.length > 0 ? SummaryDescription.calculate(summaryDescriptions, datasource, true) : void 0;
		const transformations = [];
		const filter = this.getInitialFilter(datasource).toODataString(this);
		if (filter) transformations.push(`filter(${filter})`);
		transformations.push(`aggregate(${summaryDescriptions.map((d) => d.toOData()).join(", ")})`);
		const response = await this.fetchItemsAsync({
			transformations,
			sorts: void 0
		}, false);
		if (!response.succeeded) {
			this.logger.error(this.globalResourceManager.get("FailedToLoadSummaries"), response.error);
			return;
		}
		return response.data[0];
	}
	getAutoSummaryLoadMode() {
		return this.canLoadClientSideSummaries() ? SummaryLoadMode.client : SummaryLoadMode.server;
	}
	canLoadClientSideSummaries() {
		return this.fullDataLoaded && this.groupDescriptions.length === 0;
	}
	loadClientSideTotalSummary(datasource, property) {
		if (property.totalSummaryType) {
			const summaryDescription = new SummaryDescription(property.camelCaseName, property.totalSummaryType);
			datasource.summaries = {
				...datasource.summaries,
				[property.camelCaseName]: summaryDescription.calculate(datasource)
			};
		}
	}
	async loadGroupSummaries(groupProperties, requests) {
		const groupDescriptions = groupProperties.map((p, index) => new GroupDescription(p, index, index === groupProperties.length - 1));
		if (groupDescriptions.length === 0) return [];
		const summaryDescriptions = new Map(requests.map((r) => [r, new SummaryDescription(r.property.camelCaseName, r.summaryType)]));
		if (summaryDescriptions.size === 0) return [];
		const response = await this.fetchServerGroups({
			filter: this.getInitialFilter(this.datasource),
			groupDescriptions,
			summaryDescriptions: Array.from(summaryDescriptions.values()),
			top: this.recordsByPage
		});
		if (!response.succeeded) return [];
		return response.data.map((group) => {
			const key = /* @__PURE__ */ new Map();
			for (const groupDescription of groupDescriptions) key.set(groupDescription.property, group[groupDescription.property.camelCaseName]);
			return new GroupSummaryResult(key, SummaryResult.normalizeSummaries(summaryDescriptions, group));
		});
	}
	async fetchServerGroups(request) {
		const transformations = [];
		const filter = request.filter?.toODataString(this);
		if (filter) transformations.push(`filter(${filter})`);
		transformations.push(GroupDescription.toOData(request.groupDescriptions, request.summaryDescriptions));
		return this.fetchItemsAsync({
			transformations,
			top: request.top,
			skip: request.skip,
			sorts: request.sorts
		});
	}
	getAllStyleRules() {
		return [...this.userStyleRules, ...this.devStyleRules];
	}
	evaluateStyleRules(item, propertyName) {
		let hasResult = false;
		const result = {};
		for (const styleRule of this.getAllStyleRules()) {
			const evaluated = styleRule.evaluate(item, propertyName, result);
			hasResult ||= evaluated;
			if (evaluated && Object.values(result).every((value) => value != null)) break;
		}
		return hasResult ? result : void 0;
	}
	/**
	* Gets the permissions of the current UI view.
	* @returns Permissions.
	*/
	getPermissions() {
		return this.uiViewPermitter.getPermissions(this.uiViewName);
	}
	trackEventHandler(promise) {
		if (!this.behavior.flushPendingEventHandlersOnSave) return promise;
		const trackedPromise = promise.finally(() => {
			this._pendingEventHandlers.delete(trackedPromise);
		});
		this._pendingEventHandlers.add(trackedPromise);
		return trackedPromise;
	}
	async flushPendingEventHandlers() {
		if (this.behavior.flushPendingEventHandlersOnSave) while (this._pendingEventHandlers.size > 0) {
			const currentPendingEventHandlers = [...this._pendingEventHandlers];
			await Promise.all(currentPendingEventHandlers);
		}
		for (const subViewModel of this.subViews) if (subViewModel.parentRelation.embeddedForWriting) await subViewModel.flushPendingEventHandlers();
	}
	/**
	* SNAPSHOT
	*/
	async createSnapshot() {
		const { createViewModelSnapshot } = await import("./snapshot-BYJNMP3e.js");
		return createViewModelSnapshot(this);
	}
	async applySnapshot(snapshot) {
		const { applyViewModelSnapshot } = await import("./snapshot-BYJNMP3e.js");
		await applyViewModelSnapshot(this, snapshot);
	}
};
//#endregion
//#region src/filter/FilterFunction.ts
var FilterFunction = /* @__PURE__ */ function(FilterFunction) {
	FilterFunction["none"] = "none";
	FilterFunction["toUpper"] = "toUpper";
	return FilterFunction;
}({});
//#endregion
//#region src/filter/FilterCondition.ts
var FilterCondition = class FilterCondition {
	static create(propertyPath, filterOperator, oDataPrefix, oDataSuffix, ...values) {
		const condition = new FilterCondition(propertyPath, filterOperator, ...values);
		if (oDataPrefix) condition.oDataPrefix = oDataPrefix;
		if (oDataSuffix) condition.oDataSuffix = oDataSuffix;
		return condition;
	}
	static async fromJSON(raw, viewModel) {
		if (raw.propertyPath) {
			const property = FilterCondition.findProperty(viewModel, raw.propertyPath);
			if (raw.customOperatorId) return this.getPropertyCustomFilterCondition(property, raw.customOperatorId, raw.readOnly);
			if (property && raw.filterOperator) {
				const [filterPath] = property.getFilterPathAndDataType(raw.filterOperator);
				raw.propertyPath = filterPath;
			}
		}
		const condition = FilterCondition.create(raw.propertyPath, raw.filterOperator, raw.oDataPrefix, raw.oDataSuffix, ...raw.values ? this.toFilterValues(raw.values) : []);
		condition.customOperatorId = raw.customOperatorId;
		condition.filterFunction = raw.filterFunction;
		condition.readOnly = raw.readOnly === true;
		return condition;
	}
	static async getPropertyCustomFilterCondition(property, customOperatorId, readOnly) {
		const customOperator = property?.findCustomFilterOperator(customOperatorId);
		if (!customOperator) return null;
		const condition = await customOperator.getFilterCondition();
		condition.readOnly = readOnly === true;
		return condition;
	}
	static toFilterValues(values) {
		const filterValues = [];
		for (const value of values) if (typeof value === "object" && value !== null && "operation" in value) {
			let args = [];
			if ("args" in value) args = value.args;
			filterValues.push(new FilterValue(value.operation, ...args));
		} else filterValues.push(value);
		return filterValues;
	}
	constructor(propertyPath, filterOperator, ...values) {
		this.values = [];
		this.readOnly = false;
		this.filterFunction = FilterFunction.none;
		this.propertyPath = propertyPath;
		this.filterOperator = filterOperator;
		this.values = values;
	}
	get isEmpty() {
		if (this.filterOperator === FilterOperator.isNotNull || this.filterOperator === FilterOperator.isNull) return false;
		return this.values.flat().every((v) => v == null || v === "");
	}
	get firstValue() {
		return this.values.length === 0 ? null : getFirstArrayElement(this.values);
	}
	set firstValue(value) {
		if (this.values.length === 0) this.values.push(value);
		else this.values[0] = value;
	}
	get oDataPropertyPath() {
		return this.propertyPath.replaceAll(".", "/");
	}
	equals(condition) {
		if (this === condition) return true;
		return this.propertyPath === condition.propertyPath && this.filterOperator === condition.filterOperator && this.customOperatorId === condition.customOperatorId && compareArray(this.values, condition.values);
	}
	clone() {
		const filterCondition = FilterCondition.create(this.propertyPath, this.filterOperator, this.oDataPrefix, this.oDataSuffix, ...this.values);
		if (this.customOperatorId) filterCondition.customOperatorId = this.customOperatorId;
		filterCondition.readOnly = this.readOnly;
		return filterCondition;
	}
	toString(viewModel) {
		return this.toODataString(viewModel);
	}
	toODataString(viewModel) {
		if (this.isEmpty) return "";
		let filter = this.oDataPrefix ?? "";
		switch (this.filterOperator) {
			case FilterOperator.isMatch:
				filter += this.toODataIsMatchFilter();
				break;
			case FilterOperator.isNull:
				filter += `${this.oDataPropertyPath} ${FilterOperator.equal} null`;
				break;
			case FilterOperator.isNotNull:
				filter += `${this.oDataPropertyPath} ${FilterOperator.notEqual} null`;
				break;
			case FilterOperator.equal:
				filter += this.toODataEqualFilter(viewModel);
				break;
			case FilterOperator.notEqual:
				filter += this.toODataNotEqualFilter(viewModel);
				break;
			case FilterOperator.in:
				filter += this.toODataInOrNotInFilter();
				break;
			case FilterOperator.notIn:
				filter += this.toODataInOrNotInFilter(true);
				break;
			case FilterOperator.contains:
			case FilterOperator.startsWith:
			case FilterOperator.endsWith: {
				let value = this.toODataValue(this.firstValue);
				if (this.isPropertyTypeOf(DataType.guid, viewModel)) value = `'${value}'`;
				filter += `${this.filterOperator}(search.normalize(${this.oDataPropertyPath}), search.normalize(${value}))`;
				break;
			}
			case FilterOperator.greater:
			case FilterOperator.lessOrEqual: {
				let value = this.firstValue;
				if (this.isPropertyTypeOf(DataType.dateTime, viewModel)) value = FilterCondition.getEndDateTime(value);
				filter += `${this.oDataPropertyPath} ${this.filterOperator} ${this.toODataValue(value)}`;
				break;
			}
			case FilterOperator.less:
			case FilterOperator.greaterOrEqual: {
				let value = this.firstValue;
				if (this.isPropertyTypeOf(DataType.dateTime, viewModel)) value = FilterCondition.getStartDateTime(value);
				filter += `${this.oDataPropertyPath} ${this.filterOperator} ${this.toODataValue(value)}`;
				break;
			}
			default:
				filter += this.toODataBetweenFilter(viewModel);
				break;
		}
		filter += this.oDataSuffix ?? "";
		return filter;
	}
	getFormattedText(outputToHtml, className, text) {
		return outputToHtml ? `<span class="${className}">${text}</span>` : text;
	}
	getFormattedValues(outputToHtml, values, addBrackets = false) {
		const valuesText = values.join(", ");
		return this.getFormattedText(outputToHtml, "filter-text-value", addBrackets ? `[${valuesText}]` : valuesText);
	}
	toText(culture, outputToHtml, viewModel) {
		if (this.isEmpty) return "";
		const property = FilterCondition.findProperty(viewModel, this.propertyPath);
		if (property && this.customOperatorId) {
			const customOperator = property.findCustomFilterOperator(this.customOperatorId);
			if (customOperator?.getText) {
				const text = customOperator.getText();
				return this.getFormattedText(outputToHtml, "filter-text-operator", text);
			}
		}
		return this.toTextWithStandardOperator(culture, outputToHtml, property);
	}
	toTextWithStandardOperator(culture, outputToHtml, property) {
		const propertyCaption = this.getFormattedText(outputToHtml, "filter-text-property", property?.caption ?? this.oDataPropertyPath);
		const operator = this.getFormattedText(outputToHtml, "filter-text-operator", this.getOperatorCaption());
		const values = this.toDisplayValues(culture);
		switch (this.filterOperator) {
			case FilterOperator.between: return `${propertyCaption} ${operator} ${this.getFormattedText(outputToHtml, "filter-text-value", values[0])} ${getGlobalResourceManager(void 0).get("And").toLocaleLowerCase()} ${this.getFormattedText(outputToHtml, "filter-text-value", values[1])}`;
			case FilterOperator.notIn:
			case FilterOperator.in: return `${propertyCaption} ${operator} ${this.getFormattedValues(outputToHtml, values, true)}`;
			case FilterOperator.isNull:
			case FilterOperator.isNotNull: return `${propertyCaption} ${operator}`;
			case FilterOperator.isMatch: return `${this.getFormattedText(outputToHtml, "filter-text-operator", getGlobalResourceManager(void 0).get("IsMatchFilter").toLocaleLowerCase())} ${this.getFormattedText(outputToHtml, "filter-text-value", values[0])}`;
			default: return `${propertyCaption} ${operator} ${this.getFormattedValues(outputToHtml, [values[0]])}`;
		}
	}
	getOperatorCaption() {
		if (this.filterOperator === FilterOperator.isMatch) return "";
		let operatorMetadata = allOperators[this.filterOperator];
		if (!operatorMetadata) operatorMetadata = allOperators[this.filterOperator.toLowerCase()];
		if (!operatorMetadata) return this.filterOperator;
		return operatorMetadata.caption.toLocaleLowerCase();
	}
	toDisplayValues(culture) {
		return this.values.map((v) => {
			if (v instanceof Date) return v.toLocaleDateString(culture);
			if (typeof v === "boolean") return getGlobalResourceManager(void 0).get(v ? "True" : "False");
			return escapeHtml(String(v));
		});
	}
	toODataIsMatchFilter() {
		const value = this.toODataValue(this.firstValue);
		const quotedValue = value.startsWith("'") && value.endsWith("'") ? value : `'${value}'`;
		return `${this.filterOperator}(${quotedValue})`;
	}
	toODataEqualFilter(viewModel) {
		const value = this.firstValue;
		if (this.isPropertyTypeOfDate(viewModel)) {
			const range = FilterCondition.getDateTimeRange(value);
			if (range) return `(${this.oDataPropertyPath} ${FilterOperator.greaterOrEqual} ${this.toODataValue(range.start)}${FilterLogicalOperator.and}${this.oDataPropertyPath} ${FilterOperator.lessOrEqual} ${this.toODataValue(range.end)})`;
		}
		return `${this.oDataPropertyPath} ${FilterOperator.equal} ${this.toODataValue(value)}`;
	}
	toODataNotEqualFilter(viewModel) {
		const value = this.firstValue;
		if (this.isPropertyTypeOfDate(viewModel)) {
			const range = FilterCondition.getDateTimeRange(value);
			if (range) return `(${this.oDataPropertyPath} ${FilterOperator.less} ${this.toODataValue(range.start)}${FilterLogicalOperator.or}${this.oDataPropertyPath} ${FilterOperator.greater} ${this.toODataValue(range.end)})`;
		}
		return `${this.oDataPropertyPath} ${FilterOperator.notEqual} ${this.toODataValue(value)}`;
	}
	toODataInOrNotInFilter(not = false) {
		const values = this.values.flat();
		if (values.length === 1) return `${this.oDataPropertyPath} ${not ? FilterOperator.notEqual : FilterOperator.equal} ${this.toODataValue(values[0])}`;
		const condition = `${this.oDataPropertyPath} in ${this.toODataValue(values)}`;
		return not ? `${condition} eq false` : condition;
	}
	toODataBetweenFilter(viewModel) {
		let startValue = this.firstValue;
		let endValue = this.values.length < 2 ? null : this.values[1];
		if (this.isPropertyTypeOfDate(viewModel)) {
			startValue = FilterCondition.getStartDateTime(startValue);
			endValue = FilterCondition.getEndDateTime(endValue);
		}
		if (this.shouldSwapBetweenValues(startValue, endValue)) [startValue, endValue] = [endValue, startValue];
		const clauses = [];
		if (startValue != null) clauses.push(`${this.oDataPropertyPath} ${FilterOperator.greaterOrEqual} ${this.toODataValue(startValue)}`);
		if (endValue != null) clauses.push(`${this.oDataPropertyPath} ${FilterOperator.lessOrEqual} ${this.toODataValue(endValue)}`);
		return clauses.join(FilterLogicalOperator.and);
	}
	shouldSwapBetweenValues(firstValue, secondValue) {
		if (firstValue != null && secondValue != null) {
			firstValue = FilterCondition.tryConvertFilterValue(firstValue);
			secondValue = FilterCondition.tryConvertFilterValue(secondValue);
			if (typeof firstValue === "number" && typeof secondValue === "number") return firstValue > secondValue;
			if (typeof firstValue === "string" && typeof secondValue === "string") return compareDateOnly(firstValue, secondValue) > 0;
			if (firstValue instanceof Date && secondValue instanceof Date) return compareDate(firstValue, secondValue) > 0;
		}
		return false;
	}
	toODataValue(value) {
		if (Array.isArray(value)) return toODataValue(value.map((v) => v instanceof FilterValue ? v.toODataValue() : v));
		if (value instanceof FilterValue) return value.toODataValue();
		return toODataValue(value);
	}
	evaluateItem(item, options) {
		if (this.isEmpty) return true;
		if (this.filterOperator === FilterOperator.isMatch) return this.evaluateValueWithMatchOperator(item, options);
		const value = getPropertyByPath(item, this.propertyPath);
		if (this.filterOperator === FilterOperator.isNotNull) return value != null && value !== "";
		if (this.filterOperator === FilterOperator.isNull) return value == null || value === "";
		if (this.isPropertyTypeOf(DataType.date, options)) return this.evaluateDateOnlyValue(value, options);
		return this.evaluateValue(value, options);
	}
	evaluateValue(value, options) {
		switch (this.filterOperator) {
			case FilterOperator.equal: {
				const firstValue = this.values.flat()[0];
				if (this.isExpressionFilterValue(value, firstValue, options)) return value.startsWith("=");
				if (value instanceof Date && this.isPropertyTypeOfDate(options)) {
					const range = FilterCondition.getDateTimeRange(this.firstValue);
					return range && value >= range.start && value < range.end;
				}
				return isEquivalentValues(value, firstValue);
			}
			case FilterOperator.notEqual: {
				const firstValue = this.values.flat()[0];
				if (this.isExpressionFilterValue(value, firstValue, options)) return !value.startsWith("=");
				if (value instanceof Date && this.isPropertyTypeOfDate(options)) {
					const range = FilterCondition.getDateTimeRange(this.firstValue);
					return range && (value < range.start || value >= range.end);
				}
				return !isEquivalentValues(value, firstValue);
			}
			case FilterOperator.contains: return this.evaluateStringValue(value, (v, v1) => v.includes(v1));
			case FilterOperator.startsWith: return this.evaluateStringValue(value, (v, v1) => v.startsWith(v1));
			case FilterOperator.endsWith: return this.evaluateStringValue(value, (v, v1) => v.endsWith(v1));
			case FilterOperator.in: return this.evaluateValueWithInOperator(value, (v) => v instanceof Date ? v.getTime() : v, options);
			case FilterOperator.notIn: return !this.evaluateValueWithInOperator(value, (v) => v instanceof Date ? v.getTime() : v, options);
			case FilterOperator.greater: return this.evaluateNumberOrDateValue(value, (v, v1) => v > v1, this.isPropertyTypeOf(DataType.dateTime, options) ? (v1, v2) => [FilterCondition.getEndDateTime(v1), v2] : void 0);
			case FilterOperator.greaterOrEqual: return this.evaluateNumberOrDateValue(value, (v, v1) => v >= v1, this.isPropertyTypeOf(DataType.dateTime, options) ? (v1, v2) => [FilterCondition.getStartDateTime(v1), v2] : void 0);
			case FilterOperator.less: return this.evaluateNumberOrDateValue(value, (v, v1) => v < v1, this.isPropertyTypeOf(DataType.dateTime, options) ? (v1, v2) => [FilterCondition.getStartDateTime(v1), v2] : void 0);
			case FilterOperator.lessOrEqual: return this.evaluateNumberOrDateValue(value, (v, v1) => v <= v1, this.isPropertyTypeOf(DataType.dateTime, options) ? (v1, v2) => [FilterCondition.getEndDateTime(v1), v2] : void 0);
			default: return this.evaluateNumberOrDateValue(value, FilterCondition.getBetweenFilterOperatorFn, this.isPropertyTypeOf(DataType.dateTime, options) ? (v1, v2) => [FilterCondition.getStartDateTime(v1), FilterCondition.getEndDateTime(v2)] : void 0);
		}
	}
	evaluateStringValue(value, fn) {
		const firstValue = this.values[0];
		if (typeof value === "string" && typeof firstValue === "string") return fn(value.toUpperCase(), firstValue.toUpperCase());
		return false;
	}
	evaluateNumberOrDateValue(value, fn, transformConditionValues) {
		let [firstValue, secondValue] = this.values.flat();
		if (transformConditionValues) [firstValue, secondValue] = transformConditionValues(firstValue, secondValue);
		if (typeof value === "number" && typeof firstValue === "number" && (secondValue == null || typeof secondValue === "number")) return fn(value, firstValue, secondValue);
		value = FilterCondition.tryConvertToDateTime(value);
		firstValue = FilterCondition.tryConvertToDateTime(FilterCondition.tryConvertFilterValue(firstValue));
		secondValue = FilterCondition.tryConvertToDateTime(FilterCondition.tryConvertFilterValue(secondValue));
		if (value instanceof Date && firstValue instanceof Date && (secondValue == null || secondValue instanceof Date)) return fn(value, firstValue, secondValue);
		return false;
	}
	evaluateDateOnlyValue(value, options) {
		switch (this.filterOperator) {
			case FilterOperator.equal: return FilterCondition.tryConvertToDateOnly(value) === FilterCondition.tryConvertToDateOnly(FilterCondition.tryConvertFilterValue(this.values[0]));
			case FilterOperator.notEqual: return FilterCondition.tryConvertToDateOnly(value) !== FilterCondition.tryConvertToDateOnly(FilterCondition.tryConvertFilterValue(this.values[0]));
			case FilterOperator.greater: return this.evaluateDateValue(value, (v, v1) => v > v1);
			case FilterOperator.greaterOrEqual: return this.evaluateDateValue(value, (v, v1) => v >= v1);
			case FilterOperator.less: return this.evaluateDateValue(value, (v, v1) => v < v1);
			case FilterOperator.lessOrEqual: return this.evaluateDateValue(value, (v, v1) => v <= v1);
			case FilterOperator.in: return this.evaluateValueWithInOperator(value, FilterCondition.tryConvertToDateOnly, options);
			case FilterOperator.notIn: return !this.evaluateValueWithInOperator(value, FilterCondition.tryConvertToDateOnly, options);
			case FilterOperator.between: return this.evaluateDateValue(value, FilterCondition.getBetweenFilterOperatorFn);
		}
		return false;
	}
	evaluateValueWithMatchOperator(item, options) {
		if (options && options.matchFn) {
			const firstValue = this.values[0];
			if (typeof firstValue === "string") return options.matchFn(item, firstValue);
		}
		return false;
	}
	evaluateValueWithInOperator(value, mapFn, options) {
		value = mapFn(value);
		return this.values.flat().some((filterValue) => {
			filterValue = mapFn(filterValue);
			return value === filterValue || this.isExpressionFilterValue(value, filterValue, options) && value.startsWith("=");
		});
	}
	evaluateDateValue(value, fn) {
		value = FilterCondition.tryConvertToDateTime(value);
		const values = this.values.flat();
		const firstValue = FilterCondition.tryConvertToDateTime(FilterCondition.tryConvertFilterValue(values[0]));
		const secondValue = FilterCondition.tryConvertToDateTime(FilterCondition.tryConvertFilterValue(values[1]));
		if (value instanceof Date && firstValue instanceof Date && (secondValue == null || secondValue instanceof Date)) return fn(value, firstValue, secondValue);
		return false;
	}
	static findProperty(viewModel, propertyPath) {
		return viewModel?.getProperties().find((p) => p.isFilterPath(propertyPath));
	}
	isPropertyTypeOf(dataType, optionsOrViewModel) {
		return this.getProperty(optionsOrViewModel)?.getFilterDataType() === dataType;
	}
	isPropertyTypeOfDate(optionsOrViewModel) {
		const dataType = this.getProperty(optionsOrViewModel)?.getFilterDataType();
		return dataType === DataType.date || dataType === DataType.dateTime || dataType === DataType.invariantDateTime;
	}
	getProperty(optionsOrViewModel) {
		const viewModel = optionsOrViewModel instanceof ViewModel ? optionsOrViewModel : optionsOrViewModel?.viewModel;
		return FilterCondition.findProperty(viewModel, this.propertyPath);
	}
	isExpressionCodeProperty(options) {
		return FilterCondition.findProperty(options?.viewModel, this.propertyPath)?.isExpressionCode ?? false;
	}
	isExpressionFilterValue(value, filterValue, options) {
		return typeof value === "string" && this.isExpressionCodeProperty(options) && filterValue === "=";
	}
	static getBetweenFilterOperatorFn(v, v1, v2) {
		if (v2 != null) {
			if (v2 < v1) [v1, v2] = [v2, v1];
			return v >= v1 && v <= v2;
		}
		return v >= v1;
	}
	static tryConvertFilterValue(value) {
		return value instanceof FilterValue ? value.toValue() : value;
	}
	static tryConvertToDateOnly(value) {
		return value instanceof Date ? dateTimeToDateOnly(value) : value;
	}
	static tryConvertToDateTime(value) {
		if (typeof value === "string") {
			const date = dateOnlyToDateTime(value);
			if (!Number.isNaN(date.getTime())) return date;
		}
		return value;
	}
	static convertStartDateTime(v) {
		if (v instanceof Date) {
			const start = new Date(v);
			start.setSeconds(0);
			start.setMilliseconds(0);
			return start;
		}
		if (typeof v === "string") return dateOnlyToDateTime(v);
		return v;
	}
	static convertEndDateTime(v) {
		if (v instanceof Date) {
			const end = new Date(v);
			end.setSeconds(59);
			end.setMilliseconds(999);
			return end;
		}
		if (typeof v === "string") {
			const end = new Date(dateOnlyToDateTime(v));
			end.setHours(23);
			end.setMinutes(59);
			end.setSeconds(59);
			end.setMilliseconds(999);
			return end;
		}
		return v;
	}
	static getStartDateTime(value) {
		if (value instanceof FilterValue) {
			if (value.operation === FilterOperation.specificMonth) return FilterCondition.convertStartDateTime(value.toStartOfDateValue("month"));
			if (value.operation === FilterOperation.specificYear) return FilterCondition.convertStartDateTime(value.toStartOfDateValue("year"));
		}
		return FilterCondition.convertStartDateTime(FilterCondition.tryConvertFilterValue(value));
	}
	static getEndDateTime(value) {
		if (value instanceof FilterValue) {
			if (value.operation === FilterOperation.specificMonth) return FilterCondition.convertEndDateTime(value.toEndOfDateValue("month"));
			if (value.operation === FilterOperation.specificYear) return FilterCondition.convertEndDateTime(value.toEndOfDateValue("year"));
		}
		return FilterCondition.convertEndDateTime(FilterCondition.tryConvertFilterValue(value));
	}
	static getDateTimeRange(value) {
		if (value instanceof FilterValue && (value.operation === FilterOperation.specificMonth || value.operation === FilterOperation.specificYear)) {
			const unit = value.operation === FilterOperation.specificMonth ? "month" : "year";
			return {
				start: FilterCondition.convertStartDateTime(value.toStartOfDateValue(unit)),
				end: FilterCondition.convertEndDateTime(value.toEndOfDateValue(unit))
			};
		}
		value = FilterCondition.tryConvertFilterValue(value);
		if (value instanceof Date) {
			const start = new Date(value);
			start.setSeconds(0);
			start.setMilliseconds(0);
			const end = new Date(start);
			end.setSeconds(59);
			end.setMilliseconds(999);
			return {
				start,
				end
			};
		}
		if (typeof value === "string") {
			const start = dateOnlyToDateTime(value);
			const end = new Date(start);
			end.setHours(23);
			end.setMinutes(59);
			end.setSeconds(59);
			end.setMilliseconds(999);
			return {
				start,
				end
			};
		}
	}
};
//#endregion
//#region src/filter/Filter.ts
var Filter = class Filter {
	constructor(condition = null, logicalOperator = FilterLogicalOperator.and, ...children) {
		this.children = [];
		this._readOnly = false;
		this.condition = condition;
		this.logicalOperator = logicalOperator;
		this.children = children;
	}
	static create(propertyPath, operator, ...values) {
		return new Filter(new FilterCondition(propertyPath, operator, ...values));
	}
	static createComplexFilter(propertyPath, operator, oDataPrefix, oDataSuffix, ...values) {
		return new Filter(FilterCondition.create(propertyPath, operator, oDataPrefix, oDataSuffix, ...values));
	}
	static createFromChildren(...children) {
		return new Filter(null, FilterLogicalOperator.and, ...children);
	}
	get isEmpty() {
		return this.condition ? this.condition.isEmpty : this.children.length === 0;
	}
	get isCondition() {
		return this.condition !== null;
	}
	get isSimple() {
		if (this.namedFilterId) return true;
		if (this.logicalOperator !== FilterLogicalOperator.and) return false;
		const properties = /* @__PURE__ */ new Set();
		for (const child of this.children) {
			if (!child.isSimple) return false;
			if (child.namedFilterId) continue;
			if (child.condition) {
				if (properties.has(child.condition.propertyPath)) return false;
				properties.add(child.condition.propertyPath);
			}
		}
		return true;
	}
	get readOnly() {
		return this.condition?.readOnly ?? this._readOnly;
	}
	set readOnly(value) {
		if (this.condition) this.condition.readOnly = value;
		else this._readOnly = value;
	}
	add(filter, logicalOperator = this.logicalOperator) {
		if (filter.namedFilterId) this.children.push(filter);
		else if (this.isEmpty) {
			this.children = [...filter.children];
			this.logicalOperator = filter.isCondition ? logicalOperator : filter.logicalOperator;
			this.condition = filter.condition;
		} else {
			if (this.logicalOperator !== logicalOperator || this.isCondition) this.expand(logicalOperator);
			this.children.push(filter);
		}
		return this;
	}
	remove(filter) {
		if (this.equals(filter)) {
			this.clear();
			return true;
		}
		let childrenRemoved = false;
		const childArray = this.children.filter((c) => !c.equals(filter));
		if (childArray.length !== this.children.length) {
			this.children = childArray;
			childrenRemoved = true;
		}
		for (const subChild of this.children) {
			const subChildRemoved = subChild.remove(filter);
			if (!childrenRemoved && subChildRemoved) childrenRemoved = subChildRemoved;
		}
		return childrenRemoved;
	}
	removeByName(propertyPath, onlyReadWrite = false) {
		const filter = this.find(propertyPath);
		if (filter && (!onlyReadWrite || !filter.readOnly)) return this.remove(filter);
		return false;
	}
	clear() {
		this.children.length = 0;
		this.condition = null;
		delete this.namedFilterId;
	}
	toString(viewModel) {
		return this.toODataString(viewModel);
	}
	toODataString(viewModel, hasParent = false) {
		if (this.condition) return this.condition.toODataString(viewModel);
		const stringFilter = this.children.map((filter) => filter.toODataString(viewModel, true)).filter((s) => s !== "").join(this.logicalOperator);
		if (stringFilter && hasParent) return `(${stringFilter})`;
		return stringFilter;
	}
	toText(culture, outputToHtml, viewModel, hasParent = false) {
		if (this.condition) return this.condition.toText(culture, outputToHtml, viewModel);
		const globalResourceManager = getGlobalResourceManager(void 0);
		const filterText = this.children.map((filter) => filter.toText(culture, outputToHtml, viewModel, true)).filter((f) => f !== "").join(` ${this.logicalOperator === FilterLogicalOperator.and ? globalResourceManager.get("And").toLocaleLowerCase() : globalResourceManager.get("Or").toLocaleLowerCase()} `);
		if (filterText && hasParent) return `(${filterText})`;
		return filterText;
	}
	/**
	* Finds a filter by property path recursively.
	* @param propertyPath - Property path.
	* @param includeNamedFilter - Whether to include named filters in the search.
	* @returns The found filter or undefined if not found.
	*/
	find(propertyPath, includeNamedFilter = false) {
		if (!includeNamedFilter && this.namedFilterId) return;
		if (Filter.comparePropertyPath(this, propertyPath)) return this;
		for (const childFilter of this.children) {
			const child = childFilter.find(propertyPath, includeNamedFilter);
			if (child) return child;
		}
	}
	getConditionsCount() {
		if (this.isEmpty) return 0;
		if (this.condition) return 1;
		return this.children.reduce((count, child) => count + child.getConditionsCount(), 0);
	}
	equals(filter) {
		if (this === filter) return true;
		if (!this.condition && filter.condition) return false;
		if (this.condition && (!filter.condition || !this.condition.equals(filter.condition))) return false;
		return this.logicalOperator === filter.logicalOperator && this.children.length === filter.children.length && this.namedFilterId === filter.namedFilterId && filter.children.every((f, i) => f.equals(this.children[i]));
	}
	clone() {
		const filter = new Filter(this.condition?.clone(), this.logicalOperator, ...this.children.map((c) => c.clone()));
		filter.readOnly = this.readOnly;
		if (this.namedFilterId) filter.namedFilterId = this.namedFilterId;
		return filter;
	}
	extractReadOnlyFilter() {
		if (this.readOnly) return this.clone();
		if (this.children.length > 0) {
			const children = this.children.map((c) => c.extractReadOnlyFilter()).filter((c) => !c.isEmpty);
			if (children.length > 0) return new Filter(void 0, this.logicalOperator, ...children);
		}
		return new Filter();
	}
	/**
	* Expands a filter.
	* This filter becomes a child.
	* @param logicalOperator - The logical operator.
	*/
	expand(logicalOperator) {
		const childNode = this.clone();
		this.clear();
		this.children.push(childNode);
		if (logicalOperator) this.logicalOperator = logicalOperator;
	}
	simplify() {
		if (this.condition?.isEmpty) this.condition = null;
		this.children = this.children.map((f) => f.simplify()).filter((f) => !f.isEmpty);
		this.flattenChildren();
		this.removeDuplicates();
		this.flattenSingleChild();
		return this;
	}
	flattenChildren() {
		if (this.children.length > 0) {
			const flattenedChildren = [];
			for (const child of this.children) if (!child.condition && !child.namedFilterId && child.logicalOperator === this.logicalOperator) flattenedChildren.push(...child.children);
			else flattenedChildren.push(child);
			this.children = flattenedChildren;
		}
	}
	removeDuplicates() {
		if (this.children.length > 1) {
			const uniqueChildren = [];
			for (const child of this.children) if (!uniqueChildren.some((existing) => existing.equals(child))) uniqueChildren.push(child);
			this.children = uniqueChildren;
		}
	}
	flattenSingleChild() {
		if (!this.condition && this.children.length === 1) {
			const onlyChild = this.children[0];
			this.condition = onlyChild.condition;
			this.logicalOperator = onlyChild.logicalOperator;
			this.children = onlyChild.children;
			this.namedFilterId = onlyChild.namedFilterId;
			this.readOnly = onlyChild.readOnly;
		}
	}
	toSimplifiedFilter() {
		return this.clone().simplify();
	}
	renamePropertyPaths(newPropertyPaths) {
		if (this.condition) {
			const newPropertyPath = newPropertyPaths.get(this.condition.propertyPath);
			if (newPropertyPath) this.condition.propertyPath = newPropertyPath;
		}
		for (const child of this.children) child.renamePropertyPaths(newPropertyPaths);
		return this;
	}
	static comparePropertyPath(filter, propertyPath) {
		return filter.condition?.propertyPath.toUpperCase() === propertyPath.toUpperCase();
	}
	/**
	* Creates a filter from a JSON string.
	* @param json - JSON string.
	* @param viewModel - View model.
	* @returns A filter object.
	*/
	static async fromJSON(json, viewModel) {
		return await Filter.fromJSONInternal(json, viewModel) ?? new Filter();
	}
	static async fromJSONInternal(json, viewModel) {
		const raw = typeof json === "string" ? JSON.parse(json, defaultJsonReviver) : json;
		let filter;
		if (raw.namedFilterId) filter = await this.getNamedFilter(raw.namedFilterId, viewModel);
		else {
			filter = new Filter();
			if (raw.logicalOperator === "or") filter.logicalOperator = FilterLogicalOperator.or;
			if (raw.condition) filter.condition = await FilterCondition.fromJSON(raw.condition, viewModel);
			if (raw.children) {
				const children = await Promise.all(raw.children.map((f) => Filter.fromJSONInternal(f, viewModel)));
				filter.children.push(...children.filter((f) => f !== void 0));
			}
		}
		if (!filter || filter.isEmpty) return;
		filter._readOnly = raw._readOnly === true;
		return filter;
	}
	/**
	* Converts a filter to a JSON string.
	* @returns a JSON string.
	*/
	convertToJSON() {
		const simplifyNamedFilter = (filter) => ({
			namedFilterId: filter.namedFilterId,
			readOnly: filter.readOnly || void 0
		});
		if (this.namedFilterId) return JSON.stringify(simplifyNamedFilter(this));
		return JSON.stringify(this, (key, value) => {
			if (value === null || Array.isArray(value) && value.length === 0) return;
			if (key === "logicalOperator") {
				if (value === FilterLogicalOperator.and) return;
				return "or";
			}
			if (key === "operation" && value === FilterOperation.none) return;
			if (key === "filterFunction" && value === FilterFunction.none) return;
			if ((key === "readOnly" || key === "_readOnly") && !value) return;
			if (key === "condition" && value.customOperatorId) {
				const condition = value;
				return {
					propertyPath: condition.propertyPath,
					customOperatorId: condition.customOperatorId,
					readOnly: this.readOnly
				};
			}
			if (key === "children" && Array.isArray(value)) return value.map((child) => child.namedFilterId ? simplifyNamedFilter(child) : child);
			if (key === "values" && Array.isArray(value)) return value.map((v) => v instanceof InvariantDateTime ? toIso(v, false) : v);
			return value;
		});
	}
	static async getNamedFilter(namedFilterId, viewModel) {
		const namedFilter = viewModel?.namedFilters.find((f) => f.id === namedFilterId);
		if (!namedFilter) return null;
		return namedFilter.getFilter();
	}
	evaluate(items, options) {
		return items.filter((item) => this.evaluateItem(item, options));
	}
	evaluateItem(item, options) {
		let result = this.condition ? this.condition.evaluateItem(item, options) : this.logicalOperator === FilterLogicalOperator.and;
		for (const child of this.children) {
			const childResult = child.evaluateItem(item, options);
			if (this.logicalOperator === FilterLogicalOperator.and) result &&= childResult;
			else result ||= childResult;
		}
		return result;
	}
};
//#endregion
//#region src/model/entityViewPropertyValue.ts
var cache = new LRUCache({
	max: 50,
	ttl: 1e3 * 60 * 2
});
async function getEntityViewPropertyValueAsync(apiClient, entityViewName, searchedProperty, filterProperty, filterValue) {
	if (filterValue == null) return {
		isValid: true,
		value: null
	};
	const key = `${entityViewName}.${searchedProperty}.${filterProperty}.${toODataValue(filterValue)}`;
	let result = cache.get(key);
	if (result) return result;
	const filter = new Filter(new FilterCondition(filterProperty, FilterOperator.equal, filterValue));
	const response = await apiClient.getAllAsync({
		path: entityViewName,
		filter: filter.toODataString(),
		top: 1
	});
	if (response.succeeded && response.data.length === 1) {
		const reference = response.data[0];
		result = {
			isValid: true,
			value: getPropertyByPath(reference, searchedProperty),
			reference
		};
	} else result = { isValid: false };
	cache.set(key, result);
	return result;
}
//#endregion
//#region src/model/Model.ts
var rawProperty = "$raw";
var trackedSystemProperties = new Set(["$selected"]);
/**
* Represent an EntityView model.
*/
var Model = class Model {
	/**
	* Gets the methods for invalidating the value properties.
	*/
	get $propertyValueInvalidations() {
		return this.constructor.$propertyValueInvalidations;
	}
	markAsVirtual() {
		this.$virtual = true;
		this.$originalValues.clear();
	}
	get isVirtual() {
		return this.$virtual && !this.hasChanges();
	}
	/**
	* Gets a value indicating whether the item has changes.
	*/
	get $hasChanges() {
		return this.$originalValues.size > 0;
	}
	/**
	* Gets a value indicating whether the item has errors.
	*/
	get $hasErrors() {
		if (this.$persistenceError || this.hasValidationMessages(ValidationRuleSeverityLevel.error)) return true;
		for (const [relation] of this.$relations) if (this.getRelationList(relation).hasErrors) return true;
		return false;
	}
	/**
	* Gets a value indicating whether the item has persistence errors.
	*/
	get $hasPersistenceErrors() {
		if (this.$persistenceError) return true;
		for (const [relation] of this.$relations) if (this.getRelationList(relation).hasPersistenceErrors) return true;
		return false;
	}
	/**
	* Gets the dictionnary of relations. Key is the property name and value is the relation.
	*/
	get $relations() {
		return this.constructor.$relations;
	}
	static {
		this.$relations = /* @__PURE__ */ new Map();
	}
	/**
	* A value indicating whether to track changes.
	*/
	get $trackChanges() {
		return this.$suspendTrackingStack === 0;
	}
	/**
	* Gets the properties.
	*/
	get $properties() {
		return this.constructor.$properties;
	}
	static {
		this.$properties = /* @__PURE__ */ new Map();
	}
	/**
	* Gets the key properties.
	*/
	get $keyProperties() {
		return this.constructor.$keyProperties;
	}
	static {
		this.$keyProperties = /* @__PURE__ */ new Set();
	}
	/**
	* Gets the unbound properties.
	*/
	get $unboundProperties() {
		return this.constructor.$unboundProperties;
	}
	static {
		this.$unboundProperties = /* @__PURE__ */ new Set();
	}
	/**
	* Gets the untracked properties.
	*/
	get $untrackedProperties() {
		return this.constructor.$untrackedProperties;
	}
	static {
		this.$untrackedProperties = /* @__PURE__ */ new Set();
	}
	/**
	* Create a new instance of the item.
	*/
	constructor(data, viewModel) {
		this.$runningAdditionalDataInvalidations = /* @__PURE__ */ new Map();
		this.$suspendedPropertyValueInvalidations = /* @__PURE__ */ new Set();
		this.$new = true;
		this.$virtual = false;
		this.$selected = false;
		this.$validationMessages = /* @__PURE__ */ new Map();
		this.$propertyChangedEvent = createLocalEvent();
		this.$propertyMetadataChangedEvent = createLocalEvent();
		this.$referenceLists = /* @__PURE__ */ new Map();
		this.$originalValues = /* @__PURE__ */ new Map();
		this.$invalidValues = /* @__PURE__ */ new Map();
		this.$modifiedProperties = /* @__PURE__ */ new Set();
		this.$appliedSuggestions = /* @__PURE__ */ new Map();
		this.$suspendTrackingStack = 0;
		this.$propertyChangedHandlerOnReference = /* @__PURE__ */ new WeakMap();
		this.$viewModel = viewModel;
		return new Proxy(this.getInstance(data), {
			get(target, property, receiver) {
				if (property === rawProperty) return target;
				if (target.$relations.has(property)) receiver.getRelationList(property);
				return Reflect.get(target, property, receiver);
			},
			set(target, property, value, receiver) {
				let result;
				if (receiver.isTrackedProperty(property)) {
					const oldValue = target[property];
					value = receiver.fixNewPropertyValue(property, value);
					result = Reflect.set(target, property, value, receiver);
					const deletedInvalidValue = receiver.$invalidValues.delete(property);
					if (!receiver.isEquivalentValues(oldValue, value, void 0)) {
						receiver.onPropertyChanged(property, oldValue, value);
						receiver.invalidateValueProperty(property);
					} else if (deletedInvalidValue) receiver.onInvalidValueChanged(property);
				} else result = Reflect.set(target, property, value, receiver);
				return result;
			}
		});
	}
	getInstance(data) {
		if (data) data = this.fixPropertyValues(data);
		Object.assign(this, data);
		for (const [relationName] of this.$relations) this.getRelationList(relationName);
		if (data) {
			this.$new = data.$new ?? false;
			if (!this.$new) this.$key = this.getKey();
		}
		return this;
	}
	fixPropertyValues(data) {
		for (const [propertyName, property] of this.$properties) if (property.isComputed) delete data[propertyName];
		else this.fixPropertyValue(data, propertyName, property);
		return data;
	}
	fixNewPropertyValue(propertyName, value) {
		if (value == null) return value;
		const property = this.$properties.get(propertyName);
		if (!property) return value;
		if (!property.dataType) {
			const prefix = `${propertyName}.`;
			for (const [propertyName, property] of this.$properties) if (propertyName.startsWith(prefix)) this.fixPropertyValue(value, propertyName.substring(prefix.length), property);
		}
		return value;
	}
	fixPropertyValue(data, propertyName, property) {
		return Model.fixPropertyValue(data, propertyName, property, this.$viewModel?.apiClient ?? (() => getApiClient(void 0)));
	}
	static fixPropertyValue(data, propertyName, property, apiClient) {
		if (property.dataType == DataType.dateTime || property.dataType == DataType.invariantDateTime) {
			const value = getPropertyByPath(data, propertyName);
			if (typeof value === "string") setPropertyByPath(data, propertyName, new Date(value));
		} else if (property.dataType == DataType.date) {
			const value = getPropertyByPath(data, propertyName);
			if (typeof value === "string") setPropertyByPath(data, propertyName, Model.isoDateStringToDate(value));
		} else if (property.dataType == DataType.image || property.dataType == DataType.file) {
			const value = getPropertyByPath(data, propertyName);
			if (typeof value === "string") {
				const apiClientInstance = typeof apiClient === "function" ? apiClient() : apiClient;
				setPropertyByPath(data, propertyName, FileReference.fromValue(apiClientInstance, value));
			}
		}
		return data;
	}
	static isoDateStringToDate(value) {
		const date = value.split("T")[0].split("-");
		return new Date(Number(date[0]), Number(date[1]) - 1, Number(date[2]));
	}
	onPropertyChanged(propertyName, oldValue, value) {
		this.$modifiedProperties.add(propertyName);
		if (!Model.isInternalProperty(propertyName)) {
			this.updateOriginalValue(propertyName, oldValue, value);
			if (this.isVirtual) this.$validationMessages.clear();
			this.togglePropertyChangedHandlerOnReference(propertyName, value, oldValue);
		}
		this.$propertyChangedEvent.raise({
			item: this,
			propertyName,
			oldValue,
			value,
			selectedLookupItem: this.popSelectedLookupItem(propertyName)
		});
	}
	addPropertyChangedHandlerOnReference(reference, property) {
		const propertyChangedHandler = (args) => {
			args = {
				...args,
				item: this,
				propertyName: `${property}.${args.propertyName}`
			};
			this.$propertyChangedEvent.raise(args);
		};
		this.$propertyChangedHandlerOnReference.set(reference, propertyChangedHandler);
		reference.$propertyChangedEvent.add(propertyChangedHandler);
	}
	removePropertyChangedHandlerOnReference(reference) {
		const handler = this.$propertyChangedHandlerOnReference.get(reference);
		reference.$propertyChangedEvent.remove(handler);
		this.$propertyChangedHandlerOnReference.delete(reference);
	}
	togglePropertyChangedHandlerOnReference(property, reference, oldReference) {
		const relation = this.$relations.get(property);
		if (relation && relation.type === RelationType.editableReference) {
			if (oldReference instanceof Model) this.removePropertyChangedHandlerOnReference(oldReference);
			if (reference instanceof Model) this.addPropertyChangedHandlerOnReference(reference, property);
		}
	}
	updateOriginalValue(propertyName, oldValue, value) {
		if (this.$unboundProperties.has(propertyName) || this.$viewModel?.dataSharingManager.isSharingDatasource && !this.$viewModel.dataSharingManager.isSharedProperty(propertyName)) return;
		if (!this.hasOriginalValue(propertyName) && !this.isEquivalentValues(value, oldValue, propertyName)) this.setOriginalValue(propertyName, oldValue);
		else if (this.isEquivalentValues(this.getOriginalValue(propertyName), value, propertyName)) this.$originalValues.delete(propertyName);
	}
	popSelectedLookupItem(propertyName) {
		let selectedLookupItem;
		if (this.$selectedLookupItem?.propertyName === propertyName) {
			selectedLookupItem = this.$selectedLookupItem.value ?? void 0;
			this.$selectedLookupItem = void 0;
		}
		return selectedLookupItem;
	}
	/**
	* Gets the list of items of a relation. If the relation list is not defined, it creates it.
	* @param relationName - Relation name.
	* @returns - The observable list of collection or the observable list containing the reference.
	*/
	getRelationList(relationName) {
		const relation = this.$relations.get(relationName);
		return relation.type === RelationType.collection ? this.getOrCreateCollectionList(relationName, relation) : this.getOrCreateReferenceList(relationName, relation);
	}
	getOrCreateCollectionList(relationName, relation) {
		const raw = this.toRaw();
		let property;
		if (relation.embeddedForReading && relation.path.includes(".")) {
			property = getPropertyByPath(raw, relation.path);
			if (property instanceof ObservableList) {
				raw[relationName] = property;
				return property;
			}
		}
		property = raw[relationName];
		if (property instanceof ObservableList) {
			property.setOwner(this, relation.referencePropertyName);
			return property;
		}
		const collectionList = raw.createCollectionList(relationName, relation);
		collectionList.setOwner(this, relation.referencePropertyName);
		raw[relationName] = collectionList;
		return collectionList;
	}
	createCollectionList(relationName, relation) {
		const property = this.getRawRelationItem(relation);
		const subViewModel = this.$viewModel?.findSubViewModel(relationName);
		const collectionList = new ObservableList(...Array.isArray(property) ? property.map((item) => new relation.creator(item, subViewModel)) : []);
		if (subViewModel) collectionList.setWrapInstance((item) => {
			item.$viewModel ??= subViewModel;
			return item;
		});
		return collectionList;
	}
	getOrCreateReferenceList(relationName, relation) {
		const raw = this.toRaw();
		let referenceList = raw.$referenceLists.get(relationName);
		if (!referenceList) {
			referenceList = new ObservableList();
			raw.$referenceLists.set(relationName, referenceList);
		}
		const reference = raw.getOrCreateReference(relationName, relation);
		if (reference && !referenceList.includes(reference)) referenceList.setItems([reference]);
		referenceList.setOwner(this, relation.referencePropertyName);
		return referenceList;
	}
	getOrCreateReference(relationName, relation) {
		const reference = this[relationName];
		const subViewModel = this.$viewModel?.findSubViewModel(relationName);
		let newReference;
		if (reference instanceof Model) {
			if (reference.$viewModel === subViewModel) return reference;
			this.removePropertyChangedHandlerOnReference(reference);
			newReference = reference.clone(false);
			newReference.toRaw().$viewModel = subViewModel;
		} else {
			const property = this.getRawRelationItem(relation);
			if (!property) return;
			newReference = new relation.creator(property, subViewModel);
		}
		this[relationName] = newReference;
		this.addPropertyChangedHandlerOnReference(newReference, relationName);
		return newReference;
	}
	getRawRelationItem(relation) {
		return getPropertyByPath(this, relation.entityViewPath ?? relation.path);
	}
	/**
	* Gets a value indicating whether the specified property is a relation.
	* @param propertyName - The name of the property in camel case.
	* @returns
	*/
	isRelationScalarProperty(propertyName) {
		for (const relation of this.$relations) if (relation[1].associations.includes(propertyName)) return true;
		return false;
	}
	/**
	* Clones the item.
	* The internal properties are ignored.
	* The cloned item is considered as new.
	* The relations are also cloned.
	* @param asNew - Forces the cloned item to be new.
	* @returns Cloned item.
	*/
	clone(asNew = true) {
		const data = { ...this };
		for (const property of Object.keys(data)) {
			if (property === "$additionalData" && data.$additionalData !== void 0) {
				data.$additionalData = { ...data.$additionalData };
				continue;
			}
			if (Model.isInternalProperty(property) && property !== "$virtual") delete data[property];
		}
		data.$new ||= asNew;
		const creator = this.constructor;
		const clonedItem = new creator(data, this.$viewModel);
		for (const [relationName, { referencePropertyName }] of this.$relations) {
			const relation = clonedItem[relationName];
			if (!relation) continue;
			if (relation instanceof ObservableList) {
				const clonedRelation = relation.clone(asNew);
				clonedItem.toRaw()[relationName] = clonedRelation;
				clonedRelation.setOwner(clonedItem, referencePropertyName);
				continue;
			}
			clonedItem.toRaw()[relationName] = relation.clone(false);
		}
		return clonedItem;
	}
	/**
	* Transforms the item into a simple object to be passed to the API request data.
	* The internal and unbound properties are removed as well as non-embedded relations.
	* The embedded relations are also transformed.
	* @param references - References to be removed to avoid circular references.
	* @param excludeCollectionsWithoutChanges - A value indicating whether to exclude collections without changes.
	* @returns Cloned item.
	*/
	toApiRequestData(excludeCollectionsWithoutChanges, references) {
		references = references ? new Set([...references, this]) : new Set([this]);
		const data = { ...this };
		for (const [relationName, relation] of this.$relations) {
			if (!relation.embeddedForWriting) {
				delete data[relationName];
				continue;
			}
			const property = data[relationName];
			if (!property) continue;
			let propertyName = relationName;
			if (relation.entityViewPath) {
				propertyName = relation.entityViewPath;
				delete data[relationName];
			}
			if (property instanceof Model) data[propertyName] = property.toApiRequestData(excludeCollectionsWithoutChanges, references);
			if (property instanceof ObservableList) Model.transformEmbeddedCollection(data, propertyName, property, relationName, relation, references, excludeCollectionsWithoutChanges && !this.hasAnyKeyPropertyChanged());
		}
		this.transformObject(data, this.$properties, references, excludeCollectionsWithoutChanges);
		return data;
	}
	static transformEmbeddedCollection(data, propertyName, property, relationName, relation, references, excludeCollectionsWithoutChanges) {
		if (excludeCollectionsWithoutChanges && !property.hasChanges(true)) delete data[relationName];
		else data[propertyName] = property.transformCollectionToApiData(excludeCollectionsWithoutChanges, references, relation.referencePropertyName);
	}
	transformObject(data, properties, references, excludeCollectionsWithoutChanges) {
		for (const property of Object.keys(data).filter((p) => !this.$relations.has(p))) this.transformPropertyToApiRequestData(data, properties, references, excludeCollectionsWithoutChanges, property);
	}
	/**
	* Transforms the property to be passed to the API request data.
	*/
	transformPropertyToApiRequestData(data, properties, references, excludeCollectionsWithoutChanges, propertyName) {
		const value = data[propertyName];
		if (this.isPropertyExcludedFromApiRequest(propertyName, references, value, properties)) {
			delete data[propertyName];
			return;
		}
		const property = properties.get(propertyName);
		if (this.transformDateProperty(data, propertyName, value, property)) return;
		if (value instanceof FileReference) data[propertyName] = value.value;
		else if (value instanceof Model) data[propertyName] = value.toApiRequestData(excludeCollectionsWithoutChanges, references);
		else if (value instanceof ObservableList) data[propertyName] = value.transformCollectionToApiData(excludeCollectionsWithoutChanges, references, propertyName);
		else if (value instanceof Object) this.transformObjectPropertyToApiRequestData(data, propertyName, value, property, properties, references, excludeCollectionsWithoutChanges);
	}
	transformDateProperty(data, propertyName, value, property) {
		if (property?.dataType === DataType.date && value instanceof Date) {
			data[propertyName] = formatDateTime(value, "yyyy-MM-dd");
			return true;
		}
		if (property?.dataType === DataType.invariantDateTime && value instanceof Date) {
			data[propertyName] = toIso(value, false);
			return true;
		}
		return false;
	}
	transformObjectPropertyToApiRequestData(data, propertyName, reference, property, properties, references, excludeCollectionsWithoutChanges) {
		if (property?.referenceEntityViewScalarKeyProperties) {
			const keyData = this.createKeyDataFromReference(reference, property.referenceEntityViewScalarKeyProperties);
			data[propertyName] = keyData;
			reference = keyData;
		}
		this.transformObject(reference, Model.getObjectProperties(properties, propertyName), references, excludeCollectionsWithoutChanges);
	}
	createKeyDataFromReference(reference, referenceEntityViewScalarKeyProperties) {
		const keyData = {};
		for (const key of referenceEntityViewScalarKeyProperties) {
			const value = getPropertyByPath(reference, key);
			const keys = key.split(".");
			let currentLevel = keyData;
			for (let i = 0; i < keys.length - 1; i++) {
				const k = keys[i];
				if (!currentLevel[k]) currentLevel[k] = {};
				currentLevel = currentLevel[k];
			}
			currentLevel[keys[keys.length - 1]] = value;
		}
		return keyData;
	}
	static getObjectProperties(properties, propertyName) {
		const prefix = `${propertyName}.`;
		const objectProperties = /* @__PURE__ */ new Map();
		for (const [key, value] of properties) if (key.startsWith(prefix)) objectProperties.set(key.substring(prefix.length), value);
		return objectProperties;
	}
	isPropertyExcludedFromApiRequest(property, references, value, properties) {
		if (property === "$transientKey") return false;
		let removeProperty = Model.isInternalProperty(property);
		removeProperty ||= references.has(value);
		removeProperty ||= this.$keyProperties.has(property) && value == null;
		removeProperty ||= this.$unboundProperties.has(property);
		removeProperty ||= !!properties.get(property)?.serverSideReadOnly && !this.$keyProperties.has(property);
		return removeProperty;
	}
	/**
	* Gets a value indicating whether the item has changes.
	* Returns true if it is a new element, if a property has changed or if a relation has changes.
	* @param excludeNonEmbeddedRelations - A value indicating whether to exclude non-embedded relations from the check.
	* @returns A value indicating whether the item has changes.
	*/
	hasChanges(excludeNonEmbeddedRelations = false) {
		if (this.$new && !this.$virtual || this.$hasChanges) return true;
		for (const [relation, { embeddedForWriting }] of this.$relations) if ((!excludeNonEmbeddedRelations || embeddedForWriting) && this.getRelationList(relation).hasChanges(excludeNonEmbeddedRelations)) return true;
		return false;
	}
	/**
	* Gets the raw item.
	**/
	toRaw(unProxyVueJs = false) {
		let proxy = this;
		if (unProxyVueJs && proxy.__v_raw) proxy = proxy.__v_raw;
		return proxy[rawProperty] ?? proxy;
	}
	/**
	* Gets a value indicating whether the specified property has a original value
	* @param propertyName - name of property (pascal case)
	*/
	hasOriginalValue(propertyName) {
		return this.$originalValues.has(propertyName);
	}
	/**
	* Gets the original value of a specified property.
	* @param propertyName - name of property (camel case)
	* @returns - original value if the property has been changed
	*/
	getOriginalValue(propertyName) {
		return this.$originalValues.get(propertyName);
	}
	/**
	* Gets the original or current value of a specified property.
	* @param propertyName - name of property (camel case)
	* @returns - original value if the property has been changed, current value otherwise
	*/
	getOriginalOrCurrentValue(propertyName) {
		if (this.hasOriginalValue(propertyName)) return this.$originalValues.get(propertyName);
		return this[propertyName];
	}
	/**
	* Sets the original value of a specified property.
	* @param propertyName - name of property (camel case)
	* @param value - original value
	*/
	setOriginalValue(propertyName, value) {
		this.$originalValues.set(propertyName, value);
	}
	/**
	* Accepts all changes.
	* Current changes are accepted. Original values can no longer be restored.
	* A new item is no longer new.
	* A virtual item is no longer virtual.
	*/
	acceptChanges() {
		this.$new = false;
		this.$virtual = false;
		this.$key = this.getKey();
		this.$originalValues.clear();
		this.$invalidValues.clear();
		for (const [relationName] of this.$relations) {
			const relation = this[relationName];
			if (relation) relation.acceptChanges();
		}
	}
	/**
	* Rejects all changes.
	* Original values are restored.
	*/
	rejectChanges() {
		this.suspendChangeTracking();
		try {
			for (const [propertyName, value] of this.$originalValues) this[propertyName] = value;
		} finally {
			this.resumeChangeTracking();
		}
		this.$originalValues.clear();
		this.$invalidValues.clear();
		for (const [relationName] of this.$relations) {
			const relation = this[relationName];
			if (relation) relation.rejectChanges();
		}
	}
	/**
	* Gets the key of the item with the current values.
	* @returns The key of the item.
	*/
	getKey() {
		return this.constructor.getKeyFromData(this);
	}
	static getKeyFromData(data) {
		return new Map(Array.from(this.$keyProperties, (keyProperty) => [keyProperty, getPropertyByPath(data, keyProperty)]));
	}
	updateKey() {
		this.$key = this.getKey();
	}
	/**
	* Gets the values of the key items.
	* @returns The values of the key items.
	*/
	getKeyValues() {
		if (this.$key) {
			const key = this.$key;
			return Array.from(key.keys()).map((k) => this.$originalValues.get(k) ?? key.get(k));
		}
		return [];
	}
	/**
	* Suspends the change tracking.
	*/
	suspendChangeTracking() {
		this.$suspendTrackingStack++;
	}
	/**
	* Resumes the change tracking.
	* @param forceResume - A value indicating whether to force the resume of the change tracking.
	* @returns A value indicating whether the change tracking has been resumed.
	*/
	resumeChangeTracking(forceResume = false) {
		if (forceResume) {
			this.$suspendTrackingStack = 0;
			return true;
		}
		if (this.$suspendTrackingStack > 0) this.$suspendTrackingStack--;
		return this.$suspendTrackingStack === 0;
	}
	/**
	* Gets an additional data.
	* If an invalidation is running, we wait for it to finish before returning it.
	* @param key - Key.
	* @returns Value.
	*/
	async getAdditionalDataAsync(key) {
		const promise = this.$runningAdditionalDataInvalidations.get(key);
		if (promise) await promise;
		return this.getAdditionalData(key);
	}
	/**
	* Gets an additional data.
	* @param key - Key.
	* @returns Value.
	*/
	getAdditionalData(key) {
		return this.$additionalData?.[key];
	}
	/**
	* Sets additional data related to the value that has been set to a property.
	* @param key - Key for storing additional data for the property.
	* @param getValueToStorePromise - Method for getting the value to store in additional data.
	*/
	setAdditionalDataAsync(key, getValueToStorePromise) {
		if (!this.$suspendedPropertyValueInvalidations.has(key)) {
			const execute = (async () => {
				await getValueToStorePromise.then((valueToStore) => this.$additionalData = {
					...this.$additionalData,
					[key]: valueToStore
				}).catch(() => void 0);
				this.$runningAdditionalDataInvalidations.delete(key);
			})();
			this.$runningAdditionalDataInvalidations.set(key, execute);
			return execute;
		}
	}
	/**
	* Checks whether the value set to a property with lookup is valid.
	* @param propertyName Name of the property.
	* @param value Value set to the property.
	* @param getValueToDisplayPromise Method for getting the value to display in the lookup.
	* The value to display can either be the display value or the value depending on whether the lookup has a display value configured.
	*/
	async invalidateLookupValueAsync(propertyName, value, getValueToDisplayPromise) {
		if (!(await getValueToDisplayPromise).isValid) {
			this.suspendPropertyValueInvalidations(propertyName);
			this.$invalidValues.set(propertyName, `${value}`);
			this.resumePropertyValueInvalidations(propertyName);
		}
	}
	/**
	* Invalidates the value of a property.
	* @param property - Property.
	*/
	async invalidateValueProperty(property, invalidateLookupValue = true) {
		await this.$propertyValueInvalidations?.[property]?.(this, invalidateLookupValue);
	}
	/**
	* Gets a value indicating whether there is a running additional data invalidation.
	* @param key - Key.
	*/
	hasRunningAdditionalDataInvalidation(key) {
		return this.$runningAdditionalDataInvalidations.has(key);
	}
	/**
	* Suspends the execution of a property value invalidation.
	* @param key - Key.
	*/
	suspendPropertyValueInvalidations(key) {
		this.$suspendedPropertyValueInvalidations.add(key);
	}
	/**
	* Resumes the execution of a property value invalidation.
	* @param key - Key.
	*/
	resumePropertyValueInvalidations(key) {
		this.$suspendedPropertyValueInvalidations.delete(key);
	}
	/**
	* Awaits the running additional data invalidations.
	*/
	async awaitRunningAdditionalDataInvalidations() {
		await Promise.all(this.getRunningAdditionalDataInvalidations());
	}
	/**
	* Gets the running additional data invalidations of self and child items.
	*/
	getRunningAdditionalDataInvalidations() {
		const promises = [...this.$runningAdditionalDataInvalidations.values()];
		for (const [relationName] of this.$relations) for (const item of this.getRelationList(relationName)) promises.push(...item.getRunningAdditionalDataInvalidations());
		return promises;
	}
	/**
	* Gets the value of a property of an element returned by the entity view of the lookup of a property.
	* @param propertyWithLookup - Property that has the lookup for which the value of a property of one the items returned by its entity view is searched.
	* @param searchedProperty - Property that is searched in items returned by the entity view of the lookup.
	* @param filterProperty - Properties of the lookup used to filter the items returned by the entity view of the lookup.
	* @param filterValue - Values used to filter the items returned by the entity view of the lookup.
	* @returns An object containing a value indicating whether the value is valid and the value when valid.
	*/
	async getLookupItemPropertyValueAsync(propertyWithLookup, searchedProperty, filterProperty, filterValue) {
		if (this.$viewModel == null) return {
			isValid: true,
			value: null
		};
		const viewModelProperty = this.getViewModelPropertyWithLookup(propertyWithLookup);
		return getEntityViewPropertyValueAsync(this.$viewModel.apiClient, viewModelProperty.lookupDefinition.searchEntityViewName, searchedProperty, filterProperty, filterValue);
	}
	/**
	* Gets a value indicating whether the item has validation messages with the specified severity level.
	*
	* @param severityLevel - Severity level.
	* @returns True if the item has validation errors; otherwise, false.
	*/
	hasValidationMessages(severityLevel) {
		for (const [, validationMessages] of this.$validationMessages) if (validationMessages.some((vm) => vm.severityLevel === severityLevel)) return true;
		return false;
	}
	/**
	* Gets the validation messages with the specified severity level.
	* @param severityLevel - Severity level.
	* @returns The validation messages.
	*/
	getValidationMessages(severityLevel) {
		const filteredValidationMessages = [];
		for (const [, validationMessages] of this.$validationMessages) for (const validationMessage of validationMessages) if (severityLevel === void 0 || validationMessage.severityLevel === severityLevel) filteredValidationMessages.push(validationMessage);
		return filteredValidationMessages;
	}
	setValue(propertyName, value) {
		this[propertyName] = value;
	}
	getValidationMessage(propertyName) {
		const messages = this.$validationMessages.get(propertyName);
		if (!messages) return;
		return messages.find((m) => m.severityLevel === ValidationRuleSeverityLevel.error) ?? messages.find((m) => m.severityLevel === ValidationRuleSeverityLevel.warning) ?? messages.find((m) => m.severityLevel === ValidationRuleSeverityLevel.information);
	}
	addValidationMessage(validationMessage) {
		const messages = this.$validationMessages.get(validationMessage.propertyName);
		if (messages) messages.push(validationMessage);
		else this.$validationMessages.set(validationMessage.propertyName, [validationMessage]);
		this.raisePropertyMetadataChangedEvent(validationMessage.propertyName);
	}
	replaceValidationMessage(validationMessage) {
		const messages = this.$validationMessages.get(validationMessage.propertyName);
		if (messages) {
			let existingMessageIndex;
			if (validationMessage.kind === "Constraint") existingMessageIndex = messages.findIndex((m) => m.kind === "Constraint");
			else if (ValidationRuleValidationMessage.is(validationMessage)) existingMessageIndex = messages.findIndex((m) => ValidationRuleValidationMessage.is(m) && m.name === validationMessage.name);
			else existingMessageIndex = -1;
			if (existingMessageIndex === -1) messages.push(validationMessage);
			else messages[existingMessageIndex] = validationMessage;
		} else this.$validationMessages.set(validationMessage.propertyName, [validationMessage]);
		this.raisePropertyMetadataChangedEvent(validationMessage.propertyName);
	}
	removeValidationMessage(validationMessage) {
		this.removeValidationMessageByPredicate(validationMessage.propertyName, (m) => m === validationMessage);
	}
	removeValidationMessageByPredicate(propertyName, predicate) {
		const messages = this.$validationMessages.get(propertyName);
		if (!messages) return;
		const existingMessageIndex = messages.findIndex(predicate);
		if (existingMessageIndex === -1) return;
		messages.splice(existingMessageIndex, 1);
		if (messages.length === 0) this.$validationMessages.delete(propertyName);
		this.raisePropertyMetadataChangedEvent(propertyName);
	}
	clearPropertyValidationMessages(propertyName) {
		if (this.$validationMessages.delete(propertyName)) this.raisePropertyMetadataChangedEvent(propertyName);
	}
	isEquivalentValues(value1, value2, propertyName) {
		if (value1 instanceof Model || value2 instanceof Model) return value1 === value2;
		if (value1 instanceof ObservableList || value2 instanceof ObservableList) return value1 === value2;
		if (propertyName && isObject(value1) && isObject(value2)) {
			const property = this.$properties.get(propertyName);
			if (property?.referenceEntityViewScalarKeyProperties) return property.referenceEntityViewScalarKeyProperties.every((keyProperty) => isEquivalentValues(getPropertyByPath(value1, keyProperty), getPropertyByPath(value2, keyProperty)));
		}
		return isEquivalentValues(value1, value2);
	}
	/**
	* Gets the invalid value of the specified property.
	* @param property - Property name.
	* @returns Value indicating whether the property value is invalid.
	*/
	getInvalidValue(property) {
		return this.$invalidValues.get(property) ?? null;
	}
	/**
	* Sets the invalid value for the specified property.
	* @param property - Property name.
	* @param value - Invalid value.
	*/
	setInvalidValue(property, value) {
		if (value) {
			this.$invalidValues.set(property, value);
			this.onInvalidValueChanged(property, value);
		} else if (this.$invalidValues.delete(property)) this.onInvalidValueChanged(property);
	}
	onInvalidValueChanged(property, value) {
		const propertyValue = this[property];
		this.suspendChangeTracking();
		try {
			this.onPropertyChanged(property, propertyValue, value ?? propertyValue);
		} finally {
			this.resumeChangeTracking();
		}
	}
	popRelationSelectionState(relationName) {
		if (!this.$relationSelectionStates) return;
		const state = this.$relationSelectionStates.get(relationName);
		if (this.$relationSelectionStates.delete(relationName) && this.$relationSelectionStates.size === 0) this.$relationSelectionStates = void 0;
		return state;
	}
	/**
	* Gets the value displayed in the lookup of a property.
	* @param propertyWithLookup Property with lookup.
	* @returns Value displayed in the lookup. This corresponds to the display property when one is configured on the lookup; otherwise the value of the property.
	* @throws Error if the property does not have a lookup.
	*/
	async getLookupDisplayValueAsync(propertyWithLookup) {
		const propertyName = toCamelCase(propertyWithLookup);
		if (this.getViewModelPropertyWithLookup(propertyName).lookupDefinition.displayProperty == null) return this[propertyName];
		const additionalDataKey = `${propertyWithLookup}_DisplayProperty`;
		return await this.getAdditionalDataAsync(additionalDataKey);
	}
	/**
	* Tries to set the value of a property with lookup.
	* If `value` is not part of the valid values of the lookup, the value of `propertyWithLookup` is not changed and the method returns `false`.
	* `displayValue` is ignored when `propertyWithLookup` is an embedded reference.
	* @param propertyWithLookup Property with lookup.
	* @param value Value to try to affect to the property.
	* @param displayValue Display value to pass to the lookup. This prevents the lookup from making an API request to get the display value corresponding to `value`.
	* @returns `true` when `value` is valid and has been changed; otherwise `false`.
	* @throws Error if the property does not have a lookup.
	*/
	async trySetLookupValueAsync(propertyWithLookup, value, displayValue) {
		const propertyName = toCamelCase(propertyWithLookup);
		const viewModelProperty = this.getViewModelPropertyWithLookup(propertyName);
		const getValueToDisplayInLookupResult = await getEntityViewPropertyValueAsync(this.$viewModel.apiClient, viewModelProperty.lookupDefinition.searchEntityViewName, viewModelProperty.lookupDefinition.displayProperty?.camelCaseName ?? "", viewModelProperty.lookupDefinition.valueProperty.camelCaseName, value);
		if (!getValueToDisplayInLookupResult.isValid) return false;
		this.suspendPropertyValueInvalidations(propertyName);
		this.$selectedLookupItem = {
			propertyName,
			value: new viewModelProperty.lookupDefinition.creator(getValueToDisplayInLookupResult.reference)
		};
		this[propertyName] = value;
		const valueToDisplay = displayValue ?? getValueToDisplayInLookupResult.value;
		if (valueToDisplay != null) {
			const additionalDataKey = `${propertyWithLookup}_DisplayProperty`;
			await this.setAdditionalDataAsync(additionalDataKey, Promise.resolve(valueToDisplay));
		}
		this.resumePropertyValueInvalidations(propertyName);
		return true;
	}
	/**
	* Tries to set the value of a property with lookup by setting its display value.
	* If `displayValue` is not part of the valid values of the lookup, the value of `propertyWithLookup` is not changed and the method returns `false`; however, `displayValue` is still displayed.
	* @param propertyWithLookup Property with lookup.
	* @param displayValue Display value to set in the lookup.
	* @returns `true` when `displayValue` is valid and the value of the property has been changed; otherwise `false`.
	* @throws Error if the property does not have a lookup.
	*/
	async trySetLookupValueFromDisplayValueAsync(propertyWithLookup, displayValue) {
		const propertyName = toCamelCase(propertyWithLookup);
		const viewModelProperty = this.getViewModelPropertyWithLookup(propertyName);
		if (viewModelProperty.lookupDefinition.displayProperty == null) return false;
		const getValueToSetPropertyResult = await getEntityViewPropertyValueAsync(this.$viewModel.apiClient, viewModelProperty.lookupDefinition.searchEntityViewName, viewModelProperty.lookupDefinition.valueProperty.camelCaseName, viewModelProperty.lookupDefinition.displayProperty.camelCaseName, displayValue);
		if (!getValueToSetPropertyResult.isValid) {
			this.invalidateLookupValueAsync(propertyName, displayValue, Promise.resolve(getValueToSetPropertyResult));
			return false;
		}
		this.suspendPropertyValueInvalidations(propertyName);
		this[propertyName] = getValueToSetPropertyResult.value;
		const additionalDataKey = `${propertyWithLookup}_DisplayProperty`;
		await this.setAdditionalDataAsync(additionalDataKey, Promise.resolve(displayValue));
		this.resumePropertyValueInvalidations(propertyName);
		return true;
	}
	/**
	* Gets a property that has a lookup.
	* @param propertyWithLookup Property name.
	* @returns Property.
	* @throws Error if the property does not have a lookup.
	*/
	getViewModelPropertyWithLookup(propertyWithLookupInCamelCase) {
		const property = this.$viewModel?.properties.find((p) => p.camelCaseName === propertyWithLookupInCamelCase && p.lookupDefinition != null);
		if (property == null) throw new Error(`Property "${propertyWithLookupInCamelCase}" does not have a lookup.`);
		return property;
	}
	/**
	* Returns true if at least one key property has changed (i.e. figure in $originalValues).
	*/
	hasAnyKeyPropertyChanged() {
		for (const key of this.$keyProperties) if (this.$originalValues.has(key)) return true;
		return false;
	}
	static isInternalProperty(propertyName) {
		return propertyName.startsWith("$");
	}
	isTrackedProperty(propertyName) {
		if (this.$untrackedProperties.has(propertyName)) return false;
		if (Model.isInternalProperty(propertyName)) return trackedSystemProperties.has(propertyName);
		return true;
	}
	raisePropertyMetadataChangedEvent(propertyName) {
		if (propertyName) this.$propertyMetadataChangedEvent.raise({
			item: this,
			propertyName
		});
	}
};
//#endregion
//#region src/api/FetchError.ts
var FetchError = class extends Error {
	constructor(request, message, innerError) {
		super(message);
		this.request = request;
		this.innerError = innerError;
	}
};
//#endregion
//#region src/api/FileSaverWrapper.ts
/* istanbul ignore file -- @preserve */
/**
* Wrapper class for mock in unit tests
*/
var FileSaverWrapper = class {
	static saveFile(data, filename) {
		fileSaver.saveAs(data, filename, { autoBom: false });
	}
};
//#endregion
//#region src/api/FetchApiClient.ts
/**
* API client with fetch implementation.
*/
var FetchApiClient = class FetchApiClient {
	constructor(baseUrl, applicationContext, eventManager, globalResourceManager) {
		this.jsonReviver = defaultJsonReviver;
		this._headers = /* @__PURE__ */ new Map();
		this.baseUrl = baseUrl;
		this.applicationContext = applicationContext;
		this.eventManager = eventManager;
		this.globalResourceManager = globalResourceManager;
	}
	setAcceptLanguageHeader(culture) {
		this.setHeader("Accept-Language", culture);
	}
	/** {@inheritdoc ApiClient.callDeleteServerMethodAsync} */
	async callDeleteServerMethodAsync(route, queryOrRouteData) {
		const response = await this.deleteAsync({
			path: route,
			queryOrRouteData
		});
		if (response.succeeded) return { succeeded: true };
		throw new ServerMethodError(response.error);
	}
	/** {@inheritdoc ApiClient.callGetServerMethodAsync} */
	async callGetServerMethodAsync(route, queryOrRouteData) {
		const response = await this.getAsync({
			path: route,
			queryOrRouteData
		}, defaultJsonReviver);
		if (response.succeeded) return response.data;
		throw new ServerMethodError(response.error);
	}
	/** {@inheritdoc ApiClient.callPostServerMethodAsync} */
	async callPostServerMethodAsync(route, apiBinding, bodyData, queryOrRouteData, rawBody, signal) {
		const response = await this.postAsync({
			path: route,
			apiBinding,
			data: rawBody ? bodyData : FetchApiClient.toApiRequestData(bodyData),
			queryOrRouteData
		}, defaultJsonReviver, signal);
		if (response.succeeded) return response.data;
		throw new ServerMethodError(response.error);
	}
	/** {@inheritdoc ApiClient.callPutServerMethodAsync} */
	async callPutServerMethodAsync(route, apiBinding, bodyData, queryOrRouteData) {
		const response = await this.putAsync({
			path: route,
			apiBinding,
			data: FetchApiClient.toApiRequestData(bodyData),
			queryOrRouteData
		}, defaultJsonReviver);
		if (response.succeeded) return response.data;
		throw new ServerMethodError(response.error);
	}
	/** {@inheritdoc ApiClient.getAllAsync} */
	async getAllAsync(request) {
		const url = this.getUrl(request.path, { queryOrRouteData: request.queryOrRouteData });
		this.setGetAllParams(url, request);
		const headers = this.getHeaders();
		if (request.includeTotalCount === false) headers["neos-include-total-count"] = "false";
		const req = new Request(url, {
			method: "get",
			headers
		});
		const res = await this.fetchWithTimeout(req, request.timeout, request.signal);
		if (res.ok) {
			const responseText = await res.text();
			const data = responseText.length > 0 ? this.parseJson(req, responseText) : [];
			const totalRecordCountHeader = res.headers.get("neos-total-record-count");
			return {
				succeeded: true,
				data,
				hasMoreData: res.status === 206,
				totalRecordCount: totalRecordCountHeader === null ? void 0 : Number(totalRecordCountHeader)
			};
		}
		return {
			succeeded: false,
			error: await FetchApiClient.parseError(req, res)
		};
	}
	/** {@inheritdoc ApiClient.getAsync} */
	async getAsync(request, jsonReviver) {
		const url = this.getUrl(request.path, {
			id: request.id,
			queryOrRouteData: request.queryOrRouteData
		});
		const req = new Request(url, {
			method: "get",
			headers: this.getHeaders()
		});
		const res = await this.fetchWithTimeout(req, request.timeout, request.signal);
		if (res.ok) {
			const responseText = await res.text();
			return {
				succeeded: true,
				data: responseText.length > 0 ? this.parseJson(req, responseText, jsonReviver) : void 0
			};
		}
		return {
			succeeded: false,
			error: await FetchApiClient.parseError(req, res)
		};
	}
	/** {@inheritdoc ApiClient.deleteAsync} */
	async deleteAsync(request) {
		const url = this.getUrl(request.path, {
			id: request.id,
			queryOrRouteData: request.queryOrRouteData
		});
		const req = new Request(url, {
			method: "delete",
			headers: this.getHeaders()
		});
		const res = await this.fetchWithTimeout(req, request.timeout);
		if (res.status === 204) return { succeeded: true };
		return {
			succeeded: false,
			error: await FetchApiClient.parseError(req, res)
		};
	}
	static async parseError(request, response) {
		const contentType = response.headers.get("content-type");
		if (contentType?.includes("application/json") === true || contentType?.includes("application/problem+json") === true) try {
			const error = JSON.parse(await response.text(), (key, value) => {
				if (key === "extensions" && value) return new Map(Object.entries(value));
				return value;
			});
			error.path ??= response.url;
			return error;
		} catch (error) {
			throw new FetchError(request, getGlobalResourceManager(void 0).get("FetchError_InvalidResponse"), error);
		}
		throw new FetchError(request, getGlobalResourceManager(void 0).get("FetchError_UnexpectedResponse"));
	}
	async postAsync(request, jsonReviver, signal) {
		const url = this.getUrl(request.path, { queryOrRouteData: request.queryOrRouteData });
		const req = new Request(url, this.getRequestInitWithBody("post", request.apiBinding, request.data));
		const res = await this.fetchWithTimeout(req, request.timeout, signal);
		return this.getResponseWithBody(req, res, jsonReviver);
	}
	async putAsync(request, jsonReviver) {
		const url = this.getUrl(request.path, {
			id: request.id,
			queryOrRouteData: request.queryOrRouteData
		});
		const req = new Request(url, this.getRequestInitWithBody("put", request.apiBinding, request.data));
		const res = await this.fetchWithTimeout(req, request.timeout);
		return this.getResponseWithBody(req, res, jsonReviver);
	}
	async getResponseWithBody(request, response, jsonReviver) {
		if (response.ok) {
			const responseText = await response.text();
			return {
				succeeded: true,
				data: responseText.length > 0 ? this.parseJson(request, responseText, jsonReviver) : void 0
			};
		}
		return {
			succeeded: false,
			error: await FetchApiClient.parseError(request, response)
		};
	}
	/** {@inheritdoc ApiClient.downloadFileAsync} */
	async downloadFileAsync(path) {
		const url = this.resolveUrl(path);
		const request = new Request(url, { headers: this.getHeaders() });
		const response = await this.fetchWithTimeout(request);
		if (!response.ok) {
			const apiError = await FetchApiClient.parseError(request, response);
			this.eventManager.emit("viewModel:showError", ViewModelError.fromApiError(this.globalResourceManager, apiError));
			return;
		}
		const filename = FetchApiClient.getFilename(response.headers);
		if (filename) {
			const blob = await response.blob();
			FileSaverWrapper.saveFile(blob, filename);
		} else throw new Error(this.globalResourceManager.get("DownloadFileResponseMustContainsExpectedHeader"));
	}
	/** {@inheritdoc ApiClient.getByteArrayAsync} */
	async getByteArrayAsync(path) {
		const url = this.resolveUrl(path);
		const request = new Request(url, { headers: this.getHeaders() });
		const response = await this.fetchWithTimeout(request);
		if (!response.ok) throw new Error(await response.text());
		const arrayBuffer = await response.arrayBuffer();
		const array = new Uint8Array(arrayBuffer);
		return Array.from(array);
	}
	static getFilename(headers) {
		const contentDispositionParts = headers.get("content-disposition")?.split(";").map((n) => n.trimStart());
		let prefix = "filename*=UTF-8''";
		const filenameStar = contentDispositionParts?.find((n) => n.startsWith(prefix))?.substring(prefix.length);
		if (filenameStar) return decodeURIComponent(filenameStar);
		prefix = "filename=";
		return contentDispositionParts?.find((n) => n.startsWith(prefix))?.substring(prefix.length);
	}
	static getDefaultTimeout(timeout = 30) {
		return navigator.webdriver ? timeout * 1e4 : timeout * 1e3;
	}
	/** {@inheritdoc ApiClient.printFileAsync} */
	async printFileAsync(path) {
		const url = this.resolveUrl(path);
		const request = new Request(url, { headers: this.getHeaders() });
		const response = await this.fetchWithTimeout(request);
		if (!response.ok) {
			const apiError = await FetchApiClient.parseError(request, response);
			this.eventManager.emit("viewModel:showError", ViewModelError.fromApiError(this.globalResourceManager, apiError));
			return;
		}
		const blob = await response.blob();
		const blobUrl = URL.createObjectURL(blob);
		const iFrame = await this.createIFrameForPrintingAsync(blobUrl);
		iFrame.addEventListener("load", () => {
			iFrame.focus();
			iFrame.contentWindow.print();
		});
	}
	/**
	* Creates a hidden iFrame for printing a file.
	* The file is loaded in the hidden iFrame.
	* Then a print command can be executed on the iFrame content to display the print dialog.
	* @param blobUrl URL of the blobl of the file to print.
	*/
	async createIFrameForPrintingAsync(blobUrl) {
		const printFrameId = "printFileIFrame";
		let iFrame = document.getElementById(printFrameId);
		if (iFrame) iFrame.remove();
		iFrame = document.createElement("iframe");
		iFrame.style.display = "none";
		iFrame.id = printFrameId;
		iFrame.src = blobUrl;
		document.body.prepend(iFrame);
		return iFrame;
	}
	async fetchWithTimeout(request, timeout, signal) {
		return FetchApiClient.fetchWithTimeout(request, timeout ?? FetchApiClient.getDefaultTimeout(this.applicationContext.webApiTimeout), signal);
	}
	static async fetchWithTimeout(request, timeout, signal) {
		const controller = new AbortController();
		let isAborted = false;
		if (signal?.aborted) {
			isAborted = true;
			controller.abort();
		} else if (signal) signal.addEventListener("abort", () => {
			isAborted = true;
			controller.abort();
		}, { once: true });
		const timeoutId = setTimeout(() => {
			controller.abort(new FetchError(request, getGlobalResourceManager(void 0).get("FetchError_Timeout")));
		}, timeout);
		try {
			return await fetch(request, { signal: controller.signal });
		} catch (error) {
			if (error instanceof FetchError) throw error;
			if (isAborted && error instanceof Error && error.name === "AbortError") throw error;
			throw new FetchError(request, getGlobalResourceManager(void 0).get("FetchError_NetworkError"));
		} finally {
			clearTimeout(timeoutId);
		}
	}
	/** Gets the id to insert in the route for entity view get/put/delete calls from key values */
	static getId(keyValues) {
		return keyValues.map((k) => typeof k === "string" ? encodeURIComponent(k) : k).join("/");
	}
	static getHeaders(applicationContext) {
		const headers = applicationContext.getAllApiContextValues();
		if (applicationContext.notificationConnectionId) headers["ConnectionId"] = applicationContext.notificationConnectionId;
		if ("isTauri" in window) headers["Neos-ClientAppType"] = "tauri";
		return headers;
	}
	getHeaders() {
		const headers = FetchApiClient.getHeaders(this.applicationContext);
		for (const [key, value] of this._headers) headers[key] = value;
		for (const [key, value] of Object.entries(headers)) if (key.toLowerCase().startsWith("neos-")) headers[key] = encodeURIComponent(value);
		return headers;
	}
	setHeader(name, value) {
		this._headers.set(name, value);
	}
	setGetAllParams(url, request) {
		if (request.transformations && request.transformations.length > 0) url.searchParams.set("$apply", request.transformations.filter(Boolean).join("/"));
		if (request.filter) url.searchParams.set("$filter", request.filter);
		const top = request.top ?? request.recordsByPage ?? 10;
		url.searchParams.set("$top", top.toString());
		const skip = this.getGetAllSkipParam(request, top);
		if (skip) url.searchParams.set("$skip", skip.toString());
		if (request.sorts && request.sorts.length > 0) url.searchParams.set("$orderby", request.sorts.map((d) => d.toODataString()).join(","));
		if (request.prompt) url.searchParams.set("$prompt", request.prompt);
	}
	getGetAllSkipParam(request, top) {
		if (request.skip) return request.skip;
		if (request.pageNumber) return (request.pageNumber - 1) * top;
	}
	getRequestInitWithBody(method, apiBinding, data) {
		if (data instanceof FormData) return {
			method,
			body: data,
			headers: this.getHeaders()
		};
		return {
			method,
			body: JSON.stringify(this.getBody(apiBinding, data), FetchApiClient.jsonReplacer),
			headers: {
				...this.getHeaders(),
				"Content-Type": "application/json"
			}
		};
	}
	resolveUrl(path) {
		try {
			return new URL(path);
		} catch {
			return this.getUrl(path);
		}
	}
	getUrl(path, options) {
		return FetchApiClient.getUrl(this.baseUrl, path, options);
	}
	static getUrl(baseUrl, path, options) {
		const searchParams = {};
		if (options?.queryOrRouteData) for (const key in options.queryOrRouteData) {
			const value = options.queryOrRouteData[key];
			const param = `{${key.toLowerCase()}}`;
			if (path.toLowerCase().includes(param)) path = path.replace(new RegExp(param, "i"), FetchApiClient.toStringValue(value));
			else searchParams[key] = value;
		}
		if (options?.id) path += `/${options.id}`;
		const url = new URL(path, `${baseUrl}/`);
		FetchApiClient.appendSearchParams(url, searchParams);
		return url;
	}
	static appendSearchParams(url, searchParams) {
		for (const key in searchParams) {
			const value = searchParams[key];
			if (Array.isArray(value)) value.forEach((v) => url.searchParams.append(key, v));
			else if (value != null) url.searchParams.append(key, FetchApiClient.toStringValue(value));
		}
	}
	getBody(apiBinding, data) {
		if (data) {
			if (!apiBinding) return data;
			const convertData = data;
			const keys = Object.keys(convertData);
			if (apiBinding === ApiParameterBinding.body && keys.length === 1) return convertData[keys[0]];
			return data;
		}
	}
	static toStringValue(value) {
		if (value instanceof Date) return value.toISOString();
		return value;
	}
	static jsonReplacer(_, value) {
		if (value instanceof Map) return Object.fromEntries(value.entries());
		return value;
	}
	parseJson(request, responseText, jsonReviver) {
		try {
			return JSON.parse(responseText, jsonReviver ?? this.jsonReviver);
		} catch (error) {
			throw new FetchError(request, this.globalResourceManager.get("FetchError_InvalidResponse"), error);
		}
	}
	/**
	* Transforms in a correct format data to pass in an API request.
	* @param data Data to transform.
	*/
	static toApiRequestData(data) {
		if (data instanceof Model) return data.toApiRequestData(false);
		if (data instanceof InvariantDateTime) return toIso(data, false);
		if (data && typeof data === "object") {
			const obj = data;
			for (const key of Object.keys(obj)) obj[key] = FetchApiClient.toApiRequestData(obj[key]);
		}
		return data;
	}
};
//#endregion
//#region src/snapshot/plugins/base.ts
function createSerializationPlugin(plugin) {
	return createPlugin({
		tag: plugin.tag,
		test(value) {
			return plugin.test(value);
		},
		parse: { sync(value, ctx) {
			return plugin.serialize(value, { serialize: ctx.parse.bind(ctx) });
		} },
		deserialize(node, ctx) {
			return plugin.deserialize(node, { deserialize: ctx.deserialize.bind(ctx) });
		},
		serialize() {
			throw new Error("Not implemented");
		}
	});
}
function getViewModelPath(viewModel) {
	const path = [];
	let currentViewModel = viewModel;
	while (currentViewModel.parentRelation && currentViewModel.parentViewModel) {
		path.unshift(currentViewModel.parentRelation.name);
		currentViewModel = currentViewModel.parentViewModel;
	}
	return path;
}
function getViewModelByPath(viewModel, path) {
	let currentViewModel = viewModel;
	for (const segment of path) currentViewModel = currentViewModel.getSubViewModel(segment);
	return currentViewModel;
}
//#endregion
//#region src/snapshot/plugins/custom.ts
var customPluginFn;
function registerCustomSerializationPlugins(fn) {
	customPluginFn = fn;
}
function loadCustomPlugins() {
	return customPluginFn?.() ?? [];
}
//#endregion
//#region src/sorting/ExpressionSort.ts
/**
* Represents a sort based on an OData expression.
*/
var ExpressionSort = class extends BaseSort {
	/**
	* Initializes a new instance of the ExpressionSort class.
	* @param expression - The OData expression.
	*/
	constructor(expression) {
		super();
		this._expression = expression;
	}
	/**
	* Gets the OData expression.
	* @returns The OData expression.
	*/
	get expression() {
		return this._expression;
	}
	/**
	* Gets the string representation of the sort
	* @returns The string representation of the sort.
	*/
	toString() {
		return this.expression;
	}
	/**
	* Converts the sort to an OData string.
	* @returns The OData string.
	*/
	toODataString() {
		return this.expression;
	}
};
//#endregion
export { SortingExecutionSide as $, moduleFilterComponentToCoreFilterComponentView as $t, ViewModelStandardAction as A, FilteringExecutionSide as At, LookupMainMode as B, enumOperators as Bt, FilterValue as C, getApiClientBaseUrl as Cn, SortDirection as Ct, ReferenceRetrievingTrigger as D, Group as Dt, isEnumMember as E, GroupDescription as Et, ViewModelActionLocationEntry as F, FilterOperator as Ft, getLookupPropertyFilter as G, isFilterOperatorWithoutValue as Gt, LookupTemplateMode as H, guidOperators as Ht, ViewModelActionLocation as I, allOperators as It, getLookupSearchProperty as J, moduleFilterOperatorToCoreFilterOperator as Jt, getLookupPropertyPath as K, isStandardFilterOperator as Kt, ViewModelActionDisplayMode as L, booleanOperators as Lt, ViewModelActionType as M, uiViewEvents as Mt, ViewModelActionStyle as N, ViewModelEntityViewParameters as Nt, ViewModelActions as O, FrameDisplayMode as Ot, ViewModelActionSize as P, ViewModelEntityViewParameter as Pt, SummaryResult as Q, FilterComponent as Qt, ViewModelActionConstraint as R, coreFilterOperatorToModuleFilterOperator as Rt, formatValues as S, getApiClient as Sn, ObservableList as St, ContentAlign as T, GroupingExecutionSide as Tt, getLookupDisplayProperty as U, isArrayFilterOperator as Ut, LookupSearchMode as V, getAllowedOperators as Vt, getLookupEditingUIViewId as W, isFilterOperatorOnLookupValueProperty as Wt, isLookupContainsSearchActive as X, numberOperators as Xt, isInputValueWithWildcards as Y, nullableOperators as Yt, GroupSummaryResult as Z, stringOperators as Zt, ViewModelPropertyBase as _, FileReference as _n, NavigationParameters as _t, getViewModelByPath as a, runWithEvent as an, Reporting as at, formatLookupScalarValueSync as b, ServerMethodError as bn, ListChangedType as bt, FetchError as c, ToggleOverlayOptions as cn, reactive as ct, FilterCondition as d, ConstraintValidationMessage as dn, writableComputed as dt, BaseComponentViewModel as en, Sort as et, FilterFunction as f, ValidationMessage as fn, NavigationResult as ft, ViewModelProperties as g, RelationType as gn, NavigationTarget as gt, ViewModel as h, ValidationRuleValidationTypes as hn, NavigationOptions as ht, createSerializationPlugin as i, navigate as in, PrivateBaseReportOptions as it, ViewModelActionBase as j, ServerDataExportOptions as jt, ExportFormat as k, FilterLogicalOperator as kt, Model as l, runWithGlobalMenuItemContext as ln, setReactivity as lt, StatusFilterMode as m, ValidationRuleSeverityLevel as mn, NavigationResultState as mt, loadCustomPlugins as n, ViewModelMessageButton as nn, ExecuteReportOptions as nt, getViewModelPath as o, showToast as on, computed as ot, DeletionMode as p, ValidationRuleValidationMessage as pn, NavigationResultWithoutValue as pt, getLookupSearchFilter as q, localizableStringOperators as qt, registerCustomSerializationPlugins as r, ViewModelError as rn, AbstractReportOptions as rt, FetchApiClient as s, toggleOverlay as sn, hasReactivity as st, ExpressionSort as t, ViewModelMessageButtonType as tn, BaseSort as tt, Filter as u, ApiErrorMessageOverrider as un, watch as ut, formatLookupReferenceValue as v, FileUploadState as vn, ServerGroup as vt, FilterOperation as w, registerApiClient as wn, GroupList as wt, formatValue as x, ApiParameterBinding as xn, ListLoadingState as xt, formatLookupScalarValue as y, ViewModelMessageType as yn, SummaryDescription as yt, ViewModelActionBadgeSeverity as z, dateOperators as zt };

//# sourceMappingURL=ExpressionSort-5Pa3W9aB.js.map